export const settingsTranslations = {
  en: {
    title: 'Settings',
    description: 'Manage your account settings and preferences.',
    menu: {
      dashboard: 'Dashboard',
      dashboardIBE: 'Dashboard IBE',
      analytics: 'Analytics',
      calendar: 'Calendar',
      clients: 'Clients',
      deals: 'Deals',
      invoices: 'Invoices',
      services: 'Services',
      projects: 'Projects',
      tasks: 'Tasks',
      documents: 'Documents',
      reports: 'Reports',
      whatsapp: 'WhatsApp',
      inventory: 'Inventory',
      tickets: 'Tickets',
      commissions: 'Commissions',
      fattureincloud: 'Fatture in Cloud',
      settings: 'Settings'
    },
    tabs: {
      company: 'Company',
      clients: 'Clients', 
      services: 'Services',
      'payment-methods': 'Payment Methods',
      api: 'API & Integrations',
      categories: 'Categories',
      'interest-categories': 'Interest Categories',
      menu: 'Menu',
      'quote-templates': 'Quote Templates',
      'deals': 'Deal Settings',
      'offers': 'Offers',
      'sales-targets': 'Sales Targets',
      profile: 'Profile',
      notifications: 'Notifications',
      users: 'Users & Roles',
      language: 'Language',
      search: 'Search',
      'import-export': 'Import / Export',
      'commissions': 'Commissions',
      'whatsapp-automation': 'WhatsApp Automation',
      'custom-states': 'Custom States & Sources',
      'google-workspace': 'Google Workspace',
      'permissions': 'Permissions',
      'client-columns': 'Client Columns', 
      'organization-categories': 'Organization Categories',
      'image-gallery': 'Image Gallery',
      'fattureincloud': 'Fatture in Cloud',
      'smtp': 'Email SMTP',
      'custom-fields': 'Campi Personalizzati'
    },
    company: {
      title: 'Company Settings',
      businessInfo: 'Business Information',
      businessName: 'Business Name',
      vatNumber: 'VAT Number',
      sdiCode: 'SDI Code',
      address: 'Address',
      streetAddress: 'Street Address',
      streetNumber: 'Street Number',
      postalCode: 'Postal Code',
      city: 'City',
      province: 'Province',
      contact: 'Contact Information',
      phone: 'Phone',
      email: 'Email',
      termsAndConditions: 'Terms and Conditions',
      appearance: {
        title: 'CRM Title & Appearance',
        crmTitle: 'CRM Title',
        customBackground: 'Custom Background CSS',
        backgroundExample: 'Example: linear-gradient(45deg, #ff6b6b, #4ecdc4)'
      },
      logos: {
        title: 'Company Logos',
        horizontalLogo: 'Horizontal Logo',
        iconLogo: 'Icon Logo',
        uploadHorizontal: 'Upload Horizontal Logo',
        uploadIcon: 'Upload Icon Logo',
        preview: 'Preview',
        delete: 'Delete',
        dropzoneText: 'Drag and drop an image here, or click to select a file',
        legalSignature: 'Legal Signature',
        uploadLogo: 'Upload logo',
        uploadIcon2: 'Upload icon',
        uploadSignature: 'Upload signature',
        remove: 'Remove',
        horizontal: 'Horizontal (max 2MB)',
        square: 'Square (max 1MB)',
        signature: 'Signature (max 1MB)'
      },
      save: 'Save Settings',
      header: {
        title: 'Company Information',
        description: 'Manage your company information and logos'
      },
      orgId: {
        title: 'Organization ID',
        description: 'Unique identifier of your organization',
        copied: 'Organization ID copied to clipboard!',
        copyError: 'Error copying to clipboard'
      },
      companyInfo: {
        title: 'Company Information',
        edit: 'Edit'
      },
      fields: {
        businessName: 'Business Name',
        vatNumber: 'VAT Number',
        sdiCode: 'SDI Code',
        address: 'Address',
        phone: 'Phone',
        email: 'Email'
      },
      messages: {
        uploadSuccess: 'Logo uploaded successfully',
        uploadError: 'Error uploading logo',
        deleteSuccess: 'Logo removed successfully',
        deleteError: 'Error removing logo',
        saveSuccess: 'Company settings updated successfully',
        saveError: 'Error updating settings',
        userNotAuthenticated: 'User not authenticated',
        userNotInOrganization: 'User not associated with an organization',
        migrationRequired: 'Database migration required to add logo support. Contact administrator.',
        uploadedButNotSaved: 'Image uploaded to storage but not saved to settings.'
      }
    },
    language: {
      title: 'Language Settings',
      description: 'Choose your preferred language for the CRM interface.',
      interface: 'Interface Language',
      select: 'Select the language for the user interface',
      messages: {
        updateSuccess: 'Language updated successfully',
        updateError: 'Failed to update language'
      }
    },
    search: {
      title: 'Search Settings',
      description: 'Configure which entities are included in the global search and how search results are displayed.',
      entities: {
        title: 'Searchable Entities',
        description: 'Select which types of data should be included in the global search results.'
      },
      entityLabels: {
        clients: 'Clients',
        projects: 'Projects',
        tasks: 'Tasks',
        deals: 'Deals',
        inventory: 'Inventory'
      },
      behavior: {
        title: 'Search Behavior',
        clientAssociations: {
          label: 'Search in client associations',
          description: 'When searching for a client, also include results from associated entities (projects, tasks, etc.)'
        },
        maxResults: {
          label: 'Maximum results per category',
          description: 'Limit the number of results shown for each type of entity'
        }
      },
      save: 'Save Settings',
      messages: {
        saveSuccess: 'Search settings saved successfully',
        saveError: 'Failed to save search settings'
      }
    },
    commissions: {
      title: 'Commission Settings',
      description: 'Configure the commission system for sales representatives.',
      enabled: 'Enable Commission System',
      enabledDescription: 'Allow assigning sales representatives to clients and calculate commissions on deals',
      defaultRate: 'Default Commission Rate',
      defaultRateDescription: 'The default percentage used for commission calculations if no specific rate is set for a user',
      save: 'Save Settings',
      messages: {
        saveSuccess: 'Commission settings updated successfully',
        saveError: 'Error updating settings'
      },
      loading: 'Loading...',
      error: 'Error',
      howItWorks: {
        title: 'How the commission system works',
        intro: 'The system calculates commissions following a precise hierarchy:',
        step1Title: 'Organization Default Percentage',
        step1Desc: 'Applied to all users without specific configuration',
        step2Title: 'Commission Groups ("Groups" tab)',
        step2Desc: 'Assign common percentages to multiple users. Changing a group percentage automatically updates all users in that group. Users in a group cannot have custom percentages.',
        step3Title: 'Specific User Percentage ("Users" tab)',
        step3Desc: 'Only for users NOT assigned to a group. Overrides the organization default percentage.',
        step4Title: 'Service Percentage',
        step4Desc: 'Can be set for specific user-service combinations in service management',
        step5Title: 'Deal Override',
        step5Desc: 'Overrides all previous settings for a specific deal',
        step6Title: 'Scaling with Referrers',
        step6Desc: 'If enabled in the service, the referrer percentage is subtracted from the agent percentage',
        exampleTitle: 'Practical example:',
        exampleOrg: 'Organization:',
        exampleDefault: '(default)',
        exampleUser: 'User assigned to "Senior Group":',
        exampleDeal: 'Deal with referrer at',
        exampleScaling: 'and scaling enabled',
        exampleFinalAgent: 'Final agent commission:',
        exampleReferrer: 'Referrer commission:'
      },
      reports: {
        title: 'Commission Reports',
        description: 'View and manage sales commissions by period',
        period: 'Period',
        salesRep: 'Referente Commerciale',
        status: 'Status',
        generate: 'Generate Report',
        export: 'Export to Excel',
        totals: 'Commission Totals',
        markAsPaid: 'Mark as Paid',
        notEnabled: 'Commission system is not enabled',
        enableInSettings: 'Go to settings',
        paid: 'Paid',
        unpaid: 'Unpaid',
        byAgent: 'Commissions by Agent',
        noCommissions: 'No commissions found for this period',
        loading: 'Loading data...',
        columns: {
          client: 'Client',
          deal: 'Deal',
          amount: 'Deal Amount',
          rate: 'Rate',
          commission: 'Commission',
          date: 'Date',
          status: 'Status',
          actions: 'Actions'
        }
      }
    },
    whatsappAutomation: {
      title: 'WhatsApp Automation',
      description: 'Configure automated WhatsApp notifications based on system events.',
      notConfigured: {
        title: 'WhatsApp Not Configured',
        description: 'To use WhatsApp automations, you need to configure the WAHA integration first. Go to API & Integrations to set up WhatsApp.'
      },
      form: {
        title: 'New Automation',
        editTitle: 'Edit Automation',
        name: 'Automation Name',
        entityType: 'Entity Type',
        triggerEvent: 'Trigger Event',
        recipient: 'Recipient',
        message: 'WhatsApp Message',
        active: 'Active',
        variables: 'Available Variables',
        buttons: {
          create: 'Create Automation',
          update: 'Update Automation',
          cancel: 'Cancel'
        }
      },
      list: {
        empty: 'No automations configured',
        emptyDescription: 'Create your first WhatsApp automation to send automatic notifications',
        columns: {
          name: 'Name',
          entity: 'Entity',
          trigger: 'Trigger',
          recipient: 'Recipient',
          status: 'Status'
        }
      },
      entityTypes: {
        lead: 'Lead',
        client: 'Client',
        deal: 'Deal',
        project: 'Project',
        ticket: 'Support Ticket'
      },
      triggerEvents: {
        create: 'Creation',
        update: 'Update',
        delete: 'Deletion',
        status_change: 'Status Change',
        state_transition: 'Specific State Transition',
        api_trigger: 'API Event'
      },
      recipientTypes: {
        client: 'Client',
        assigned_user: 'Assigned User'
      },
      variables: {
        lead: {
          name: { label: 'Lead Name', description: 'The name of the lead' },
          email: { label: 'Lead Email', description: 'The email of the lead' },
          phone: { label: 'Lead Phone', description: 'The phone number of the lead' },
          state: { label: 'Lead State', description: 'The current state of the lead' },
          source: { label: 'Lead Source', description: 'The source of the lead' },
          assigned_user: { label: 'Assigned User', description: 'The name of the user assigned to the lead' },
          created_at: { label: 'Creation Date', description: 'The creation date of the lead' }
        },
        client: {
          business_name: { label: 'Business Name', description: 'The business name of the client' },
          email: { label: 'Client Email', description: 'The email of the client' },
          phone: { label: 'Client Phone', description: 'The phone number of the client' },
          state: { label: 'Client State', description: 'The current state of the client' },
          vat_number: { label: 'VAT Number', description: 'The VAT number of the client' },
          assigned_user: { label: 'Assigned User', description: 'The name of the user assigned to the client' },
          created_at: { label: 'Creation Date', description: 'The creation date of the client' }
        },
        deal: {
          title: { label: 'Deal Title', description: 'The title of the deal' },
          status: { label: 'Status', description: 'The current status of the deal' },
          amount: { label: 'Amount', description: 'The total amount of the deal' },
          client_name: { label: 'Client Name', description: 'The name of the associated client' },
          user_name: { label: 'User Name', description: 'The name of the assigned user' }
        },
        project: {
          name: { label: 'Project Name', description: 'The name of the project' },
          status: { label: 'Status', description: 'The current status of the project' },
          due_date: { label: 'Due Date', description: 'The due date of the project' },
          client_name: { label: 'Client Name', description: 'The name of the associated client' },
          user_name: { label: 'User Name', description: 'The name of the assigned user' }
        },
        ticket: {
          subject: { label: 'Ticket Subject', description: 'The subject of the support ticket' },
          status: { label: 'Status', description: 'The current status of the ticket' },
          priority: { label: 'Priority', description: 'The priority of the ticket' },
          client_name: { label: 'Client Name', description: 'The name of the associated client' },
          user_name: { label: 'User Name', description: 'The name of the assigned user' }
        }
      },
      statusLabels: {
        active: 'Active',
        inactive: 'Inactive',
        anyStatus: 'Any status'
      },
      configSource: {
        title: 'WhatsApp Configuration',
        user: 'User WhatsApp (personal)',
        global: 'Global CRM WhatsApp',
        currentConfig: 'Current configuration'
      },
      stateTransition: {
        fromState: 'From State',
        toState: 'To State',
        anyState: 'Any state',
        selectFrom: 'Select source state',
        selectTo: 'Select target state',
        description: 'The automation will trigger when the entity transitions from the specified state (or any state) to this state.'
      },
      statusChange: {
        specificStatus: 'Specific Status',
        selectStatus: 'Select status (optional)',
        description: 'Leave empty to trigger on any status change, or choose a specific status.'
      },
      apiTrigger: {
        title: 'API Trigger Automation',
        description: 'This automation can be triggered via external API calls. After saving, you will receive a unique webhook URL that you can use to trigger this automation.',
        webhookUrl: 'Webhook URL',
        copy: 'Copy',
        copied: 'URL copied to clipboard!'
      },
      recipientDescription: {
        client: 'The message will be sent to the client associated with the element',
        assigned_user: 'The message will be sent to the user assigned to the element'
      },
      placeholders: {
        name: 'Enter a name for this automation',
        selectEntity: 'Select entity type',
        selectTrigger: 'Select trigger event',
        selectRecipient: 'Select recipient type',
        writeMessage: 'Write the WhatsApp message here...'
      },
      messages: {
        createSuccess: 'Automation rule created successfully',
        createError: 'Error creating automation rule',
        updateSuccess: 'Automation rule updated successfully',
        updateError: 'Error updating automation rule',
        deleteSuccess: 'Automation rule deleted successfully',
        deleteError: 'Error deleting automation rule',
        toggleSuccess: 'Automation status updated'
      },
      confirmDelete: {
        title: 'Delete rule',
        message: 'Are you sure you want to delete this automation rule? This action cannot be undone.',
        confirm: 'Delete',
        cancel: 'Cancel'
      }
    },
    customStates: {
      title: 'Custom States & Sources',
      description: 'Configure states and sources to organize clients and leads',
      generateDefault: 'Generate Default Data',
      generateConfirm: 'Are you sure you want to generate default states and sources? This will add predefined data to your organization.',
      generateSuccess: 'Default states and sources generated successfully!',
      generateError: 'Error generating default data. Check the console for more details.',
      sections: {
        clientStates: 'Client States',
        leadStates: 'Lead States',
        leadSources: 'Lead Sources'
      },
      clientStates: {
        title: 'Client States',
        description: 'Manage the states that clients can have in your organization',
        addState: 'Add State',
        leadStates: 'Lead States',
        leadStatesNote: '(appear in the Leads section)',
        clientStates: 'Client States',
        clientStatesNote: '(appear in the Clients section)'
      },
      leadStates: {
        title: 'Lead States',
        description: 'Manage the states that leads can have in the qualification process',
        addState: 'Add State'
      },
      leadSources: {
        title: 'Lead Sources',
        description: 'Manage the sources from which your leads come',
        addSource: 'Add Source'
      },
      form: {
        newClientState: 'New Client State',
        editClientState: 'Edit Client State',
        newLeadState: 'New Lead State',
        editLeadState: 'Edit Lead State',
        newLeadSource: 'New Lead Source',
        editLeadSource: 'Edit Lead Source',
        name: 'Name',
        description: 'Description',
        color: 'Color',
        sortOrder: 'Order',
        isLeadState: 'Is a Lead state',
        isLeadStateDescription: 'Clients with this state will appear in the Leads section instead of the Clients section',
        conversionType: 'Conversion Type',
        isWon: '"Won" state (successful conversion)',
        isLost: '"Lost" state (failed conversion)',
        setAsDefault: 'Set as default',
        cancel: 'Cancel',
        save: 'Save'
      },
      badges: {
        lead: 'LEAD',
        won: 'WON',
        lost: 'LOST'
      },
      placeholders: {
        clientStateName: 'E.g. Prospect, Active, Inactive',
        leadStateName: 'E.g. New, Qualified, Converted',
        leadSourceName: 'E.g. Website, Referral, Social Media',
        descriptionOptional: 'Optional description'
      },
      messages: {
        leadStateOrderSaved: 'Lead state order saved',
        clientStateOrderSaved: 'Client state order saved',
        sourceOrderSaved: 'Lead source order saved',
        deleteStateConfirm: 'Delete this state? If it has been used in clients\' history it will be deactivated instead of deleted.',
        stateDeleted: 'State deleted',
        stateDeactivated: 'The state is used in clients\' history: it has been deactivated instead of deleted'
      }
    },
    interestCategories: {
      title: 'Interest Categories',
      description: 'Manage the hierarchy of interest categories for leads and clients',
      buttons: {
        expand: 'Expand',
        collapse: 'Collapse',
        newCategory: 'New Category',
        createFirst: 'Create first category'
      },
      search: {
        placeholder: 'Search categories...'
      },
      inline: {
        placeholder: 'Category name... (Enter=save, Tab=sub-cat, Esc=cancel)',
        rootPlaceholder: 'Root category name... (Enter=save, Esc=cancel)',
        saveTitle: 'Save (Enter)',
        cancelTitle: 'Cancel (Esc)'
      },
      form: {
        editTitle: 'Edit Category',
        name: 'Name *',
        description: 'Description',
        parentCategory: 'Parent Category',
        noParent: 'None (root category)',
        color: 'Color',
        active: 'Active',
        cancel: 'Cancel',
        save: 'Save'
      },
      actions: {
        addSubcategory: 'Add sub-category',
        edit: 'Edit',
        delete: 'Delete'
      },
      badges: {
        level: 'Lv.',
        inactive: 'Inactive'
      },
      emptyState: {
        noResults: 'No categories found',
        noCategories: 'No categories',
        tryModifySearch: 'Try modifying your search',
        startCreating: 'Start by creating your first interest category'
      },
      stats: {
        totalCategories: 'total categories',
        results: 'results'
      },
      messages: {
        createSuccess: 'Category "{name}" created',
        createError: 'Error creating category',
        updateSuccess: 'Category updated',
        updateError: 'Error updating category',
        deleteSuccess: 'Category deleted',
        deleteError: 'Error deleting category'
      },
      confirm: {
        deleteWithChildren: 'Are you sure you want to delete "{name}" and its {count} sub-categories?',
        deleteSingle: 'Are you sure you want to delete "{name}"?'
      }
    },
    clientSettings: {
      title: 'Client and Lead Settings',
      description: 'Configure settings related to clients and leads',
      tabs: {
        menuLabels: {
          name: 'Menu Labels',
          description: 'Customize the names of the Clients and Leads menu items'
        },
        defaultEntityType: {
          name: 'Default Entity Type',
          description: 'Configure the preselected entity type in forms'
        },
        states: {
          name: 'States and Sources',
          description: 'Manage custom states and lead sources'
        },
        tags: {
          name: 'Client Tags',
          description: 'Manage tags to categorize clients and leads'
        },
        interests: {
          name: 'Client Interests',
          description: 'Manage interest categories for clients and leads'
        },
        settori: {
          name: 'Sectors',
          description: 'Manage business sectors to classify clients and leads'
        },
        customFields: {
          name: 'Custom Fields',
          description: 'Manage custom fields for clients'
        },
        kanbanSettings: {
          name: 'Lead Kanban',
          description: 'Configure the kanban behavior in lead management'
        },
        leadsColumns: {
          name: 'Leads Columns',
          description: 'Configure which columns are visible in the leads table'
        },
        clientsColumns: {
          name: 'Clients Columns',
          description: 'Configure which columns are visible in the clients table'
        },
        leadQuickInputFields: {
          name: 'Quick Lead Popup',
          description: 'Configure which fields are visible in the quick lead creation popup'
        }
      },
      menuLabels: {
        infoBox: 'Customize the names of the "Clients" and "Leads" menu items. Specify both singular and plural forms. If you leave a field empty, the default name will be used.',
        clientsSection: 'Clients',
        leadsSection: 'Leads',
        pluralLabel: 'Plural',
        singularLabel: 'Singular',
        clientsLabel: 'Clients section label',
        clientsPlaceholder: 'e.g. Patients',
        clientsPlaceholderSingular: 'e.g. Patient',
        clientsDefault: 'Default: "Clients"',
        clientsDefaultPlural: 'Default: Clients',
        clientsDefaultSingular: 'Default: Client',
        clientsGender: 'Gender',
        leadsLabel: 'Leads section label',
        leadsPlaceholder: 'e.g. Opportunities',
        leadsPlaceholderSingular: 'e.g. Opportunity',
        leadsDefault: 'Default: "Leads"',
        leadsDefaultPlural: 'Default: Leads',
        leadsDefaultSingular: 'Default: Lead',
        leadsGender: 'Gender',
        genderMasculine: 'Masculine (il/questo/nuovo)',
        genderFeminine: 'Feminine (la/questa/nuova)',
        saveButton: 'Save changes',
        saving: 'Saving...',
        resetButton: 'Reset to defaults',
        preview: 'Preview:',
        previewClients: 'Clients:',
        previewLeads: 'Leads:',
        previewExample: 'Example: "{nuovo} {label}"',
        quickCreateButton: {
          label: 'Use quick create button (Leads)',
          description: 'Replaces "+ New" button with "Create quickly" in the leads page. The button opens a dropdown to select the lead status before showing the quick creation form.'
        },
        quickCreateButtonClients: {
          label: 'Use quick create button (Clients)',
          description: 'Replaces "+ New" button with "Create quickly" in the clients page.'
        },
        messages: {
          saveSuccess: 'Menu labels saved successfully',
          saveError: 'Error saving menu labels'
        }
      },
      defaultEntityType: {
        infoBox: 'Choose which entity type should be preselected by default when creating a new client or lead. This setting does not modify existing clients.',
        label: 'Default Entity Type',
        company: 'Company',
        companyDescription: 'Preselect "Company" in forms',
        individual: 'Individual',
        individualDescription: 'Preselect "Individual" in forms',
        saveButton: 'Save changes',
        saving: 'Saving...',
        preview: 'Preview:',
        previewText: 'When creating a new client or lead, the entity type will be preselected to',
        messages: {
          saveSuccess: 'Default entity type saved successfully',
          saveError: 'Error saving default entity type'
        }
      },
      kanbanSettings: {
        title: 'Lead Kanban Settings',
        description: 'Configure the kanban behavior in lead management',
        noteRequired: {
          label: 'Note required on status change',
          description: 'If enabled, when dragging a lead in the kanban, a drawer will open to enter a required note. If disabled, the lead will be moved directly.'
        },
        messages: {
          saveSuccess: 'Setting saved successfully',
          saveError: 'Error saving setting'
        }
      },
      leadsColumns: {
        infoBox: 'Configure which columns are visible in the leads table. The "Name" and "Actions" columns are always visible.',
        visibleCount: '{count} of {total} columns visible',
        columns: {
          stage: 'Stage',
          score: 'Score',
          value: 'Value',
          assignee: 'Assignee',
          source: 'Source',
          date: 'Date'
        },
        showColumn: 'Show column',
        hideColumn: 'Hide column',
        resetButton: 'Reset to default',
        messages: {
          saveSuccess: 'Column visibility settings saved',
          saveError: 'Error saving settings'
        }
      },
      clientsColumns: {
        infoBox: 'Configure which columns are visible in the clients table. The "Name" and "Actions" columns are always visible.',
        visibleCount: '{count} of {total} columns visible',
        columns: {
          email: 'Email',
          phone: 'Phone',
          owner: 'Owner',
          state: 'Status',
          interests: 'Interests',
          updated: 'Updated'
        },
        showColumn: 'Show column',
        hideColumn: 'Hide column',
        resetButton: 'Reset to default',
        messages: {
          saveSuccess: 'Column visibility settings saved',
          saveError: 'Error saving settings'
        }
      },
      leadQuickInputFields: {
        infoBox: 'Configure which fields are visible in the quick lead creation popup.',
        visibleCount: '{count} of {total} fields visible',
        fields: {
          lead_source: 'Lead Source',
          interests: 'Interests',
          score: 'Score',
          opportunity_value: 'Value',
          street_address: 'Street Address',
          street_number: 'Street Number',
          postal_code: 'Postal Code',
          city: 'City',
          province: 'Province',
          country: 'Country',
          region: 'Region',
          vat_number: 'VAT Number',
          tax_code: 'Tax Code',
          sdi_code: 'SDI Code',
          pec: 'PEC',
          annual_revenue: 'Annual Revenue',
          website: 'Website',
          general_comments: 'Notes'
        },
        showField: 'Show field',
        hideField: 'Hide field',
        resetButton: 'Reset to default',
        messages: {
          saveSuccess: 'Field visibility settings saved',
          saveError: 'Error saving settings'
        }
      },
      customFields: {
        title: 'Client Custom Fields',
        description: 'Manage custom fields to collect specific information about clients',
        newField: 'New Field',
        editField: 'Edit Field',
        newCustomField: 'New Custom Field',
        fieldTypes: {
          text: { label: 'Text', description: 'Simple text field' },
          textarea: { label: 'Long Text', description: 'Multiline text area' },
          number: { label: 'Number', description: 'Numeric field' },
          date: { label: 'Date', description: 'Date selection' },
          boolean: { label: 'Yes/No', description: 'True/false checkbox' },
          select: { label: 'Selection', description: 'Dropdown menu with options' },
          email: { label: 'Email', description: 'Email address' },
          phone: { label: 'Phone', description: 'Phone number' },
          url: { label: 'URL', description: 'Web address' }
        },
        form: {
          fieldName: 'Field Name',
          fieldNamePlaceholder: 'e.g. business_sector',
          fieldNameHelp: 'Technical field name (only letters, numbers and underscores)',
          fieldLabel: 'Label',
          fieldLabelPlaceholder: 'e.g. Business Sector',
          fieldLabelHelp: 'Name shown to users',
          fieldType: 'Field Type',
          options: 'Options',
          addOption: 'Add an option',
          defaultValue: 'Default Value',
          defaultValuePlaceholder: 'Optional default value',
          helpText: 'Help Text',
          helpTextPlaceholder: 'Hint for the user',
          isRequired: 'Required field',
          cancel: 'Cancel',
          create: 'Create',
          update: 'Update'
        },
        list: {
          name: 'Name:',
          type: 'Type:',
          required: 'Required',
          options: 'Options:',
          emptyState: {
            title: 'No custom fields created',
            description: 'Create the first field to collect specific information about clients'
          }
        },
        validation: {
          fieldNameRequired: 'Field name is required',
          fieldNameFormat: 'Field name must start with a letter and contain only letters, numbers and underscores',
          fieldLabelRequired: 'Field label is required',
          selectOptionsRequired: 'Selection fields must have at least one option',
          duplicateName: 'A field with this name already exists'
        },
        confirm: {
          title: 'Delete Custom Field',
          message: 'Are you sure you want to delete the field "{label}"? All associated data will be lost.',
          confirmText: 'Delete',
          cancelText: 'Cancel'
        },
        messages: {
          loadError: 'Error loading custom fields',
          orgError: 'Error loading organization',
          orgIdNotAvailable: 'Organization ID not available',
          orgNotFound: 'Organization not found',
          fieldCreated: 'Field created successfully',
          fieldUpdated: 'Field updated successfully',
          fieldDeleted: 'Field deleted successfully',
          saveError: 'Error saving field',
          deleteError: 'Error deleting field',
          orderUpdated: 'Field order updated',
          orderError: 'Error updating order'
        }
      }
    },
    partners: {
      page: {
        title: 'Partner Settings',
        description: 'Configure partner settings and classification',
        tabs: {
          partnerTypes: {
            name: 'Partner Types',
            description: 'Configure the partner types available for classification'
          },
          businessSectors: {
            name: 'Business Sectors',
            description: 'Configure the business sectors for partners'
          }
        }
      },
      businessSectors: {
        title: 'Business Sectors',
        description: 'Manage the business sectors available for partner classification',
        addButton: 'Add Sector',
        editTitle: 'Edit Business Sector',
        newTitle: 'New Business Sector',
        form: {
          name: 'Name',
          namePlaceholder: 'e.g. Technology, Marketing, Legal',
          color: 'Color',
          description: 'Description',
          descriptionPlaceholder: 'Description of the business sector...',
          active: 'Active',
          cancel: 'Cancel',
          saving: 'Saving...',
          update: 'Update',
          create: 'Create'
        },
        status: {
          active: 'Active',
          inactive: 'Inactive'
        },
        emptyState: {
          title: 'No business sectors configured',
          action: 'Add the first business sector'
        },
        confirm: {
          delete: 'Are you sure you want to delete this business sector?'
        }
      },
      partnerTypes: {
        title: 'Partner Types',
        description: 'Manage the partner types available for classification',
        addButton: 'Add Type',
        editTitle: 'Edit Partner Type',
        newTitle: 'New Partner Type',
        form: {
          name: 'Name',
          namePlaceholder: 'e.g. Consultant, Supplier, Agency',
          color: 'Color',
          description: 'Description',
          descriptionPlaceholder: 'Description of the partner type...',
          active: 'Active',
          cancel: 'Cancel',
          saving: 'Saving...',
          update: 'Update',
          create: 'Create'
        },
        status: {
          active: 'Active',
          inactive: 'Inactive'
        },
        emptyState: {
          title: 'No partner types configured',
          action: 'Add the first partner type'
        },
        confirm: {
          delete: 'Are you sure you want to delete this partner type?'
        }
      }
    },
    integrations: {
      smtp: {
        title: 'SMTP Configuration',
        description: 'Configure the email server for automatic notification delivery',
        enable: 'Enable SMTP',
        enableDescription: 'Enable automatic email notification delivery',
        server: 'SMTP Server',
        port: 'Port',
        username: 'Username/Email',
        password: 'Password',
        fromEmail: 'Sender Email',
        fromEmailHint: 'This address will appear as the sender of automatic emails',
        testConnection: 'Test Connection',
        testConnectionDescription: 'Verify that the configuration is correct',
        testSuccess: 'Success',
        testError: 'Error',
        testing: 'Testing...',
        test: 'Test',
        commonConfigs: 'Common Configurations',
        saveButton: 'Save Configuration',
        saving: 'Saving...',
        messages: {
          saveSuccess: 'SMTP configuration saved successfully',
          saveError: 'Error saving SMTP configuration',
          testSuccess: 'SMTP test completed successfully! Test email sent.',
          testError: 'Error during SMTP connection test',
          testFailed: 'SMTP test failed',
          fillRequired: 'Fill in all required fields before testing the connection'
        }
      },
      databaseBackup: {
        title: 'Database Backup',
        description: 'Export a full database backup including structure and data',
        filteringOrg: 'Filtering data only for the current organization',
        fullBackup: 'Full Backup',
        fullBackupDescription: 'Full backup that includes database structure and all data. Useful for restoring the entire database.',
        fullBackupItems: {
          schema: 'Database schema (tables, indexes, constraints)',
          data: 'All table data',
          triggers: 'Triggers and functions',
          policies: 'RLS security policies'
        },
        downloadFullBackup: 'Download Full Backup',
        schemaOnly: 'Schema Only',
        schemaOnlyDescription: 'Backup of structure only without data. Useful for creating development environments or documenting the schema.',
        schemaOnlyItems: {
          schema: 'Database schema (tables, indexes, constraints)',
          structure: 'Table structure and relations',
          functions: 'Functions and triggers',
          noData: 'No data included'
        },
        downloadSchemaOnly: 'Download Schema Only',
        importantNotes: 'Important Notes',
        notes: {
          largeDb: 'Backups may take several minutes for large databases',
          stableConnection: 'Ensure you have a stable internet connection during the process',
          sensitiveData: 'Full backups include sensitive data such as passwords',
          secureStorage: 'Store backups securely and follow data protection policies'
        },
        progress: {
          connecting: 'Connecting to database...',
          schema: 'Extracting database schema...',
          structure: 'Extracting table structures...',
          processingTable: 'Processing table',
          rows: 'rows',
          finalizing: 'Finalizing backup file...',
          complete: 'Backup completed successfully!'
        },
        messages: {
          fullSuccess: 'Full database backup completed successfully!',
          schemaSuccess: 'Schema backup completed successfully!',
          downloadSuccess: 'Database backup downloaded successfully',
          downloadError: 'Failed to create database backup',
          fullError: 'Full backup failed',
          schemaError: 'Schema backup failed'
        }
      },
      fattureincloud: {
        title: 'Fatture in Cloud',
        description: 'Connect and configure the Fatture in Cloud integration for automatic invoicing',
        goToFIC: 'Go to Fatture in Cloud',
        tabs: {
          connection: { name: 'Connection', description: 'Manage OAuth connection' },
          defaults: { name: 'Invoicing', description: 'Default values and automation' },
          mappings: { name: 'Clients', description: 'CRM ↔ FIC client mapping' },
          sync: { name: 'Sync', description: 'Sync data manually' }
        },
        setupCompleted: 'Setup completed successfully!',
        messages: {
          connectionSuccess: 'Connection to Fatture in Cloud completed successfully!',
          connectionError: 'Connection error',
          loadError: 'Error loading configuration',
          authError: 'Error starting authorization flow',
          disconnectSuccess: 'Disconnection from Fatture in Cloud completed',
          disconnectError: 'Error during disconnection',
          testSuccess: 'Connection valid',
          testFailed: 'Connection invalid or token expired',
          testError: 'Error testing connection'
        },
        confirm: {
          disconnect: 'Are you sure you want to disconnect Fatture in Cloud? You will need to reauthorize access to use the integration again.'
        }
      },
      apiTokens: {
        title: 'API tokens',
        description: 'Tokens let external applications and AI assistants read and change your data through the Flusia API.',
        apiDocumentation: 'API documentation',
        warning: 'Anyone who has a token can act with its permissions. Store it like a password and give each integration only what it needs.',
        listTitle: 'Your organization tokens',
        listDescription: 'The full value is shown only once, when the token is created.',
        generateNew: 'New token',
        noTokens: 'No tokens yet',
        noTokensDescription: 'Create one to connect an external application.',
        created: 'Created',
        lastUsed: 'Last used',
        neverUsed: 'Never used',
        expires: 'Expires',
        active: 'Active',
        inactive: 'Disabled',
        toggleActive: 'Token active',
        copyToken: 'Copy',
        copied: 'Copied',
        ipRestricted: 'Allowed IPs',
        deleteToken: 'Delete token',
        deleteConfirm: 'Delete?',
        deleteYes: 'Delete',
        deleteNo: 'Cancel',
        actions: { read: 'read', write: 'edit', delete: 'delete' },
        actionColumns: { read: 'Read', write: 'Edit', delete: 'Delete' },
        levels: { full: 'Full access', read_write: 'Read and edit', read: 'Read only', other: 'Other' },
        drawer: {
          title: 'New API token',
          subtitle: 'Choose a name and what the token can do',
          details: 'Details',
          tokenName: 'Name',
          tokenNamePlaceholder: 'e.g. Website integration',
          description: 'Description',
          descriptionPlaceholder: 'What is this token for?',
          permissions: 'Permissions',
          permissionsDescription: 'You can only give permissions you have yourself.',
          preset: 'Quick choice',
          presets: { all: 'Everything you can grant', read_only: 'Everything, read only', custom: 'Custom' },
          resource: 'Area',
          notGrantable: 'You do not have this permission',
          unavailable: 'Not available for your role',
          selectedCount: '{count} areas selected',
          ipWhitelist: 'Allowed IP addresses',
          ipHint: 'Leave empty to allow any address. CIDR notation supported (e.g. 192.168.1.0/24).',
          ipPlaceholder: 'e.g. 192.168.1.1',
          add: 'Add',
          removeIp: 'Remove',
          cancel: 'Cancel',
          generate: 'Create token',
          creating: 'Creating...'
        },
        resources: {
          leads: 'Leads and clients',
          deals: 'Quotes',
          tasks: 'Tasks',
          projects: 'Projects',
          activities: 'Calendar',
          services: 'Services',
          invoices: 'Invoices',
          whatsapp: 'WhatsApp',
          newsletter: 'Newsletter',
          warehouse: 'Warehouse',
          pipelines: 'Pipelines',
          qr_codes: 'QR codes',
          wiki: 'Wiki',
          workflows: 'Automations',
          settings: 'Settings',
          templates: 'Quote templates'
        },
        success: {
          title: 'Token created',
          saveWarning: 'Copy it now: you will not be able to see it again. If you lose it, create a new one.',
          yourToken: 'Your token',
          tokenSaved: 'I saved it'
        },
        messages: {
          nameRequired: 'Enter a name for the token.',
          permissionRequired: 'Select at least one permission.',
          createError: 'The token could not be created. Please try again.',
          notGrantable: 'You selected a permission you do not have. Reload the page and try again.',
          deleteError: 'The token could not be deleted. Please try again.',
          updateError: 'The token could not be updated. Please try again.',
          invalidIp: 'Invalid IP address. Use the format 192.168.1.1 or 192.168.1.0/24.',
          ipExists: 'This address is already in the list.',
          loadErrorTitle: 'Token list unavailable',
          loadError: 'The token list could not be read, so it may be incomplete. Reload the page and, if the problem persists, report this message:'
        }
      },
      wooCommerce: {
        title: 'WooCommerce Integration',
        description: 'Configure synchronization with your WooCommerce store',
        tabs: {
          config: 'Configuration',
          webhooks: 'Webhooks',
          orders: 'Orders',
          logs: 'Logs'
        },
        config: {
          title: 'WooCommerce API Configuration',
          description: 'Enter the connection data to your WooCommerce store',
          siteUrl: 'Site URL',
          apiVersion: 'API Version',
          consumerKey: 'Consumer Key',
          consumerSecret: 'Consumer Secret',
          integrationActive: 'Integration active',
          syncOrders: 'Sync orders',
          syncStock: 'Sync stock',
          testConnection: 'Test Connection',
          saveConfig: 'Save Configuration'
        },
        stats: {
          title: 'Sync Statistics',
          syncedOrders: 'Synced orders',
          webhooks24h: 'Webhooks 24h',
          syncedProducts: 'Synced products'
        },
        webhooks: {
          title: 'Webhook Configuration',
          description: 'Configure these endpoints in your WordPress plugin',
          orderCreated: 'Order Created',
          orderUpdated: 'Order Updated',
          testConnection: 'Test Connection',
          apiKey: 'API Key'
        },
        orders: {
          title: 'WooCommerce Orders',
          description: 'Recently synced orders',
          loadOrders: 'Load Orders',
          orderId: 'Order ID',
          number: 'Number',
          customer: 'Customer',
          total: 'Total',
          status: 'Status',
          date: 'Date',
          noOrders: 'No orders found'
        },
        logs: {
          title: 'Webhook Logs',
          description: 'Recent webhook activity',
          loadLogs: 'Load Logs',
          event: 'Event',
          status: 'Status',
          message: 'Message',
          success: 'Success',
          error: 'Error',
          processedSuccessfully: 'Processed successfully',
          noLogs: 'No logs found'
        },
        messages: {
          loadConfigError: 'Error loading configuration',
          loadLogsError: 'Error loading logs',
          loadOrdersError: 'Error loading orders',
          saveSuccess: 'Configuration saved successfully',
          saveError: 'Error saving configuration',
          testSuccess: 'Connection tested successfully!',
          testFailed: 'Test failed',
          testError: 'Error during connection test',
          copiedToClipboard: 'copied to clipboard',
          copyError: 'Error copying'
        }
      },
      emailMigration: {
        banner: {
          title: 'You have a new faster and more reliable email system.',
          subtitle: 'Configure your domain to activate it.',
          cta: 'Configure',
        },
        emptyState: {
          title: 'Configure your domain to send emails',
          description: 'The new system delivers faster and with better deliverability.',
          cta: 'Configure domain',
        },
        inlineCallout: {
          title: 'You have a domain configured on the old system.',
          description: 'Add your domain below to activate the new email system.',
        },
        toast: {
          sendBlocked: 'Configure your email domain in Settings to send emails.',
        },
        wizard: {
          header: 'Add email domain',
          stepLabel: 'Step',
          stepOf: 'of',
          step1: {
            title: 'Which domain do you want to use?',
            helper: 'Enter your main domain (the part after @ in your email).',
            domainLabel: 'Domain',
            domainPlaceholder: 'example: mycompany.com',
            advancedToggle: 'Advanced: set a subdomain',
            subdomainLabel: 'Subdomain (optional)',
            subdomainPlaceholder: 'newsletter',
            subdomainHint: 'Leave empty unless you specifically need it.',
            invalidDomain: 'Please enter a valid domain (e.g. mycompany.com).',
          },
          step2: {
            title: 'Configure your DNS records',
            instructions:
              'Add the following 3 TXT records in your DNS provider control panel (Cloudflare, GoDaddy, Aruba, etc.).',
            spfMergeWarning:
              'If you already have an SPF (TXT) record on your root domain, MERGE the new include into the existing one — do NOT create a second SPF record.',
            recordType: 'Type',
            recordHost: 'Host',
            recordValue: 'Value',
            copyTooltip: 'Copy',
            copied: 'Copied',
            ctaNext: "I've configured the records, verify",
          },
          step3: {
            title: "Let's verify your DNS records",
            loading: 'Checking with the email provider...',
            verifiedMsg: 'All records verified! You can now configure your sender.',
            pendingMsg:
              'Records not yet propagated. DNS can take up to 30 minutes — retry shortly.',
            failedMsg: 'Some records are incorrect. Please review them and retry.',
            retryCta: 'Retry verification',
            backToDns: 'Review DNS records',
          },
          step4: {
            title: 'Configure your sender',
            description: "This is how your contacts will see emails sent from this domain.",
            localPartLabel: 'Sender email',
            localPartPlaceholder: 'newsletter',
            localPartHint: 'Only the part before @; the domain is added automatically.',
            senderNameLabel: 'Sender name',
            senderNamePlaceholder: 'Your Company',
            saveCta: 'Save and activate',
          },
          footer: {
            back: 'Back',
            cancel: 'Cancel',
            next: 'Next',
            close: 'Close',
          },
          successToast: 'Domain activated! You can now send emails with your brand.',
          createError: 'Could not create the domain',
          verifyError: 'Could not verify the domain',
          saveSenderError: 'Could not save the sender',
        },
      },
      emailDomain: {
        title: 'Email Domains',
        description: 'Configure your domain to send professional newsletters from your brand',
        addDomain: 'Add Domain',
        superadminBanner: {
          label: 'Superadmin mode',
          prefix: 'you are operating on behalf of',
          fallbackOrg: 'this organization',
        },
        activeDomain: {
          title: 'Active Domain for Newsletter',
          shared: 'Shared',
          custom: 'Custom',
          domain: 'Domain',
          senderEmail: 'Sender email',
          senderName: 'Sender name',
          monthlyLimit: 'Monthly limit',
          defaultLimit: '50 emails/month',
          customLimit: '1,000 emails/month',
          emailsPerMonth: 'emails/month',
          unlimited: 'Unlimited',
          suspended: 'Account suspended',
          toBeConfigured: 'To be configured',
          bonus: 'bonus',
        },
        upgrade: {
          title: 'Increase your limit to 1,000 emails/month',
          benefits: 'Verify your custom domain for:',
          benefit1: 'Send up to 1,000 emails per month (instead of 50)',
          benefit2: 'More professional emails from you@yourdomain.com',
          benefit3: 'Improve deliverability and reduce spam',
        },
        form: {
          newDomain: 'New Domain',
          domain: 'Domain',
          domainPlaceholder: 'example: mycompany.com',
          domainPlaceholderInline: 'yourdomain.com',
          subdomain: 'Subdomain (optional)',
          subdomainPlaceholder: 'newsletter',
          result: 'Result',
          add: 'Add',
          cancel: 'Cancel',
        },
        status: {
          verified: 'Verified',
          pending: 'Pending verification',
          failed: 'Verification failed',
        },
        dns: {
          title: 'DNS records to configure (3 TXT):',
          type: 'Type',
          host: 'Host',
          value: 'Value',
          instructions: 'Instructions',
          step1: 'Open your DNS provider panel (e.g. Cloudflare, GoDaddy)',
          step2: 'Add the 3 TXT records listed above',
          step3: 'Wait for DNS propagation (5-30 minutes)',
          step4: 'Click "Verify" to complete configuration',
          spfMergeWarning: 'Merge required',
          spfMergeNote:
            'If you already have an SPF TXT record on the root domain, MERGE the include into the existing one (do NOT create a second SPF record).',
          purposeOwnership: 'Domain ownership',
          purposeDkim: 'DKIM signing',
          purposeSpf: 'SPF authorization',
          purposeDmarc: 'DMARC policy',
        },
        sender: {
          title: 'Sender Email Configuration:',
          senderEmail: 'Sender Email',
          senderEmailPlaceholder: 'newsletter@',
          senderEmailHint: 'Must use the verified domain',
          senderName: 'Sender Name',
          senderNamePlaceholder: 'Your Name or Company',
          saveConfig: 'Save Configuration',
          localPartHint: 'Only the local part; the @domain is automatic.',
          localPartRequired: 'Please enter the local part of the sender email',
        },
        actions: {
          showDns: 'Show DNS',
          hideDns: 'Hide DNS',
          configSender: 'Configure Sender',
          editSender: 'Edit Sender',
          verify: 'Verify',
          delete: 'Delete',
          senderPending: 'Pending approval',
          senderActive: 'Active',
          resendValidation: 'Resend email',
          resendValidationTooltip: 'Resend the activation email to the Flusia administrator',
          metasenderFileMissing: 'File missing',
          metasenderActive: 'Ready to send',
          downloadMetasenderFile: 'Download file',
          downloadMetasenderTooltip: 'Download the validation file and upload it to your website root',
          verifyMetasender: 'Activate',
          refresh: 'Refresh',
        },
        otherDomains: {
          title: 'Other domains',
        },
        emptyState: {
          title: 'No domains configured',
          description: 'Add a domain to start sending professional emails with your brand',
        },
        messages: {
          loadError: 'Error loading domains',
          addSuccess: 'Domain added! Configure DNS records to complete verification',
          addError: 'Error adding domain',
          senderActivated: 'Sender activated! You can now send emails',
          resendSuccess: 'Activation email re-sent. Please check your inbox',
          resendError: 'Could not resend activation email',
          metasenderActivated: 'Domain authentication complete! You can now send from any address on this domain.',
          metasenderNotYet: 'Activation not complete yet — check the file is uploaded and try again.',
          metasenderFilenameMissing: 'No metasender configured for this domain',
          metasenderDownloadError: 'Could not download validation file',
          metasenderFileNotReachable: 'The validation file is not reachable. Make sure it is uploaded and publicly accessible at:',
          metasenderWaitingMailjet: 'File detected on your domain. We are verifying — this can take a few minutes.',
          verifySuccess: 'Domain verified successfully!',
          verifyFailed: 'Verification failed. Check DNS records and try again',
          verifyError: 'Error during verification',
          deleteConfirm: 'Are you sure you want to delete the domain',
          deleteSuccess: 'Domain deleted',
          deleteError: 'Error during deletion',
          senderUpdated: 'Sender configuration updated',
          senderError: 'Error updating',
          copiedToClipboard: 'Copied to clipboard',
          permissionDenied: 'Update failed: insufficient permissions on this domain',
        },
        metasender: {
          calloutTitle: 'Upload the validation file to activate sending',
          calloutDescription:
            'This proves your domain ownership so we can send emails from any address on your domain (e.g. newsletter@yourdomain.com, info@yourdomain.com).',
          step1: 'Download the file',
          step2: 'Upload it to the root of your website so it is reachable at',
          step3: 'Click here to verify:',
          note: 'It can take a few minutes for the verification to complete after the upload.',
          waitingTitle: 'Almost there',
          waitingDescription:
            'The file is published correctly. Click "Activate" to complete the setup — activation is immediate.',
          upfrontTimingTitle: 'How does it work?',
          upfrontTiming:
            'Once the file is uploaded, click "Activate" — your domain is ready in seconds.',
        },
        dmarc: {
          warningTitle: 'DMARC policy too strict',
          warningDescription:
            'Your domain has a strict SPF alignment (aspf=s). Gmail may flag your emails as "unusual" because our bounce server is on a different domain. Relax the SPF alignment to keep deliverability high.',
          missingTitle: 'DMARC record missing',
          missingDescription:
            'You do not have a DMARC record on this domain. Without it, providers like Gmail and Outlook may downgrade your deliverability or reject your emails. We recommend adding at minimum a "p=none" policy to monitor.',
          recommendedFix: 'Recommended DMARC record (replace your current value):',
          suggestedRecord: 'Add this TXT record on your DNS:',
        },
      },
      sendGrid: {
        title: 'SendGrid Email Domains',
        description: 'Configure and verify domains for professional newsletter delivery',
        addDomain: 'Add Domain',
        activeDomain: {
          title: 'Active Domain for Newsletter',
          shared: 'Shared',
          custom: 'Custom',
          domain: 'Domain',
          senderEmail: 'Sender email',
          senderName: 'Sender name',
          monthlyLimit: 'Monthly limit',
          defaultLimit: '50 emails/month',
          customLimit: '1,000 emails/month'
        },
        upgrade: {
          title: 'Increase your limit to 1,000 emails/month',
          benefits: 'Verify your custom domain for:',
          benefit1: 'Send up to 1,000 emails per month (instead of 50)',
          benefit2: 'More professional emails from you@yourdomain.com',
          benefit3: 'Improve deliverability and reduce spam'
        },
        form: {
          newDomain: 'New Domain',
          domain: 'Domain',
          domainPlaceholder: 'example: mycompany.com',
          subdomain: 'Subdomain',
          subdomainPlaceholder: 'mail',
          result: 'Result',
          add: 'Add',
          cancel: 'Cancel'
        },
        status: {
          verified: 'Verified',
          pending: 'Pending verification',
          failed: 'Verification failed'
        },
        dns: {
          title: 'DNS records to configure:',
          type: 'Type',
          host: 'Host',
          value: 'Value',
          instructions: 'Instructions',
          step1: 'Access your DNS provider panel (e.g. Cloudflare, GoDaddy)',
          step2: 'Add the CNAME DNS records listed above',
          step3: 'Wait for DNS propagation (5-30 minutes)',
          step4: 'Click "Verify" to complete the configuration'
        },
        sender: {
          title: 'Sender Email Configuration:',
          senderEmail: 'Sender Email',
          senderEmailPlaceholder: 'newsletter@',
          senderEmailHint: 'Must use the verified domain',
          senderName: 'Sender Name',
          senderNamePlaceholder: 'Your Name or Company',
          saveConfig: 'Save Configuration'
        },
        actions: {
          showDns: 'Show DNS',
          hideDns: 'Hide DNS',
          configSender: 'Configure Sender',
          editSender: 'Edit Sender',
          verify: 'Verify',
          delete: 'Delete'
        },
        emptyState: {
          title: 'No domains configured',
          description: 'Add a domain to start sending professional emails with your brand'
        },
        messages: {
          loadError: 'Error loading domains',
          addSuccess: 'Domain added! Configure DNS records to complete verification',
          addError: 'Error adding domain',
          verifySuccess: 'Domain verified successfully!',
          verifyFailed: 'Verification failed. Check DNS records and try again',
          verifyError: 'Error during verification',
          deleteConfirm: 'Are you sure you want to delete the domain',
          deleteSuccess: 'Domain deleted',
          deleteError: 'Error during deletion',
          senderUpdated: 'Sender configuration updated',
          senderError: 'Error updating',
          copiedToClipboard: 'Copied to clipboard'
        }
      }
    },
    dealSettings: {
      title: 'Quote Settings',
      description: 'Configure settings for quotes and deals',
      numbering: {
        title: 'Quote Numbering',
        description: 'Configure how quote numbers are generated',
        startNumber: 'Starting quote number',
        startNumberHelp: 'Quote numbers will start from this value each year',
        lastNumber: 'Last number used',
        lastNumberHelp: 'This is the last quote number used in the current year',
        referenceYear: 'Reference year',
        referenceYearHelp: 'Year to which the last quote number used refers'
      },
      statusChange: {
        title: 'Deal Status Change',
        description: 'Configure behavior when changing deal status',
        noteRequired: 'Note required on status change',
        noteRequiredHelp: 'If enabled, a note will be required each time the status of a deal is changed',
        simplifiedProbability: 'Use simplified probability',
        simplifiedProbabilityHelp: 'If enabled, probability options will be 25%, 50%, 75% instead of the 1-10 scale',
        showPaymentDueDate: 'Separate payment deadline in installments',
        showPaymentDueDateHelp: 'If enabled, each installment shows an additional row to set the payment deadline separately from the invoice issue date'
      },
      whatsappNotifications: {
        title: 'WhatsApp Notifications',
        description: 'Configure notifications for signed and uploaded contracts',
        chatIdLabel: 'WhatsApp Chat ID for notifications',
        chatIdHelp: 'WhatsApp chat ID where notifications will be sent when a contract is uploaded (e.g. 123456789@c.us)',
        attachContract: 'Attach contract to notification',
        customMessage: 'Custom message',
        restoreDefault: 'Restore default',
        availableVariables: 'Available variables:',
        clientName: 'Client name',
        dealTitle: 'Quote title',
        dealAmount: 'Total amount',
        servicesLoop: 'Loop for services, with:',
        serviceTitle: 'Service name',
        serviceAmount: 'Service amount',
        serviceType: 'Service type (Monthly/One-time)'
      },
      saveButton: 'Save Settings',
      messages: {
        saveSuccess: 'Settings saved successfully',
        saveError: 'Error saving settings',
        testSuccess: 'Test WhatsApp notification sent successfully!',
        testError: 'Error sending test notification',
        chatIdRequired: 'Please enter a valid WhatsApp chat ID first'
      }
    },
    leadPipeline: {
      title: 'Lead Pipelines',
      description: 'Manage pipelines and stages to organize your leads',
      newPipeline: 'New Pipeline',
      howItWorks: {
        title: 'How pipelines work',
        description: 'Pipelines allow you to organize leads into different workflows with custom stages. The default pipeline is used for leads that don\'t have a specific pipeline assigned.'
      },
      pipeline: {
        editTitle: 'Edit Pipeline',
        newTitle: 'New Pipeline',
        name: 'Name',
        namePlaceholder: 'e.g. B2B Sales, Partner Network',
        description: 'Description',
        descriptionPlaceholder: 'Optional description',
        color: 'Color',
        isDefault: 'Default pipeline',
        systemManaged: 'Managed by the system',
        systemManagedHint: 'Stages and pipeline are updated automatically and cannot be edited or deleted here',
        cancel: 'Cancel',
        save: 'Save Pipeline'
      },
      stage: {
        editTitle: 'Edit Stage',
        newTitle: 'New Stage',
        name: 'Name',
        namePlaceholder: 'e.g. Qualification, Proposal, Closing',
        description: 'Description',
        descriptionPlaceholder: 'Optional description',
        color: 'Color',
        conversionType: 'Conversion Type',
        isWon: 'Won Stage',
        isLost: 'Lost Stage',
        wonHelp: 'Stages marked as "Won" will be considered successful conversions in statistics.',
        cancel: 'Cancel',
        save: 'Save Stage',
        addStage: 'Add Stage',
        stagesTitle: 'Pipeline stages',
        noStages: 'No stages configured',
        won: 'WON',
        lost: 'LOST'
      },
      actions: {
        setDefault: 'Set as default',
        manageStages: 'Manage stages',
        clickToCopyId: 'Click to copy ID'
      },
      emptyState: {
        title: 'No pipelines configured',
        description: 'Create your first pipeline to organize leads into custom workflows',
        createFirst: 'Create First Pipeline'
      },
      messages: {
        pipelineNameRequired: 'Pipeline name is required',
        pipelineCreated: 'Pipeline created successfully',
        pipelineUpdated: 'Pipeline updated successfully',
        pipelineSaveError: 'Error saving pipeline',
        pipelineDeleted: 'Pipeline deleted successfully',
        pipelineDeleteError: 'Error deleting pipeline',
        selectPipeline: 'Select a pipeline',
        stageNameRequired: 'Stage name is required',
        stageCreated: 'Stage created successfully',
        stageUpdated: 'Stage updated successfully',
        stageSaveError: 'Error saving stage',
        stageDeleted: 'Stage deleted successfully',
        stageDeleteError: 'Error deleting stage',
        defaultUpdated: 'Default pipeline updated',
        defaultError: 'Error setting default pipeline',
        orderSaved: 'Pipeline order saved',
        stageOrderSaved: 'Stage order saved'
      },
      confirm: {
        deletePipeline: 'Are you sure you want to delete the pipeline "{name}"? This action is irreversible.',
        deleteStage: 'Are you sure you want to delete the stage "{name}"? This action is irreversible.'
      },
      deletionResolver: {
        title: 'Leads assigned to this pipeline',
        description: '{count} leads/clients are assigned to "{name}". Choose what to do with them before deleting the pipeline.',
        moveOption: {
          title: 'Move to another pipeline',
          description: 'Leads/clients keep all their data and move to the pipeline and stage you choose below.',
          targetPipelineLabel: 'Destination pipeline',
          targetPipelinePlaceholder: 'Select a pipeline',
          targetStageLabel: 'Destination stage',
          targetStagePlaceholder: 'Select a stage'
        },
        unassignOption: {
          title: 'Unlink from this pipeline',
          description: 'Leads/clients stay in the CRM but are no longer linked to any pipeline (they will only disappear from this board).'
        },
        summary: {
          move: 'You are about to move {count} leads/clients from "{sourceName}" to "{targetName} → {targetStage}", then delete "{sourceName}".',
          unassign: 'You are about to unlink {count} leads/clients from "{sourceName}", then delete it.'
        },
        actions: {
          cancel: 'Cancel',
          continue: 'Continue',
          back: 'Back',
          confirmAndDelete: 'Confirm and delete'
        },
        messages: {
          success: 'Leads moved and pipeline deleted successfully',
          error: 'Error while resolving pipeline deletion'
        }
      }
    },
    importExport: {
      title: 'Import / Export',
      description: 'Import or export CRM data',
      importClients: 'Import Clients',
      dragDrop: 'Drag and drop a CSV/Excel file, or click to select',
      supportedFormats: 'Supported: CSV, Excel (.xls, .xlsx)',
      parsing: 'Parsing file...',
      mapColumns: 'Map the columns of the file with client fields:',
      csvColumn: 'File Column',
      clientField: 'Client Field',
      doNotMap: 'Do not map this field',
      preview: 'Preview of data to import:',
      previewCount: 'Previewing',
      of: 'of',
      records: 'records',
      backToMapping: 'Back to mapping',
      importNow: 'Import Clients',
      importing: 'Importing...',
      completed: 'completed',
      imported: 'imported',
      failed: 'failed',
      total: 'total',
      new: 'new',
      updated: 'updated',
      errors: 'Errors:',
      importMore: 'Import more clients',
      insufficientPermissions: 'Insufficient permissions',
      permissionsMessage: 'You do not have the necessary permissions to use the import and export functionality. Contact your system administrator.',
      // Export section
      modeChoice: {
        title: 'What do you want to do?',
        subtitle: 'Choose whether to import or export data from your CRM',
        import: 'Import',
        importDesc: 'Import clients, leads, products or deals from Excel or CSV files',
        export: 'Export',
        exportDesc: 'Export clients and leads with advanced filters and associated contacts'
      },
      exportType: {
        title: 'What do you want to export?',
        subtitle: 'Select the type of data you want to export',
        clients: 'Clients Only',
        clientsDesc: 'Export only active clients with all their data and contacts',
        leads: 'Leads Only',
        leadsDesc: 'Export only leads with all their data and contacts',
        both: 'Clients + Leads',
        bothDesc: 'Export both clients and leads in a single file'
      },
      exportConfig: {
        title: 'Configure export',
        subtitle: 'Configure filters and options for export',
        filters: {
          title: 'Filters',
          interests: 'Filter by interests',
          tags: 'Filter by tags',
          country: 'Filter by country',
          noFilters: 'No filters available. All records will be exported.'
        },
        filterLogic: {
          title: 'Filter logic',
          or: 'At least one criterion (OR)',
          and: 'All criteria (AND)'
        },
        options: {
          title: 'Options',
          includeContacts: 'Include contacts on same row',
          maxContacts: 'Maximum contacts number'
        },
        format: {
          title: 'File format',
          excel: 'Excel (.xlsx)',
          csv: 'CSV'
        },
        recordCount: 'Records to export',
        calculating: 'Calculating...',
        noRecords: 'No records match the selected filters.',
        exportButton: 'Export',
        exporting: 'Exporting...'
      }
    },
    googleWorkspace: {
      title: 'Google Integration',
      description: 'Integrate your Google account to sync calendars, documents, and contacts',
      enabled: 'Enable Google Integration',
      enableDescription: 'Enable integrations with Google services',
      clientId: 'Client ID',
      clientSecret: 'Client Secret',
      domain: 'Google Workspace Domain',
      syncOptions: 'Synchronization Options',
      syncCalendar: 'Calendar',
      syncCalendarDescription: 'Sync events and appointments with Google Calendar',
      syncDrive: 'Google Drive',
      syncDriveDescription: 'Manage folders and documents automatically',
      syncContacts: 'Contacts',
      syncContactsDescription: 'Sync contacts between CRM and Google Contacts',
      syncGmail: 'Gmail',
      syncGmailDescription: 'Read and send emails directly from the CRM',
      gmail: {
        connectToSync: 'Connect your Google account to sync Gmail',
        ready: 'Gmail enabled. Reconnect your Google account to activate Gmail permissions.',
      },
      setupInstructions: 'Setup Instructions',
      setupSteps: 'To configure Google Workspace integration:',
      calendarSelection: 'Select calendars to synchronize',
      fetchCalendars: 'Fetch Calendars',
      noCalendarsFound: 'No calendars found. Click "Fetch Calendars" to retrieve available calendars.',
      calendarError: 'Failed to fetch Google calendars. Make sure Google integration is properly set up and at least one user has connected their Google account.',
      userConnection: {
        title: 'Google Account',
        connected: 'Google account connected',
        notConnected: 'Google account not connected',
        connect: 'Connect',
        connecting: 'Connecting...',
        disconnect: 'Disconnect',
        disconnecting: 'Disconnecting...',
        connectDescription: 'Connect your account to enable integrations',
        email: 'Google account email',
        redirecting: 'You will be redirected to Google for authorization',
        missingToken: 'Missing authorization token',
        reconnect: 'Disconnect and reconnect',
        reconnectDescription: 'Your account is connected but missing some critical information. We recommend disconnecting and reconnecting your account.'
      },
      activities: {
        title: 'Activities Sync',
        description: 'Sales activities will be automatically synced with Google Calendar',
        destinationCalendar: 'Destination calendar',
        selectCalendar: 'Select a calendar',
        loadingCalendars: 'Loading calendars...',
        calendarSelected: 'Selected calendar',
        save: 'Save'
      },
      drive: {
        useExistingFolder: 'Use an existing folder',
        noFolderSelected: 'No folder selected',
        browse: 'Browse',
        close: 'Close',
        connectToGoogle: 'Connect your Google account to browse folders',
        rootFolderName: 'Root folder name',
        clientFolderPattern: 'Client folder pattern',
        createAutomatically: 'Create folders automatically',
        createAutomaticallyDescription: 'Folders are created when needed',
        advancedOptions: 'Advanced options',
        variables: 'Variables'
      },
      contacts: {
        importFromGoogle: 'Import from Google',
        import: 'Import',
        importing: 'Importing...',
        importDescription: 'Sync your Google contacts to the Address Book',
        contactsImported: '{count} contacts imported',
        lastImport: 'Last import',
        exportToGoogle: 'Export to Google',
        export: 'Export',
        exporting: 'Exporting...',
        exportDescription: 'Sync client contacts to Google Contacts',
        connectToSync: 'Connect your Google account to sync contacts'
      },
      messages: {
        settingsSaved: 'Settings saved successfully',
        saveError: 'An error occurred while saving',
        enableAtLeastOne: 'Enable at least one sync option',
        accountDisconnected: 'Google account disconnected successfully',
        disconnectError: 'Could not disconnect Google account',
        accountLoadError: 'Error loading Google account info',
        clientIdNotConfigured: 'Google Client ID not configured in organization settings',
        authError: 'Could not start the Google authorization process',
        activitiesSaved: 'Settings saved successfully',
        activitiesSaveError: 'Error saving settings',
        contactsSynced: 'Synced {count} contacts',
        contactsSyncError: 'Error syncing contacts',
        contactsExported: 'Exported {count} contacts to Google',
        contactsExportError: 'Error exporting contacts',
        calendarsSavedWarning: 'Settings saved but could not save selected calendars'
      },
      confirm: {
        disconnect: 'Are you sure you want to disconnect your Google account? All integrations will stop working.'
      },
      saveSettings: 'Save Settings',
      saving: 'Saving...'
    },
    leadAutoAssignment: {
      title: 'Lead Auto-Assignment',
      description: 'Automatically assign leads based on custom rules',
      enabled: 'Rule active',
      editor: {
        newRule: 'New Rule',
        editRule: 'Edit Rule',
        ruleName: 'Rule name',
        ruleNamePlaceholder: 'e.g. Leads from Google campaigns',
        ruleNameRequired: 'Rule name'
      },
      operator: {
        title: 'When does this rule apply?',
        or: 'OR',
        orDescription: 'At least one condition must be true',
        orExample: 'E.g.: "From Google Ads" or "From Facebook"',
        and: 'ALL (AND)',
        andDescription: 'All conditions must be true',
        andExample: 'E.g.: "Premium Interest" and "Enterprise Pipeline"'
      },
      conditions: {
        title: 'Conditions',
        selectAtLeastOne: 'Select at least one',
        interests: 'Interests',
        pipeline: 'Pipeline',
        leadSources: 'Lead Sources',
        noInterests: 'No interests configured',
        noPipelines: 'No pipelines configured',
        noSources: 'No sources configured'
      },
      preview: {
        title: 'This rule will match:',
        interest: 'interest',
        pipeline: 'pipeline',
        source: 'source',
        leadsWith: 'Leads with',
        and: ' AND ',
        or: ' OR '
      },
      userPool: {
        title: 'Who should leads be assigned to?',
        selectedCount: '{count} user(s) selected - the one with least workload will be chosen',
        selectedCountSingular: '{count} user selected - will receive the lead'
      },
      validation: {
        selectConditionAndUser: 'Select at least one condition and at least one user',
        selectCondition: 'Select at least one condition',
        selectUser: 'Select at least one user'
      },
      buttons: {
        cancel: 'Cancel',
        saving: 'Saving...',
        saveChanges: 'Save changes',
        createRule: 'Create rule',
        newRule: 'New rule'
      },
      rules: {
        title: 'Assignment Rules',
        description: 'The first matching rule is applied (priority order)',
        empty: 'No rules configured',
        emptyDescription: 'Create your first rule to get started',
        noConditions: 'No conditions',
        assignTo: 'Assign to:',
        active: 'Active',
        edit: 'Edit',
        delete: 'Delete',
        increasePriority: 'Increase priority',
        decreasePriority: 'Decrease priority',
        confirmDelete: 'Are you sure you want to delete this rule?'
      },
      fallback: {
        title: 'Fallback',
        description: 'If no rule matches, the lead is assigned to one of these users',
        noUsers: 'No active users available',
        recommendation: 'Tip: select at least one fallback user for leads that don\'t match any rule'
      },
      advanced: {
        title: 'Advanced settings',
        loadCalculation: 'How to calculate user "workload"?',
        loadOptions: {
          lead: 'Count only assigned Leads',
          deal: 'Count only assigned Deals',
          client: 'Count only assigned Clients',
          all: 'Count everything (Leads + Deals + Clients)'
        },
        loadDescription: 'The user with fewer assigned entities will receive the next lead'
      },
      howItWorks: {
        title: 'How it works',
        step1: 'When a new lead arrives, the system evaluates rules from top to bottom',
        step2: 'The first matching rule determines the user pool',
        step3: 'If no rule matches, the fallback pool is used',
        step4: 'From the pool, the user with the least workload is chosen'
      },
      error: 'Error',
      deleted: 'Deleted'
    },
    bulkDelete: {
      title: 'Bulk Delete Management',
      description: 'Delete clients or deals in bulk with advanced safety controls',
      noPermission: 'You don\'t have permission to use this feature.',
      historyButton: 'Operations History',
      recentOperations: 'Recent Operations',
      noOperations: 'No operations executed',
      clients: 'Clients',
      deals: 'Deals',
      elements: 'elements',
      deleted: 'Deleted',
      errors: 'Errors',
      warning: {
        title: '⚠️ Warning - Irreversible Operation',
        message: 'Bulk deletions cannot be undone. All related data (deals, contacts, activities, projects, etc.) will be permanently deleted.'
      },
      entitySelection: {
        title: 'Entity Selection',
        clientsLabel: 'Clients',
        clientsDescription: 'Delete clients and related data',
        dealsLabel: 'Deals',
        dealsDescription: 'Delete deals and related services'
      },
      filters: {
        title: 'Filter Criteria',
        dateRange: 'Date Range',
        noDateFilter: '-- No date filter --',
        createdAt: 'Creation Date',
        updatedAt: 'Update Date',
        signedAt: 'Signed Date',
        closedAt: 'Closed Date',
        status: 'Status',
        assignedTo: 'Assigned To',
        tags: 'Tags'
      },
      preview: {
        button: 'Generate Preview',
        generating: 'Generating...',
        title: 'Deletion Preview',
        toDelete: 'to delete',
        relatedData: 'Related data involved',
        involvedTables: 'Involved tables:',
        noRelatedData: 'No related data',
        impactTitle: 'Impact on related data',
        impactMessage: 'Deleting {count} {entity} will also delete {related} related records. This operation cannot be undone.'
      },
      actions: {
        cancel: 'Cancel',
        proceedWithDeletion: 'Proceed with Deletion'
      },
      confirm: {
        title: 'Confirm Deletion',
        message: 'You are about to permanently delete:',
        irreversible: '⚠️ This operation is IRREVERSIBLE',
        confirmButton: 'CONFIRM DELETION',
        deleting: 'Deleting...'
      },
      messages: {
        success: 'Deletion completed successfully!',
        errorLoading: 'Error loading initial data:',
        errorPreview: 'Error generating preview:',
        errorDelete: 'Error during deletion:',
        userNotAuthenticated: 'User not authenticated',
        organizationNotFound: 'Organization not found',
        unknownError: 'Unknown error'
      },
      related: {
        deals: 'deals',
        contacts: 'contacts',
        activities: 'activities',
        services: 'services',
        projects: 'projects'
      }
    },
    permissions: {
      // Dashboard
      dashboard_view: { name: 'View Dashboard', description: 'Access to view the main dashboard and analytics' },
      dashboard_filter_by_user: { name: 'Filter Dashboard by User', description: 'Ability to filter dashboard data by specific users' },
      // Clients
      clients_view: { name: 'View Clients', description: 'View client data and their history' },
      clients_create: { name: 'Create Clients', description: 'Add new clients to the system' },
      clients_update: { name: 'Update Clients', description: 'Modify existing client data' },
      clients_delete: { name: 'Delete Clients', description: 'Remove clients from the system (critical operation)' },
      clients_merge: { name: 'Merge Clients/Leads', description: 'Merge duplicate clients or leads into a single record' },
      clients_filter_by_user: { name: 'Filter Clients by User', description: 'Filter clients by assigned user' },
      // Calendar
      calendar_view: { name: 'View Calendar', description: 'Access to view calendar events' },
      calendar_create: { name: 'Create Events', description: 'Create new calendar events' },
      calendar_update: { name: 'Update Events', description: 'Modify existing calendar events' },
      calendar_delete: { name: 'Delete Events', description: 'Delete calendar events' },
      // Deals
      deals_view: { name: 'View Deals', description: 'View all contracts and sales opportunities' },
      deals_create: { name: 'Create Deals', description: 'Create new contracts and opportunities' },
      deals_update: { name: 'Update Deals', description: 'Modify existing contracts' },
      deals_delete: { name: 'Delete Deals', description: 'Delete contracts (warning: may affect commissions)' },
      deals_filter_by_user: { name: 'Filter Deals by User', description: 'Filter deals by assigned user' },
      // All other permissions... (abbreviated for space)
    },
    users: {
      title: 'Users',
      description: 'Manage your organization users',
      addUser: 'Add User',
      add: 'Add',
      editUser: 'Edit user',
      deactivateUser: 'Deactivate user',
      reactivateUser: 'Reactivate user',
      changePassword: 'Change password',
      reset2FA: 'Reset 2FA',
      copyId: 'Copy ID',
      userIdCopied: 'User ID copied!',
      noName: 'No name',
      deactivated: 'Deactivated',
      active: 'Active',
      deactivatedUsers: 'Deactivated',
      activeUsers: 'Active',
      noPlan: 'No plan',
      selectRole: 'Select...',
      columns: {
        name: 'Name',
        email: 'Email',
        phone: 'Phone',
        role: 'Role',
        manager: 'Manager',
        newsletter: 'Newsletter',
        actions: 'Actions'
      },
      confirmations: {
        deactivate: {
          title: 'Deactivate User',
          message: 'Are you sure you want to deactivate this user? The user will no longer be able to access the system but can be reactivated later.',
          confirm: 'Deactivate',
          cancel: 'Cancel'
        },
        deactivateMultiple: {
          title: 'Deactivate Users',
          message: 'Are you sure you want to deactivate {count} user(s)? Users will no longer be able to access but can be reactivated.',
          confirm: 'Deactivate',
          cancel: 'Cancel'
        },
        reactivate: {
          title: 'Reactivate User',
          message: 'Are you sure you want to reactivate this user? The user will be able to access the system again.',
          confirm: 'Reactivate',
          cancel: 'Cancel'
        },
        reset2FA: {
          title: 'Reset 2FA',
          message: 'Are you sure you want to reset two-factor authentication for {name}? The user will need to set up 2FA again on their next login.',
          confirm: 'Reset 2FA',
          cancel: 'Cancel'
        }
      },
      messages: {
        deactivateSuccess: 'User deactivated successfully',
        deactivateError: 'Error deactivating user',
        deactivateMultipleSuccess: '{count} user(s) deactivated successfully',
        deactivateMultipleError: 'Error deactivating users',
        reactivateSuccess: 'User reactivated successfully',
        reactivateError: 'Error reactivating user',
        roleUpdated: 'User role updated successfully',
        roleRemoved: 'User role removed successfully',
        roleUpdateError: 'Error updating user role',
        selectRole: 'Select a role',
        cannotDeactivateSelf: 'You cannot deactivate your own account',
        mustKeepAdmin: 'You must keep at least one administrator in the organization',
        selectAtLeastOne: 'Select at least one user to deactivate',
        reset2FASuccess: '2FA reset successfully for {name}',
        reset2FAError: 'Error resetting 2FA'
      },
      plan: {
        noPlanTitle: 'No subscription plan assigned',
        noPlanDescription: 'Your organization does not have an assigned subscription plan. You can add unlimited users until a plan is assigned. Contact the system administrator to configure the appropriate plan.'
      },
      selection: {
        selected: '{count} user(s) selected',
        deactivateSelected: 'Deactivate {count} user(s)',
        deactivating: 'Deactivating...'
      },
      userForm: {
        titleNew: 'New User',
        titleEdit: 'Edit User',
        subtitleNew: 'Fill in all fields to create a new user',
        subtitleEdit: 'Edit the details of the selected user',
        fullName: 'Full Name',
        fullNamePlaceholder: 'John Doe',
        email: 'Email',
        emailPlaceholder: 'john.doe@example.com',
        emailCannotChange: 'Email cannot be changed',
        password: 'Password',
        passwordPlaceholder: 'At least 6 characters',
        passwordHint: 'Password must contain at least 6 characters',
        phone: 'Phone',
        phonePlaceholder: '+1 123 456 7890',
        role: 'Role',
        rolePlaceholder: 'Select a role...',
        manager: 'Manager',
        managerPlaceholder: 'No manager',
        managerHint: 'The manager of this user for the company hierarchy',
        newsletterConsent: 'Newsletter consent',
        newsletterConsentHint: 'The user agreed to receive the newsletter',
        profileImageHint: 'JPG or PNG, max 2MB',
        cancel: 'Cancel',
        saving: 'Saving...',
        createButton: 'Create User',
        updateButton: 'Update User',
        messages: {
          createSuccess: 'User created successfully',
          updateSuccess: 'User updated successfully',
          createError: 'Error creating user',
          updateError: 'Error updating user',
          roleRequired: 'Select a role before saving',
          imageUploadSuccess: 'Image uploaded successfully',
          imageUploadError: 'Error uploading image',
          imageInvalidFormat: 'Unsupported file format. Use JPG or PNG.',
          imageTooBig: 'File is too large. Maximum size: 2MB',
          newsletterConsentError: 'Newsletter consent not saved'
        }
      },
      passwordChange: {
        title: 'Change Password',
        user: 'User',
        newPassword: 'New Password',
        newPasswordPlaceholder: 'Enter the new password',
        passwordHint: 'Password must contain at least 6 characters',
        confirmPassword: 'Confirm Password',
        confirmPasswordPlaceholder: 'Confirm the new password',
        cancel: 'Cancel',
        saving: 'Saving...',
        changeButton: 'Change Password',
        messages: {
          minLength: 'Password must be at least 6 characters',
          mismatch: 'Passwords do not match',
          success: 'Password updated successfully',
          error: 'Error updating password'
        }
      }
    },
    roles: {
      title: 'Roles',
      description: 'Manage roles and permissions',
      addRole: 'Add Role',
      editRole: 'Edit Role',
      deleteRole: 'Delete Role',
      roleName: 'Role Name',
      roleDescription: 'Role Description',
      permissions: 'Permissions',
      permissionsCount: '{selected} / {total} permissions',
      criticalCount: '{count} critical',
      selectAll: 'All',
      selectNone: 'None',
      search: 'Search...',
      noPermissionsFound: 'No permissions found',
      groups: {
        core: 'Core',
        financial: 'Financial',
        communication: 'Communication',
        operations: 'Operations',
        services: 'Services',
        system: 'System',
        other: 'Other'
      },
      confirmations: {
        delete: {
          title: 'Delete Role',
          message: 'Are you sure you want to delete this role? Users with this role will lose their permissions.',
          confirm: 'Delete',
          cancel: 'Cancel'
        }
      },
      messages: {
        createSuccess: 'Role created successfully',
        createError: 'Error creating role',
        updateSuccess: 'Role updated successfully',
        updateError: 'Error updating role',
        deleteSuccess: 'Role deleted successfully',
        deleteError: 'Error deleting role'
      },
      permissionPicker: {
        permissionsCount: 'permissions',
        critical: 'critical',
        searchPlaceholder: 'Search...',
        selectAll: 'All',
        selectNone: 'None',
        other: 'Other',
        noPermissionsFound: 'No permissions found'
      },
      form: {
        titleNew: 'New Role',
        titleEdit: 'Edit Role',
        name: 'Name',
        namePlaceholder: 'Role name',
        description: 'Description',
        descriptionPlaceholder: 'Optional description',
        cancel: 'Cancel',
        save: 'Save',
        create: 'Create'
      },
      confirmDelete: {
        title: 'Delete Role',
        message: 'Are you sure you want to delete this role? This action will affect all users assigned to this role.',
        confirm: 'Delete',
        cancel: 'Cancel'
      }
    },
    permissionLabels: {
      // CORE
      clients: {
        view: { name: 'View Clients', description: 'Can view client details and information' },
        create: { name: 'Create Clients', description: 'Can add new clients to the CRM' },
        update: { name: 'Edit Clients', description: 'Can modify existing client data' },
        delete: { name: 'Delete Clients', description: 'Can delete clients from the system' },
        merge: { name: 'Merge Clients/Leads', description: 'Can merge duplicate clients or leads into a single record' },
        manage: { name: 'Full Management', description: 'Full access to client management' },
        filter_by_user: { name: 'Assigned Clients Only', description: 'Can only see clients they are responsible for' },
        view_subordinates: { name: 'View Subordinates\' Clients', description: 'Can view clients assigned to direct subordinates' },
        view_all_subordinates: { name: 'View All Subordinates\' Clients', description: 'Can view clients assigned to all subordinates in the hierarchy' }
      },
      deals: {
        view: { name: 'View Deals', description: 'Can view commercial deals' },
        create: { name: 'Create Deals', description: 'Can create new deals' },
        update: { name: 'Edit Deals', description: 'Can modify existing deals' },
        delete: { name: 'Delete Deals', description: 'Can delete deals' },
        assign: { name: 'Assign Deals', description: 'Can assign deals to other users' },
        filter_by_user: { name: 'Assigned Deals Only', description: 'Can only see deals they are responsible for' },
        generate: { name: 'Generate PDF', description: 'Can generate PDF documents for deals' },
        hours_view: { name: 'View Hour Packages', description: 'Can view hour packages associated with deals' },
        hours_update: { name: 'Edit Hour Packages', description: 'Can modify hour packages associated with deals' },
        view_subordinates: { name: 'View Subordinates\' Deals', description: 'Can view deals assigned to direct subordinates' },
        view_all_subordinates: { name: 'View All Subordinates\' Deals', description: 'Can view deals assigned to all subordinates in the hierarchy' }
      },
      leads: {
        view: { name: 'View Leads', description: 'Can view leads' },
        create: { name: 'Create Leads', description: 'Can add new leads' },
        update: { name: 'Edit Leads', description: 'Can modify lead data' },
        delete: { name: 'Delete Leads', description: 'Can delete leads' },
        convert: { name: 'Convert Leads', description: 'Can convert a lead to a client' },
        filter_by_user: { name: 'Assigned Leads Only', description: 'Can only see leads they are responsible for' },
        view_subordinates: { name: 'View Subordinates\' Leads', description: 'Can view leads assigned to direct subordinates' },
        view_all_subordinates: { name: 'View All Subordinates\' Leads', description: 'Can view leads assigned to all subordinates in the hierarchy' }
      },
      lead_notes: {
        view: { name: 'View Notes', description: 'Can view lead notes' },
        create: { name: 'Create Notes', description: 'Can add notes to leads' },
        update: { name: 'Edit Notes', description: 'Can modify existing notes' },
        delete: { name: 'Delete Notes', description: 'Can delete notes' }
      },
      lead_sources: {
        view: { name: 'View Sources', description: 'Can view lead sources' },
        create: { name: 'Create Sources', description: 'Can add new sources' },
        update: { name: 'Edit Sources', description: 'Can modify existing sources' },
        delete: { name: 'Delete Sources', description: 'Can delete sources' },
        manage: { name: 'Full Management', description: 'Full access to source management' }
      },
      contacts: {
        view: { name: 'View Contacts', description: 'Can view the contact directory' },
        create: { name: 'Create Contacts', description: 'Can add new contacts to the directory' },
        update: { name: 'Edit Contacts', description: 'Can modify existing contacts' },
        delete: { name: 'Delete Contacts', description: 'Can delete contacts from the directory' },
        manage: { name: 'Sync Management', description: 'Can sync contacts with Google' }
      },
      dashboard: {
        view: { name: 'View Dashboard', description: 'Can view the main dashboard' },
        filter_by_user: { name: 'Personal Dashboard', description: 'Can only see data related to their own activities' }
      },
      calendar: {
        view: { name: 'View Calendar', description: 'Can view the calendar' },
        create: { name: 'Create Events', description: 'Can create events and appointments' },
        update: { name: 'Edit Events', description: 'Can modify existing events' },
        delete: { name: 'Delete Events', description: 'Can delete events' },
        share: { name: 'Manage sharing', description: 'Decides who sees whose calendar, also by department and role (everyone can always share their own)' },
        sync: { name: 'Connect Google or Outlook', description: 'Connects their own Google or Outlook account to the calendar' },
        configure: { name: 'Calendar settings', description: 'Types, colors, outcomes, Google and Outlook rules for the whole company' }
      },
      calendar_others: {
        view: { name: 'Sees everyone\'s calendars', description: 'Sees the appointments of all company members, without needing shares' },
        update: { name: 'Edits colleagues\' items', description: 'Moves, changes and sets outcomes on colleagues\' appointments in every calendar they see' },
        delete: { name: 'Deletes colleagues\' items', description: 'Deletes colleagues\' appointments in every calendar they see' },
        schedule: { name: 'Marks colleagues as unavailable', description: 'Marks on colleagues\' calendars the periods when they are not working (anyone can do it on their own calendar)' }
      },
      // FINANCIAL
      fattureincloud: {
        view: { name: 'View Invoicing', description: 'Can view invoices and fiscal documents' },
        create: { name: 'Create Documents', description: 'Can create invoices and documents' },
        update: { name: 'Edit Documents', description: 'Can modify existing documents' },
        delete: { name: 'Delete Documents', description: 'Can delete documents' },
        sync: { name: 'Sync', description: 'Can sync data with Fatture in Cloud' },
        configure: { name: 'Configure', description: 'Can configure Fatture in Cloud integration' }
      },
      user_commissions: {
        view: { name: 'View Commissions', description: 'Can see user commissions' },
        update: { name: 'Edit Commissions', description: 'Can modify commissions' },
        manage: { name: 'Full Management', description: 'Full access to user commission management' }
      },
      commissions: {
        view: { name: 'View Commissions', description: 'Can see accrued commissions' },
        manage: { name: 'Manage Commissions', description: 'Can manage and approve commissions' },
        update: { name: 'Edit Commissions', description: 'Can modify commissions' },
        refresh: { name: 'Recalculate', description: 'Can recalculate commissions' }
      },
      commission_groups: {
        view: { name: 'View Groups', description: 'Can see commission groups' },
        create: { name: 'Create Groups', description: 'Can create new groups' },
        update: { name: 'Edit Groups', description: 'Can modify existing groups' },
        delete: { name: 'Delete Groups', description: 'Can delete groups' }
      },
      // COMMUNICATION
      whatsapp: {
        view: { name: 'View WhatsApp', description: 'Can view WhatsApp chats' },
        create: { name: 'Send Messages', description: 'Can send WhatsApp messages' },
        assign: { name: 'Assigned Chats Only', description: 'Can only see chats they are responsible for' }
      },
      newsletter: {
        view: { name: 'View Newsletter', description: 'Can view newsletter campaigns' },
        create: { name: 'Create Campaigns', description: 'Can create new campaigns' },
        update: { name: 'Edit Campaigns', description: 'Can modify existing campaigns' },
        delete: { name: 'Delete Campaigns', description: 'Can delete campaigns' },
        send: { name: 'Send Newsletter', description: 'Can send campaigns to recipients' }
      },
      voice_agents: {
        view: { name: 'View Voice Agents', description: 'Can view voice agents and call history' },
        create: { name: 'Create Voice Agents', description: 'Can create and activate voice agent bindings' },
        update: { name: 'Edit Voice Agents', description: 'Can modify voice agent configuration' },
        delete: { name: 'Delete Voice Agents', description: 'Can remove voice agent bindings' },
        dial: { name: 'Approve Outbound Calls', description: 'Can authorize and launch outbound calls (HITL approver)' },
        manage: { name: 'Manage Voice Setup', description: 'Can manage BYOK provider keys and phone numbers' }
      },
      email_signature: {
        generate: { name: 'Generate Signature', description: 'Can generate custom email signatures' }
      },
      // OPERATIONS
      inventory: {
        view: { name: 'View Warehouse', description: 'Can view products and stock' },
        create: { name: 'Create Products', description: 'Can add new products' },
        update: { name: 'Edit Products', description: 'Can modify product data' },
        delete: { name: 'Delete Products', description: 'Can delete products' },
        manage: { name: 'Full Management', description: 'Full access to warehouse management' }
      },
      rentals: {
        view: { name: 'View Rentals', description: 'Can view rentals' },
        create: { name: 'Create Rentals', description: 'Can create new rentals' },
        update: { name: 'Edit Rentals', description: 'Can modify existing rentals' },
        delete: { name: 'Delete Rentals', description: 'Can delete rentals' }
      },
      projects: {
        view: { name: 'View Projects', description: 'Can view projects' },
        create: { name: 'Create Projects', description: 'Can create new projects' },
        update: { name: 'Edit Projects', description: 'Can modify existing projects' },
        delete: { name: 'Delete Projects', description: 'Can delete projects' },
        manage: { name: 'Full Management', description: 'Full access to project management' }
      },
      tasks: {
        view: { name: 'View Tasks', description: 'Can view tasks' },
        create: { name: 'Create Tasks', description: 'Can create new tasks' },
        update: { name: 'Edit Tasks', description: 'Can modify existing tasks' },
        delete: { name: 'Delete Tasks', description: 'Can delete tasks' },
        complete: { name: 'Complete', description: 'Can mark tasks as completed' },
        assign: { name: 'Assign', description: 'Can assign tasks to other users' },
        filter_by_user: { name: 'Assigned Tasks Only', description: 'Can only see tasks they are responsible for' }
      },
      tickets: {
        view: { name: 'View Tickets', description: 'Can view tickets' },
        create: { name: 'Create Tickets', description: 'Can create new tickets' },
        update: { name: 'Edit Tickets', description: 'Can modify existing tickets' },
        delete: { name: 'Delete Tickets', description: 'Can delete tickets' },
        assign: { name: 'Assign', description: 'Can assign tickets to other users' },
        generate: { name: 'Export Tickets', description: 'Can export tickets to CSV/Excel' },
        manage: { name: 'Full Management', description: 'Full access to ticket management' },
        report: { name: 'Ticket Reports', description: 'Can generate ticket reports' },
        export: { name: 'Export Data', description: 'Can export ticket data' }
      },
      ticket_types: {
        view: { name: 'View Ticket Types', description: 'Can view intervention type configurations' },
        create: { name: 'Create Ticket Types', description: 'Can create new intervention types' },
        update: { name: 'Edit Ticket Types', description: 'Can modify existing intervention types' },
        delete: { name: 'Delete Ticket Types', description: 'Can delete intervention types' },
        manage: { name: 'Full Management', description: 'Full access to intervention type management' }
      },
      ticket_time_entries: {
        view: { name: 'View Time Entries', description: 'Can view time tracking on tickets' },
        create: { name: 'Log Time', description: 'Can log time on tickets' },
        update: { name: 'Edit Time Entries', description: 'Can modify existing time entries' },
        delete: { name: 'Delete Time Entries', description: 'Can delete time entries' },
        manage: { name: 'Full Management', description: 'Full access to all time entries' }
      },
      ticket_sla_configs: {
        view: { name: 'View SLA Configs', description: 'Can view SLA configurations' },
        create: { name: 'Create SLA Configs', description: 'Can create new SLA configurations' },
        update: { name: 'Edit SLA Configs', description: 'Can modify existing SLA configurations' },
        delete: { name: 'Delete SLA Configs', description: 'Can delete SLA configurations' },
        manage: { name: 'Full Management', description: 'Full access to SLA management' }
      },
      // SERVICES
      services: {
        view: { name: 'View Services', description: 'Can view the service catalog' },
        create: { name: 'Create Services', description: 'Can add new services' },
        update: { name: 'Edit Services', description: 'Can modify existing services' },
        delete: { name: 'Delete Services', description: 'Can delete services' },
        manage: { name: 'Full Management', description: 'Full access to service management' }
      },
      service_costs: {
        view: { name: 'View Costs', description: 'Can view service costs' },
        create: { name: 'Create Costs', description: 'Can add new costs' },
        update: { name: 'Edit Costs', description: 'Can modify existing costs' },
        delete: { name: 'Delete Costs', description: 'Can delete costs' }
      },
      service_margins: {
        view: { name: 'View Margins', description: 'Can see service margins' }
      },
      client_expenses: {
        view: { name: 'View Expenses', description: 'Can view client expenses' },
        create: { name: 'Create Expenses', description: 'Can add new expenses' },
        update: { name: 'Edit Expenses', description: 'Can modify existing expenses' },
        delete: { name: 'Delete Expenses', description: 'Can delete expenses' },
        filter_by_user: { name: 'Assigned Expenses Only', description: 'Can only see expenses they are responsible for' }
      },
      service_task_templates: {
        view: { name: 'View Templates', description: 'Can view task templates' },
        create: { name: 'Create Templates', description: 'Can create new templates' },
        update: { name: 'Edit Templates', description: 'Can modify existing templates' },
        delete: { name: 'Delete Templates', description: 'Can delete templates' },
        manage: { name: 'Full Management', description: 'Full access to template management' }
      },
      external_partners: {
        view: { name: 'View Partners', description: 'Can view external partners' },
        create: { name: 'Create Partners', description: 'Can add new partners' },
        update: { name: 'Edit Partners', description: 'Can modify existing partners' },
        delete: { name: 'Delete Partners', description: 'Can delete partners' }
      },
      // SYSTEM
      users: {
        view: { name: 'View Users', description: 'Can see the user list' },
        create: { name: 'Create Users', description: 'Can invite new users' },
        update: { name: 'Edit Users', description: 'Can modify user data' },
        delete: { name: 'Delete Users', description: 'Can remove users from the system' }
      },
      roles: {
        view: { name: 'View Roles', description: 'Can see available roles' },
        create: { name: 'Create Roles', description: 'Can create new roles' },
        update: { name: 'Edit Roles', description: 'Can modify role permissions' },
        delete: { name: 'Delete Roles', description: 'Can delete roles' }
      },
      settings: {
        view: { name: 'View Settings', description: 'Can view settings' },
        update: { name: 'Edit Settings', description: 'Can modify settings' },
        manage: { name: 'Full Management', description: 'Full access to all settings' }
      },
      api_config: {
        view: { name: 'View API', description: 'Can view API configurations' },
        create: { name: 'Create API', description: 'Can create new configurations' },
        update: { name: 'Edit API', description: 'Can modify existing configurations' },
        delete: { name: 'Delete API', description: 'Can delete configurations' }
      },
      categories: {
        view: { name: 'View Categories', description: 'Can view categories' },
        create: { name: 'Create Categories', description: 'Can create new categories' },
        update: { name: 'Edit Categories', description: 'Can modify existing categories' },
        delete: { name: 'Delete Categories', description: 'Can delete categories' }
      },
      templates: {
        view: { name: 'View Templates', description: 'Can view templates' },
        create: { name: 'Create Templates', description: 'Can create new templates' },
        update: { name: 'Edit Templates', description: 'Can modify existing templates' },
        delete: { name: 'Delete Templates', description: 'Can delete templates' }
      },
      signatures: {
        view: { name: 'View Signatures', description: 'Can see digital signatures' },
        create: { name: 'Create Signatures', description: 'Can create new signatures' }
      },
      payment_methods: {
        view: { name: 'View Payments', description: 'Can view payment methods' },
        create: { name: 'Create Payments', description: 'Can add new methods' },
        update: { name: 'Edit Payments', description: 'Can modify existing methods' },
        delete: { name: 'Delete Payments', description: 'Can delete methods' }
      },
      referrers: {
        view: { name: 'View Referrers', description: 'Can view referrers' },
        create: { name: 'Create Referrers', description: 'Can add new referrers' },
        update: { name: 'Edit Referrers', description: 'Can modify existing referrers' },
        delete: { name: 'Delete Referrers', description: 'Can delete referrers' }
      },
      custom_fields: {
        manage: { name: 'Manage Fields', description: 'Can manage custom fields' }
      },
      custom_queries: {
        view: { name: 'View Queries', description: 'Can view custom queries' },
        create: { name: 'Create Queries', description: 'Can create new queries' }
      },
      invoices: {
        view: { name: 'View Invoices', description: 'Can view invoices' },
        create: { name: 'Create Invoices', description: 'Can create new invoices' },
        update: { name: 'Edit Invoices', description: 'Can modify existing invoices' },
        delete: { name: 'Delete Invoices', description: 'Can delete invoices' }
      },
      settings_sendgrid: {
        view: { name: 'View SendGrid', description: 'Can view SendGrid domains' },
        create: { name: 'Create Domains', description: 'Can add new domains' },
        update: { name: 'Edit Domains', description: 'Can modify existing domains' },
        delete: { name: 'Delete Domains', description: 'Can delete domains' },
        verify: { name: 'Verify Domains', description: 'Can verify domains' }
      },
      settings_microsoft_workspace: {
        view: { name: 'Microsoft Integration Access', description: 'Can access Microsoft/OneDrive integration settings' }
      },
      settings_google_workspace: {
        view: { name: 'Google Integration Access', description: 'Can access Google Workspace integration settings' }
      },
      // ANALYTICS
      reports: {
        view: { name: 'View Reports', description: 'Can view custom reports' },
        create: { name: 'Create Reports', description: 'Can create new custom reports' },
        update: { name: 'Edit Reports', description: 'Can modify existing reports' },
        delete: { name: 'Delete Reports', description: 'Can delete reports' },
        execute: { name: 'Execute Reports', description: 'Can run reports and view results' },
        export: { name: 'Export Reports', description: 'Can export reports to PDF, Excel or CSV' },
        schedule: { name: 'Schedule Reports', description: 'Can schedule automatic report execution' },
        share: { name: 'Share Reports', description: 'Can share reports with other users' }
      },
      dashboards: {
        view: { name: 'View Dashboards', description: 'Can view analytics dashboards' },
        create: { name: 'Create Dashboards', description: 'Can create new dashboards with widgets' },
        update: { name: 'Edit Dashboards', description: 'Can modify dashboard layout and widgets' },
        delete: { name: 'Delete Dashboards', description: 'Can delete dashboards' },
        share: { name: 'Share Dashboards', description: 'Can share dashboards with other users' }
      },
      kpis: {
        view: { name: 'View KPIs', description: 'Can view key performance indicators' },
        create: { name: 'Create KPIs', description: 'Can define new KPIs and targets' },
        update: { name: 'Edit KPIs', description: 'Can modify KPI configurations' },
        delete: { name: 'Delete KPIs', description: 'Can delete KPIs' }
      },
      insights: {
        view: { name: 'View Insights', description: 'Can view automatic data insights' },
        dismiss: { name: 'Dismiss Insights', description: 'Can hide irrelevant insights' }
      },
      // BOOKING
      booking: {
        view: { name: 'View Bookings', description: 'Can view bookings and appointments' },
        create: { name: 'Create Bookings', description: 'Can create new bookings' },
        update: { name: 'Edit Bookings', description: 'Can modify existing bookings' },
        delete: { name: 'Delete Bookings', description: 'Can cancel or delete bookings' },
        manage: { name: 'Manage Bookings', description: 'Full booking management (event types, availability)' },
        filter_by_user: { name: 'Personal Visibility', description: 'Only see bookings assigned to the current user' }
      },
      // SURVEYS
      surveys: {
        view: { name: 'View Surveys', description: 'Can view surveys and responses' },
        create: { name: 'Create Surveys', description: 'Can create new surveys' },
        update: { name: 'Edit Surveys', description: 'Can modify existing surveys' },
        delete: { name: 'Delete Surveys', description: 'Can delete surveys' }
      },
      // PARTNER TASKS
      partner_tasks: {
        view: { name: 'View Partner Tasks', description: 'Can view tasks assigned to partners' },
        create: { name: 'Create Partner Tasks', description: 'Can create new tasks for partners' },
        update: { name: 'Edit Partner Tasks', description: 'Can modify existing partner tasks' },
        delete: { name: 'Delete Partner Tasks', description: 'Can delete partner tasks' },
        manage: { name: 'Full Management', description: 'Full access to partner task management' },
        assign: { name: 'Assign Partner Tasks', description: 'Can assign tasks to partners' }
      },
      // SETTINGS
      settings_company: {
        view: { name: 'View Company Settings', description: 'Can access company settings' },
        manage: { name: 'Manage Company Settings', description: 'Can modify company settings' }
      },
      settings_clients: {
        view: { name: 'View Client Settings', description: 'Can access client settings' },
        manage: { name: 'Manage Client Settings', description: 'Can modify client settings' }
      },
      settings_partners: {
        view: { name: 'View Partner Settings', description: 'Can access partner settings' },
        manage: { name: 'Manage Partner Settings', description: 'Can modify partner settings' }
      },
      settings_services: {
        view: { name: 'View Service Settings', description: 'Can access service settings' },
        manage: { name: 'Manage Service Settings', description: 'Can modify service settings' }
      },
      settings_payment_methods: {
        view: { name: 'View Payment Settings', description: 'Can access payment method settings' },
        manage: { name: 'Manage Payment Settings', description: 'Can modify payment method settings' }
      },
      settings_api: {
        view: { name: 'View API Settings', description: 'Can access API settings' },
        manage: { name: 'Manage API Settings', description: 'Can modify API settings' }
      },
      settings_categories: {
        view: { name: 'View Category Settings', description: 'Can access category settings' },
        manage: { name: 'Manage Category Settings', description: 'Can modify category settings' }
      },
      settings_interest_categories: {
        view: { name: 'View Interest Categories', description: 'Can access interest category settings' },
        manage: { name: 'Manage Interest Categories', description: 'Can modify interest category settings' }
      },
      settings_menu: {
        view: { name: 'View Menu Settings', description: 'Can access menu settings' },
        manage: { name: 'Manage Menu Settings', description: 'Can modify menu settings' }
      },
      settings_quote_templates: {
        view: { name: 'View Quote Templates', description: 'Can access quote template settings' },
        manage: { name: 'Manage Quote Templates', description: 'Can modify quote templates' }
      },
      settings_deals: {
        view: { name: 'View Deal Settings', description: 'Can access deal settings' },
        manage: { name: 'Manage Deal Settings', description: 'Can modify deal settings' }
      },
      settings_sales_targets: {
        view: { name: 'View Sales Targets', description: 'Can access sales target settings' },
        manage: { name: 'Manage Sales Targets', description: 'Can modify sales targets' }
      },
      settings_users: {
        view: { name: 'View User Settings', description: 'Can access user management settings' },
        manage: { name: 'Manage User Settings', description: 'Can modify user management settings' }
      },
      settings_language: {
        view: { name: 'View Language Settings', description: 'Can access language settings' },
        manage: { name: 'Manage Language Settings', description: 'Can modify language settings' }
      },
      settings_search: {
        view: { name: 'View Search Settings', description: 'Can access search settings' },
        manage: { name: 'Manage Search Settings', description: 'Can modify search settings' }
      },
      settings_import_export: {
        view: { name: 'View Import/Export', description: 'Can access import/export settings' },
        manage: { name: 'Manage Import/Export', description: 'Can perform import/export operations' }
      },
      settings_commissions: {
        view: { name: 'View Commission Settings', description: 'Can access commission settings' },
        manage: { name: 'Manage Commission Settings', description: 'Can modify commission settings' }
      },
      settings_whatsapp: {
        view: { name: 'View WhatsApp Settings', description: 'Can access WhatsApp settings' },
        manage: { name: 'Manage WhatsApp Settings', description: 'Can modify WhatsApp settings' }
      },
      settings_automation: {
        view: { name: 'View Automation Settings', description: 'Can access workflow automation settings' },
        manage: { name: 'Manage Automation Settings', description: 'Can modify workflow automations' }
      },
      settings_fattureincloud: {
        view: { name: 'View Fatture in Cloud Settings', description: 'Can access Fatture in Cloud integration settings' },
        manage: { name: 'Manage Fatture in Cloud Settings', description: 'Can modify Fatture in Cloud integration' }
      },
      settings_business_central: {
        view: { name: 'View Business Central Settings', description: 'Can access Business Central integration settings' }
      },
      settings_smtp: {
        view: { name: 'View SMTP Settings', description: 'Can access SMTP email settings' },
        manage: { name: 'Manage SMTP Settings', description: 'Can modify SMTP email settings' }
      },
      settings_custom_fields: {
        view: { name: 'View Custom Field Settings', description: 'Can access custom field settings' },
        manage: { name: 'Manage Custom Field Settings', description: 'Can modify custom field settings' }
      },
      settings_subscription: {
        view: { name: 'View Subscription', description: 'Can access subscription settings' },
        manage: { name: 'Manage Subscription', description: 'Can modify subscription settings' }
      },
      settings_team_chat: {
        view: { name: 'View Team Chat Settings', description: 'Can access team chat settings' },
        manage: { name: 'Manage Team Chat Settings', description: 'Can modify team chat settings' }
      },
      settings_bulk_delete: {
        view: { name: 'Access Bulk Delete', description: 'Can access the bulk delete tool' }
      },
      settings_woocommerce: {
        view: { name: 'View WooCommerce Settings', description: 'Can access WooCommerce integration settings' }
      },
      settings_ecommerce: {
        view: { name: 'View Connected Stores', description: 'Can see connected online stores, their status, activity log and history' },
        manage: { name: 'Manage Connected Stores', description: 'Can connect, pause, reconnect and disconnect stores and change their settings' }
      },
      settings_notifications: {
        view: { name: 'View Team Notifications', description: 'Can see the organization notification defaults and who receives each notification' },
        manage: { name: 'Manage Team Notifications', description: 'Can change which notifications the organization receives and on which channels' }
      },
      settings_security: {
        view: { name: 'View Security Settings', description: 'Can access security settings' }
      },
      settings_meeting_bot: {
        view: { name: 'View Meeting Bot Settings', description: 'Can access meeting bot settings' }
      },
      settings_legal: {
        view: { name: 'View Legal Settings', description: 'Can access legal information settings' }
      },
      settings_booking: {
        view: { name: 'View Booking Settings', description: 'Can access booking settings' },
        manage: { name: 'Manage Booking Settings', description: 'Can modify booking settings' }
      },
      // MARKETING
      marketing: {
        view: { name: 'View Marketing', description: 'Can view marketing dashboard and campaign analytics' },
        create: { name: 'Create Campaigns', description: 'Can create new marketing campaigns' },
        update: { name: 'Edit Campaigns', description: 'Can modify existing campaigns' },
        delete: { name: 'Delete Campaigns', description: 'Can delete campaigns' }
      },
      settings_meta_ads: {
        view: { name: 'View Meta Ads Settings', description: 'Can access Meta Ads integration settings' },
        manage: { name: 'Manage Meta Ads Settings', description: 'Can configure Meta Ads integration' }
      },
      settings_google_ads: {
        view: { name: 'View Google Ads Settings', description: 'Can access Google Ads integration settings' },
        manage: { name: 'Manage Google Ads Settings', description: 'Can configure Google Ads integration' }
      },
      ai_text_generation: {
        view: { name: 'AI Text Generation', description: 'Can use AI to generate text content (descriptions, contracts, etc.)' }
      },
      ai_task_generation: {
        view: { name: 'AI Task Generation', description: 'Can use AI to automatically generate project tasks' }
      },
      ai_suggestions: {
        view: { name: 'AI Suggestions', description: 'Can use AI suggestions (next activity, deal from WhatsApp)' }
      },
      ai_upsell_scanner: {
        view: { name: 'AI Upsell Scanner', description: 'Can use AI to scan projects for upsell opportunities' }
      },
      ai_insights: {
        view: { name: 'AI Data Insights', description: 'Can use AI to generate data insights and analytics' }
      },
      ai_whatsapp_agent: {
        view: { name: 'View WhatsApp Agent', description: 'Can view WhatsApp AI agent responses and status' },
        manage: { name: 'Manage WhatsApp Agent', description: 'Can configure WhatsApp AI agent settings' }
      },
      ai_meeting_bot: {
        view: { name: 'View Meeting Bot', description: 'Can view AI meeting bot recordings and summaries' },
        manage: { name: 'Manage Meeting Bot', description: 'Can configure AI meeting bot (Vexa) settings' }
      },
      ai_status_updates: {
        view: { name: 'View AI Status Updates', description: 'Can view AI-generated project status updates' },
        manage: { name: 'Manage AI Status Updates', description: 'Can configure AI status update generation settings' }
      }
    },
    resourceNames: {
      // Core CRM
      clients: 'Client Directory',
      deals: 'Deals & Quotes',
      leads: 'Leads',
      lead_notes: 'Lead Notes',
      lead_sources: 'Acquisition Sources',
      lead_enrichment: 'Lead Enrichment B2B',
      contacts: 'Contact Book',
      dashboard: 'Main Dashboard',
      calendar: 'Calendar',
      calendar_others: 'Colleagues\' calendars',
      meetings: 'Meetings & Video Calls',
      documents: 'Document Management',
      categories: 'Client Categories & Tags',
      templates: 'Quote Templates',
      lead_forms: 'Lead Capture Forms',
      // Communication
      whatsapp: 'WhatsApp Chat',
      meta_messaging: 'Instagram and Messenger Messages',
      newsletter: 'Newsletter Campaigns',
      email_signature: 'Company Email Signature',
      surveys: 'Surveys & Questionnaires',
      qr_codes: 'Dynamic QR Codes',
      screen_recordings: 'Screen Recordings',
      agents: 'AI Agents (marketplace + workflow bindings)',
      voice_agents: 'Voice agents',
      // Services & Payments
      services: 'Service Catalog',
      service_costs: 'Service Hourly Rates',
      service_margins: 'Margins & Profitability',
      service_task_templates: 'Service Task Checklists',
      service_cost_templates: 'Cost Templates',
      client_expenses: 'Client Costs & Expenses',
      signatures: 'Quote Signatures',
      payment_methods: 'Payment Methods',
      // Billing & Finance
      fattureincloud: 'Fatture in Cloud',
      invoices: 'Invoices & Tax Documents',
      financial_dashboard: 'Financial Dashboard',
      bank_movements: 'Bank Movements (Cashbook)',
      expense_categories: 'Expense Categories',
      contracts: 'Contracts',
      // Commissions
      commissions: 'Commissions',
      user_commissions: 'My Commissions',
      commission_groups: 'Commission Groups',
      commission_reports: 'Commission Reports',
      commission_advanced: 'Advanced Commissions',
      // Projects & Operations
      projects: 'Projects',
      tasks: 'Tasks & Activities',
      tickets: 'Support Tickets',
      ticket_types: 'Ticket Types',
      ticket_time_entries: 'Time Tracking (Timesheet)',
      ticket_sla_configs: 'SLA Rules',
      booking: 'Online Bookings',
      orders: 'Orders',
      products: 'Product Catalog',
      warehouse: 'Warehouse',
      inventory: 'Warehouse',
      rentals: 'Rentals',
      // Partners & Referral
      external_partners: 'External Partners',
      partner_tasks: 'Partner Tasks',
      referrers: 'Referrers',
      // Report & Analytics
      analytics: 'Analytics',
      reports: 'Reporting (dashboards)',
      dashboards: 'Analytics Dashboards',
      kpis: 'KPI Indicators',
      insights: 'Automatic Insights',
      marketing: 'Marketing Intelligence',
      // System
      users: 'User Management',
      roles: 'Roles & Permissions',
      settings: 'General Settings',
      custom_fields: 'Custom Fields',
      custom_queries: 'Custom Queries',
      api_config: 'API Configuration',
      // Settings Tabs
      settings_company: 'Company Info',
      settings_clients: 'Client Configuration',
      settings_partners: 'Partner Configuration',
      settings_services: 'Service Configuration',
      settings_payment_methods: 'Payment Methods',
      settings_categories: 'Category Management',
      settings_interest_categories: 'Interest Categories',
      settings_quote_templates: 'Quote Templates',
      settings_deals: 'Deal Configuration',
      settings_sales_targets: 'Sales Targets',
      settings_users: 'User & Role Management',
      settings_language: 'Interface Language',
      settings_search: 'Global Search',
      settings_menu: 'Menu Customization',
      settings_import_export: 'Data Import/Export',
      settings_security: 'Security & Access',
      settings_legal: 'Legal Information',
      settings_subscription: 'Subscription Management',
      // Integration Settings
      settings_whatsapp: 'WhatsApp Configuration',
      settings_smtp: 'SMTP Email Server',
      settings_sendgrid: 'SendGrid Domains',
      settings_google_workspace: 'Google Workspace',
      settings_microsoft_workspace: 'Microsoft 365',
      settings_fattureincloud: 'Fatture in Cloud Connection',
      settings_business_central: 'Business Central',
      settings_woocommerce: 'WooCommerce Sync',
      settings_ecommerce: 'E-commerce: Connected Stores',
      settings_notifications: 'Team Notifications',
      settings_team_chat: 'Team Chat',
      settings_booking: 'Booking Configuration',
      settings_automation: 'Workflows & Automations',
      settings_commissions: 'Commission Configuration',
      settings_custom_fields: 'Custom Field Management',
      settings_newsletter: 'Newsletter Configuration',
      settings_meta_ads: 'Meta Ads Connection',
      settings_google_ads: 'Google Ads Connection',
      settings_meeting_bot: 'Meeting Bot Configuration',
      settings_api: 'API Tokens & Documentation',
      settings_bulk_delete: 'Bulk Delete',
      // AI
      ai_text_generation: 'AI — Service Descriptions',
      ai_task_generation: 'AI — Task Generation from Meetings',
      ai_suggestions: 'AI — Lead Note Suggestions',
      ai_upsell_scanner: 'AI — Upsell Opportunity Scanner',
      ai_insights: 'AI — Analytical Insights',
      ai_whatsapp_agent: 'AI — WhatsApp Agent',
      ai_meeting_bot: 'AI — Meeting Recording & Transcription',
      ai_status_updates: 'AI — Automatic Status Updates',
      // Manufacturing Warehouse
      wh_suppliers: 'Warehouse — Supplier Registry',
      wh_contractors: 'Warehouse — Contractor Registry',
      wh_ddts: 'Warehouse — Inbound DDTs',
      wh_lots: 'Warehouse — Lot Management',
      wh_bom: 'Warehouse — Bills of Materials (BOM)',
      wh_production: 'Warehouse — Production Cycles',
      wh_processing: 'Warehouse — External Processing',
      wh_samples: 'Warehouse — Lot Samples',
      wh_labels: 'Warehouse — Labels & Templates',
      wh_disposals: 'Warehouse — Disposal Registry',
      wh_traceability: 'Warehouse — Lot Traceability',
    },
    voice: {
      title: 'Voice agents',
      setup: {
        title: 'Voice setup',
        stepTos: 'Accept terms of use',
        stepElevenlabsKey: 'ElevenLabs API key',
        stepWebhookSecret: 'Webhook secret',
        stepTwilio: 'Twilio connection',
        stepImportNumber: 'Import phone number',
        stepTestCall: 'Test call',
        skipForNow: 'Skip for now',
        intro: 'Set up voice agents in a few steps. You can skip any step and resume later.',
        back: 'Back',
        next: 'Continue',
        finish: 'Finish',
        saved: 'Saved',
        tosIntro: 'Before you begin, please accept the voice agents terms of use.',
        tosAcceptContinue: 'Accept and continue',
        apiKeyLabel: 'API key',
        apiKeyExisting: 'A key is already saved — enter a new one to replace it.',
        apiKeyPlaceholder: 'Paste the key here',
        elevenlabsKeyDesc: 'Enter your ElevenLabs API key. It is stored encrypted and used server-side only.',
        webhookSecretLabel: 'Webhook secret',
        webhookSecretDesc: 'Enter the signing secret ElevenLabs uses for post-call webhooks.',
        webhookUrlTitle: 'Webhook URL to register',
        webhookUrlInstructions: 'Copy this URL into your ElevenLabs dashboard under Conversational AI → Webhooks (post-call):',
        copyUrl: 'Copy URL',
        copied: 'Copied',
        twilioSidLabel: 'Twilio Account SID',
        twilioTokenLabel: 'Twilio Auth Token',
        twilioDesc: 'Connect your Twilio account to place outbound calls.',
        importNumberDesc: 'Enter a number from your Twilio account (purchased or verified as caller ID): it will be linked to ElevenLabs for calls. E.164 format, e.g. +12695201973 — spaces and dashes are stripped automatically.',
        testCallDesc: 'Optionally place a test call to verify the setup. The call goes to the internal test number.',
        testCallNumber: 'Test number',
        testCallAgent: 'Voice agent',
        testCallAgentHint: 'The test call is placed by one of your installed voice agents.',
        testCallSelectPlaceholder: 'Select an agent…',
        testCallNoAgents:
          'You have not installed a voice agent yet, so the test call is not available here. Your setup is complete: click Finish, then install an agent from the Voice agents section — you can place a test call from there.',
        startTestCall: 'Start test call',
        testCallStarted: 'Test call started',
        completeTitle: 'Setup complete',
        completeDesc: 'Your voice agents are ready. You can adjust these settings anytime.',
        completeNextStep: 'The connection is set up. Next step: install your first voice agent to start receiving and placing calls.',
        goToAgents: 'Install a voice agent',
        goToNumbers: 'Manage phone numbers',
        saveError: 'Could not save. Try again.',
        errors: {
          twilioRejected:
            'Twilio rejected the connection. Check the following on your Twilio account:\n- Account SID and Auth Token are correct (copy them from the Twilio Console)\n- The account is active and has credit — if it is suspended or out of funds, top it up under Billing → Add funds\n- The phone number belongs to this Twilio account',
          providerAuth:
            'ElevenLabs rejected the API key (401). Check the BYOK key in the "ElevenLabs API key" step.',
          providerQuota:
            'ElevenLabs quota exhausted (402). Check the plan on your ElevenLabs account.',
          providerGeneric: 'The voice provider call failed. Please try again in a few minutes.',
          numberAlreadyImported:
            'This number is already imported and active — no need to import it again. Click Continue to proceed.',
        },
      },
      byok: {
        title: 'Provider credentials',
        keyConfigured: 'API key configured',
        keyMissing: 'API key not configured',
        subtitle: 'Credentials for voice providers (BYOK). Keys are stored encrypted and used server-side only.',
        updatedAt: 'Updated {date}',
        edit: 'Edit',
        configure: 'Configure',
        active: 'active',
        paused: 'paused',
        notConfigured: 'not configured',
        providerElevenlabsKey: 'ElevenLabs API key',
        providerElevenlabsSecret: 'ElevenLabs webhook secret',
        providerTwilioSid: 'Twilio Account SID',
        providerTwilioToken: 'Twilio Auth Token',
        drawerDesc: 'BYOK — the credential is stored encrypted and used server-side. You pay the provider directly.',
        save: 'Save',
        cancel: 'Cancel',
        saveError: 'Could not save the credential.',
        loading: 'Loading credentials…',
        secretHidden: 'Value hidden for security',
      },
      numbers: {
        title: 'Phone numbers',
        import: 'Import number',
        label: 'Label',
        inboundAgent: 'Inbound agent',
        subtitle: 'Phone numbers imported from ElevenLabs for inbound and outbound calls.',
        empty: 'No phone numbers imported yet.',
        phoneNumber: 'Phone number',
        countryCode: 'Country code',
        importTitle: 'Import a number',
        importDesc: 'Import a number already configured in your ElevenLabs account.',
        importButton: 'Import',
        importError: 'Could not import the number.',
        sipTab: 'SIP trunk',
        sipDesc:
          'Connect an existing number from your phone system (PBX) via SIP trunk, without moving it to Twilio. Ask your telephony provider for the trunk credentials.',
        sipInboundTitle: 'Inbound (receive calls)',
        sipOutboundTitle: 'Outbound (make calls)',
        sipUsername: 'SIP username',
        sipPassword: 'SIP password',
        sipAddressPlaceholder: 'Trunk address (e.g. sip.provider.com)',
        sipHint: 'Fill at least one section: inbound to answer calls, outbound to place them.',
        saveError: 'Could not save changes.',
        deleteConfirm: 'Remove this number? Inbound routing will stop immediately.',
        delete: 'Remove',
        noInboundAgent: 'No inbound agent',
        save: 'Save',
        edit: 'Edit',
        cancel: 'Cancel',
        loading: 'Loading numbers…',
        phonePlaceholder: '+39...',
        labelPlaceholder: 'e.g. Reception, Support',
        disabled: 'disabled',
      },
      list: {
        title: 'Voice agents',
        subtitle: 'AI agents that place and answer phone calls via ElevenLabs.',
        count: '{count} agents',
        countOne: '1 agent',
        empty: 'No voice agents yet',
        emptyDesc: 'Install a ready-made voice template to get started. Voice creation happens server-side.',
        installFromTemplate: 'Install from template',
        installTemplate: 'Install',
        installing: 'Installing…',
        installError: 'Could not install the template.',
        pickerTitle: 'Voice templates',
        pickerSubtitle: 'Choose a template to install as a voice agent.',
        pickerEmpty: 'No voice templates available.',
        off: 'Off',
        openDetail: 'Open',
        openBindings: 'Bindings',
      },
      calls: {
        title: 'Call history',
        subtitle: 'Calls placed and received by your voice agents.',
        duration: 'Duration',
        transcript: 'Transcript',
        viewTranscript: 'View transcript',
        summary: 'Summary',
        noTranscript: 'No transcript available.',
        recording: 'Recording',
        playRecording: 'Play recording',
        audioError: 'Could not load the recording.',
        noSummary: 'No summary available.',
        status: 'Status',
        empty: 'No calls recorded yet.',
        filterStatus: 'Status',
        filterTemplate: 'Agent',
        filterAll: 'All',
        filterFrom: 'From',
        filterTo: 'To',
        columnDate: 'Date',
        columnTitle: 'Call',
        columnCost: 'Cost',
        test: 'Test',
        credits: '{n} credits',
        close: 'Close',
        contactId: 'Contact',
      },
      hitl: {
        title: 'Pending approvals',
        subtitle: 'Outbound calls waiting for your approval before dialing.',
        approve: 'Approve',
        reject: 'Reject',
        approveBatch: 'Approve selected',
        rejectReason: 'Reason for rejection',
        rejectPlaceholder: 'Optional reason…',
        confirmReject: 'Confirm rejection',
        cancel: 'Cancel',
        pendingCount: '{count} calls awaiting approval',
        empty: 'No calls awaiting approval.',
        selectAll: 'Select all',
        deselectAll: 'Clear selection',
        selectedCount: '{count} selected',
        groupBinding: 'Binding',
        startedAt: 'Requested',
        requestedBy: 'Requested by',
        actionError: 'The action could not be completed.',
      },
      usage: {
        title: 'Usage and budget',
        subtitle: 'Voice credits consumed and monthly budget limits.',
        monthQuota: 'Monthly quota',
        dailyBudget: 'Daily budget',
        alertThreshold: 'Alert threshold',
        byTemplate: 'By template',
        currentMonth: 'Current month',
        creditsUsed: 'Credits used',
        callCount: 'Calls',
        totalUsd: 'Estimated cost',
        history: 'Last 6 months',
        month: 'Month',
        quotaUsed: '{used} of {max} credits',
        noBudget: 'No budget configured — usage is unlimited.',
        budgetTitle: 'Budget limits',
        maxCreditsDay: 'Max credits per day',
        maxCreditsMonth: 'Max credits per month',
        save: 'Save budget',
        saving: 'Saving…',
        saved: 'Budget saved',
        saveError: 'Could not save the budget.',
        empty: 'No usage recorded yet.',
      },
      dial: {
        button: 'Call with AI',
        cardTitle: 'AI Calls',
        pickTemplate: 'Choose a template',
        pickTemplateHint: 'Select the voice agent to use for this call.',
        confirm: 'Start call',
        dialing: 'Starting…',
        queued: 'Call queued. It will start shortly.',
        consentMissing: 'This contact has not given consent to be contacted by phone.',
        tosMissing: 'You must accept the voice agents terms of use before calling.',
        tosLink: 'Go to voice setup',
        noTemplates: 'No callable voice templates available.',
        error: 'The call could not be started.',
        empty: 'No AI calls for this contact yet.',
      },
      agent: {
        detail: 'Voice agent details',
        subtitle: 'Configure the voice, review the prompt and control HITL per binding.',
        voiceId: 'Voice',
        disclaimerVerified: 'Disclaimer verified',
        disclaimerNotVerified: 'Disclaimer not verified',
        promptTitle: 'Prompt',
        promptReadonly: 'The prompt is defined by the installed template and is read-only here.',
        promptEditDesc:
          'Customize how the agent behaves: describe the use case, tone of voice and information to collect. Changes are applied to the voice engine on save.',
        promptCustomizedBadge: 'Customized',
        firstMessageLabel: 'Opening message',
        firstMessagePlaceholder: 'Leave empty to use the template opening message',
        firstMessageHint:
          'Required by law: the message must introduce the assistant as virtual and state that the call is recorded.',
        promptSave: 'Save prompt',
        promptSaved: 'Saved',
        promptDisclaimerError:
          'The opening message must introduce the assistant as virtual and state that the call is recorded (e.g. "I\'m the virtual assistant of... this call is recorded...").',
        transferTitle: 'Transfer to operator',
        transferDesc:
          'When configured, the agent can hand the live call over to this number when the caller asks for a person or the request cannot be handled.',
        transferPhoneLabel: 'Operator phone number (E.164 format)',
        transferInvalidPhone: 'Enter a number in international format, e.g. +393331234567',
        transferDisabledHint: 'Empty = transfer disabled.',
        transferModeLabel: 'Transfer mode',
        transferModeConference: 'Warm transfer (conference with context message)',
        transferModeBlind: 'Blind transfer (direct, keeps the caller\'s number)',
        transferConferenceHint:
          'The operator joins a conference and hears a short context message before speaking with the caller.',
        transferBlindHint:
          'The call is forwarded directly; the operator sees the original caller\'s number.',
        transferSave: 'Save transfer',
        transferSaved: 'Saved',
        transferRubricaDesc:
          'Department directory: each entry is a number the agent can transfer the live call to, with its own trigger condition. Empty list = transfer disabled.',
        transferLabelPlaceholder: 'Department/person (e.g. Support)',
        transferConditionPlaceholder: 'When to transfer here (e.g. "billing or invoice questions") — optional',
        transferAdd: 'Add destination',
        transferRemove: 'Remove',
        hoursTitle: 'Business hours',
        hoursDesc:
          'Outside these hours the agent tells callers the office is closed and takes a message (which becomes a task) instead of transferring. Disabled = always open.',
        hoursEnable: 'Enable business hours',
        hoursDayLabels: 'Mon,Tue,Wed,Thu,Fri,Sat,Sun',
        hoursNoDays: 'Select at least one day',
        hoursSave: 'Save hours',
        hoursSaved: 'Saved',
        triageTitle: 'Hand-off to specialized assistants',
        triageDesc:
          'Routing rules: the agent hands the conversation over to another voice assistant (same call) when the condition matches — e.g. a switchboard agent routing to the booking assistant.',
        triageNoTargets: 'No other active voice agents to hand off to. Install another agent first.',
        triageEmptyHint: 'No rules: the agent never hands off to other assistants.',
        triageSelectAgent: 'Choose assistant...',
        triageConditionPlaceholder: 'When to hand off (e.g. "wants to book an appointment")',
        triageAdd: 'Add rule',
        triageMissingCondition: 'Each rule needs a condition',
        voicePick: 'Choose a voice',
        voiceFallbackHint: 'Configure the ElevenLabs API key to load available voices. You can enter a voice ID manually.',
        voiceIdManual: 'Voice ID',
        voiceSave: 'Save voice',
        voiceSaved: 'Voice saved',
        voiceSaveError: 'Could not save the voice.',
        voiceSearchPlaceholder: 'Search voices (e.g. female, italian...)',
        voiceNoResults: 'No voices match the search.',
        voicePlayPreview: 'Play sample',
        voiceStopPreview: 'Stop sample',
        voiceNoPreview: 'No sample available for this voice',
        voiceLangIt: 'Italian',
        voiceLangAll: 'All voices',
        voiceNoItalianHint:
          'No Italian voices in your account yet: add them from the ElevenLabs library below.',
        testTitle: 'Try the agent from your browser',
        testDesc: 'Talk to the agent right now using your microphone, without a phone call. Free of telephony costs, ideal for tuning prompt and voice.',
        testStart: 'Start voice test',
        testConnecting: 'Connecting...',
        testStop: 'End test',
        testListening: 'Listening... speak now',
        testSpeaking: 'Agent is speaking',
        testError: 'Could not start the test session.',
        testMicDenied: 'Microphone access denied: allow it in your browser to run the test.',
        testToolsHint: 'In browser tests CRM tools (bookings, messages, transfers) are disabled: to test those, place a real call.',
        libraryToggle: 'Add voices from the ElevenLabs library',
        libraryDesc: 'Search thousands of shared voices, listen to a sample and add the ones you like to your account.',
        librarySearchPlaceholder: 'Search by name or style...',
        libraryAnyLanguage: 'Any language',
        libraryAdd: 'Add',
        libraryAdded: 'Added',
        speedLabel: 'Response speed',
        speedBalanced: 'Balanced (higher quality)',
        speedFast: 'Fast (minimum latency)',
        bookingTitle: 'Bookings',
        bookingDesc: 'Link the agent to your booking event types: it will offer the same slots as your public booking link, and confirmations, video call links and reminders are sent automatically.',
        bookingVideoLabel: 'Event type for video calls',
        bookingOnsiteLabel: 'Event type for on-site meetings',
        bookingNone: 'Not offered',
        bookingNoEventTypes: 'No booking event types found. Create one under Calendar → Bookings, then come back here to link it.',
        bookingFallbackHint: 'With no event type linked, the agent proposes slots from CRM work slots (legacy behavior).',
        bookingWhatsappLabel: 'WhatsApp number for links and confirmations',
        bookingWhatsappAuto: 'Automatic (active organization number)',
        bookingWhatsappHint: 'The agent sends the booking link on WhatsApp for video calls and an automatic confirmation for on-site appointments.',
        bookingWhatsappMissing: 'No WhatsApp number connected: the agent cannot send links or confirmations. Connect one under Settings → WhatsApp.',
        bookingSave: 'Save bookings',
        bookingSaved: 'Saved',
        hitlTitle: 'Approval per binding (HITL)',
        hitlDesc: 'When on, outbound calls for this binding wait for manual approval before dialing.',
        hitlOn: 'Ask before dialing',
        hitlOff: 'Dial automatically',
        noBindings: 'No bindings for this agent yet.',
        manageBindings: 'Manage bindings',
        back: 'Back to voice agents',
        notFound: 'Voice agent not found.',
        loading: 'Loading…',
      },
      bindings: {
        title: 'Bindings',
        subtitle: 'Triggers that make this voice agent act.',
        back: 'Back to agent',
      },
      tos: {
        checkbox1: 'I confirm I have a valid legal basis: explicit, specific consent for automated promotional calls (art. 130 Italian Privacy Code); for service-only communications I rely on contract or legitimate interest.',
        checkbox2: 'Before every commercial call I will check the Public Opposition Register (RPO) and honour opt-out and erasure requests.',
        checkbox3: 'The call will state upfront that it is an AI voice assistant and that it is recorded: I will not remove these notices from the messages (AI Act art. 50 + recording notice).',
        checkbox4: 'I will provide recipients with the privacy notice (art. 13 GDPR) and I act as the data controller.',
        checkbox5: 'I will use a caller number that is identifiable and traceable to me (no spoofing) and, if I run commercial telemarketing, I am registered with the ROC.',
        checkbox6: 'I will use my own ElevenLabs and Twilio credentials (BYOK) and accept their terms of service, including data processing outside the EU.',
        disclaimer: 'These confirmations are a reminder of the main obligations (Italy/EU) and do not replace legal advice: responsibility for compliance — consent, notices, registers, contracts — remains with your organisation.',
      },
    },
    twoFactor: {
      title: 'Two-Factor Authentication',
      description: 'Protect your account with an extra layer of security',
      status: {
        active: '2FA Active',
        activeMessage: 'Your account is protected',
        notConfigured: '2FA Not Configured',
        notConfiguredMessage: 'Set up at least one method to protect your account'
      },
      methods: {
        totp: {
          title: 'Authenticator App',
          configuredOn: 'Configured on',
          notConfigured: 'Not configured'
        },
        email: {
          title: 'Email Code',
          configuredOn: 'Configured on',
          notConfigured: 'Not configured'
        }
      },
      buttons: {
        configure: 'Configure',
        disable: 'Disable',
        required: 'Required'
      },
      badges: {
        active: 'Active'
      },
      messages: {
        totpConfigured: 'Authenticator App configured successfully',
        emailConfigured: 'Email OTP configured successfully',
        methodDisabled: 'disabled',
        disableError: 'Error disabling method',
        mustHaveOneMethod: 'You must have at least one 2FA method active',
        superadminCannotDisable: 'Superadmins cannot disable authenticator app'
      },
      confirm: {
        title: 'Disable',
        message: 'Are you sure you want to disable this authentication method?',
        confirmButton: 'Disable',
        cancelButton: 'Cancel'
      },
      securityInfo: {
        title: 'Security information',
        authenticatorOffline: 'The authenticator app generates codes even offline and is the most secure method',
        emailCodeExpiry: 'Email codes expire after 5 minutes',
        lostAccess: 'If you lose access to 2FA, contact an administrator for a reset',
        superadminRequired: 'As a superadmin, authenticator app is mandatory and cannot be disabled'
      },
      accountLabel: 'Account'
    },
    gdpr: {
      deleteAccount: {
        modalTitle: 'Delete your account',
        modalTitleSuccess: 'Account deactivated',
        warningTitle: 'You are about to delete your account',
        warningMessage: 'This action is irreversible after the grace period.',
        consequences: 'Consequences:',
        consequencesList: {
          deactivated: 'Your account will be deactivated immediately',
          deletion: 'Permanent deletion after 30 days',
          dataDeleted: 'Your personal data will be permanently deleted',
          email: 'You will receive an email to cancel (valid for 7 days)'
        },
        understoodCheckbox: 'I understand the consequences and want to proceed with deleting my account',
        exportCheckbox: 'I want to export my data before deleting the account',
        confirmEmail: 'Confirm your email',
        confirmEmailDescription: 'Enter your email to confirm it\'s you',
        enterPassword: 'Enter password',
        confirmIdentity: 'Confirm your identity by entering your password',
        verify2FA: '2FA Verification',
        enter2FACode: 'Enter the 6-digit code from your authentication app',
        code2FA: '2FA Code',
        requestSent: 'Request sent',
        accountDeactivated: 'Your account has been deactivated.',
        permanentDeletion: 'Permanent deletion:',
        cancelUntil: 'You can cancel until:',
        emailSentInfo: 'We sent you an email with a link to cancel the request if you change your mind.',
        errors: {
          mustConfirm: 'You must confirm that you understand the consequences',
          emailMismatch: 'Email does not match your account',
          enterPassword: 'Enter the password',
          enter6DigitCode: 'Enter the 6-digit code',
          deletionError: 'Error during deletion'
        },
        buttons: {
          cancel: 'Cancel',
          back: 'Back',
          continue: 'Continue',
          deleteAccount: 'Delete Account',
          deleting: 'Deleting...',
          close: 'Close'
        }
      },
      dataExport: {
        modalTitle: 'Export your data',
        gdprTitle: 'GDPR Art. 20 - Right to Portability',
        gdprDescription: 'Download all your personal data in JSON format, readable and reusable.',
        includedData: 'Data included in the export:',
        categories: {
          personalProfile: 'Personal Profile',
          personalProfileDesc: 'Email, name, phone, avatar',
          organization: 'Organization',
          organizationDesc: 'Company name, role, registration date',
          preferences: 'Preferences',
          preferencesDesc: 'Personal settings and configurations',
          consents: 'Consents',
          consentsDesc: 'Privacy policy, marketing, cookies (with dates)',
          activityLog: 'Activity Log',
          activityLogDesc: 'Last 100 actions performed'
        },
        notIncludedTitle: 'Business data not included',
        notIncludedDesc: 'Clients, leads, deals and other business data are not included in this export because they are third-party data managed by your organization.',
        notIncludedHint: 'To export business data, go to',
        settingsImportExport: 'Settings → Import/Export',
        lastExport: 'Last export:',
        totalExports: 'total exports',
        noExportsYet: 'You haven\'t made any exports yet.',
        exportComplete: 'Export complete! The file has been downloaded.',
        rateLimitWarning: 'You can only make one export per day. Try again tomorrow.',
        buttons: {
          close: 'Close',
          download: 'Download my data',
          exporting: 'Exporting...'
        },
        errors: {
          exportError: 'Error during export'
        }
      }
    },
    notificationPreferences: {
      title: 'Notification Preferences',
      description: 'Customize how and when you receive notifications for each type of event',
      categories: {
        tasks: 'Tasks',
        leads: 'Leads',
        deals: 'Deals',
        calendar: 'Calendar',
        clients: 'Clients',
        projects: 'Projects',
        whatsapp: 'WhatsApp',
        invoices: 'Invoices',
        partners: 'Partners',
        tickets: 'Tickets',
        inventory: 'Warehouse',
        orders: 'Orders',
        ecommerce: 'E-commerce',
        newsletter: 'Newsletter',
        system: 'System'
      },
      digestFrequency: {
        title: 'Email Digest Frequency',
        description: 'Notifications with digest enabled will be grouped and sent at this frequency',
        daily: 'Daily (at 8:00 AM)',
        weekly: 'Weekly (Monday at 8:00 AM)'
      },
      pushNotifications: {
        title: 'Browser Push Notifications',
        blocked: 'Push notifications are blocked. Enable permissions from browser settings.',
        enable: 'Enable push notifications to receive alerts even when you\'re not on the page',
        enableButton: 'Enable Push'
      },
      tableHeaders: {
        notification: 'Notification',
        inApp: 'In-App',
        email: 'Email',
        digest: 'Digest',
        push: 'Push'
      },
      info: {
        inApp: 'Notifications visible in the bell icon at the top right',
        email: 'Receive an email for each notification in real-time',
        digest: 'Receive a periodic summary of notifications',
        push: 'Native browser notifications even when you\'re not on the page'
      },
      messages: {
        loadError: 'Error loading preferences',
        saveError: 'Error saving'
      }
    },
    clientTags: {
      title: 'Client Tags',
      description: 'Manage tags to categorize clients and leads. Drag to reorder.',
      newTag: 'New Tag',
      emptyState: {
        title: 'No tags configured',
        description: 'Create your first tag to start categorizing clients and leads.',
        createFirst: 'Create first tag'
      },
      form: {
        newTitle: 'New Tag',
        editTitle: 'Edit Tag',
        nameLabel: 'Tag Name *',
        namePlaceholder: 'E.g. VIP Client, Hot Lead, etc.',
        descriptionLabel: 'Description',
        descriptionPlaceholder: 'Optional tag description',
        colorLabel: 'Tag Color',
        customColor: 'Custom color:',
        activeLabel: 'Active tag',
        cancel: 'Cancel',
        saving: 'Saving...',
        update: 'Update',
        create: 'Create Tag'
      },
      actions: {
        hideTag: 'Hide tag',
        showTag: 'Show tag',
        editTag: 'Edit tag',
        deleteTag: 'Delete tag'
      },
      messages: {
        orderUpdated: 'Tag order updated',
        tagUpdated: 'Tag updated successfully',
        tagCreated: 'Tag created successfully',
        tagDeleted: 'Tag deleted successfully',
        tagActivated: 'Tag activated',
        tagDeactivated: 'Tag deactivated',
        saveError: 'Error saving the tag',
        deleteConfirm: 'Are you sure you want to delete this tag? This action will remove the tag from all clients.'
      }
    },
    salesTargets: {
      title: 'Sales Targets',
      description: 'Set and manage revenue targets for your sales team',
      addTarget: 'Add Target',
      editTarget: 'Edit Target',
      periodTypes: {
        yearly: 'Yearly',
        quarterly: 'Quarterly',
        monthly: 'Monthly'
      },
      form: {
        salesRep: 'Sales Representative',
        selectUser: 'Select a user',
        targetAmount: 'Target Amount',
        month: 'Month',
        quarter: 'Quarter',
        year: 'Year',
        notes: 'Notes',
        cancel: 'Cancel',
        createTarget: 'Create Target',
        updateTarget: 'Update Target'
      },
      table: {
        salesRep: 'Sales Representative',
        targetAmount: 'Target Amount',
        period: 'Period',
        notes: 'Notes',
        actions: 'Actions'
      },
      quarters: {
        q1: 'Q1 (Jan-Mar)',
        q2: 'Q2 (Apr-Jun)',
        q3: 'Q3 (Jul-Sep)',
        q4: 'Q4 (Oct-Dec)'
      },
      emptyState: {
        title: 'No sales targets found for this period',
        description: 'Create targets to track your sales team performance'
      },
      messages: {
        loadUsersError: 'Unable to load users',
        monthlyUpdated: 'Monthly target updated successfully',
        monthlyCreated: 'Monthly target created successfully',
        quarterlyUpdated: 'Quarterly target updated successfully',
        quarterlyCreated: 'Quarterly target created successfully',
        yearlyUpdated: 'Yearly target updated successfully',
        yearlyCreated: 'Yearly target created successfully',
        duplicateError: 'A target already exists for this user in this period',
        saveError: 'Unable to save sales target',
        deleteSuccess: 'Sales target deleted successfully',
        deleteError: 'Unable to delete sales target',
        organizationRequired: 'Organization ID required'
      },
      confirmDelete: {
        title: 'Delete Sales Target',
        message: 'Are you sure you want to delete this sales target? This action cannot be undone.',
        confirm: 'Delete',
        cancel: 'Cancel'
      },
      confirmUpdate: {
        title: 'Existing Target',
        messageMonthly: 'A monthly target already exists for this user in {period}. Do you want to update it with the new amount?',
        messageQuarterly: 'A quarterly target already exists for this user in {period}. Do you want to update it with the new amount?',
        messageYearly: 'A yearly target already exists for this user for year {year}. Do you want to update it with the new amount?',
        confirm: 'Update',
        cancel: 'Cancel'
      },
      autoGenerate: {
        quarterlyCreated: '{count} quarterly targets created successfully',
        monthlyCreated: '{count} monthly targets created successfully',
        quarterlyExist: 'Quarterly targets already exist for this user in year {year}. Cannot auto-generate.',
        monthlyExist: 'Monthly targets already exist for this user in period {period}. Cannot auto-generate.',
        error: 'Error during automatic target generation',
        fromYearly: 'Auto-generated from yearly target',
        fromQuarterly: 'Auto-generated from quarterly target Q{quarter}'
      },
      unknownUser: 'Unknown User',
      autoGenerateDialog: {
        title: 'Automatic Target Generation',
        yearLabel: 'Year {year}',
        quarterLabel: 'Q{quarter} {year}',
        targetYearly: 'Yearly Target',
        targetQuarterly: 'Quarterly Target',
        generateQuarterly: 'Generate Quarterly Targets',
        generateQuarterlyDesc: 'Create 4 quarterly targets of {amount} each',
        generateMonthly: 'Generate Monthly Targets',
        generateMonthlyYearly: 'Create 12 monthly targets of {amount} each',
        generateMonthlyQuarterly: 'Create 3 monthly targets of {amount} each',
        selectOption: 'Select at least one option to proceed with automatic generation.',
        cancel: 'Cancel',
        generate: 'Generate Targets',
        generating: 'Generating...',
        months: {
          january: 'January',
          february: 'February',
          march: 'March',
          april: 'April',
          may: 'May',
          june: 'June',
          july: 'July',
          august: 'August',
          september: 'September',
          october: 'October',
          november: 'November',
          december: 'December'
        }
      }
    },
    subscription: {
      title: 'Your subscription',
      trial: {
        active: 'Free Trial Active',
        lastDay: 'Last day of trial!',
        daysRemaining: 'Only {days} days remaining',
        expiresOn: 'Expires on',
        activateToday: 'Activate today to not lose access',
        daysLeft: 'You still have {days} days to explore Flusia'
      },
      status: {
        active: 'Active',
        cancelling: 'Cancelling',
        cancelled: 'Cancelled'
      },
      paymentFailed: {
        title: 'Payment failed',
        description: 'The last payment was unsuccessful.',
        updateBefore: 'Update by {date} to avoid suspension.',
        updatePayment: 'Update Payment Method'
      },
      cancellingAlert: {
        title: 'Subscription cancelling',
        activeUntil: 'Your subscription will remain active until',
        loseAccess: '. After this date, you will lose access to premium features.',
        reactivate: 'Reactivate Subscription'
      },
      info: {
        plan: 'Plan',
        price: 'Price',
        users: 'Users',
        nextRenewal: 'Next renewal',
        endsOn: 'Ends on',
        perMonth: '/month',
        perYear: '/year'
      },
      actions: {
        managePayment: 'Manage payment',
        cancelSubscription: 'Cancel subscription',
        reactivateSubscription: 'Reactivate subscription',
        comparePlans: 'Compare other plans'
      },
      invoices: {
        title: 'Recent invoices',
        viewAll: 'View all',
        noInvoices: 'No invoices available',
        paid: 'Paid',
        downloadPdf: 'Download PDF'
      },
      messages: {
        activated: 'Subscription activated! Thank you for choosing Flusia.',
        checkoutCancelled: 'Checkout cancelled. You can try again whenever you want.',
        cancelled: 'Subscription cancelled',
        reactivated: 'Subscription reactivated!',
        cancelError: 'Error during cancellation',
        reactivateError: 'Error during reactivation'
      },
      cancelDialog: {
        title: 'Cancel subscription',
        message: 'Are you sure you want to cancel your subscription? Your subscription will remain active until {date}. After this date, you will lose access to premium features. You can always reactivate it before expiration.',
        confirm: 'Confirm cancellation',
        confirming: 'Cancelling...',
        cancel: 'Keep subscription'
      },
      help: {
        questions: 'Have questions? Write us at'
      }
    },
    whatsappSetup: {
      title: 'Configure WhatsApp',
      subtitle: 'Enter your WAHA instance credentials',
      wahaUrl: {
        label: 'WAHA Instance URL',
        placeholder: 'https://waha.example.com',
        hint: 'The URL of your WAHA instance (without trailing slash)'
      },
      apiKey: {
        label: 'API Key',
        placeholder: '••••••••••••••••••••••',
        hint: 'Your WAHA API Key'
      },
      buttons: {
        cancel: 'Cancel',
        back: 'Back',
        testAndContinue: 'Test and Continue',
        testing: 'Testing...',
        goToWhatsApp: 'Go to WhatsApp',
        saving: 'Saving...'
      },
      qrStep: {
        title: 'Scan QR Code',
        subtitle: 'Use WhatsApp on your phone to scan the QR code',
        creatingSession: 'Creating session...',
        howToConnect: 'How to connect:',
        steps: {
          step1: 'Open WhatsApp on your phone',
          step2: 'Go to Menu (⋮) or Settings',
          step3: 'Tap "Linked devices"',
          step4: 'Tap "Link a device"',
          step5: 'Scan this QR code'
        },
        waitingForScan: 'Waiting for scan...',
        connected: 'Connected!',
        connectionFailed: 'Connection failed'
      },
      successStep: {
        title: 'WhatsApp Configured!',
        subtitle: 'Your WhatsApp configuration is complete and ready to use',
        session: 'Session:',
        phone: 'Phone:',
        status: 'Status:',
        statusConnected: 'Connected',
        webhookConfigured: '🔗 Webhook configured automatically',
        webhookFeatures: {
          realtime: 'Real-time message sync',
          events: 'Events: message.any',
          auth: 'Authentication configured'
        }
      },
      messages: {
        enterUrlAndKey: 'Enter URL and API Key',
        invalidUrl: 'Invalid URL',
        connectionFailed: 'Unable to connect to WAHA. Verify URL and API Key',
        connectionSuccess: 'Connection successful!',
        connectionTestError: 'Error testing connection',
        creatingConfig: 'Creating configuration...',
        creatingSessionWithWebhook: 'Creating session with webhook...',
        sessionCreated: 'Session created! Scan the QR code',
        sessionCreationError: 'Error creating session',
        connectionFailedRetry: 'Connection failed. Please try again',
        configSaved: 'Configuration saved!',
        saveError: 'Error saving configuration'
      }
    },
    ficSetup: {
      title: 'Fatture in Cloud Guided Setup',
      steps: {
        connection: 'Verify Connection',
        clients: 'Client Sync',
        invoices: 'Historical Invoices',
        complete: 'Complete'
      },
      connectionStep: {
        title: 'Verify Connection',
        subtitle: 'Checking that the connection to Fatture in Cloud is active',
        notConnected: {
          title: 'Fatture in Cloud not connected',
          description: 'Before proceeding with setup, you need to connect Fatture in Cloud. Go to the "Connection" tab to complete OAuth authentication.'
        },
        connected: {
          title: 'Connection Active',
          companyId: 'Company ID:',
          lastSync: 'Last sync:',
          never: 'Never'
        },
        testConnection: 'Test Connection',
        testing: 'Testing...',
        testSuccess: 'Connection valid!',
        testFailed: 'Connection not valid'
      },
      clientsStep: {
        title: 'Client Sync',
        subtitle: 'Choose how to handle client synchronization between CRM and Fatture in Cloud',
        ficClients: 'Clients in FIC',
        crmClients: 'Clients in CRM',
        importFromFic: {
          title: 'Import from FIC',
          description: 'Import clients from Fatture in Cloud to CRM'
        },
        exportToFic: {
          title: 'Export to FIC',
          description: 'Export clients from CRM to Fatture in Cloud'
        },
        skip: {
          title: 'Skip',
          description: 'I will sync clients later'
        },
        infoMessages: {
          import: 'You chose to import clients from FIC. Complete the import in the modal to continue.',
          export: 'You chose to export clients to FIC. Complete the export in the modal to continue.',
          skip: 'You can sync clients at any time from the "Clients" section in settings.'
        }
      },
      invoicesStep: {
        title: 'Historical Invoices',
        subtitle: 'Import historical invoices from Fatture in Cloud to complete setup',
        syncPeriod: 'Sync Period',
        startDate: 'Start Date',
        endDate: 'End Date',
        defaultPeriodInfo: {
          title: 'Default Period',
          description: 'By default, a 12-month period is set. You can modify the dates to sync a different period.'
        },
        syncButton: 'Sync Invoices',
        syncing: 'Syncing...'
      },
      completeStep: {
        title: 'Setup Complete!',
        subtitle: 'Initial configuration has been completed successfully',
        summary: 'Operations Summary',
        items: {
          connectionVerified: 'Connection verified',
          clientsImported: 'Clients imported from FIC',
          clientsExported: 'Clients exported to FIC',
          clientsSkipped: 'Client sync skipped',
          invoicesSynced: '{count} invoices synced'
        },
        nextSteps: {
          title: 'Next Steps',
          step1: 'Go to "Invoice Matching" section to link invoices to deals',
          step2: 'Configure automatic sync in settings',
          step3: 'Review client mappings in the "Clients" tab'
        }
      },
      buttons: {
        cancel: 'Cancel',
        back: 'Back',
        next: 'Next',
        close: 'Close'
      },
      messages: {
        loadConfigError: 'Error loading configuration',
        testSuccess: 'Connection valid!',
        testFailed: 'Connection not valid',
        testError: 'Error testing connection',
        syncComplete: 'Sync completed',
        syncError: 'Error syncing invoices'
      }
    },
    importChoice: {
      back: 'Back',
      title: 'Import Clients or Leads?',
      subtitle: 'Choose whether to import acquired clients or potential leads',
      clientCard: {
        title: 'Import Clients',
        description: 'Import already acquired clients with complete contact and contract data'
      },
      leadCard: {
        title: 'Import Leads',
        description: 'Import potential clients to qualify and convert through the sales funnel'
      }
    },
    quoteEditor: {
      tabs: {
        editor: 'Editor',
        blocks: 'Template Blocks',
        images: 'Images',
        pdfSettings: 'PDF Settings',
        terms: 'Terms and Conditions',
        preview: 'Preview'
      },
      variables: {
        show: 'Show Variables',
        hide: 'Hide Variables',
        autocompleteHint: 'Use {shortcut} in the editor for variable autocomplete.'
      },
      sections: {
        header: 'Header',
        content: 'Content',
        footer: 'Footer'
      },
      placeholders: {
        header: 'Enter the HTML code for the header here...',
        content: 'Enter the HTML code for the content here...',
        footer: 'Enter the HTML code for the footer here...'
      },
      blocks: {
        description: 'Select a pre-designed block to add to your template. Blocks will be inserted at the cursor position in the editor.'
      },
      images: {
        description: 'Select an image from the library to insert into the template.'
      },
      preview: {
        info: 'Preview with sample data',
        infoDescription: 'Template variables have been replaced with placeholder data to show how the final quote will look.',
        sectionLabels: {
          header: 'Header',
          content: 'Content',
          footer: 'Footer',
          terms: 'Terms and Conditions'
        }
      }
    },
    quoteToolbar: {
      formatting: {
        bold: 'Bold',
        italic: 'Italic',
        bulletList: 'Bullet list',
        orderedList: 'Numbered list'
      },
      alignment: {
        left: 'Align left',
        center: 'Center',
        right: 'Align right',
        justify: 'Justify'
      },
      logos: {
        horizontal: 'Insert Horizontal Logo',
        icon: 'Insert Icon Logo'
      },
      modeToggle: {
        toVisual: 'Switch to visual mode',
        toHtml: 'Switch to HTML mode',
        visual: 'Visual',
        html: 'HTML'
      }
    },
    pdfCustomField: {
      title: 'Custom PDF Field',
      subtitle: 'Configure a custom text that will appear in generated PDF documents',
      howToUse: {
        title: 'How to use the custom field:',
        item1: 'The text entered here will appear in all contract and quote PDFs',
        item2: 'You can use variables like {variable} in templates',
        item3: 'Supports plain text and some basic HTML tags',
        item4: 'Leave empty to not show any custom field'
      },
      form: {
        label: 'Custom PDF Text',
        placeholder: 'Enter the text that will appear in PDFs...'
      },
      preview: 'Preview:',
      buttons: {
        save: 'Save Changes',
        saving: 'Saving...',
        cancel: 'Cancel Changes'
      },
      messages: {
        saved: 'Custom PDF field saved successfully',
        saveError: 'Error saving custom PDF field'
      },
      templateVariable: {
        title: 'Template Variable',
        description: 'To use this field in quote and contract templates, insert the variable {variable} where you want the custom text to appear.'
      }
    },
    quoteTemplates: {
      confirmDelete: {
        title: 'Are you sure?',
        message: 'This action will permanently delete the template.'
      },
      messages: {
        deleted: 'Template deleted successfully',
        deleteError: 'Error deleting template',
        nameRequired: 'Template name is required',
        updated: 'Template updated successfully',
        created: 'Template created successfully',
        unknownError: 'Unknown error',
        saveError: 'Error saving template: {error}',
        variableAddedToHeader: 'Variable {variable} added to header',
        variableAddedToFooter: 'Variable {variable} added to footer',
        variableAddedToContent: 'Variable {variable} added to content',
        blockInserted: 'Block inserted successfully',
        imageInserted: 'Image inserted successfully'
      }
    },
    ficSync: {
      // Toast messages
      connectFirst: 'Connect Fatture in Cloud first',
      tokenExpired: 'The Fatture in Cloud token has expired. Reconnect your account in settings.',
      malformedResponse: 'Malformed response from server',
      syncCompleted: 'Sync completed successfully!',
      syncCompletedNow: 'Sync completed! Now sync payments.',
      clientsCompleted: 'Client sync completed: {imported} new, {updated} updated',
      clientsSyncError: 'Error syncing clients',
      invoicesFetchError: 'Error fetching invoices',
      invoicesUpdateError: 'Error updating invoices',
      invoicesSyncError: 'Error syncing invoices',
      partialSync: 'Partial sync: {invoices} invoices, {payments} payments. Warnings: {warnings}',
      fullSyncCompleted: 'Sync completed: {invoices} invoices, {payments} payments',
      syncBatch: 'Syncing {label} ({current}/{total})...',
      error: 'Error: {message}',
      functionCallError: 'Error during function call',
      noDataReceived: 'No data received from sync',
      savingToDatabase: 'Saving to database...',
      startingClientSync: 'Starting client sync...',
      fetchingClientsFromFIC: 'Fetching clients from Fatture in Cloud...',
      preparingBatches: 'Preparing {total} batches from {startYear} to {endYear}...',
      updatingBatch: 'Updating {label} ({current}/{total})...',
      updateCompleted: 'Update completed! Now sync payments.',
      invoicesUpdated: '{total} invoices updated! Now click "Sync Payments" to complete.',
      invoicesSynced: '{total} invoices synced! Now click "Sync Payments" to complete.',
      extractingPayments: 'Extracting payments {label} ({current}/{total})...',
      paymentsCompleted: 'Payment sync completed!',
      paymentsSynced: '{payments} payments synced, {deals} deals updated',
      paymentsSyncError: 'Error syncing payments',
      fetchingInvoicesFromFIC: 'Fetching invoices from Fatture in Cloud...',
      // UI labels
      ui: {
        connectToSync: 'Connect Fatture in Cloud to sync data',
        clientsSynced: 'Clients synced',
        invoicesSynced: 'Invoices synced',
        paymentsTracked: 'Payments tracked',
        invoicesWithoutDeal: 'Invoices without deal',
        syncClients: 'Sync Clients',
        syncClientsDesc: 'Download clients from Fatture in Cloud. Then use the "Clients" tab to import them into the CRM.',
        sync: 'Sync',
        syncing: 'Sync...',
        lastSync: 'Last sync:',
        syncInvoices: 'Sync Invoices',
        syncInvoicesDesc: 'Import invoices from Fatture in Cloud into the CRM',
        fullSyncCheckbox: 'Full sync (all history from 2015)',
        destructiveOperation: 'Destructive Operation',
        destructiveWarning: 'This operation will delete all {count} existing invoices and their deal links. Payments will be unlinked. This action is irreversible.',
        smartSyncActive: 'Smart Sync Active',
        lastInvoice: 'Last invoice:',
        willSyncFrom: 'Will sync invoices from {fromDate} to today',
        from: 'From',
        to: 'To',
        syncNew: 'Sync New',
        updateExisting: 'Update Existing',
        updating: 'Updating...',
        updateExistingDesc: 'Updates the {count} invoices to include payment data.',
        syncPayments: 'Sync Payments',
        syncPaymentsDesc: 'Update payment status for all invoices',
        recalcCommissions: 'Recalculate commissions',
        unmatchedInvoicesWarning: '{count} invoices are not associated with any deal',
        goToMatching: 'Go to Matching',
        goToMatchingDesc: 'Go to the "Invoice Matching" section to associate them manually',
        syncFromFIC: 'Sync from Fatture in Cloud',
        confirmFullSync: 'Confirm Full Sync',
        confirmFullSyncMessage: 'WARNING: You are about to delete all {count} existing invoices and their deal links. Payments will be unlinked. This action is IRREVERSIBLE.\n\nAre you sure you want to proceed?',
        proceedAnyway: 'Proceed anyway',
        cancel: 'Cancel'
      }
    },
    templateBlockLibrary: {
      categories: {
        header: 'Headers',
        section: 'Sections',
        footer: 'Footers'
      },
      blocks: {
        standardHeader: { name: 'Standard Header', description: 'Basic header with company info and logo' },
        modernHeader: { name: 'Modern Header', description: 'Modern header with company info' },
        quoteInfo: { name: 'Quote Information', description: 'Shows quote details and date' },
        clientInfo: { name: 'Client Information', description: 'Client details section' },
        servicesList: { name: 'Services List', description: 'List of services with descriptions' },
        costSummary: { name: 'Cost Summary', description: 'Table with costs and total' },
        paymentSchedule: { name: 'Payment Method', description: 'Section dedicated to payment methods per service' },
        termsConditions: { name: 'Terms and Conditions', description: 'Terms and conditions from company settings' },
        notesSection: { name: 'Notes Section', description: 'Section for additional notes' },
        imageSection: { name: 'Image Section', description: 'Section with image and caption' },
        standardFooter: { name: 'Standard Footer', description: 'Basic footer with company info' },
        signatureFooter: { name: 'Signature Footer', description: 'Footer with signature areas' }
      }
    },
    chunkedUploader: {
      title: 'Step 4: Database Upload',
      summary: {
        title: 'Upload Summary',
        validRecords: 'Valid Records',
        batchSize: 'Batch Size',
        totalBatches: 'Total Batches',
        organization: 'Organization'
      },
      clientState: {
        title: 'Client State (Optional)',
        label: 'Assign a state to imported clients:',
        placeholder: '-- No specific state --',
        loading: 'Loading states...',
        selected: 'Selected state:',
        noSelectionInfo: 'If no state is selected, clients will keep the default state or the one specified in transformation rules.'
      },
      userMapping: {
        title: 'User Association (Optional)',
        hide: 'Hide',
        configure: 'Configure',
        description: 'Configure rules to automatically assign imported clients to specific users.',
        excelColumn: 'Excel Column',
        selectColumn: '-- Select Column --',
        matchType: 'Match Type',
        contains: 'Contains',
        equals: 'Equals',
        startsWith: 'Starts with',
        endsWith: 'Ends with',
        valueToSearch: 'Value to Search',
        valuePlaceholder: 'e.g. Milan, @gmail.com',
        assignToUser: 'Assign to User',
        selectUser: '-- Select User --',
        active: 'Active',
        remove: 'Remove',
        rulePreview: 'Rule:',
        addRule: '+ Add Association Rule'
      },
      leadStage: {
        title: 'Lead Stage (Optional)',
        label: 'Assign a stage to imported leads:',
        placeholder: '-- No specific stage --',
        loading: 'Loading stages...',
        selected: 'Selected stage:',
        info: 'The lead stage determines at what point of the sales process the client is.'
      },
      leadSource: {
        title: 'Lead Source (Optional)',
        label: 'Assign a source to imported leads:',
        placeholder: '-- No specific source --',
        loading: 'Loading sources...',
        selected: 'Selected source:',
        info: 'The lead source indicates where the clients come from (e.g. Google Ads, Facebook, Referral).',
        newButton: 'New'
      },
      updateExisting: {
        title: 'Update existing records',
        selectFieldsLabel: 'Select fields to use for identifying existing records:',
        matchWarning: 'A record is considered existing if at least one of the selected fields matches',
        selectAtLeastOne: 'Select at least one field for matching when enabling update existing records'
      },
      activeSelections: {
        title: 'Active Selections for Import:',
        state: 'State:',
        stage: 'Stage:',
        source: 'Source:'
      },
      buttons: {
        startUpload: 'Start Upload',
        startImportUpdate: 'Start Import/Update',
        processing: 'Processing...',
        uploading: 'Uploading...'
      },
      progress: {
        processingBatch: 'Processing batch {current} of {total}',
        insertUpdateMode: '(Insert/Update mode)',
        uploaded: 'Uploaded',
        successful: 'Successful',
        failed: 'Failed'
      },
      results: {
        title: 'Upload Results',
        recordsProcessed: 'Records Processed',
        recordsUploaded: 'Records Uploaded',
        recordsFailed: 'Failed Records',
        errorsTitle: 'Upload Errors',
        exportErrors: 'Export Errors',
        exportTip: 'Click "Export Errors" to download an Excel with all failed records'
      },
      newSourceModal: {
        title: 'Create New Lead Source',
        nameLabel: 'Source Name *',
        namePlaceholder: 'e.g. Google Ads, Facebook, Milan Fair',
        descLabel: 'Description (Optional)',
        descPlaceholder: 'Lead source description...',
        cancel: 'Cancel',
        create: 'Create Source',
        creating: 'Creating...'
      },
      matchFields: {
        email: 'Email',
        vatNumber: 'VAT Number',
        phone: 'Phone',
        taxCode: 'Tax Code',
        businessName: 'Business Name'
      },
      messages: {
        noErrorsToExport: 'No records with errors to export',
        fileExported: 'File exported: {filename}',
        exportError: 'Error during file export',
        sourceNameRequired: 'Lead source name required',
        sourceCreateError: 'Error creating lead source',
        sourceCreated: 'Lead source "{name}" created and selected',
        noValidRecords: 'No valid records to upload',
        uploadError: 'Error during data upload',
        uploadComplete: 'Process completed! {count} records {action} successfully.',
        uploadCompleteWithErrors: '{action} completed with errors: {success} successes, {failed} failures.',
        importInProgress: 'Import in progress. Are you sure you want to close? Data may be lost.'
      },
      leadStages: {
        newLead: 'New Lead',
        contacted: 'Contacted',
        qualified: 'Qualified',
        proposalSent: 'Proposal Sent',
        negotiation: 'Negotiation',
        closedWon: 'Closed Won',
        closedLost: 'Closed Lost',
        closed: 'Closed'
      }
    },
    unifiedImportExport: {
      title: 'Import & Export Data',
      description: 'Manage the import and export of your business data. Choose the option that best suits your needs.',
      mainFunctions: 'Main Functions',
      recommended: 'Recommended',
      backToOverview: '← Back to overview',
      importClientsAI: 'Import Clients with AI',
      exportData: 'Export Data',
      options: {
        importClients: {
          title: 'Import Clients/Leads from Excel',
          description: 'AI-powered system to import clients, contacts and categories from Excel files with intelligent mapping and batch processing.',
          badge: 'AI-Powered'
        },
        exportData: {
          title: 'Export Data',
          description: 'Export clients, invoices, projects and other data in Excel, CSV or PDF format for backup and analysis.'
        }
      },
      recommendedEfficiency: 'Recommended for maximum efficiency',
      helpSection: {
        title: 'Which import system to choose?',
        aiPowered: {
          label: 'AI-Powered Import:',
          description: 'Perfect for complex Excel files with many columns. AI automatically recognizes data types and handles intelligent transformations.'
        },
        export: {
          label: 'Export Data:',
          description: 'To export your existing data for backup, analysis or migration to other systems.'
        }
      },
      legacySystems: 'Legacy Systems (Not Recommended)',
      legacy: {
        basicImport: {
          title: 'Basic Excel Import',
          description: 'Basic system for simple imports (deprecated)',
          badge: 'Legacy'
        },
        advancedImport: {
          title: 'Advanced Manual Import',
          description: 'System with manual mapping (deprecated)',
          badge: 'Legacy'
        },
        note: 'Legacy systems are maintained for compatibility but are no longer recommended. Use the new AI-Powered system for superior performance and reliability.'
      }
    },
    userCommissionModal: {
      title: 'Edit Commission Rate',
      user: 'User',
      currentCommission: 'Current commission: {rate}% (since {date})',
      label: 'Commission Rate (%)',
      placeholder: 'Commission rate',
      description: 'This percentage will be used to calculate commissions on deals closed by the user.',
      cancel: 'Cancel',
      saving: 'Saving...',
      save: 'Save',
      messages: {
        loadError: 'Unable to load commission rate',
        invalidRange: 'Percentage must be between 0 and 100',
        noChanges: 'No changes made',
        updateSuccess: 'Commission rate updated successfully',
        updateError: 'Error updating commission rate'
      }
    },
    emailBlacklist: {
      title: 'Email Blacklist',
      subtitle: 'Manage blocked emails that will not receive newsletters',
      addEmail: 'Add Email',
      loading: 'Loading blacklist...',
      stats: {
        total: 'Total',
        hardBounce: 'Hard Bounce',
        softBounce: 'Soft Bounce',
        spam: 'Spam',
        unsubscribed: 'Unsubscribed',
        manual: 'Manual'
      },
      filters: {
        searchPlaceholder: 'Search email...',
        allReasons: 'All reasons',
        unsubscribed: 'Unsubscribed'
      },
      table: {
        email: 'Email',
        reason: 'Reason',
        bounceCount: 'Bounce Count',
        lastBounce: 'Last Bounce',
        notes: 'Notes',
        actions: 'Actions'
      },
      badges: {
        hardBounce: 'Hard Bounce',
        softBounce: 'Soft Bounce',
        spam: 'Spam',
        unsubscribed: 'Unsubscribed',
        manual: 'Manual'
      },
      emptyState: {
        noResults: 'No emails found with the applied filters',
        empty: 'No emails in blacklist'
      },
      confirmReactivate: 'Are you sure you want to reactivate {email}? This email will be able to receive newsletters again.',
      confirmDelete: 'Are you sure you want to remove {email} from the blacklist?',
      addModal: {
        title: 'Add Email to Blacklist',
        emailLabel: 'Email Address',
        emailPlaceholder: 'email@example.com',
        notesLabel: 'Notes (optional)',
        notesPlaceholder: 'Reason for adding...',
        cancel: 'Cancel',
        add: 'Add to Blacklist'
      },
      buttons: {
        reactivate: 'Reactivate email',
        remove: 'Remove from blacklist'
      },
      messages: {
        invalidEmail: 'Please enter a valid email address',
        alreadyExists: 'This email is already in the blacklist',
        addError: 'Error adding to blacklist',
        reactivateError: 'Error reactivating email',
        deleteError: 'Error removing from blacklist',
        addedManually: 'Added manually'
      }
    },
    unlayerEditor: {
      tip: 'Tip',
      tipDescription: 'Use the "Merge Tags" menu on the right to insert template variables. All tables and HTML attributes will be preserved correctly.',
      applyChanges: 'Apply Changes'
    },
    commissionReports: {
      title: 'Commission Reports',
      subtitle: 'Performance analysis and commissions by period',
      exportExcel: 'Export Excel',
      period: 'Period',
      userReports: 'User Reports',
      noDataAvailable: 'No data available for the selected period',
      stats: {
        totalCommissions: 'Total Commissions',
        closedDeals: 'Closed Deals',
        avgRate: 'Average Rate',
        topPerformer: 'Top Performer'
      },
      table: {
        user: 'User',
        group: 'Group',
        deals: 'Deals',
        totalValue: 'Total Value',
        commissions: 'Commissions',
        rate: 'Rate %',
        target: 'Target'
      },
      excel: {
        userName: 'User Name',
        email: 'Email',
        group: 'Group',
        noGroup: 'No group',
        dealsCount: 'Number of Deals',
        totalDealValue: 'Total Deal Value',
        totalCommissions: 'Total Commissions',
        avgCommissionRate: 'Average Commission Rate',
        monthlyTarget: 'Monthly Target',
        notDefined: 'Not defined',
        targetAchievement: 'Target Achievement',
        period: 'Period',
        sheetName: 'Commission Report',
        summarySheetName: 'Summary',
        summary: 'Summary',
        value: 'Value',
        summaryTotalCommissions: 'Total Commissions',
        summaryTotalDeals: 'Total Deals',
        summaryAvgRate: 'Average Commission Rate',
        summaryTopPerformer: 'Top Performer',
        summaryTopPerformerCommission: 'Top Performer Commission'
      },
      messages: {
        loadError: 'Error loading commission reports',
        exportSuccess: 'Commission report exported successfully',
        exportError: 'Error exporting report'
      }
    },
    variableCatalog: {
      categories: {
        company: 'Company Variables',
        client: 'Client Variables',
        deal: 'Base Quote Variables',
        totals: 'Total Variables',
        signatures: 'Signature Variables',
        user: 'User Variables',
        referente: 'Referente Variables',
        rappresentanteLegale: 'Legal Representative Variables',
        articles: 'Item Variables'
      },
      conditionalBlocks: {
        title: 'Conditional Blocks',
        conditional: 'Conditional',
        snippet: 'Snippet',
        copy: 'Copy',
        copied: 'Copied!'
      },
      badges: {
        new: 'New',
        discounted: 'Discounted',
        original: 'Original',
        conditional: 'Conditional'
      },
      howToUse: {
        title: 'How to use variables:',
        clickVariable: 'Click on a variable to insert it',
        typeForAutocomplete: 'Or type { in the editor for autocomplete',
        conditionalContent: 'Conditional blocks allow you to show dynamic content',
        ctrlSpace: 'Use Ctrl+Space in the editor to see all suggestions'
      },
      variables: {
        businessName: 'Business name',
        logoHorizontal: 'Horizontal logo URL',
        logoIcon: 'Icon logo URL',
        address: 'Full address',
        vatNumber: 'VAT number',
        sdiCode: 'SDI code',
        email: 'Email',
        phone: 'Phone',
        termsConditions: 'Terms and conditions',
        clientBusinessName: 'Client business name',
        clientAddress: 'Client full address',
        clientVat: 'Client VAT number',
        taxCode: 'Tax code',
        clientSdi: 'Client SDI code',
        pec: 'PEC email',
        clientEmail: 'Client email',
        clientPhone: 'Client phone',
        assignee: 'Assignee',
        salesRep: 'Sales representative',
        quoteTitle: 'Quote title',
        date: 'Date',
        quoteNumber: 'Quote number',
        shippingAddress: 'Delivery address (chosen on the quote, otherwise the client\'s address)',
        description: 'Description',
        dealAssignee: 'Deal assignee',
        total: 'Total (with discounts)',
        monthlyTotal: 'Monthly services total (with discounts)',
        annualTotal: 'Annual services total (with discounts)',
        onetimeTotal: 'One-time total discounted',
        onetimeOriginal: 'One-time total before discounts',
        totalWords: 'Total in words',
        costTotal: 'Internal costs total',
        costTotalWords: 'Costs total in words',
        legalSignature: 'Legal representative signature',
        clientSignature: 'Client signature (digital only)',
        signatureDate: 'Digital signature date',
        signatureTime: 'Digital signature time',
        clientIp: 'Client IP address',
        userName: 'Current user name',
        userEmail: 'Current user email',
        referenteName: 'Referente name',
        referentePhone: 'Referente phone',
        referenteEmail: 'Referente email',
        referenteRole: 'Referente role',
        referenteArea: 'Referente area',
        rappresentanteLegaleName: 'Legal representative name',
        rappresentanteLegalePhone: 'Legal representative phone',
        rappresentanteLegaleEmail: 'Legal representative email',
        rappresentanteLegaleRole: 'Legal representative role',
        rappresentanteLegaleArea: 'Legal representative area',
        productsTotal: 'Items total'
      },
      conditionalDescriptions: {
        hasOnetimeDiscount: 'Show different content if there are discounts on one-time services',
        hasOnetimeDiscountExample: 'Highlight when there are discounts on one-time services',
        hasServices: 'Check if there are services in the quote',
        hasServicesExample: 'Show a message if there are no services',
        hasProducts: 'Check if there are items (products) in the quote',
        hasProductsExample: 'Show a message if there are no items',
        client: 'Check if there is a client associated with the quote',
        clientExample: 'Show client info only if present',
        digitalSignature: 'Content visible only in digitally signed PDF',
        digitalSignatureExample: 'Digital signature information',
        unlessDigitalSignature: 'Content visible only in normal PDF (not signed)',
        unlessDigitalSignatureExample: 'Space for manual signature in non-digital PDFs',
        discountAmount: 'Check if a service has a discount (use inside {{#each services}})',
        discountAmountExample: 'Show crossed out original price if there is a discount',
        eachProducts: 'Loop over the items (products) of the quote, separate from services',
        eachProductsExample: 'Show a table row for each item with name, SKU, quantity, price and discount. Also available: {{discount}}, {{discount_percentage}}, {{packaging}} (e.g. 5 × 1,000 g) with {{#if discount}} / {{#if packaging}}',
        referente: 'Check if the client has a referente contact person',
        referenteExample: 'Show referente contact info only if present',
        rappresentanteLegale: 'Check if the client has a legal representative contact',
        rappresentanteLegaleExample: 'Show legal representative info only if present'
      }
    },
    ficClientImport: {
      modalTitle: 'Import Clients to CRM',
      syncRequired: 'Sync Required',
      syncDescription: 'Before importing clients to the CRM, you need to sync them from Fatture in Cloud.',
      syncOutdated: '(Data has not been synced in the last 7 days or the table is empty)',
      whatWillHappen: 'What will happen:',
      syncAllClients: 'I will sync all clients from Fatture in Cloud (no limits)',
      canSelectAfter: 'You can then select which ones to import into the CRM',
      operationTakesTime: 'The operation may take a few minutes',
      cancel: 'Cancel',
      startSync: 'Start Sync',
      selectClientsTitle: 'Select Clients to Import',
      selectClientsDescription: 'Choose clients from Fatture in Cloud to import into the CRM',
      searchPlaceholder: 'Search by name, VAT, Tax Code...',
      filterAll: 'All',
      filterUnmapped: 'Unmapped only',
      filterWithMatch: 'With match only',
      noClientsFound: 'No clients found',
      syncFirstMessage: 'Sync clients from FIC first in the "Sync" section',
      selectAll: 'Select all',
      alreadyMapped: '✓ Already mapped',
      vatLabel: 'VAT:',
      taxCodeLabel: 'TC:',
      mappedTo: 'Mapped to:',
      clientsSelected: 'clients selected',
      importOptionsTitle: 'Import Options',
      importOptionsDescription: 'Choose how to handle clients that already exist in the CRM',
      mappingOnly: 'Mapping Only',
      mappingOnlyDescription: 'Create only the link between CRM and FIC clients without modifying existing data',
      overwriteCRM: 'Overwrite CRM Data',
      overwriteCRMDescription: 'Update existing CRM clients with data from Fatture in Cloud',
      matchingLogic: 'Matching Logic',
      matchExactVat: 'Exact match on VAT or Tax Code',
      matchEmail: 'Match on email (if available)',
      noMatchCreate: 'If no match found, a new client will be created',
      resultsTitle: 'Import Results',
      imported: 'Imported',
      mapped: 'Mapped',
      errors: 'Errors',
      importErrors: 'Import Errors',
      clientId: 'Client ID:',
      importComplete: 'Import complete! Clients are now available in the CRM and linked to Fatture in Cloud.',
      back: 'Back',
      close: 'Close',
      next: 'Next',
      importing: 'Importing...',
      importClients: 'Import Clients',
      syncModalTitle: 'Sync Clients from Fatture in Cloud',
      messages: {
        checkSyncError: 'Error checking sync status',
        syncStarting: 'Starting client sync...',
        fetchingFromFIC: 'Fetching clients from Fatture in Cloud...',
        functionCallError: 'Error during function call',
        noDataReceived: 'No data received from sync',
        savingToDb: 'Saving to database...',
        syncCompleted: 'Sync completed successfully!',
        syncSuccess: 'Client sync completed: {imported} new, {updated} updated',
        syncError: 'Error syncing clients',
        loadError: 'Error loading clients',
        selectAtLeastOne: 'Select at least one client to import',
        importSuccess: 'Successfully imported {count} clients',
        importWithErrors: 'Import completed with {count} errors',
        importError: 'Error importing clients',
        selectClient: 'Select at least one client'
      }
    },
    apiDocumentation: {
      title: 'API Documentation',
      subtitle: 'Integrate Flusia with your applications',
      manageTokens: 'Manage API Tokens',
      copyCode: 'Copy code',
      copyId: 'Copy ID',
      sections: {
        gettingStarted: 'Getting Started',
        zapierIntegration: 'Zapier Integration',
        leadsApi: 'Leads API',
        pipelinesApi: 'Pipeline API',
        dealsApi: 'Deals API',
        tasksApi: 'Tasks API',
        projectsApi: 'Projects API',
        servicesApi: 'Services API',
        activitiesApi: 'Activities API',
        invoicesApi: 'Invoices API (Read-Only)',
        whatsappApi: 'WhatsApp API',
        newsletterApi: 'Newsletter API',
        warehouseApi: 'Warehouse API',
        errorCodes: 'Error Codes',
        bestPractices: 'Best Practices'
      },
      gettingStarted: {
        step1: 'Generate an API token from the',
        step1Link: '"API Tokens"',
        step1End: 'section in settings',
        step2: 'Include the token in the',
        step2End: 'header of every request',
        step3: 'The API base URL is:',
        apiGatewayTitle: 'API Gateway - api.flusia.it',
        apiGatewayDescription: 'All API requests are handled by the dedicated subdomain',
        apiGatewayFeatures: {
          validateToken: 'Automatically validates your API token',
          addJwt: 'Adds necessary JWT authentication for Supabase',
          rateLimit: 'Applies rate limiting to prevent abuse (100 req/min per token)',
          logging: 'Logs all calls for debugging and analytics'
        },
        dontWorry: "You don't need to worry about managing JWT or Supabase keys - just use your API token!",
        authentication: 'Authentication',
        authDescription: 'Every request must include the API token in the Authorization header:',
        granularPermissions: '🔐 Granular Permissions',
        permissionsDescription: 'Each API token has granular permissions for each resource. When creating a token, you can specify for each resource (leads, deals, tasks, projects, activities):',
        permissionRead: 'Allows GET operations (list and detail resources)',
        permissionWrite: 'Allows POST and PUT operations (create and modify)',
        permissionDelete: 'Allows DELETE operations (permanent deletion)',
        permissionsWarning: "If you try to perform an operation without the necessary permission, you will receive a",
        permissionsWarningMessage: 'error with the message "Insufficient permissions to [action] [resource]".',
        permissionsExample: 'Permission structure example:',
        ipWhitelist: '🌐 IP Whitelist',
        ipWhitelistDescription: 'You can optionally configure a whitelist of authorized IP addresses for each token. If configured, only requests from the specified IPs can use the token.',
        ipSingleAddress: 'Supports single IPv4 addresses (e.g.',
        ipCidr: 'Supports CIDR notation for ranges (e.g.',
        ipEmpty: 'If the whitelist is empty (not configured), the token works from any IP',
        ipDenied: 'Access Denied:',
        ipDeniedMessage: 'If an unauthorized IP tries to use the token, it will receive a'
      },
      zapier: {
        intro: 'Flusia is now available on Zapier!',
        introDescription: 'Connect Flusia to over 6,000 apps and automate your workflows: create leads from forms, sync contacts, send notifications, and much more.',
        inviteTitle: 'Accept the invite to use Flusia on Zapier',
        inviteDescription: 'The Flusia app is currently available by invitation. Click the link below to accept the invite and enable Flusia in your Zapier account.',
        acceptInvite: 'Accept Zapier invite →',
        howToStart: '🚀 How to Start',
        step1: 'Accept the Zapier invite',
        step1Desc: 'Click the button above to accept the invite and enable Flusia in your Zapier account.',
        step2: 'Generate an API Token',
        step2Desc: 'Go to the API Tokens section above and create a new token with the necessary permissions (at least "Leads").',
        step3: 'Search for "Flusia" on Zapier',
        step3Desc: 'Go to zapier.com, create a new Zap, and search for "Flusia" as an app.',
        step4: 'Connect your account',
        step4Desc: 'When prompted, paste the API token you generated. Zapier will automatically verify the connection.',
        step5: 'Configure your Zap',
        step5Desc: 'Choose a trigger (e.g., new contact from Typeform) and the Flusia action (e.g., "New Lead"). Map the fields and activate!',
        availableActions: '⚡ Available Actions',
        newLeadAction: 'New Lead',
        newLeadDescription: 'Automatically create a new lead in Flusia when an event occurs in another app.',
        requiredFields: 'Required Fields:',
        availableFields: 'Available fields:',
        field: 'Field',
        description: 'Description',
        notes: 'Notes',
        required: 'Required',
        moreFieldsComing: 'More fields coming:',
        moreFieldsDescription: 'In upcoming versions, additional fields will be available such as full address, VAT, tax code, WhatsApp, PEC, and other business data.',
        useCases: '💡 Usage Examples',
        autoAssignment: '🎯 Lead Auto-Assignment',
        autoAssignmentDescription: 'Leads created via Zapier can be automatically assigned to your team users based on:',
        autoAssignByInterest: 'Interest categories - Assign to who manages specific categories',
        autoAssignByPipeline: 'Pipeline - Assign based on the selected pipeline',
        autoAssignBySource: 'Lead source - Assign based on where the lead comes from',
        autoAssignLogic: 'The system automatically assigns the lead to the user in the pool with the least workload (fewer active leads/deals).',
        configureAutoAssign: 'Configure Auto-Assignment →',
        comingSoon: '🔔 Coming Soon',
        comingSoonDescription: "We're working on new triggers and actions:",
        troubleshooting: 'Connection Issues',
        troubleConnectionFails: 'Connection fails',
        troubleConnectionFailsDesc: 'Verify you copied the API token correctly (it must start with',
        troublePermissions: '"Insufficient permissions" error',
        troublePermissionsDesc: 'The token does not have the necessary permissions. Create a new token with the "Leads" permission enabled.',
        troubleLeadNotCreated: 'Lead is not created',
        troubleLeadNotCreatedDesc: 'Make sure you mapped both the Name and Pipeline (required fields).'
      },
      leads: {
        importantInfo: 'Important Information',
        orgIsolation: 'Organization Isolation:',
        orgIsolationDesc: 'All data is automatically filtered by your organization.',
        pipelineDefault: 'Pipeline Default:',
        pipelineDefaultDesc: 'If pipeline_id and pipeline_stage_id are null, the lead is in the default pipeline.',
        relationalData: 'Relational Data:',
        relationalDataDesc: 'You can include contacts, selected_tags, and selected_interest_categories when creating/updating.',
        autoAssignment: 'Lead Auto-Assignment',
        autoAssignmentDesc: 'If you create a lead without specifying the assigned_to field, the system can automatically assign it based on configurable rules.',
        howItWorks: 'How it works:',
        autoAssignRules: 'Rules are evaluated in priority order (first match wins)',
        autoAssignFilters: 'Each rule can filter by: Interest Categories, Pipeline, Lead Source',
        autoAssignConditions: 'Conditions can be combined with AND or OR',
        autoAssignAlgorithm: 'The user is chosen from the rule pool using the "least-loaded + random" algorithm',
        autoAssignDefault: 'If no rule matches, the default pool is used',
        autoAssignConfig: 'Configuration:',
        autoAssignConfigPath: 'Go to Settings → Clients and Leads → Lead Auto-Assignment',
        autoAssignTip: 'Tip:',
        autoAssignTipDesc: 'Use the "Reference Data" section to get the IDs to use in the rules.',
        parameters: 'Parameters',
        supportedFields: 'Supported Fields (70+ fields)',
        relationalDataTitle: 'Relational Data',
        pipelineTitle: 'Pipeline',
        pipelineOmit: 'If you omit pipeline_id/pipeline_stage_id:',
        pipelineOmitDesc: 'The lead will be in the default pipeline (both null)',
        pipelineIdOnly: 'If you specify only pipeline_id:',
        pipelineIdOnlyDesc: 'The lead will be automatically assigned to the first active stage of the pipeline',
        pipelineBoth: 'If you specify both:',
        pipelineBothDesc: 'You must ensure the stage belongs to the specified pipeline',
        pipelineWarning: 'If you specify pipeline_stage_id, you must ALWAYS also specify pipeline_id',
        updateNote: 'Note:',
        updateNoteDesc: 'Update supports all POST fields. If you include contacts, selected_tags, or selected_interest_categories, they will be completely replaced (no merge).',
        deleteWarning: '⚠️ Warning:',
        deleteWarningDesc: 'This operation permanently deletes the lead and all associated data (contacts, tags, interests). It is not reversible.'
      },
      pipelines: {
        note: 'Note:',
        noteDesc: 'Pipelines allow you to organize leads into customized workflows with specific stages.',
        management: 'Pipeline Management',
        stageManagement: 'Stage Management'
      },
      deals: {
        listDescription: 'List of all deals',
        detailDescription: 'Deal details with included services',
        createDescription: 'Create a new deal',
        updateDescription: 'Update a deal',
        statusDescription: 'Change deal status',
        deleteDescription: 'Delete a deal'
      },
      tasks: {
        listDescription: 'List of all tasks',
        detailDescription: 'Task details with comments and checklist',
        createDescription: 'Create a new task',
        updateDescription: 'Update a task',
        completeDescription: 'Mark a task as completed',
        commentsDescription: 'Get task comments',
        addCommentDescription: 'Add a comment',
        deleteDescription: 'Delete a task'
      },
      projects: {
        listDescription: 'List of all projects',
        detailDescription: 'Project details with client and tasks',
        createDescription: 'Create a new project',
        updateDescription: 'Update a project',
        statusDescription: 'Change project status',
        deleteDescription: 'Delete a project'
      },
      services: {
        listDescription: 'List of all services',
        detailDescription: 'Service details',
        createDescription: 'Create a new service',
        updateDescription: 'Update a service',
        deleteDescription: 'Delete a service'
      },
      activities: {
        listDescription: 'List of all client activities',
        detailDescription: 'Client activity details',
        createDescription: 'Log a new client activity',
        updateDescription: 'Update an activity',
        deleteDescription: 'Delete an activity'
      },
      invoices: {
        warning: 'Note:',
        warningDesc: 'Invoices are read-only via API. Creation is handled internally by the system.',
        listDescription: 'List of all invoices',
        detailDescription: 'Invoice details with items and client'
      },
      whatsapp: {
        messagesListDescription: 'List of all WhatsApp messages',
        messageDetailDescription: 'Message details',
        sendDescription: 'Send a WhatsApp message',
        sessionsListDescription: 'List of active WhatsApp Worker sessions',
        sessionDetailDescription: 'Session details'
      },
      newsletter: {
        campaignsListDescription: 'List of all newsletter campaigns',
        campaignDetailDescription: 'Campaign details',
        createCampaignDescription: 'Create a new campaign',
        updateCampaignDescription: 'Update a campaign',
        deleteCampaignDescription: 'Delete a campaign',
        subscribersListDescription: 'List of all subscribers',
        addSubscriberDescription: 'Add a subscriber',
        removeSubscriberDescription: 'Remove a subscriber',
        listsDescription: 'List of all newsletter lists'
      },
      warehouse: {
        productsListDescription: 'List of all products',
        productDetailDescription: 'Product details',
        createProductDescription: 'Create a new product',
        updateProductDescription: 'Update a product',
        deleteProductDescription: 'Delete a product',
        movementsListDescription: 'List of stock movements',
        createMovementDescription: 'Record a stock movement',
        zonesListDescription: 'List of warehouse zones',
        reservationsListDescription: 'List of stock reservations'
      },
      errorCodes: {
        title: 'Error Codes',
        code: 'Code',
        description: 'Description',
        code200: 'Success - Request completed',
        code201: 'Created - Resource created',
        code400: 'Bad Request - Missing or invalid parameters',
        code401: 'Unauthorized - Missing or invalid API Token',
        code403: 'Forbidden - Insufficient permissions',
        code404: 'Not Found - Resource not found',
        code405: 'Method Not Allowed - HTTP method not supported',
        code500: 'Internal Server Error - Server error',
        responseFormat: 'Error response format'
      },
      bestPractices: {
        title: 'Best Practices',
        rateLimiting: 'Rate Limiting:',
        rateLimitingDesc: 'Implement exponential backoff for retries',
        errorHandling: 'Error Handling:',
        errorHandlingDesc: 'Always check the status code and handle errors',
        pagination: 'Pagination:',
        paginationDesc: 'Use pagination for large datasets',
        filters: 'Filters:',
        filtersDesc: 'Filter server-side using query parameters',
        security: 'Security:',
        securityDesc: 'Never expose API tokens in client-side code',
        caching: 'Caching:',
        cachingDesc: 'Implement caching to reduce API calls'
      },
      referenceData: {
        title: 'Reference Data',
        searchPlaceholder: 'Search by name or ID...',
        loading: 'Loading data...',
        interestCategories: 'Interest Categories',
        pipelines: 'Pipelines',
        leadSources: 'Lead Sources',
        noResults: 'No results for',
        noDataConfigured: 'No reference data configured'
      },
      noCodeInstructions: {
        zapier: {
          step1: "1. Add a \"Webhooks by Zapier\" action",
          step2: '2. Choose "Custom Request"',
          step3: '3. Configure:',
          method: 'Method:',
          url: 'URL:',
          headers: 'Headers:',
          data: 'Data:',
          step4: '4. Test the connection'
        },
        make: {
          step1: '1. Add module "HTTP" → "Make a request"',
          step2: '2. Configure:',
          bodyType: 'Body type: Raw',
          contentType: 'Content type: JSON',
          requestContent: 'Request content:',
          step3: '3. Run to test'
        },
        n8n: {
          step1: '1. Add "HTTP Request" node',
          step2: '2. Configure:',
          authentication: 'Authentication: Header Auth',
          name: 'Name:',
          value: 'Value:',
          bodyContentType: 'Body Content Type: JSON',
          body: 'Body:',
          step3: '3. Run the node to test'
        }
      }
    },
    cadenceSettings: {
      title: 'Order frequency',
      description: 'Configure thresholds for declining client alerts and reorder reminders',
      declineSection: 'Declining client alerts',
      reorderSection: 'Reorder reminders',
      baselineSection: 'Baseline calculation',
      overridesSection: 'Per-product override',
      declineDropPct: 'Quantity drop threshold (%)',
      declineLookback: 'Comparison window (days)',
      reorderAdvance: 'Advance factor (×)',
      reorderOverdue: 'Overdue factor (×)',
      minOrders: 'Minimum orders',
      baselineLookback: 'Baseline window (days)',
      enabled: 'Enabled',
      save: 'Save settings',
      reset: 'Reset defaults',
      saved: 'Settings saved'
    },
    duplicates: {
      title: 'Duplicate management',
      description: 'Find and merge duplicate clients and leads',
      tabScan: 'Scan',
      tabSearch: 'Search',
      rescan: 'Rescan',
      scanStarted: 'Scan started. Results will be available when complete.',
      scanInProgress: 'Scan in progress...',
      noRecentScan: 'No recent scan',
      clustersFound: '{count} duplicate clusters found',
      loading: 'Loading...',
      searchPlaceholder: 'Enter client ID or name...',
      search: 'Search',
      examineAndMerge: 'Examine and merge',
      confirmed: 'Confirmed',
      possible: 'Possible',
      noResults: 'No duplicates found. Run a scan to search for similar records.',
      mergeViewTitle: 'Examine and merge duplicates',
      master: 'Master',
      linked: 'Linked',
      noLinkedData: 'No linked data',
      conflictingFields: 'Conflicting fields — choose which value to keep',
      resultingState: 'Resulting status',
      backendStateRule: 'The backend applies the "client wins" rule if not specified.',
      confirmTitle: 'Confirm merge',
      confirmText: 'You are about to merge',
      confirmInto: 'into',
      itemsToMove: 'The following items will be moved',
      confirmWarning: 'The record will be deactivated. The operation can be undone immediately after.',
      cancel: 'Cancel',
      merge: 'Merge',
      mergeSuccess: 'Merge complete.',
      mergeError: 'Error during merge',
      undoMerge: 'Undo',
      undoSuccess: 'Merge undone',
      undoError: 'Unable to undo merge',
      close: 'Close'
    }
  },
  it: {
    title: 'Impostazioni',
    description: 'Gestisci le impostazioni e le preferenze del tuo account.',
    menu: {
      dashboard: 'Dashboard',
      dashboardIBE: 'Dashboard IBE',
      analytics: 'Analisi',
      calendar: 'Calendario',
      clients: 'Clienti',
      deals: 'Trattative',
      invoices: 'Fatture',
      services: 'Servizi',
      projects: 'Progetti',
      tasks: 'Attività',
      documents: 'Documenti',
      reports: 'Report',
      whatsapp: 'WhatsApp',
      inventory: 'Inventario',
      tickets: 'Ticket',
      commissions: 'Provvigioni',
      fattureincloud: 'Fatture in Cloud',
      settings: 'Impostazioni'
    },
    tabs: {
      company: 'Azienda',
      clients: 'Clienti',
      services: 'Servizi',
      'payment-methods': 'Metodi di Pagamento',
      api: 'API e Integrazioni',
      categories: 'Categorie',
      'interest-categories': 'Interessi cliente',
      menu: 'Menu',
      'quote-templates': 'Template Preventivi',
      'deals': 'Impostazioni Preventivi',
      'offers': 'Offerte',
      'sales-targets': 'Obiettivi di Vendita',
      profile: 'Profilo',
      notifications: 'Notifiche',
      users: 'Utenti',
      language: 'Lingua',
      search: 'Ricerca',
      'import-export': 'Importa / Esporta',
      'commissions': 'Provvigioni',
      'whatsapp-automation': 'Automazioni WhatsApp',
      'custom-states': 'Stati e Fonti Personalizzate',
      'google-workspace': 'Google Workspace',
      'permissions': 'Permessi',
      'client-columns': 'Colonne Cliente', 
      'organization-categories': 'Categorie Organizzazione',
      'image-gallery': 'Galleria Immagini',
      'fattureincloud': 'Fatture in Cloud',
      'smtp': 'Email SMTP',
      'custom-fields': 'Campi Personalizzati'
    },
    company: {
      title: 'Impostazioni Aziendali',
      businessInfo: 'Informazioni Aziendali',
      businessName: 'Ragione Sociale',
      vatNumber: 'Partita IVA',
      sdiCode: 'Codice SDI',
      address: 'Indirizzo',
      streetAddress: 'Via',
      streetNumber: 'Numero Civico',
      postalCode: 'CAP',
      city: 'Città',
      province: 'Provincia',
      contact: 'Informazioni di Contatto',
      phone: 'Telefono',
      email: 'Email',
      termsAndConditions: 'Termini e Condizioni',
      appearance: {
        title: 'Titolo CRM e Aspetto',
        crmTitle: 'Titolo CRM',
        customBackground: 'CSS Sfondo Personalizzato',
        backgroundExample: 'Esempio: linear-gradient(45deg, #ff6b6b, #4ecdc4)'
      },
      logos: {
        title: 'Logo Aziendali',
        horizontalLogo: 'Logo Orizzontale',
        iconLogo: 'Logo Icona',
        uploadHorizontal: 'Carica Logo Orizzontale',
        uploadIcon: 'Carica Logo Icona',
        preview: 'Anteprima',
        delete: 'Elimina',
        dropzoneText: 'Trascina e rilascia un\'immagine qui, o clicca per selezionare un file',
        legalSignature: 'Firma Legale',
        uploadLogo: 'Carica logo',
        uploadIcon2: 'Carica icona',
        uploadSignature: 'Carica firma',
        remove: 'Rimuovi',
        horizontal: 'Orizzontale (max 2MB)',
        square: 'Quadrato (max 1MB)',
        signature: 'Firma (max 1MB)'
      },
      save: 'Salva Impostazioni',
      header: {
        title: 'Informazioni Azienda',
        description: 'Gestisci le informazioni della tua azienda e i loghi'
      },
      orgId: {
        title: 'Organization ID',
        description: 'Identificativo univoco della tua organizzazione',
        copied: 'Organization ID copiato negli appunti!',
        copyError: 'Errore nella copia negli appunti'
      },
      companyInfo: {
        title: 'Informazioni Azienda',
        edit: 'Modifica'
      },
      fields: {
        businessName: 'Ragione Sociale',
        vatNumber: 'P.IVA',
        sdiCode: 'Codice SDI',
        address: 'Indirizzo',
        phone: 'Telefono',
        email: 'Email'
      },
      messages: {
        uploadSuccess: 'Logo caricato con successo',
        uploadError: 'Errore nel caricamento del logo',
        deleteSuccess: 'Logo rimosso con successo',
        deleteError: 'Errore nella rimozione del logo',
        saveSuccess: 'Impostazioni aziendali aggiornate con successo',
        saveError: "Errore durante l'aggiornamento delle impostazioni",
        userNotAuthenticated: 'Utente non autenticato',
        userNotInOrganization: "Utente non associato ad un'organizzazione",
        migrationRequired: 'È necessario applicare la migrazione del database per aggiungere il supporto per i loghi. Contatta l\'amministratore.',
        uploadedButNotSaved: "L'immagine è stata caricata nello storage ma non salvata nelle impostazioni."
      }
    },
    language: {
      title: 'Impostazioni Lingua',
      description: 'Scegli la lingua preferita per l\'interfaccia del CRM.',
      interface: 'Lingua Interfaccia',
      select: 'Seleziona la lingua per l\'interfaccia utente',
      messages: {
        updateSuccess: 'Lingua aggiornata con successo',
        updateError: 'Errore nell\'aggiornamento della lingua'
      }
    },
    search: {
      title: 'Impostazioni di Ricerca',
      description: 'Configura quali entità sono incluse nella ricerca globale e come vengono visualizzati i risultati.',
      entities: {
        title: 'Entità Ricercabili',
        description: 'Seleziona quali tipi di dati devono essere inclusi nei risultati della ricerca globale.'
      },
      entityLabels: {
        clients: 'Clienti',
        projects: 'Progetti',
        tasks: 'Attività',
        deals: 'Trattative',
        inventory: 'Magazzino'
      },
      behavior: {
        title: 'Comportamento di Ricerca',
        clientAssociations: {
          label: 'Cerca nelle associazioni dei clienti',
          description: 'Quando cerchi un cliente, includi anche risultati dalle entità associate (progetti, attività, ecc.)'
        },
        maxResults: {
          label: 'Risultati massimi per categoria',
          description: 'Limita il numero di risultati mostrati per ogni tipo di entità'
        }
      },
      save: 'Salva Impostazioni',
      messages: {
        saveSuccess: 'Impostazioni di ricerca salvate con successo',
        saveError: 'Errore nel salvataggio delle impostazioni di ricerca'
      }
    },
    commissions: {
      title: 'Impostazioni Provvigioni',
      description: 'Configura il sistema di provvigioni per gli agenti commerciali.',
      enabled: 'Abilita Sistema Provvigioni',
      enabledDescription: 'Consenti di assegnare agenti commerciali ai clienti e calcolare provvigioni sugli affari',
      defaultRate: 'Percentuale Provvigione Predefinita',
      defaultRateDescription: 'La percentuale predefinita utilizzata per il calcolo delle provvigioni se non è impostata una tariffa specifica per un utente',
      save: 'Salva Impostazioni',
      messages: {
        saveSuccess: 'Impostazioni provvigioni aggiornate con successo',
        saveError: 'Errore nell\'aggiornamento delle impostazioni'
      },
      loading: 'Caricamento...',
      error: 'Errore',
      howItWorks: {
        title: 'Come funziona il sistema provvigioni',
        intro: 'Il sistema calcola le provvigioni seguendo una gerarchia precisa:',
        step1Title: 'Percentuale Default Organizzazione',
        step1Desc: 'Applicata a tutti gli utenti senza configurazione specifica',
        step2Title: 'Gruppi Commissioni (tab "Gruppi")',
        step2Desc: 'Assegna percentuali comuni a più utenti. Modificando la percentuale di un gruppo, tutti gli utenti nel gruppo vengono aggiornati automaticamente. Gli utenti in un gruppo non possono avere percentuali personalizzate.',
        step3Title: 'Percentuale Utente Specifica (tab "Utenti")',
        step3Desc: 'Solo per utenti NON assegnati a un gruppo. Sovrascrive la percentuale default dell\'organizzazione.',
        step4Title: 'Percentuale per Servizio',
        step4Desc: 'Impostabile per combinazioni utente-servizio specifiche nella gestione servizi',
        step5Title: 'Override per Deal',
        step5Desc: 'Sovrascrive tutte le impostazioni precedenti per una specifica trattativa',
        step6Title: 'Scaling con Segnalatori',
        step6Desc: 'Se abilitato nel servizio, la percentuale del segnalatore viene sottratta da quella dell\'agente',
        exampleTitle: 'Esempio pratico:',
        exampleOrg: 'Organizzazione:',
        exampleDefault: '(default)',
        exampleUser: 'Utente assegnato a "Gruppo Senior":',
        exampleDeal: 'Deal con segnalatore al',
        exampleScaling: 'e scaling attivo',
        exampleFinalAgent: 'Provvigione finale agente:',
        exampleReferrer: 'Provvigione segnalatore:'
      },
      reports: {
        title: 'Report Provvigioni',
        description: 'Visualizza e gestisci le provvigioni di vendita per periodo',
        period: 'Periodo',
        salesRep: 'Referente Commerciale',
        status: 'Stato',
        generate: 'Genera Report',
        export: 'Esporta in Excel',
        totals: 'Totale Provvigioni',
        markAsPaid: 'Segna come Pagato',
        notEnabled: 'Sistema di provvigioni non attivo',
        enableInSettings: 'Vai alle impostazioni',
        paid: 'Pagate',
        unpaid: 'Da pagare',
        byAgent: 'Provvigioni per agente',
        noCommissions: 'Nessuna provvigione trovata per questo periodo',
        loading: 'Caricamento dati...',
        columns: {
          client: 'Cliente',
          deal: 'Affare',
          amount: 'Importo Affare',
          rate: 'Percentuale',
          commission: 'Provvigione',
          date: 'Data',
          status: 'Stato',
          actions: 'Azioni'
        }
      }
    },
    whatsappAutomation: {
      title: 'Automazioni WhatsApp',
      description: 'Configura notifiche WhatsApp automatiche basate su eventi di sistema.',
      notConfigured: {
        title: 'WhatsApp Non Configurato',
        description: 'Per utilizzare le automazioni WhatsApp, devi prima configurare l\'integrazione WAHA. Vai in API e Integrazioni per configurare WhatsApp.'
      },
      form: {
        title: 'Nuova Automazione',
        editTitle: 'Modifica Automazione',
        name: 'Nome Automazione',
        entityType: 'Tipo di Elemento',
        triggerEvent: 'Evento Trigger',
        recipient: 'Destinatario',
        message: 'Messaggio WhatsApp',
        active: 'Attiva',
        variables: 'Variabili Disponibili',
        buttons: {
          create: 'Crea Automazione',
          update: 'Aggiorna Automazione',
          cancel: 'Annulla'
        }
      },
      list: {
        empty: 'Nessuna automazione configurata',
        emptyDescription: 'Crea la tua prima automazione WhatsApp per inviare notifiche automatiche',
        columns: {
          name: 'Nome',
          entity: 'Elemento',
          trigger: 'Trigger',
          recipient: 'Destinatario',
          status: 'Stato'
        }
      },
      entityTypes: {
        lead: 'Lead',
        client: 'Cliente',
        deal: 'Offerta',
        project: 'Progetto',
        ticket: 'Ticket Supporto'
      },
      triggerEvents: {
        create: 'Creazione',
        update: 'Modifica',
        delete: 'Eliminazione',
        status_change: 'Cambio Stato',
        state_transition: 'Transizione Stato Specifica',
        api_trigger: 'Evento API'
      },
      recipientTypes: {
        client: 'Cliente',
        assigned_user: 'Utente Assegnato'
      },
      variables: {
        lead: {
          name: { label: 'Nome Lead', description: 'Il nome del lead' },
          email: { label: 'Email Lead', description: "L'email del lead" },
          phone: { label: 'Telefono Lead', description: 'Il numero di telefono del lead' },
          state: { label: 'Stato Lead', description: 'Lo stato attuale del lead' },
          source: { label: 'Fonte Lead', description: 'La fonte di provenienza del lead' },
          assigned_user: { label: 'Utente Assegnato', description: "Il nome dell'utente assegnato al lead" },
          created_at: { label: 'Data Creazione', description: 'La data di creazione del lead' }
        },
        client: {
          business_name: { label: 'Ragione Sociale', description: 'La ragione sociale del cliente' },
          email: { label: 'Email Cliente', description: "L'email del cliente" },
          phone: { label: 'Telefono Cliente', description: 'Il numero di telefono del cliente' },
          state: { label: 'Stato Cliente', description: 'Lo stato attuale del cliente' },
          vat_number: { label: 'Partita IVA', description: 'La partita IVA del cliente' },
          assigned_user: { label: 'Utente Assegnato', description: "Il nome dell'utente assegnato al cliente" },
          created_at: { label: 'Data Creazione', description: 'La data di creazione del cliente' }
        },
        deal: {
          title: { label: 'Titolo Offerta', description: "Il titolo dell'offerta" },
          status: { label: 'Stato', description: "Lo stato attuale dell'offerta" },
          amount: { label: 'Importo', description: "L'importo totale dell'offerta" },
          client_name: { label: 'Nome Cliente', description: 'Il nome del cliente associato' },
          user_name: { label: 'Nome Utente', description: "Il nome dell'utente assegnato" }
        },
        project: {
          name: { label: 'Nome Progetto', description: 'Il nome del progetto' },
          status: { label: 'Stato', description: 'Lo stato attuale del progetto' },
          due_date: { label: 'Data Scadenza', description: 'La data di scadenza del progetto' },
          client_name: { label: 'Nome Cliente', description: 'Il nome del cliente associato' },
          user_name: { label: 'Nome Utente', description: "Il nome dell'utente assegnato" }
        },
        ticket: {
          subject: { label: 'Oggetto Ticket', description: "L'oggetto del ticket di supporto" },
          status: { label: 'Stato', description: 'Lo stato attuale del ticket' },
          priority: { label: 'Priorità', description: 'La priorità del ticket' },
          client_name: { label: 'Nome Cliente', description: 'Il nome del cliente associato' },
          user_name: { label: 'Nome Utente', description: "Il nome dell'utente assegnato" }
        }
      },
      statusLabels: {
        active: 'Attiva',
        inactive: 'Disattiva',
        anyStatus: 'Qualsiasi stato'
      },
      configSource: {
        title: 'Configurazione WhatsApp',
        user: 'WhatsApp Utente (personale)',
        global: 'WhatsApp Globale CRM',
        currentConfig: 'Configurazione attuale'
      },
      stateTransition: {
        fromState: 'Da Stato',
        toState: 'A Stato',
        anyState: 'Qualsiasi stato',
        selectFrom: 'Seleziona stato di partenza',
        selectTo: 'Seleziona stato di arrivo',
        description: "L'automazione scatterà quando l'entità passa dallo stato specificato (o da qualsiasi stato) a questo stato."
      },
      statusChange: {
        specificStatus: 'Stato Specifico',
        selectStatus: 'Seleziona stato (opzionale)',
        description: 'Lascia vuoto per triggerare su ogni cambio di stato, oppure scegli uno stato specifico.'
      },
      apiTrigger: {
        title: 'Automazione Trigger API',
        description: 'Questa automazione può essere triggerata tramite chiamate API esterne. Dopo aver salvato, riceverai un URL webhook univoco che potrai utilizzare per triggerare questa automazione.',
        webhookUrl: 'Webhook URL',
        copy: 'Copia',
        copied: 'URL copiato negli appunti!'
      },
      recipientDescription: {
        client: "Il messaggio verrà inviato al cliente associato all'elemento",
        assigned_user: "Il messaggio verrà inviato all'utente assegnato all'elemento"
      },
      placeholders: {
        name: 'Inserisci un nome per questa automazione',
        selectEntity: 'Seleziona tipo di elemento',
        selectTrigger: 'Seleziona evento trigger',
        selectRecipient: 'Seleziona tipo di destinatario',
        writeMessage: 'Scrivi il messaggio WhatsApp qui...'
      },
      messages: {
        createSuccess: 'Regola di automazione creata con successo',
        createError: 'Errore nella creazione della regola di automazione',
        updateSuccess: 'Regola di automazione aggiornata con successo',
        updateError: "Errore nell'aggiornamento della regola di automazione",
        deleteSuccess: 'Regola di automazione eliminata con successo',
        deleteError: "Errore nell'eliminazione della regola di automazione",
        toggleSuccess: 'Stato automazione aggiornato'
      },
      confirmDelete: {
        title: 'Elimina regola',
        message: 'Sei sicuro di voler eliminare questa regola di automazione? Questa azione non può essere annullata.',
        confirm: 'Elimina',
        cancel: 'Annulla'
      }
    },
    customStates: {
      title: 'Stati e Fonti Personalizzate',
      description: 'Configura gli stati e le fonti per organizzare clienti e lead',
      generateDefault: 'Genera Dati Default',
      generateConfirm: 'Sei sicuro di voler generare gli stati e fonti di default? Questo aggiungerà dati predefiniti alla tua organizzazione.',
      generateSuccess: 'Stati e fonti di default generati con successo!',
      generateError: 'Errore durante la generazione dei dati di default. Controlla la console per maggiori dettagli.',
      sections: {
        clientStates: 'Stati Cliente',
        leadStates: 'Stati Lead',
        leadSources: 'Fonti Lead'
      },
      clientStates: {
        title: 'Stati Cliente',
        description: 'Gestisci gli stati che i clienti possono avere nella tua organizzazione',
        addState: 'Aggiungi Stato',
        leadStates: 'Stati Lead',
        leadStatesNote: '(appaiono nella sezione Leads)',
        clientStates: 'Stati Cliente',
        clientStatesNote: '(appaiono nella sezione Clienti)'
      },
      leadStates: {
        title: 'Stati Lead',
        description: 'Gestisci gli stati che i lead possono avere nel processo di qualificazione',
        addState: 'Aggiungi Stato'
      },
      leadSources: {
        title: 'Fonti Lead',
        description: 'Gestisci le fonti da cui provengono i tuoi lead',
        addSource: 'Aggiungi Fonte'
      },
      form: {
        newClientState: 'Nuovo Stato Cliente',
        editClientState: 'Modifica Stato Cliente',
        newLeadState: 'Nuovo Stato Lead',
        editLeadState: 'Modifica Stato Lead',
        newLeadSource: 'Nuova Fonte Lead',
        editLeadSource: 'Modifica Fonte Lead',
        name: 'Nome',
        description: 'Descrizione',
        color: 'Colore',
        sortOrder: 'Ordine',
        isLeadState: 'È uno stato Lead',
        isLeadStateDescription: 'I clienti con questo stato appariranno nella sezione Leads invece che nella sezione Clienti',
        conversionType: 'Tipo di Conversione',
        isWon: 'Stato "Vinto" (conversione riuscita)',
        isLost: 'Stato "Perso" (conversione fallita)',
        setAsDefault: 'Imposta come predefinito',
        cancel: 'Annulla',
        save: 'Salva'
      },
      badges: {
        lead: 'LEAD',
        won: 'VINTO',
        lost: 'PERSO'
      },
      placeholders: {
        clientStateName: 'Es. Prospect, Attivo, Inattivo',
        leadStateName: 'Es. Nuovo, Qualificato, Convertito',
        leadSourceName: 'Es. Sito web, Passaparola, Social Media',
        descriptionOptional: 'Descrizione opzionale'
      },
      messages: {
        leadStateOrderSaved: 'Ordine stati lead salvato',
        clientStateOrderSaved: 'Ordine stati cliente salvato',
        sourceOrderSaved: 'Ordine fonti lead salvato',
        deleteStateConfirm: 'Eliminare questo stato? Se è stato usato nello storico dei clienti verrà disattivato invece che eliminato.',
        stateDeleted: 'Stato eliminato',
        stateDeactivated: 'Lo stato è usato nello storico dei clienti: è stato disattivato invece che eliminato'
      }
    },
    interestCategories: {
      title: 'Categorie di Interesse',
      description: 'Gestisci la gerarchia delle categorie di interesse per lead e clienti',
      buttons: {
        expand: 'Espandi',
        collapse: 'Comprimi',
        newCategory: 'Nuova Categoria',
        createFirst: 'Crea prima categoria'
      },
      search: {
        placeholder: 'Cerca categorie...'
      },
      inline: {
        placeholder: 'Nome categoria... (Enter=salva, Tab=sotto-cat, Esc=annulla)',
        rootPlaceholder: 'Nome categoria root... (Enter=salva, Esc=annulla)',
        saveTitle: 'Salva (Enter)',
        cancelTitle: 'Annulla (Esc)'
      },
      form: {
        editTitle: 'Modifica Categoria',
        name: 'Nome *',
        description: 'Descrizione',
        parentCategory: 'Categoria Padre',
        noParent: 'Nessuna (categoria root)',
        color: 'Colore',
        active: 'Attiva',
        cancel: 'Annulla',
        save: 'Salva'
      },
      actions: {
        addSubcategory: 'Aggiungi sotto-categoria',
        edit: 'Modifica',
        delete: 'Elimina'
      },
      badges: {
        level: 'Lv.',
        inactive: 'Inattiva'
      },
      emptyState: {
        noResults: 'Nessuna categoria trovata',
        noCategories: 'Nessuna categoria',
        tryModifySearch: 'Prova a modificare la ricerca',
        startCreating: 'Inizia creando la tua prima categoria di interesse'
      },
      stats: {
        totalCategories: 'categorie totali',
        results: 'risultati'
      },
      messages: {
        createSuccess: 'Categoria "{name}" creata',
        createError: 'Errore nella creazione della categoria',
        updateSuccess: 'Categoria aggiornata',
        updateError: 'Errore nell\'aggiornamento della categoria',
        deleteSuccess: 'Categoria eliminata',
        deleteError: 'Errore nell\'eliminazione della categoria'
      },
      confirm: {
        deleteWithChildren: 'Sei sicuro di voler eliminare "{name}" e le sue {count} sotto-categorie?',
        deleteSingle: 'Sei sicuro di voler eliminare "{name}"?'
      }
    },
    clientSettings: {
      title: 'Impostazioni clienti e leads',
      description: 'Configura le impostazioni relative a clienti e leads',
      tabs: {
        menuLabels: {
          name: 'Etichette menu',
          description: 'Personalizza i nomi delle voci di menu clienti e leads'
        },
        defaultEntityType: {
          name: 'Tipo entità default',
          description: 'Configura il tipo di entità preselezionato di default nei form'
        },
        states: {
          name: 'Stati e fonti',
          description: 'Gestisci gli stati personalizzati e le fonti lead'
        },
        tags: {
          name: 'Tag clienti',
          description: 'Gestisci i tag per categorizzare clienti e lead'
        },
        interests: {
          name: 'Interessi cliente',
          description: 'Gestisci le categorie di interesse per clienti e lead'
        },
        settori: {
          name: 'Settori',
          description: 'Gestisci i settori merceologici per classificare clienti e lead'
        },
        customFields: {
          name: 'Campi personalizzati',
          description: 'Gestisci i campi personalizzati per clienti'
        },
        kanbanSettings: {
          name: 'Kanban lead',
          description: 'Configura il comportamento del kanban nella gestione dei lead'
        },
        leadsColumns: {
          name: 'Colonne leads',
          description: 'Configura quali colonne sono visibili nella tabella dei leads. Le colonne "Nome" e "Azioni" sono sempre visibili.'
        },
        clientsColumns: {
          name: 'Colonne clienti',
          description: 'Configura quali colonne sono visibili nella tabella dei clienti. Le colonne "Nome" e "Azioni" sono sempre visibili.'
        },
        leadQuickInputFields: {
          name: 'Popup creazione rapida lead',
          description: 'Configura quali campi sono visibili nel popup di creazione rapida lead'
        }
      },
      menuLabels: {
        infoBox: 'Personalizza i nomi delle voci di menu "Clienti" e "Leads". Specifica sia la forma singolare che plurale. Se lasci un campo vuoto, verrà utilizzato il nome predefinito.',
        clientsSection: 'Clienti',
        leadsSection: 'Leads',
        pluralLabel: 'Plurale',
        singularLabel: 'Singolare',
        clientsLabel: 'Label sezione \'Clienti\'',
        clientsPlaceholder: 'es. Pazienti',
        clientsPlaceholderSingular: 'es. Paziente',
        clientsDefault: 'Predefinito: "Clienti"',
        clientsDefaultPlural: 'Default: Clienti',
        clientsDefaultSingular: 'Default: Cliente',
        clientsGender: 'Genere',
        leadsLabel: 'Label sezione \'Leads\'',
        leadsPlaceholder: 'es. Opportunità',
        leadsPlaceholderSingular: 'es. Opportunità',
        leadsDefault: 'Predefinito: "Leads"',
        leadsDefaultPlural: 'Default: Leads',
        leadsDefaultSingular: 'Default: Lead',
        leadsGender: 'Genere',
        genderMasculine: 'Maschile (il/questo/nuovo)',
        genderFeminine: 'Femminile (la/questa/nuova)',
        saveButton: 'Salva modifiche',
        saving: 'Salvataggio...',
        resetButton: 'Ripristina default',
        preview: 'Anteprima:',
        previewClients: 'Clienti:',
        previewLeads: 'Leads:',
        previewExample: 'Esempio: "{nuovo} {label}"',
        quickCreateButton: {
          label: 'Usa pulsante creazione rapida (Leads)',
          description: 'Sostituisce il pulsante "+ Nuovo" con "Crea velocemente" nella pagina leads. Il pulsante apre un dropdown per selezionare lo stato del lead prima di mostrare il form di creazione rapida.'
        },
        quickCreateButtonClients: {
          label: 'Usa pulsante creazione rapida (Clienti)',
          description: 'Sostituisce il pulsante "+ Nuovo" con "Crea velocemente" nella pagina clienti.'
        },
        messages: {
          saveSuccess: 'Etichette menu salvate con successo',
          saveError: 'Errore nel salvare le etichette menu'
        }
      },
      defaultEntityType: {
        infoBox: 'Scegli quale tipo di entità deve essere preselezionato di default quando crei un nuovo cliente o lead. Questa impostazione non modifica i clienti esistenti.',
        label: 'Tipo Entità Predefinito',
        company: 'Azienda',
        companyDescription: 'Preseleziona "Azienda" nei form',
        individual: 'Persona Fisica',
        individualDescription: 'Preseleziona "Persona Fisica" nei form',
        saveButton: 'Salva modifiche',
        saving: 'Salvataggio...',
        preview: 'Anteprima:',
        previewText: 'Quando crei un nuovo cliente o lead, il tipo entità sarà preselezionato su',
        messages: {
          saveSuccess: 'Tipo entità default salvato con successo',
          saveError: 'Errore nel salvare il tipo entità default'
        }
      },
      kanbanSettings: {
        title: 'Impostazioni Kanban Lead',
        description: 'Configura il comportamento del kanban nella gestione dei lead',
        noteRequired: {
          label: 'Nota obbligatoria al cambio stato',
          description: 'Se abilitato, quando si trascina un lead nel kanban si aprirà un drawer per inserire una nota obbligatoria. Se disabilitato, il lead verrà spostato direttamente.'
        },
        messages: {
          saveSuccess: 'Impostazione salvata correttamente',
          saveError: 'Errore nel salvataggio dell\'impostazione'
        }
      },
      leadsColumns: {
        infoBox: 'Configura quali colonne sono visibili nella tabella dei leads. Le colonne "Nome" e "Azioni" sono sempre visibili.',
        visibleCount: '{count} di {total} colonne visibili',
        columns: {
          stage: 'Fase pipeline',
          score: 'Score',
          value: 'Valore',
          assignee: 'Assegnatario',
          source: 'Fonte',
          date: 'Data'
        },
        showColumn: 'Mostra colonna',
        hideColumn: 'Nascondi colonna',
        resetButton: 'Ripristina default',
        messages: {
          saveSuccess: 'Impostazioni visibilità colonne salvate',
          saveError: 'Errore nel salvataggio delle impostazioni'
        }
      },
      clientsColumns: {
        infoBox: 'Configura quali colonne sono visibili nella tabella dei clienti. Le colonne "Nome" e "Azioni" sono sempre visibili.',
        visibleCount: '{count} di {total} colonne visibili',
        columns: {
          email: 'Email',
          phone: 'Telefono',
          owner: 'Proprietario',
          state: 'Stato',
          interests: 'Interessi',
          updated: 'Aggiornato'
        },
        showColumn: 'Mostra colonna',
        hideColumn: 'Nascondi colonna',
        resetButton: 'Ripristina default',
        messages: {
          saveSuccess: 'Impostazioni visibilità colonne salvate',
          saveError: 'Errore nel salvataggio delle impostazioni'
        }
      },
      leadQuickInputFields: {
        infoBox: 'Configura quali campi sono visibili nel popup di creazione rapida lead.',
        visibleCount: '{count} di {total} campi visibili',
        fields: {
          lead_source: 'Fonte',
          interests: 'Interessi',
          score: 'Score',
          opportunity_value: 'Valore',
          street_address: 'Via/Indirizzo',
          street_number: 'Numero civico',
          postal_code: 'CAP',
          city: 'Città',
          province: 'Provincia',
          country: 'Paese',
          region: 'Regione',
          vat_number: 'Partita IVA',
          tax_code: 'Codice Fiscale',
          sdi_code: 'Codice SDI',
          pec: 'PEC',
          annual_revenue: 'Fatturato Annuale',
          website: 'Sito Web',
          general_comments: 'Note'
        },
        showField: 'Mostra campo',
        hideField: 'Nascondi campo',
        resetButton: 'Ripristina default',
        messages: {
          saveSuccess: 'Impostazioni visibilità campi salvate',
          saveError: 'Errore nel salvataggio delle impostazioni'
        }
      },
      customFields: {
        title: 'Campi Personalizzati Clienti',
        description: 'Gestisci campi personalizzati per raccogliere informazioni specifiche sui clienti',
        newField: 'Nuovo Campo',
        editField: 'Modifica Campo',
        newCustomField: 'Nuovo Campo Personalizzato',
        fieldTypes: {
          text: { label: 'Testo', description: 'Campo di testo semplice' },
          textarea: { label: 'Testo Lungo', description: 'Area di testo multilinea' },
          number: { label: 'Numero', description: 'Campo numerico' },
          date: { label: 'Data', description: 'Selezione data' },
          boolean: { label: 'Sì/No', description: 'Checkbox vero/falso' },
          select: { label: 'Selezione', description: 'Menu a tendina con opzioni' },
          email: { label: 'Email', description: 'Indirizzo email' },
          phone: { label: 'Telefono', description: 'Numero di telefono' },
          url: { label: 'URL', description: 'Indirizzo web' }
        },
        form: {
          fieldName: 'Nome Campo',
          fieldNamePlaceholder: 'es. settore_attivita',
          fieldNameHelp: 'Nome tecnico del campo (solo lettere, numeri e underscore)',
          fieldLabel: 'Etichetta',
          fieldLabelPlaceholder: 'es. Settore di Attività',
          fieldLabelHelp: 'Nome mostrato agli utenti',
          fieldType: 'Tipo di Campo',
          options: 'Opzioni',
          addOption: 'Aggiungi una opzione',
          defaultValue: 'Valore Predefinito',
          defaultValuePlaceholder: 'Valore opzionale predefinito',
          helpText: 'Testo di Aiuto',
          helpTextPlaceholder: 'Suggerimento per l\'utente',
          isRequired: 'Campo obbligatorio',
          cancel: 'Annulla',
          create: 'Crea',
          update: 'Aggiorna'
        },
        list: {
          name: 'Nome:',
          type: 'Tipo:',
          required: 'Obbligatorio',
          options: 'Opzioni:',
          emptyState: {
            title: 'Nessun campo personalizzato creato',
            description: 'Crea il primo campo per raccogliere informazioni specifiche sui clienti'
          }
        },
        validation: {
          fieldNameRequired: 'Il nome del campo è obbligatorio',
          fieldNameFormat: 'Il nome del campo deve iniziare con una lettera e contenere solo lettere, numeri e underscore',
          fieldLabelRequired: 'L\'etichetta del campo è obbligatoria',
          selectOptionsRequired: 'I campi di selezione devono avere almeno una opzione',
          duplicateName: 'Esiste già un campo con questo nome'
        },
        confirm: {
          title: 'Elimina Campo Personalizzato',
          message: 'Sei sicuro di voler eliminare il campo "{label}"? Tutti i dati associati verranno persi.',
          confirmText: 'Elimina',
          cancelText: 'Annulla'
        },
        messages: {
          loadError: 'Errore nel caricamento dei campi personalizzati',
          orgError: 'Errore nel caricamento dell\'organizzazione',
          orgIdNotAvailable: 'ID organizzazione non disponibile',
          orgNotFound: 'Organizzazione non trovata',
          fieldCreated: 'Campo creato con successo',
          fieldUpdated: 'Campo aggiornato con successo',
          fieldDeleted: 'Campo eliminato con successo',
          saveError: 'Errore durante il salvataggio del campo',
          deleteError: 'Errore durante l\'eliminazione del campo',
          orderUpdated: 'Ordine dei campi aggiornato',
          orderError: 'Errore durante l\'aggiornamento dell\'ordine'
        }
      }
    },
    partners: {
      page: {
        title: 'Impostazioni Partner',
        description: 'Configura le impostazioni relative ai partner e la loro classificazione',
        tabs: {
          partnerTypes: {
            name: 'Tipologie Partner',
            description: 'Configura i tipi di partner disponibili per la classificazione'
          },
          businessSectors: {
            name: 'Settori Business',
            description: 'Configura i settori business per i partner'
          }
        }
      },
      businessSectors: {
        title: 'Settori Business',
        description: 'Gestisci i settori business disponibili per la classificazione dei partner',
        addButton: 'Aggiungi Settore',
        editTitle: 'Modifica Settore Business',
        newTitle: 'Nuovo Settore Business',
        form: {
          name: 'Nome',
          namePlaceholder: 'es. Tecnologia, Marketing, Legale',
          color: 'Colore',
          description: 'Descrizione',
          descriptionPlaceholder: 'Descrizione del settore business...',
          active: 'Attivo',
          cancel: 'Annulla',
          saving: 'Salvando...',
          update: 'Aggiorna',
          create: 'Crea'
        },
        status: {
          active: 'Attivo',
          inactive: 'Inattivo'
        },
        emptyState: {
          title: 'Nessun settore business configurato',
          action: 'Aggiungi il primo settore business'
        },
        confirm: {
          delete: 'Sei sicuro di voler eliminare questo settore business?'
        }
      },
      partnerTypes: {
        title: 'Tipologie Partner',
        description: 'Gestisci i tipi di partner disponibili per la classificazione',
        addButton: 'Aggiungi Tipo',
        editTitle: 'Modifica Tipo Partner',
        newTitle: 'Nuovo Tipo Partner',
        form: {
          name: 'Nome',
          namePlaceholder: 'es. Consulente, Fornitore, Agenzia',
          color: 'Colore',
          description: 'Descrizione',
          descriptionPlaceholder: 'Descrizione del tipo di partner...',
          active: 'Attivo',
          cancel: 'Annulla',
          saving: 'Salvando...',
          update: 'Aggiorna',
          create: 'Crea'
        },
        status: {
          active: 'Attivo',
          inactive: 'Inattivo'
        },
        emptyState: {
          title: 'Nessun tipo di partner configurato',
          action: 'Aggiungi il primo tipo di partner'
        },
        confirm: {
          delete: 'Sei sicuro di voler eliminare questo tipo di partner?'
        }
      }
    },
    integrations: {
      smtp: {
        title: 'Configurazione SMTP',
        description: 'Configura il server email per l\'invio automatico di notifiche',
        enable: 'Abilita SMTP',
        enableDescription: 'Attiva l\'invio automatico di notifiche email',
        server: 'Server SMTP',
        port: 'Porta',
        username: 'Username/Email',
        password: 'Password',
        fromEmail: 'Email Mittente',
        fromEmailHint: 'Questo indirizzo apparirà come mittente delle email automatiche',
        testConnection: 'Test Connessione',
        testConnectionDescription: 'Verifica che la configurazione sia corretta',
        testSuccess: 'Successo',
        testError: 'Errore',
        testing: 'Testing...',
        test: 'Test',
        commonConfigs: 'Configurazioni Comuni',
        saveButton: 'Salva Configurazione',
        saving: 'Salvando...',
        messages: {
          saveSuccess: 'Configurazione SMTP salvata con successo',
          saveError: 'Errore durante il salvataggio della configurazione SMTP',
          testSuccess: 'Test SMTP completato con successo! Email di test inviata.',
          testError: 'Errore durante il test della connessione SMTP',
          testFailed: 'Test SMTP fallito',
          fillRequired: 'Compila tutti i campi obbligatori prima di testare la connessione'
        }
      },
      databaseBackup: {
        title: 'Database Backup',
        description: 'Esporta un backup completo del database includendo struttura e dati',
        filteringOrg: 'Filtrando i dati solo per l\'organizzazione corrente',
        fullBackup: 'Backup Completo',
        fullBackupDescription: 'Backup completo che include struttura del database e tutti i dati. Utile per ripristinare l\'intero database.',
        fullBackupItems: {
          schema: 'Schema database (tabelle, indici, vincoli)',
          data: 'Tutti i dati delle tabelle',
          triggers: 'Trigger e funzioni',
          policies: 'Policy di sicurezza RLS'
        },
        downloadFullBackup: 'Scarica Backup Completo',
        schemaOnly: 'Solo Schema',
        schemaOnlyDescription: 'Backup della sola struttura senza dati. Utile per creare ambienti di sviluppo o documentare lo schema.',
        schemaOnlyItems: {
          schema: 'Schema database (tabelle, indici, vincoli)',
          structure: 'Struttura e relazioni tabelle',
          functions: 'Funzioni e trigger',
          noData: 'Nessun dato incluso'
        },
        downloadSchemaOnly: 'Scarica Solo Schema',
        importantNotes: 'Note importanti',
        notes: {
          largeDb: 'I backup potrebbero richiedere diversi minuti per database di grandi dimensioni',
          stableConnection: 'Assicurati di avere una connessione internet stabile durante il processo',
          sensitiveData: 'I backup completi includono dati sensibili come password',
          secureStorage: 'Conserva i backup in modo sicuro e segui le policy di protezione dati'
        },
        progress: {
          connecting: 'Connessione al database...',
          schema: 'Estrazione schema database...',
          structure: 'Estrazione struttura tabelle...',
          processingTable: 'Elaborazione tabella',
          rows: 'righe',
          finalizing: 'Finalizzazione file backup...',
          complete: 'Backup completato con successo!'
        },
        messages: {
          fullSuccess: 'Backup completo del database completato con successo!',
          schemaSuccess: 'Backup dello schema completato con successo!',
          downloadSuccess: 'Backup del database scaricato con successo',
          downloadError: 'Impossibile creare il backup del database',
          fullError: 'Backup completo fallito',
          schemaError: 'Backup schema fallito'
        }
      },
      fattureincloud: {
        title: 'Fatture in Cloud',
        description: 'Connetti e configura l\'integrazione con Fatture in Cloud per la gestione automatica della fatturazione',
        goToFIC: 'Vai a Fatture in Cloud',
        tabs: {
          connection: { name: 'Connessione', description: 'Gestisci la connessione OAuth' },
          defaults: { name: 'Fatturazione', description: 'Valori default e automazione' },
          mappings: { name: 'Clienti', description: 'Mapping clienti CRM ↔ FIC' },
          sync: { name: 'Sincronizzazione', description: 'Sincronizza dati manualmente' }
        },
        setupCompleted: 'Setup completato con successo!',
        messages: {
          connectionSuccess: 'Connessione a Fatture in Cloud completata con successo!',
          connectionError: 'Errore di connessione',
          loadError: 'Errore durante il caricamento della configurazione',
          authError: 'Errore durante l\'avvio del flusso di autorizzazione',
          disconnectSuccess: 'Disconnessione da Fatture in Cloud completata',
          disconnectError: 'Errore durante la disconnessione',
          testSuccess: 'Connessione valida',
          testFailed: 'Connessione non valida o token scaduto',
          testError: 'Errore durante il test della connessione'
        },
        confirm: {
          disconnect: 'Sei sicuro di voler disconnettere Fatture in Cloud? Dovrai riautorizzare l\'accesso per utilizzare nuovamente l\'integrazione.'
        }
      },
      apiTokens: {
        title: 'Token API',
        description: 'I token permettono ad applicazioni esterne e assistenti AI di leggere e modificare i tuoi dati attraverso le API di Flusia.',
        apiDocumentation: 'Documentazione API',
        warning: 'Chi ha un token può agire con i suoi permessi. Conservalo come una password e dai a ogni integrazione solo quello che le serve.',
        listTitle: 'Token della tua azienda',
        listDescription: 'Il valore completo si vede una sola volta, quando crei il token.',
        generateNew: 'Nuovo token',
        noTokens: 'Nessun token',
        noTokensDescription: 'Creane uno per collegare un\'applicazione esterna.',
        created: 'Creato',
        lastUsed: 'Ultimo utilizzo',
        neverUsed: 'Mai usato',
        expires: 'Scade',
        active: 'Attivo',
        inactive: 'Disattivato',
        toggleActive: 'Token attivo',
        copyToken: 'Copia',
        copied: 'Copiato',
        ipRestricted: 'IP consentiti',
        deleteToken: 'Elimina token',
        deleteConfirm: 'Eliminare?',
        deleteYes: 'Elimina',
        deleteNo: 'Annulla',
        actions: { read: 'lettura', write: 'modifica', delete: 'cancellazione' },
        actionColumns: { read: 'Lettura', write: 'Modifica', delete: 'Cancellazione' },
        levels: { full: 'Accesso completo', read_write: 'Lettura e modifica', read: 'Sola lettura', other: 'Altro' },
        drawer: {
          title: 'Nuovo token API',
          subtitle: 'Scegli un nome e cosa può fare il token',
          details: 'Dettagli',
          tokenName: 'Nome',
          tokenNamePlaceholder: 'es. Integrazione sito web',
          description: 'Descrizione',
          descriptionPlaceholder: 'A cosa serve questo token?',
          permissions: 'Permessi',
          permissionsDescription: 'Puoi dare solo i permessi che hai anche tu.',
          preset: 'Scelta rapida',
          presets: { all: 'Tutto quello che puoi dare', read_only: 'Tutto in sola lettura', custom: 'Personalizzato' },
          resource: 'Area',
          notGrantable: 'Non hai questo permesso',
          unavailable: 'Non disponibili per il tuo ruolo',
          selectedCount: '{count} aree selezionate',
          ipWhitelist: 'Indirizzi IP consentiti',
          ipHint: 'Lascia vuoto per accettare qualsiasi indirizzo. Puoi indicare una rete intera (es. 192.168.1.0/24).',
          ipPlaceholder: 'es. 192.168.1.1',
          add: 'Aggiungi',
          removeIp: 'Rimuovi',
          cancel: 'Annulla',
          generate: 'Crea token',
          creating: 'Creazione...'
        },
        resources: {
          leads: 'Lead e clienti',
          deals: 'Offerte',
          tasks: 'Task',
          projects: 'Progetti',
          activities: 'Calendario',
          services: 'Servizi',
          invoices: 'Fatture',
          whatsapp: 'WhatsApp',
          newsletter: 'Newsletter',
          warehouse: 'Magazzino',
          pipelines: 'Pipeline',
          qr_codes: 'QR code',
          wiki: 'Wiki',
          workflows: 'Automazioni',
          settings: 'Impostazioni',
          templates: 'Modelli di preventivo'
        },
        success: {
          title: 'Token creato',
          saveWarning: 'Copialo adesso: non potrai più vederlo. Se lo perdi, ne crei uno nuovo.',
          yourToken: 'Il tuo token',
          tokenSaved: 'L\'ho salvato'
        },
        messages: {
          nameRequired: 'Scrivi un nome per il token.',
          permissionRequired: 'Scegli almeno un permesso.',
          createError: 'Non siamo riusciti a creare il token. Riprova.',
          notGrantable: 'Hai scelto un permesso che non hai. Ricarica la pagina e riprova.',
          deleteError: 'Non siamo riusciti a eliminare il token. Riprova.',
          updateError: 'Non siamo riusciti ad aggiornare il token. Riprova.',
          invalidIp: 'Indirizzo IP non valido. Usa il formato 192.168.1.1 oppure 192.168.1.0/24.',
          ipExists: 'Questo indirizzo è già nella lista.',
          loadErrorTitle: 'Elenco dei token non disponibile',
          loadError: 'Non siamo riusciti a leggere l\'elenco dei token, quindi potrebbe essere incompleto. Ricarica la pagina e, se il problema resta, segnala questo messaggio:'
        }
      },
      wooCommerce: {
        title: 'Integrazione WooCommerce',
        description: 'Configura la sincronizzazione con il tuo store WooCommerce',
        tabs: {
          config: 'Configurazione',
          webhooks: 'Webhook',
          orders: 'Ordini',
          logs: 'Log'
        },
        config: {
          title: 'Configurazione API WooCommerce',
          description: 'Inserisci i dati di connessione al tuo store WooCommerce',
          siteUrl: 'URL del sito',
          apiVersion: 'Versione API',
          consumerKey: 'Consumer Key',
          consumerSecret: 'Consumer Secret',
          integrationActive: 'Integrazione attiva',
          syncOrders: 'Sincronizza ordini',
          syncStock: 'Sincronizza stock',
          testConnection: 'Test Connessione',
          saveConfig: 'Salva Configurazione'
        },
        stats: {
          title: 'Statistiche Sincronizzazione',
          syncedOrders: 'Ordini sincronizzati',
          webhooks24h: 'Webhook 24h',
          syncedProducts: 'Prodotti sincronizzati'
        },
        webhooks: {
          title: 'Configurazione Webhook',
          description: 'Configura questi endpoint nel tuo plugin WordPress',
          orderCreated: 'Order Created',
          orderUpdated: 'Order Updated',
          testConnection: 'Test Connection',
          apiKey: 'API Key'
        },
        orders: {
          title: 'Ordini WooCommerce',
          description: 'Ultimi ordini sincronizzati',
          loadOrders: 'Carica Ordini',
          orderId: 'ID Ordine',
          number: 'Numero',
          customer: 'Cliente',
          total: 'Totale',
          status: 'Stato',
          date: 'Data',
          noOrders: 'Nessun ordine trovato'
        },
        logs: {
          title: 'Log Webhook',
          description: 'Attività recente dei webhook',
          loadLogs: 'Carica Log',
          event: 'Evento',
          status: 'Stato',
          message: 'Messaggio',
          success: 'Successo',
          error: 'Errore',
          processedSuccessfully: 'Processato con successo',
          noLogs: 'Nessun log trovato'
        },
        messages: {
          loadConfigError: 'Errore nel caricamento della configurazione',
          loadLogsError: 'Errore nel caricamento dei log',
          loadOrdersError: 'Errore nel caricamento degli ordini',
          saveSuccess: 'Configurazione salvata con successo',
          saveError: 'Errore nel salvataggio della configurazione',
          testSuccess: 'Connessione testata con successo!',
          testFailed: 'Test fallito',
          testError: 'Errore durante il test di connessione',
          copiedToClipboard: 'copiato negli appunti',
          copyError: 'Errore nella copia'
        }
      },
      emailMigration: {
        banner: {
          title: 'Hai un nuovo sistema email più veloce e affidabile.',
          subtitle: 'Configura il tuo dominio per attivarlo.',
          cta: 'Configura',
        },
        emptyState: {
          title: 'Configura il tuo dominio per inviare email',
          description: 'Il nuovo sistema invia più velocemente e con migliore deliverability.',
          cta: 'Configura il dominio',
        },
        inlineCallout: {
          title: 'Hai un dominio configurato sul vecchio sistema.',
          description: 'Aggiungi qui sotto il tuo dominio per attivare il nuovo sistema email.',
        },
        toast: {
          sendBlocked: 'Configura il tuo dominio email in Impostazioni per inviare.',
        },
        wizard: {
          header: 'Aggiungi dominio email',
          stepLabel: 'Step',
          stepOf: 'di',
          step1: {
            title: 'Quale dominio vuoi usare?',
            helper: 'Inserisci il tuo dominio principale (la parte dopo @ nelle email).',
            domainLabel: 'Dominio',
            domainPlaceholder: 'esempio: miaazienda.com',
            advancedToggle: 'Avanzato: imposta un sottodominio',
            subdomainLabel: 'Sottodominio (opzionale)',
            subdomainPlaceholder: 'newsletter',
            subdomainHint: 'Lascia vuoto se non ti serve specificamente.',
            invalidDomain: 'Inserisci un dominio valido (es. miaazienda.com).',
          },
          step2: {
            title: 'Configura i record DNS',
            instructions:
              'Aggiungi questi 3 record TXT nel pannello DNS del tuo provider (Cloudflare, GoDaddy, Aruba, ecc.).',
            spfMergeWarning:
              "Se hai già un record SPF (TXT) sul dominio root, FAI MERGE del nuovo include nell'SPF esistente — NON creare un secondo record SPF.",
            recordType: 'Tipo',
            recordHost: 'Host',
            recordValue: 'Valore',
            copyTooltip: 'Copia',
            copied: 'Copiato',
            ctaNext: 'Ho configurato i record, verifica',
          },
          step3: {
            title: 'Verifichiamo i tuoi record DNS',
            loading: 'Controllo con il provider email...',
            verifiedMsg: 'Tutti i record sono verificati! Ora puoi configurare il mittente.',
            pendingMsg:
              'I record non sono ancora propagati. Il DNS può richiedere fino a 30 minuti — riprova tra poco.',
            failedMsg: 'Alcuni record non sono corretti. Controllali e riprova.',
            retryCta: 'Riprova verifica',
            backToDns: 'Rivedi record DNS',
          },
          step4: {
            title: 'Imposta il tuo mittente',
            description: 'Così appariranno le email inviate da questo dominio ai tuoi contatti.',
            localPartLabel: 'Email mittente',
            localPartPlaceholder: 'newsletter',
            localPartHint: 'Solo la parte prima della @; il dominio si aggiunge automaticamente.',
            senderNameLabel: 'Nome mittente',
            senderNamePlaceholder: 'La tua azienda',
            saveCta: 'Salva e attiva',
          },
          footer: {
            back: 'Indietro',
            cancel: 'Annulla',
            next: 'Avanti',
            close: 'Chiudi',
          },
          successToast: 'Dominio attivato! Da ora invii con il tuo brand.',
          createError: 'Impossibile creare il dominio',
          verifyError: 'Impossibile verificare il dominio',
          saveSenderError: 'Impossibile salvare il mittente',
        },
      },
      emailDomain: {
        title: 'Domini Email',
        description: 'Configura il tuo dominio per inviare newsletter professionali dal tuo brand',
        addDomain: 'Aggiungi Dominio',
        superadminBanner: {
          label: 'Modalità superadmin',
          prefix: 'stai operando per conto di',
          fallbackOrg: 'questa organizzazione',
        },
        activeDomain: {
          title: 'Dominio Attivo per Newsletter',
          shared: 'Condiviso',
          custom: 'Personalizzato',
          domain: 'Dominio',
          senderEmail: 'Email mittente',
          senderName: 'Nome mittente',
          monthlyLimit: 'Limite mensile',
          defaultLimit: '50 email/mese',
          customLimit: '1.000 email/mese',
          emailsPerMonth: 'email/mese',
          unlimited: 'Illimitato',
          suspended: 'Account sospeso',
          toBeConfigured: 'Da configurare',
          bonus: 'bonus',
        },
        upgrade: {
          title: 'Aumenta il tuo limite a 1.000 email/mese',
          benefits: 'Verifica il tuo dominio personalizzato per:',
          benefit1: 'Inviare fino a 1.000 email al mese (invece di 50)',
          benefit2: 'Email più professionali da tuo@tuodominio.com',
          benefit3: 'Migliorare la deliverability e ridurre lo spam',
        },
        form: {
          newDomain: 'Nuovo Dominio',
          domain: 'Dominio',
          domainPlaceholder: 'esempio: miaazienda.com',
          domainPlaceholderInline: 'tuodominio.com',
          subdomain: 'Sottodominio (opzionale)',
          subdomainPlaceholder: 'newsletter',
          result: 'Risultato',
          add: 'Aggiungi',
          cancel: 'Annulla',
        },
        status: {
          verified: 'Verificato',
          pending: 'In attesa di verifica',
          failed: 'Verifica fallita',
        },
        dns: {
          title: 'Record DNS da configurare (3 TXT):',
          type: 'Tipo',
          host: 'Host',
          value: 'Valore',
          instructions: 'Istruzioni',
          step1: 'Apri il pannello DNS del tuo provider (es. Cloudflare, GoDaddy)',
          step2: 'Aggiungi i 3 record TXT sopra elencati',
          step3: 'Attendi la propagazione DNS (5-30 minuti)',
          step4: 'Clicca "Verifica" per completare la configurazione',
          spfMergeWarning: 'Merge richiesto',
          spfMergeNote:
            'Se hai già un record TXT SPF sul dominio root, FAI MERGE dell\'include nell\'SPF esistente (NON creare un secondo record SPF).',
          purposeOwnership: 'Proprietà dominio',
          purposeDkim: 'Firma DKIM',
          purposeSpf: 'Autorizzazione SPF',
          purposeDmarc: 'Policy DMARC',
        },
        sender: {
          title: 'Configurazione Sender Email:',
          senderEmail: 'Email Sender',
          senderEmailPlaceholder: 'newsletter@',
          senderEmailHint: 'Deve utilizzare il dominio verificato',
          senderName: 'Nome Sender',
          senderNamePlaceholder: 'Il Tuo Nome o Azienda',
          saveConfig: 'Salva Configurazione',
          localPartHint: 'Solo la parte iniziale; il @dominio è automatico.',
          localPartRequired: 'Inserisci la parte iniziale dell\'email mittente',
        },
        actions: {
          showDns: 'Mostra DNS',
          hideDns: 'Nascondi DNS',
          configSender: 'Configura Sender',
          editSender: 'Modifica Sender',
          verify: 'Verifica',
          delete: 'Elimina',
          senderPending: 'In approvazione',
          senderActive: 'Attivato',
          resendValidation: 'Rinvia email',
          resendValidationTooltip: 'Rinvia l\'email di attivazione all\'amministratore Flusia',
          metasenderFileMissing: 'File mancante',
          metasenderActive: 'Pronto all\'invio',
          downloadMetasenderFile: 'Scarica file',
          downloadMetasenderTooltip: 'Scarica il file di validazione e caricalo nella root del tuo sito',
          verifyMetasender: 'Attiva',
          refresh: 'Aggiorna',
        },
        otherDomains: {
          title: 'Altri domini',
        },
        emptyState: {
          title: 'Nessun dominio configurato',
          description: 'Aggiungi un dominio per iniziare a inviare email professionali con il tuo marchio',
        },
        messages: {
          loadError: 'Errore nel caricamento dei domini',
          addSuccess: 'Dominio aggiunto! Configura i record DNS per completare la verifica',
          addError: 'Errore nell\'aggiunta del dominio',
          senderActivated: 'Mittente attivato! Puoi inviare email ora',
          resendSuccess: 'Email di attivazione rinviata. Controlla la tua casella di posta',
          resendError: 'Impossibile rinviare l\'email di attivazione',
          metasenderActivated: 'Autenticazione dominio completata! Puoi inviare da qualsiasi mittente del dominio.',
          metasenderNotYet: 'Attivazione non ancora completata — verifica che il file sia caricato e riprova.',
          metasenderFilenameMissing: 'Nessun metasender configurato per questo dominio',
          metasenderDownloadError: 'Impossibile scaricare il file di validazione',
          metasenderFileNotReachable: 'Il file di validazione non è raggiungibile. Verifica che sia caricato e accessibile pubblicamente a:',
          metasenderWaitingMailjet: 'File rilevato sul tuo dominio. Stiamo verificando — può richiedere qualche minuto.',
          verifySuccess: 'Dominio verificato con successo!',
          verifyFailed: 'Verifica fallita. Controlla i record DNS e riprova',
          verifyError: 'Errore nella verifica',
          deleteConfirm: 'Sei sicuro di voler eliminare il dominio',
          deleteSuccess: 'Dominio eliminato',
          deleteError: 'Errore nell\'eliminazione',
          senderUpdated: 'Configurazione sender aggiornata',
          senderError: 'Errore nell\'aggiornamento',
          copiedToClipboard: 'Copiato negli appunti',
          permissionDenied: 'Aggiornamento fallito: permessi insufficienti sul dominio',
        },
        metasender: {
          calloutTitle: 'Carica il file di validazione per attivare l\'invio',
          calloutDescription:
            'Serve a dimostrare che sei il proprietario del dominio. Una volta caricato, potrai inviare da qualsiasi mittente (es. newsletter@tuodominio.com, info@tuodominio.com).',
          step1: 'Scarica il file',
          step2: 'Caricalo nella root del tuo sito in modo che sia raggiungibile a',
          step3: 'Clicca qui per verificare:',
          note: 'La verifica può richiedere qualche minuto dopo il caricamento.',
          waitingTitle: 'Ci siamo quasi',
          waitingDescription:
            'Il file è pubblicato correttamente. Clicca "Attiva" per completare il setup — l\'attivazione è immediata.',
          upfrontTimingTitle: 'Come funziona?',
          upfrontTiming:
            'Una volta caricato il file, clicca "Attiva" — il tuo dominio è pronto in pochi secondi.',
        },
        dmarc: {
          warningTitle: 'Policy DMARC troppo restrittiva',
          warningDescription:
            'Il tuo dominio ha un allineamento SPF stretto (aspf=s). Gmail potrebbe contrassegnare le tue email come "anomale" perché il nostro server di bounce è su un dominio diverso. Rilassa l\'allineamento SPF per mantenere alta la deliverability.',
          missingTitle: 'Record DMARC mancante',
          missingDescription:
            'Non hai un record DMARC su questo dominio. Senza, provider come Gmail e Outlook possono degradare la deliverability o rifiutare le tue email. Consigliamo di aggiungere almeno una policy "p=none" per monitorare.',
          recommendedFix: 'Record DMARC consigliato (sostituisci il valore attuale):',
          suggestedRecord: 'Aggiungi questo record TXT al tuo DNS:',
        },
      },
      sendGrid: {
        title: 'Domini Email SendGrid',
        description: 'Configura e verifica i domini per l\'invio di newsletter professionali',
        addDomain: 'Aggiungi Dominio',
        activeDomain: {
          title: 'Dominio Attivo per Newsletter',
          shared: 'Condiviso',
          custom: 'Personalizzato',
          domain: 'Dominio',
          senderEmail: 'Email mittente',
          senderName: 'Nome mittente',
          monthlyLimit: 'Limite mensile',
          defaultLimit: '50 email/mese',
          customLimit: '1.000 email/mese'
        },
        upgrade: {
          title: 'Aumenta il tuo limite a 1.000 email/mese',
          benefits: 'Verifica il tuo dominio personalizzato per:',
          benefit1: 'Inviare fino a 1.000 email al mese (invece di 50)',
          benefit2: 'Email più professionali da tuo@tuodominio.com',
          benefit3: 'Migliorare la deliverability e ridurre lo spam'
        },
        form: {
          newDomain: 'Nuovo Dominio',
          domain: 'Dominio',
          domainPlaceholder: 'esempio: mycompany.com',
          subdomain: 'Sottodominio',
          subdomainPlaceholder: 'mail',
          result: 'Risultato',
          add: 'Aggiungi',
          cancel: 'Annulla'
        },
        status: {
          verified: 'Verificato',
          pending: 'In attesa di verifica',
          failed: 'Verifica fallita'
        },
        dns: {
          title: 'Record DNS da configurare:',
          type: 'Tipo',
          host: 'Host',
          value: 'Valore',
          instructions: 'Istruzioni',
          step1: 'Accedi al pannello DNS del tuo provider (es: Cloudflare, GoDaddy)',
          step2: 'Aggiungi i record DNS CNAME sopra indicati',
          step3: 'Attendi la propagazione DNS (5-30 minuti)',
          step4: 'Clicca "Verifica" per completare la configurazione'
        },
        sender: {
          title: 'Configurazione Sender Email:',
          senderEmail: 'Email Sender',
          senderEmailPlaceholder: 'newsletter@',
          senderEmailHint: 'Deve utilizzare il dominio verificato',
          senderName: 'Nome Sender',
          senderNamePlaceholder: 'Il Tuo Nome o Azienda',
          saveConfig: 'Salva Configurazione'
        },
        actions: {
          showDns: 'Mostra DNS',
          hideDns: 'Nascondi DNS',
          configSender: 'Configura Sender',
          editSender: 'Modifica Sender',
          verify: 'Verifica',
          delete: 'Elimina'
        },
        emptyState: {
          title: 'Nessun dominio configurato',
          description: 'Aggiungi un dominio per iniziare a inviare email professionali con il tuo marchio'
        },
        messages: {
          loadError: 'Errore nel caricamento dei domini',
          addSuccess: 'Dominio aggiunto! Configura i record DNS per completare la verifica',
          addError: 'Errore nell\'aggiunta del dominio',
          verifySuccess: 'Dominio verificato con successo!',
          verifyFailed: 'Verifica fallita. Controlla i record DNS e riprova',
          verifyError: 'Errore nella verifica',
          deleteConfirm: 'Sei sicuro di voler eliminare il dominio',
          deleteSuccess: 'Dominio eliminato',
          deleteError: 'Errore nell\'eliminazione',
          senderUpdated: 'Configurazione sender aggiornata',
          senderError: 'Errore nell\'aggiornamento',
          copiedToClipboard: 'Copiato negli appunti'
        }
      }
    },
    dealSettings: {
      title: 'Impostazioni Preventivi',
      description: 'Configura le impostazioni per i preventivi e le offerte',
      numbering: {
        title: 'Numerazione Preventivi',
        description: 'Configura come vengono generati i numeri dei preventivi',
        startNumber: 'Numero iniziale preventivi',
        startNumberHelp: 'I numeri di preventivo inizieranno da questo valore ogni anno',
        lastNumber: 'Ultimo numero utilizzato',
        lastNumberHelp: 'Questo è l\'ultimo numero di preventivo utilizzato nell\'anno corrente',
        referenceYear: 'Anno di riferimento',
        referenceYearHelp: 'Anno a cui si riferisce l\'ultimo numero di preventivo utilizzato'
      },
      statusChange: {
        title: 'Cambio Stato Offerte',
        description: 'Configura il comportamento al cambio di stato delle offerte',
        noteRequired: 'Nota obbligatoria al cambio stato',
        noteRequiredHelp: 'Se abilitato, sarà necessario inserire una nota ogni volta che si cambia lo stato di un\'offerta',
        simplifiedProbability: 'Usa probabilità semplificata',
        simplifiedProbabilityHelp: 'Se abilitato, le opzioni di probabilità saranno 25%, 50%, 75% invece della scala 1-10',
        showPaymentDueDate: 'Scadenza pagamento separata nelle rate',
        showPaymentDueDateHelp: 'Se attivo, ogni rata mostra una riga aggiuntiva per impostare la scadenza di pagamento distinta dalla data di emissione fattura'
      },
      whatsappNotifications: {
        title: 'Notifiche WhatsApp',
        description: 'Configura le notifiche per i contratti firmati e caricati',
        chatIdLabel: 'ID Chat WhatsApp per notifiche',
        chatIdHelp: 'ID della chat WhatsApp dove inviare le notifiche quando un contratto viene caricato (es: 123456789@c.us)',
        attachContract: 'Allega contratto alla notifica',
        customMessage: 'Messaggio personalizzato',
        restoreDefault: 'Ripristina default',
        availableVariables: 'Variabili disponibili:',
        clientName: 'Nome del cliente',
        dealTitle: 'Titolo del preventivo',
        dealAmount: 'Importo totale',
        servicesLoop: 'Ciclo per i servizi, con:',
        serviceTitle: 'Nome del servizio',
        serviceAmount: 'Importo del servizio',
        serviceType: 'Tipo di servizio (Mensile/Una tantum)'
      },
      saveButton: 'Salva Impostazioni',
      messages: {
        saveSuccess: 'Impostazioni salvate correttamente',
        saveError: 'Errore durante il salvataggio delle impostazioni',
        testSuccess: 'Notifica WhatsApp di test inviata con successo!',
        testError: 'Errore nell\'invio della notifica di test',
        chatIdRequired: 'Inserisci prima un ID chat WhatsApp valido'
      }
    },
    leadPipeline: {
      title: 'Pipeline Lead',
      description: 'Gestisci le pipeline e gli stage per organizzare i tuoi lead',
      newPipeline: 'Nuova Pipeline',
      howItWorks: {
        title: 'Come funzionano le pipeline',
        description: 'Le pipeline ti permettono di organizzare i lead in flussi di lavoro diversi con stage personalizzati. La pipeline predefinita viene usata per i lead che non hanno una pipeline specifica assegnata.'
      },
      pipeline: {
        editTitle: 'Modifica Pipeline',
        newTitle: 'Nuova Pipeline',
        name: 'Nome',
        namePlaceholder: 'Es. Vendite B2B, Partner Network',
        description: 'Descrizione',
        descriptionPlaceholder: 'Descrizione opzionale',
        color: 'Colore',
        isDefault: 'Pipeline predefinita',
        systemManaged: 'Gestita dal sistema',
        systemManagedHint: 'Fasi e pipeline si aggiornano da sole e non si possono modificare o eliminare da qui',
        cancel: 'Annulla',
        save: 'Salva Pipeline'
      },
      stage: {
        editTitle: 'Modifica Stage',
        newTitle: 'Nuovo Stage',
        name: 'Nome',
        namePlaceholder: 'Es. Qualificazione, Proposta, Chiusura',
        description: 'Descrizione',
        descriptionPlaceholder: 'Descrizione opzionale',
        color: 'Colore',
        conversionType: 'Tipo di Conversione',
        isWon: 'Stage "Vinto"',
        isLost: 'Stage "Perso"',
        wonHelp: 'Gli stage marcati come "Vinto" saranno considerati come conversioni riuscite nelle statistiche.',
        cancel: 'Annulla',
        save: 'Salva Stage',
        addStage: 'Aggiungi Stage',
        stagesTitle: 'Stage di questa pipeline',
        noStages: 'Nessuno stage configurato',
        won: 'VINTO',
        lost: 'PERSO'
      },
      actions: {
        setDefault: 'Imposta come predefinita',
        manageStages: 'Gestisci stage',
        clickToCopyId: 'Clicca per copiare ID'
      },
      emptyState: {
        title: 'Nessuna pipeline configurata',
        description: 'Crea la tua prima pipeline per organizzare i lead in flussi di lavoro personalizzati',
        createFirst: 'Crea Prima Pipeline'
      },
      messages: {
        pipelineNameRequired: 'Il nome della pipeline è obbligatorio',
        pipelineCreated: 'Pipeline creata con successo',
        pipelineUpdated: 'Pipeline aggiornata con successo',
        pipelineSaveError: 'Errore nel salvataggio della pipeline',
        pipelineDeleted: 'Pipeline eliminata con successo',
        pipelineDeleteError: 'Errore nell\'eliminazione della pipeline',
        selectPipeline: 'Seleziona una pipeline',
        stageNameRequired: 'Il nome dello stage è obbligatorio',
        stageCreated: 'Stage creato con successo',
        stageUpdated: 'Stage aggiornato con successo',
        stageSaveError: 'Errore nel salvataggio dello stage',
        stageDeleted: 'Stage eliminato con successo',
        stageDeleteError: 'Errore nell\'eliminazione dello stage',
        defaultUpdated: 'Pipeline predefinita aggiornata',
        defaultError: 'Errore nell\'impostazione della pipeline predefinita',
        orderSaved: 'Ordine pipeline salvato',
        stageOrderSaved: 'Ordine stage salvato'
      },
      confirm: {
        deletePipeline: 'Sei sicuro di voler eliminare la pipeline "{name}"? Questa azione è irreversibile.',
        deleteStage: 'Sei sicuro di voler eliminare lo stage "{name}"? Questa azione è irreversibile.'
      },
      deletionResolver: {
        title: 'Lead assegnati a questa pipeline',
        description: '{count} lead/clienti sono assegnati a "{name}". Scegli cosa farne prima di eliminare la pipeline.',
        moveOption: {
          title: 'Sposta su un\'altra pipeline',
          description: 'I lead/clienti mantengono tutti i loro dati e vengono spostati sulla pipeline e sullo stage che scegli qui sotto.',
          targetPipelineLabel: 'Pipeline di destinazione',
          targetPipelinePlaceholder: 'Seleziona una pipeline',
          targetStageLabel: 'Stage di destinazione',
          targetStagePlaceholder: 'Seleziona uno stage'
        },
        unassignOption: {
          title: 'Scollega da questa pipeline',
          description: 'I lead/clienti restano nel CRM ma non sono più collegati a nessuna pipeline (spariscono solo da questa board).'
        },
        summary: {
          move: 'Stai per spostare {count} lead/clienti da "{sourceName}" a "{targetName} → {targetStage}", poi eliminare "{sourceName}".',
          unassign: 'Stai per scollegare {count} lead/clienti da "{sourceName}", poi eliminarla.'
        },
        actions: {
          cancel: 'Annulla',
          continue: 'Continua',
          back: 'Indietro',
          confirmAndDelete: 'Conferma ed elimina'
        },
        messages: {
          success: 'Lead spostati e pipeline eliminata con successo',
          error: 'Errore nella risoluzione dell\'eliminazione della pipeline'
        }
      }
    },
    importExport: {
      title: 'Importa / Esporta',
      description: 'Importa o esporta dati del CRM',
      importClients: 'Importa Clienti',
      dragDrop: 'Trascina e rilascia un file CSV/Excel, o clicca per selezionarlo',
      supportedFormats: 'Supportati: CSV, Excel (.xls, .xlsx)',
      parsing: 'Parsing del file in corso...',
      mapColumns: 'Mappa le colonne del file con i campi cliente:',
      csvColumn: 'Colonna File',
      clientField: 'Campo Cliente',
      doNotMap: 'Non mappare questo campo',
      preview: 'Anteprima dei dati da importare:',
      previewCount: 'Visualizzazione',
      of: 'di',
      records: 'record',
      backToMapping: 'Torna al mapping',
      importNow: 'Importa Clienti',
      importing: 'Importazione in corso...',
      completed: 'completato',
      imported: 'importati',
      failed: 'falliti',
      total: 'totali',
      new: 'nuovi',
      updated: 'aggiornati',
      errors: 'Errori:',
      importMore: 'Importa altri clienti',
      insufficientPermissions: 'Permessi insufficienti',
      permissionsMessage: 'Non hai i permessi necessari per utilizzare le funzionalità di importazione ed esportazione. Contatta l\'amministratore del sistema.',
      // Export section
      modeChoice: {
        title: 'Cosa vuoi fare?',
        subtitle: 'Scegli se importare o esportare dati dal tuo CRM',
        import: 'Importa',
        importDesc: 'Importa clienti, leads, prodotti o offerte da file Excel o CSV',
        export: 'Esporta',
        exportDesc: 'Esporta clienti e leads con filtri avanzati e contatti associati'
      },
      exportType: {
        title: 'Cosa vuoi esportare?',
        subtitle: 'Seleziona il tipo di dati che desideri esportare',
        clients: 'Solo Clienti',
        clientsDesc: 'Esporta solo i clienti attivi con tutti i loro dati e contatti',
        leads: 'Solo Leads',
        leadsDesc: 'Esporta solo i leads con tutti i loro dati e contatti',
        both: 'Clienti + Leads',
        bothDesc: 'Esporta sia clienti che leads in un unico file'
      },
      exportConfig: {
        title: 'Configura esportazione',
        subtitle: 'Configura i filtri e le opzioni per l\'esportazione',
        filters: {
          title: 'Filtri',
          interests: 'Filtra per interessi',
          tags: 'Filtra per tag',
          country: 'Filtra per paese',
          noFilters: 'Nessun filtro disponibile. Verranno esportati tutti i record.'
        },
        filterLogic: {
          title: 'Logica filtri',
          or: 'Almeno uno dei criteri (OR)',
          and: 'Tutti i criteri (AND)'
        },
        options: {
          title: 'Opzioni',
          includeContacts: 'Includi contatti sulla stessa riga',
          maxContacts: 'Numero massimo contatti'
        },
        format: {
          title: 'Formato file',
          excel: 'Excel (.xlsx)',
          csv: 'CSV'
        },
        recordCount: 'Record da esportare',
        calculating: 'Calcolo...',
        noRecords: 'Nessun record corrisponde ai filtri selezionati.',
        exportButton: 'Esporta',
        exporting: 'Esportazione...'
      }
    },
    googleWorkspace: {
      title: 'Integrazione Google',
      description: 'Integra il tuo account Google per sincronizzare calendari, documenti e contatti',
      enabled: 'Abilita Integrazione Google',
      enableDescription: 'Attiva le integrazioni con i servizi Google',
      clientId: 'Client ID',
      clientSecret: 'Client Secret',
      domain: 'Dominio Google Workspace',
      syncOptions: 'Opzioni di Sincronizzazione',
      syncCalendar: 'Calendario',
      syncCalendarDescription: 'Sincronizza eventi e appuntamenti con Google Calendar',
      syncDrive: 'Google Drive',
      syncDriveDescription: 'Gestisci cartelle e documenti automaticamente',
      syncContacts: 'Contatti',
      syncContactsDescription: 'Sincronizza contatti tra CRM e Google Contacts',
      syncGmail: 'Gmail',
      syncGmailDescription: 'Leggi e invia email direttamente dal CRM',
      gmail: {
        connectToSync: 'Connetti il tuo account Google per sincronizzare Gmail',
        ready: 'Gmail abilitato. Riconnetti il tuo account Google per attivare i permessi Gmail.',
      },
      setupInstructions: 'Istruzioni di Configurazione',
      setupSteps: 'Per configurare l\'integrazione con Google Workspace:',
      calendarSelection: 'Seleziona i calendari da sincronizzare',
      fetchCalendars: 'Recupera Calendari',
      noCalendarsFound: 'Nessun calendario trovato. Clicca "Recupera Calendari" per ottenere i calendari disponibili.',
      calendarError: 'Impossibile recuperare i calendari Google. Assicurati che l\'integrazione con Google sia configurata correttamente e che almeno un utente abbia collegato il proprio account Google.',
      userConnection: {
        title: 'Account Google',
        connected: 'Account Google collegato',
        notConnected: 'Account Google non collegato',
        connect: 'Connetti',
        connecting: 'Connessione...',
        disconnect: 'Disconnetti',
        disconnecting: 'Disconnessione...',
        connectDescription: 'Connetti il tuo account per abilitare le integrazioni',
        email: 'Email account Google',
        redirecting: 'Verrai reindirizzato a Google per l\'autorizzazione',
        missingToken: 'Token di autorizzazione mancante',
        reconnect: 'Disconnetti e ricollega',
        reconnectDescription: 'Il tuo account è collegato ma mancano alcune informazioni critiche. Ti consigliamo di disconnettere e ricollegare il tuo account.'
      },
      activities: {
        title: 'Sincronizzazione Attività',
        description: 'Le attività di vendita saranno sincronizzate automaticamente con Google Calendar',
        destinationCalendar: 'Calendario di destinazione',
        selectCalendar: 'Seleziona un calendario',
        loadingCalendars: 'Caricamento calendari...',
        calendarSelected: 'Calendario selezionato',
        save: 'Salva'
      },
      drive: {
        useExistingFolder: 'Usa una cartella esistente',
        noFolderSelected: 'Nessuna cartella selezionata',
        browse: 'Sfoglia',
        close: 'Chiudi',
        connectToGoogle: 'Connetti il tuo account Google per sfogliare le cartelle',
        rootFolderName: 'Nome cartella principale',
        clientFolderPattern: 'Pattern cartella cliente',
        createAutomatically: 'Crea cartelle automaticamente',
        createAutomaticallyDescription: 'Le cartelle vengono create quando necessario',
        advancedOptions: 'Opzioni avanzate',
        variables: 'Variabili'
      },
      contacts: {
        importFromGoogle: 'Importa da Google',
        import: 'Importa',
        importing: 'Importazione...',
        importDescription: 'Sincronizza i tuoi contatti Google nella Rubrica',
        contactsImported: '{count} contatti importati',
        lastImport: 'Ultimo import',
        exportToGoogle: 'Esporta verso Google',
        export: 'Esporta',
        exporting: 'Esportazione...',
        exportDescription: 'Sincronizza i contatti dei clienti verso Google Contacts',
        connectToSync: 'Connetti il tuo account Google per sincronizzare i contatti'
      },
      messages: {
        settingsSaved: 'Impostazioni salvate con successo',
        saveError: 'Si è verificato un errore durante il salvataggio',
        enableAtLeastOne: 'Abilita almeno un\'opzione di sincronizzazione',
        accountDisconnected: 'Account Google disconnesso con successo',
        disconnectError: 'Non è stato possibile disconnettere l\'account Google',
        accountLoadError: 'Errore nel caricamento delle informazioni account Google',
        clientIdNotConfigured: 'Google Client ID non configurato nelle impostazioni dell\'organizzazione',
        authError: 'Non è stato possibile avviare il processo di autorizzazione Google',
        activitiesSaved: 'Impostazioni salvate con successo',
        activitiesSaveError: 'Errore nel salvataggio delle impostazioni',
        contactsSynced: 'Sincronizzati {count} contatti',
        contactsSyncError: 'Errore nella sincronizzazione dei contatti',
        contactsExported: 'Esportati {count} contatti verso Google',
        contactsExportError: 'Errore nell\'esportazione dei contatti',
        calendarsSavedWarning: 'Impostazioni salvate ma non è stato possibile salvare i calendari selezionati'
      },
      confirm: {
        disconnect: 'Sei sicuro di voler disconnettere il tuo account Google? Tutte le integrazioni cesseranno di funzionare.'
      },
      saveSettings: 'Salva Impostazioni',
      saving: 'Salvataggio...'
    },
    bulkDelete: {
      title: 'Gestione Cancellazioni Massive',
      description: 'Elimina in massa clienti o deals con controlli di sicurezza avanzati',
      noPermission: 'Non hai i permessi per utilizzare questa funzionalità.',
      historyButton: 'Storico Operazioni',
      recentOperations: 'Operazioni Recenti',
      noOperations: 'Nessuna operazione eseguita',
      clients: 'Clienti',
      deals: 'Deals',
      elements: 'elementi',
      deleted: 'Eliminati',
      errors: 'Errori',
      warning: {
        title: '⚠️ Attenzione - Operazione Irreversibile',
        message: 'Le cancellazioni massive non possono essere annullate. Tutti i dati correlati (deals, contatti, attività, progetti, ecc.) verranno eliminati permanentemente.'
      },
      entitySelection: {
        title: 'Selezione Entità',
        clientsLabel: 'Clienti',
        clientsDescription: 'Elimina clienti e dati correlati',
        dealsLabel: 'Deals',
        dealsDescription: 'Elimina deals e servizi correlati'
      },
      filters: {
        title: 'Criteri di Filtro',
        dateRange: 'Range di Date',
        noDateFilter: '-- Nessun filtro data --',
        createdAt: 'Data Creazione',
        updatedAt: 'Data Aggiornamento',
        signedAt: 'Data Firma',
        closedAt: 'Data Chiusura',
        status: 'Status',
        assignedTo: 'Assegnato A',
        tags: 'Tag'
      },
      preview: {
        button: 'Genera Anteprima',
        generating: 'Generazione...',
        title: 'Anteprima Eliminazioni',
        toDelete: 'da eliminare',
        relatedData: 'Dati correlati coinvolti',
        involvedTables: 'Tabelle coinvolte:',
        noRelatedData: 'Nessun dato correlato',
        impactTitle: 'Impatto sui dati correlati',
        impactMessage: 'L\'eliminazione di {count} {entity} comporterà anche l\'eliminazione di {related} record correlati. Questa operazione non può essere annullata.'
      },
      actions: {
        cancel: 'Annulla',
        proceedWithDeletion: 'Procedi con Eliminazione'
      },
      confirm: {
        title: 'Conferma Eliminazione',
        message: 'Stai per eliminare definitivamente:',
        irreversible: '⚠️ Questa operazione è IRREVERSIBILE',
        confirmButton: 'CONFERMA ELIMINAZIONE',
        deleting: 'Eliminazione...'
      },
      messages: {
        success: 'Cancellazione completata con successo!',
        errorLoading: 'Errore nel caricamento dati iniziali:',
        errorPreview: 'Errore nella generazione anteprima:',
        errorDelete: 'Errore nella cancellazione:',
        userNotAuthenticated: 'Utente non autenticato',
        organizationNotFound: 'Organizzazione non trovata',
        unknownError: 'Errore sconosciuto'
      },
      related: {
        deals: 'deals',
        contacts: 'contatti',
        activities: 'attività',
        services: 'servizi',
        projects: 'progetti'
      }
    },
    leadAutoAssignment: {
      title: 'Auto-Assegnazione Lead',
      description: 'Assegna automaticamente i lead in base a regole personalizzate',
      enabled: 'Regola attiva',
      editor: {
        newRule: 'Nuova Regola',
        editRule: 'Modifica Regola',
        ruleName: 'Nome della regola',
        ruleNamePlaceholder: 'es. Lead da campagne Google',
        ruleNameRequired: 'Nome della regola'
      },
      operator: {
        title: 'Quando si applica questa regola?',
        or: 'OPPURE (OR)',
        orDescription: 'Basta che almeno una condizione sia vera',
        orExample: 'Es: "Da Google Ads" oppure "Da Facebook"',
        and: 'TUTTE (AND)',
        andDescription: 'Tutte le condizioni devono essere vere',
        andExample: 'Es: "Interesse Premium" e "Pipeline Enterprise"'
      },
      conditions: {
        title: 'Condizioni',
        selectAtLeastOne: 'Seleziona almeno una',
        interests: 'Interessi',
        pipeline: 'Pipeline',
        leadSources: 'Fonti Lead',
        noInterests: 'Nessun interesse configurato',
        noPipelines: 'Nessuna pipeline configurata',
        noSources: 'Nessuna fonte configurata'
      },
      preview: {
        title: 'Questa regola corrisponderà a:',
        interest: 'interesse',
        pipeline: 'pipeline',
        source: 'fonte',
        leadsWith: 'Lead con',
        and: ' E ',
        or: ' OPPURE '
      },
      userPool: {
        title: 'A chi assegnare i lead?',
        selectedCount: '{count} utenti selezionati - verrà scelto quello con meno carico',
        selectedCountSingular: '{count} utente selezionato - riceverà il lead'
      },
      validation: {
        selectConditionAndUser: 'Seleziona almeno una condizione e almeno un utente',
        selectCondition: 'Seleziona almeno una condizione',
        selectUser: 'Seleziona almeno un utente'
      },
      buttons: {
        cancel: 'Annulla',
        saving: 'Salvataggio...',
        saveChanges: 'Salva modifiche',
        createRule: 'Crea regola',
        newRule: 'Nuova regola'
      },
      rules: {
        title: 'Regole di Assegnazione',
        description: 'La prima regola che corrisponde viene applicata (ordine prioritario)',
        empty: 'Nessuna regola configurata',
        emptyDescription: 'Crea la tua prima regola per iniziare',
        noConditions: 'Nessuna condizione',
        assignTo: 'Assegna a:',
        active: 'Attiva',
        edit: 'Modifica',
        delete: 'Elimina',
        increasePriority: 'Aumenta priorità',
        decreasePriority: 'Diminuisci priorità',
        confirmDelete: 'Sei sicuro di voler eliminare questa regola?'
      },
      fallback: {
        title: 'Fallback',
        description: 'Se nessuna regola corrisponde, il lead viene assegnato a uno di questi utenti',
        noUsers: 'Nessun utente attivo disponibile',
        recommendation: 'Consiglio: seleziona almeno un utente di fallback per i lead che non corrispondono a nessuna regola'
      },
      advanced: {
        title: 'Impostazioni avanzate',
        loadCalculation: 'Come calcolare il "carico" di un utente?',
        loadOptions: {
          lead: 'Conta solo i Lead assegnati',
          deal: 'Conta solo le Trattative assegnate',
          client: 'Conta solo i Clienti assegnati',
          all: 'Conta tutto (Lead + Trattative + Clienti)'
        },
        loadDescription: 'L\'utente con meno entità assegnate riceverà il prossimo lead'
      },
      howItWorks: {
        title: 'Come funziona',
        step1: 'Quando arriva un nuovo lead, il sistema valuta le regole dall\'alto verso il basso',
        step2: 'La prima regola che corrisponde determina il pool di utenti',
        step3: 'Se nessuna regola corrisponde, usa il pool di fallback',
        step4: 'Dal pool, viene scelto l\'utente con meno carico di lavoro'
      },
      error: 'Errore',
      deleted: 'Eliminato'
    },
    permissions: {
      // Dashboard
      dashboard_view: { name: 'Visualizza Dashboard', description: 'Accesso per visualizzare la dashboard principale e le analisi' },
      dashboard_filter_by_user: { name: 'Filtra Dashboard per Utente', description: 'Capacità di filtrare i dati della dashboard per utenti specifici' },
      // Clienti
      clients_view: { name: 'Visualizza Clienti', description: 'Visualizzare i dati dei clienti e la loro cronologia' },
      clients_create: { name: 'Crea Clienti', description: 'Aggiungere nuovi clienti al sistema' },
      clients_update: { name: 'Aggiorna Clienti', description: 'Modificare i dati esistenti dei clienti' },
      clients_delete: { name: 'Elimina Clienti', description: 'Eliminare clienti dal sistema (operazione critica)' },
      clients_merge: { name: 'Unisci Clienti/Lead', description: 'Unire clienti o lead duplicati in un unico record' },
      clients_filter_by_user: { name: 'Filtra Clienti per Utente', description: 'Filtrare clienti per utente assegnato' },
      // Calendario
      calendar_view: { name: 'Visualizza Calendario', description: 'Accesso per visualizzare eventi del calendario' },
      calendar_create: { name: 'Crea Eventi', description: 'Creare nuovi eventi del calendario' },
      calendar_update: { name: 'Aggiorna Eventi', description: 'Modificare eventi del calendario esistenti' },
      calendar_delete: { name: 'Elimina Eventi', description: 'Eliminare eventi del calendario' },
      // Contratti/Deals
      deals_view: { name: 'Visualizza Contratti', description: 'Vedere tutti i contratti e le opportunità di vendita' },
      deals_create: { name: 'Crea Contratti', description: 'Creare nuovi contratti e opportunità' },
      deals_update: { name: 'Aggiorna Contratti', description: 'Modificare contratti esistenti' },
      deals_delete: { name: 'Elimina Contratti', description: 'Eliminare contratti (attenzione: può influire su commissioni)' },
      deals_filter_by_user: { name: 'Filtra Contratti per Utente', description: 'Filtrare contratti per utente assegnato' },
      // Progetti
      projects_view: { name: 'Visualizza Progetti', description: 'Visualizzare informazioni e progresso dei progetti' },
      projects_create: { name: 'Crea Progetti', description: 'Creare nuovi progetti' },
      projects_update: { name: 'Aggiorna Progetti', description: 'Modificare progetti esistenti' },
      projects_delete: { name: 'Elimina Progetti', description: 'Eliminare progetti dal sistema' },
      // WhatsApp
      whatsapp_view: { name: 'Visualizza WhatsApp', description: 'Accedere alle conversazioni WhatsApp' },
      whatsapp_create: { name: 'Invia Messaggi WhatsApp', description: 'Inviare messaggi WhatsApp ai clienti' },
      whatsapp_assign: { name: 'Visualizza Solo Chat WhatsApp Assegnate', description: 'Vedere solo le chat WhatsApp dei clienti assegnati' },
      whatsapp_update: { name: 'Gestisci Configurazioni WhatsApp', description: 'Assegnare configurazioni WhatsApp agli utenti e gestire le impostazioni' },
      // Fatture
      invoices_view: { name: 'Visualizza Fatture', description: 'Accedere alle informazioni delle fatture' },
      invoices_create: { name: 'Crea Fatture', description: 'Generare nuove fatture' },
      invoices_update: { name: 'Aggiorna Fatture', description: 'Modificare fatture esistenti' },
      invoices_delete: { name: 'Elimina Fatture', description: 'Eliminare fatture dal sistema' },
      // FattureInCloud
      fattureincloud_view: { name: 'Visualizza Dati Fatture in Cloud', description: 'Accedere ai dati dell\'integrazione Fatture in Cloud' },
      fattureincloud_create: { name: 'Crea Fatture in Cloud', description: 'Creare fatture tramite Fatture in Cloud' },
      fattureincloud_update: { name: 'Aggiorna Impostazioni Fatture in Cloud', description: 'Modificare la configurazione Fatture in Cloud' },
      fattureincloud_delete: { name: 'Elimina Dati Fatture in Cloud', description: 'Rimuovere dati dall\'integrazione Fatture in Cloud' },
      // Documenti
      documents_view: { name: 'Visualizza Documenti', description: 'Accedere alla libreria documenti' },
      documents_create: { name: 'Carica Documenti', description: 'Caricare nuovi documenti nel sistema' },
      documents_delete: { name: 'Elimina Documenti', description: 'Rimuovere documenti dal sistema' },
      // Contratti
      contracts_view: { name: 'Visualizza Contratti', description: 'Accedere alle informazioni dei contratti' },
      contracts_create: { name: 'Crea Contratti', description: 'Generare nuovi contratti' },
      contracts_update: { name: 'Aggiorna Contratti', description: 'Modificare contratti esistenti' },
      contracts_delete: { name: 'Elimina Contratti', description: 'Rimuovere contratti dal sistema' },
      // Impostazioni
      settings_view: { name: 'Visualizza Impostazioni', description: 'Accedere alle impostazioni del sistema' },
      settings_update: { name: 'Aggiorna Impostazioni', description: 'Modificare la configurazione del sistema' },
      settings_create: { name: 'Importa/Esporta Dati', description: 'Importare ed esportare dati del sistema' },
      // Utenti
      users_view: { name: 'Visualizza Utenti', description: 'Accedere alle informazioni degli utenti' },
      users_create: { name: 'Crea Utenti', description: 'Aggiungere nuovi utenti al sistema' },
      users_update: { name: 'Aggiorna Utenti', description: 'Modificare dati utenti esistenti' },
      users_delete: { name: 'Elimina Utenti', description: 'Rimuovere utenti dal sistema' },
      // Ruoli
      roles_view: { name: 'Visualizza Ruoli', description: 'Accedere alla configurazione dei ruoli' },
      roles_create: { name: 'Crea Ruoli', description: 'Creare nuovi ruoli utente' },
      roles_update: { name: 'Aggiorna Ruoli', description: 'Modificare ruoli esistenti' },
      roles_delete: { name: 'Elimina Ruoli', description: 'Rimuovere ruoli dal sistema' },
      // Servizi
      services_view: { name: 'Visualizza Servizi', description: 'Accedere al catalogo servizi' },
      services_create: { name: 'Crea Servizi', description: 'Aggiungere nuovi servizi' },
      services_update: { name: 'Aggiorna Servizi', description: 'Modificare servizi esistenti' },
      services_delete: { name: 'Elimina Servizi', description: 'Rimuovere servizi' },
      // Metodi di Pagamento
      payment_methods_view: { name: 'Visualizza Metodi di Pagamento', description: 'Accedere alle impostazioni dei metodi di pagamento' },
      payment_methods_create: { name: 'Crea Metodi di Pagamento', description: 'Aggiungere nuovi metodi di pagamento' },
      payment_methods_update: { name: 'Aggiorna Metodi di Pagamento', description: 'Modificare metodi di pagamento' },
      payment_methods_delete: { name: 'Elimina Metodi di Pagamento', description: 'Rimuovere metodi di pagamento' },
      // Configurazione API
      api_config_view: { name: 'Visualizza Configurazioni API', description: 'Accedere alle impostazioni API' },
      api_config_create: { name: 'Crea Configurazioni API', description: 'Aggiungere nuove configurazioni API' },
      api_config_update: { name: 'Aggiorna Configurazioni API', description: 'Modificare impostazioni API' },
      api_config_delete: { name: 'Elimina Configurazioni API', description: 'Rimuovere configurazioni API' },
      // Categorie
      categories_view: { name: 'Visualizza Categorie', description: 'Accedere alla gestione categorie' },
      categories_create: { name: 'Crea Categorie', description: 'Aggiungere nuove categorie' },
      categories_update: { name: 'Aggiorna Categorie', description: 'Modificare categorie esistenti' },
      categories_delete: { name: 'Elimina Categorie', description: 'Rimuovere categorie' },
      // Inventario
      inventory_view: { name: 'Visualizza Inventario', description: 'Accedere alle informazioni dell\'inventario' },
      inventory_create: { name: 'Crea Inventario', description: 'Aggiungere nuovi articoli inventario' },
      inventory_update: { name: 'Aggiorna Inventario', description: 'Modificare articoli inventario' },
      inventory_delete: { name: 'Elimina Inventario', description: 'Rimuovere articoli inventario' },
      inventory_manage: { name: 'Gestisci Inventario', description: 'Accesso completo alla gestione inventario' },
      // Commissioni
      commissions_view: { name: 'Visualizza Commissioni', description: 'Accedere alle informazioni sulle commissioni' },
      commissions_update: { name: 'Gestisci Commissioni', description: 'Gestire le commissioni di vendita (operazione finanziaria)' },
      // Analisi
      analytics_view: { name: 'Visualizza Analisi', description: 'Accedere alle analisi e business intelligence' },
      // Report
      reports_view: { name: 'Visualizza Report', description: 'Accedere ai report di sistema' },
      reports_create: { name: 'Crea Report', description: 'Generare nuovi report' },
      reports_delete: { name: 'Elimina Report', description: 'Rimuovere report' },
      // Template
      templates_view: { name: 'Visualizza Template', description: 'Accedere ai template documenti' },
      templates_create: { name: 'Crea Template', description: 'Creare nuovi template documenti' },
      templates_update: { name: 'Aggiorna Template', description: 'Modificare template esistenti' },
      templates_delete: { name: 'Elimina Template', description: 'Rimuovere template documenti' },
      // Firme
      signatures_view: { name: 'Visualizza Firme', description: 'Accedere alle firme documenti' },
      signatures_create: { name: 'Crea Firme', description: 'Creare nuove firme per documenti' },
      // Lead
      leads_view: { name: 'Visualizza Lead', description: 'Accedere alle informazioni sui lead' },
      leads_create: { name: 'Crea Lead', description: 'Aggiungere nuovi lead' },
      leads_update: { name: 'Aggiorna Lead', description: 'Modificare lead esistenti' },
      leads_delete: { name: 'Elimina Lead', description: 'Rimuovere lead dal sistema' },
      leads_convert: { name: 'Converti Lead', description: 'Convertire lead in clienti' }
    },
    users: {
      title: 'Utenti',
      description: 'Gestisci gli utenti della tua organizzazione',
      addUser: 'Aggiungi Utente',
      add: 'Aggiungi',
      editUser: 'Modifica utente',
      deactivateUser: 'Disattiva utente',
      reactivateUser: 'Riattiva utente',
      changePassword: 'Cambia password',
      reset2FA: 'Reset 2FA',
      copyId: 'Copia ID',
      userIdCopied: 'ID utente copiato!',
      noName: 'Nessun nome',
      deactivated: 'Disattivato',
      active: 'Attivo',
      deactivatedUsers: 'Disattivati',
      activeUsers: 'Attivi',
      noPlan: 'Nessun piano',
      selectRole: 'Seleziona...',
      columns: {
        name: 'Nome',
        email: 'Email',
        phone: 'Telefono',
        role: 'Ruolo',
        manager: 'Manager',
        newsletter: 'Newsletter',
        actions: 'Azioni'
      },
      confirmations: {
        deactivate: {
          title: 'Disattiva Utente',
          message: 'Sei sicuro di voler disattivare questo utente? L\'utente non potrà più accedere al sistema ma potrà essere riattivato in seguito.',
          confirm: 'Disattiva',
          cancel: 'Annulla'
        },
        deactivateMultiple: {
          title: 'Disattiva Utenti',
          message: 'Sei sicuro di voler disattivare {count} utent{count === 1 ? \'e\' : \'i\'}? Gli utenti non potranno più accedere ma potranno essere riattivati.',
          confirm: 'Disattiva',
          cancel: 'Annulla'
        },
        reactivate: {
          title: 'Riattiva Utente',
          message: 'Sei sicuro di voler riattivare questo utente? L\'utente potrà nuovamente accedere al sistema.',
          confirm: 'Riattiva',
          cancel: 'Annulla'
        },
        reset2FA: {
          title: 'Reset 2FA',
          message: 'Sei sicuro di voler resettare l\'autenticazione a due fattori per {name}? L\'utente dovrà configurare nuovamente il 2FA al prossimo accesso.',
          confirm: 'Reset 2FA',
          cancel: 'Annulla'
        }
      },
      messages: {
        deactivateSuccess: 'Utente disattivato con successo',
        deactivateError: 'Errore durante la disattivazione dell\'utente',
        deactivateMultipleSuccess: '{count} utent{count === 1 ? \'e disattivato\' : \'i disattivati\'} con successo',
        deactivateMultipleError: 'Errore durante la disattivazione degli utenti',
        reactivateSuccess: 'Utente riattivato con successo',
        reactivateError: 'Errore durante la riattivazione dell\'utente',
        roleUpdated: 'Ruolo utente aggiornato con successo',
        roleRemoved: 'Ruolo utente rimosso con successo',
        roleUpdateError: 'Errore durante l\'aggiornamento del ruolo utente',
        selectRole: 'Seleziona un ruolo',
        cannotDeactivateSelf: 'Non puoi disattivare il tuo stesso account',
        mustKeepAdmin: 'Devi mantenere almeno un amministratore nell\'organizzazione',
        selectAtLeastOne: 'Seleziona almeno un utente da disattivare',
        reset2FASuccess: '2FA resettato con successo per {name}',
        reset2FAError: 'Errore durante il reset del 2FA'
      },
      plan: {
        noPlanTitle: 'Piano di abbonamento non assegnato',
        noPlanDescription: 'La tua organizzazione non ha un piano di abbonamento assegnato. Puoi aggiungere utenti illimitati fino all\'assegnazione di un piano. Contatta l\'amministratore di sistema per configurare il piano appropriato.'
      },
      selection: {
        selected: '{count} utent{count === 1 ? \'e selezionato\' : \'i selezionati\'}',
        deactivateSelected: 'Disattiva {count} utent{count === 1 ? \'e\' : \'i\'}',
        deactivating: 'Disattivazione...'
      },
      userForm: {
        titleNew: 'Nuovo Utente',
        titleEdit: 'Modifica Utente',
        subtitleNew: 'Compila tutti i campi per creare un nuovo utente',
        subtitleEdit: 'Modifica i dettagli dell\'utente selezionato',
        fullName: 'Nome Completo',
        fullNamePlaceholder: 'Mario Rossi',
        email: 'Email',
        emailPlaceholder: 'mario.rossi@esempio.it',
        emailCannotChange: 'L\'email non può essere modificata',
        password: 'Password',
        passwordPlaceholder: 'Almeno 6 caratteri',
        passwordHint: 'La password deve contenere almeno 6 caratteri',
        phone: 'Telefono',
        phonePlaceholder: '+39 123 456 7890',
        role: 'Ruolo',
        rolePlaceholder: 'Seleziona un ruolo...',
        manager: 'Manager',
        managerPlaceholder: 'Nessun manager',
        managerHint: 'Il manager di questo utente per la gerarchia aziendale',
        newsletterConsent: 'Consenso newsletter',
        newsletterConsentHint: "L'utente ha accettato di ricevere la newsletter",
        profileImageHint: 'JPG o PNG, max 2MB',
        cancel: 'Annulla',
        saving: 'Salvataggio...',
        createButton: 'Crea Utente',
        updateButton: 'Aggiorna Utente',
        messages: {
          createSuccess: 'Utente creato con successo',
          updateSuccess: 'Utente aggiornato con successo',
          createError: 'Errore durante la creazione dell\'utente',
          updateError: 'Errore durante l\'aggiornamento dell\'utente',
          roleRequired: 'Seleziona un ruolo prima di salvare',
          imageUploadSuccess: 'Immagine caricata con successo',
          imageUploadError: 'Errore durante il caricamento dell\'immagine',
          imageInvalidFormat: 'Formato file non supportato. Usa JPG o PNG.',
          imageTooBig: 'Il file è troppo grande. Dimensione massima: 2MB',
          newsletterConsentError: 'Consenso newsletter non salvato'
        }
      },
      passwordChange: {
        title: 'Cambia Password',
        user: 'Utente',
        newPassword: 'Nuova Password',
        newPasswordPlaceholder: 'Inserisci la nuova password',
        passwordHint: 'La password deve contenere almeno 6 caratteri',
        confirmPassword: 'Conferma Password',
        confirmPasswordPlaceholder: 'Conferma la nuova password',
        cancel: 'Annulla',
        saving: 'Salvataggio...',
        changeButton: 'Cambia Password',
        messages: {
          minLength: 'La password deve essere di almeno 6 caratteri',
          mismatch: 'Le password non corrispondono',
          success: 'Password aggiornata con successo',
          error: 'Errore durante l\'aggiornamento della password'
        }
      }
    },
    roles: {
      title: 'Ruoli',
      description: 'Gestisci ruoli e permessi',
      addRole: 'Aggiungi Ruolo',
      editRole: 'Modifica Ruolo',
      deleteRole: 'Elimina Ruolo',
      roleName: 'Nome Ruolo',
      roleDescription: 'Descrizione Ruolo',
      permissions: 'Permessi',
      permissionsCount: '{selected} / {total} permessi',
      criticalCount: '{count} critici',
      selectAll: 'Tutti',
      selectNone: 'Nessuno',
      search: 'Cerca...',
      noPermissionsFound: 'Nessun permesso trovato',
      groups: {
        core: 'Core',
        financial: 'Finanziaria',
        communication: 'Comunicazione',
        operations: 'Operazioni',
        services: 'Servizi',
        system: 'Sistema',
        other: 'Altro'
      },
      confirmations: {
        delete: {
          title: 'Elimina Ruolo',
          message: 'Sei sicuro di voler eliminare questo ruolo? Gli utenti con questo ruolo perderanno i loro permessi.',
          confirm: 'Elimina',
          cancel: 'Annulla'
        }
      },
      messages: {
        createSuccess: 'Ruolo creato con successo',
        createError: 'Errore nella creazione del ruolo',
        updateSuccess: 'Ruolo aggiornato con successo',
        updateError: 'Errore nell\'aggiornamento del ruolo',
        deleteSuccess: 'Ruolo eliminato con successo',
        deleteError: 'Errore nell\'eliminazione del ruolo'
      },
      permissionPicker: {
        permissionsCount: 'permessi',
        critical: 'critici',
        searchPlaceholder: 'Cerca...',
        selectAll: 'Tutti',
        selectNone: 'Nessuno',
        other: 'Altro',
        noPermissionsFound: 'Nessun permesso trovato'
      },
      form: {
        titleNew: 'Nuovo Ruolo',
        titleEdit: 'Modifica Ruolo',
        name: 'Nome',
        namePlaceholder: 'Nome del ruolo',
        description: 'Descrizione',
        descriptionPlaceholder: 'Descrizione opzionale',
        cancel: 'Annulla',
        save: 'Salva',
        create: 'Crea'
      },
      confirmDelete: {
        title: 'Elimina Ruolo',
        message: 'Sei sicuro di voler eliminare questo ruolo? Questa azione influenzerà tutti gli utenti assegnati a questo ruolo.',
        confirm: 'Elimina',
        cancel: 'Annulla'
      }
    },
    permissionLabels: {
      // CORE
      clients: {
        view: { name: 'Accesso Clienti', description: 'Può visualizzare l\'anagrafica e i dettagli dei clienti' },
        create: { name: 'Creazione Clienti', description: 'Può aggiungere nuovi clienti al CRM' },
        update: { name: 'Modifica Clienti', description: 'Può modificare i dati dei clienti esistenti' },
        delete: { name: 'Eliminazione Clienti', description: 'Può eliminare clienti dal sistema' },
        merge: { name: 'Unione Lead/Clienti', description: 'Può unire lead o clienti duplicati in un unico record' },
        manage: { name: 'Gestione Completa', description: 'Accesso completo alla gestione clienti' },
        filter_by_user: { name: 'Solo Clienti Assegnati', description: 'Vede solo i clienti di cui è responsabile' },
        view_subordinates: { name: 'Clienti dei Subordinati', description: 'Può vedere i clienti assegnati ai subordinati diretti' },
        view_all_subordinates: { name: 'Clienti di Tutti i Subordinati', description: 'Può vedere i clienti assegnati a tutti i subordinati nella gerarchia' }
      },
      deals: {
        view: { name: 'Accesso Offerte', description: 'Può visualizzare le offerte commerciali' },
        create: { name: 'Creazione Offerte', description: 'Può creare nuove offerte' },
        update: { name: 'Modifica Offerte', description: 'Può modificare offerte esistenti' },
        delete: { name: 'Eliminazione Offerte', description: 'Può eliminare offerte' },
        assign: { name: 'Assegnazione Offerte', description: 'Può assegnare offerte ad altri utenti' },
        filter_by_user: { name: 'Solo Offerte Assegnate', description: 'Vede solo le offerte di cui è responsabile' },
        generate: { name: 'Generazione PDF', description: 'Può generare PDF delle offerte' },
        hours_view: { name: 'Visualizza Pacchetti Ore', description: 'Può visualizzare i pacchetti ore associati alle offerte' },
        hours_update: { name: 'Modifica Pacchetti Ore', description: 'Può modificare i pacchetti ore associati alle offerte' },
        view_subordinates: { name: 'Offerte dei Subordinati', description: 'Può vedere le offerte assegnate ai subordinati diretti' },
        view_all_subordinates: { name: 'Offerte di Tutti i Subordinati', description: 'Può vedere le offerte assegnate a tutti i subordinati nella gerarchia' }
      },
      leads: {
        view: { name: 'Accesso Lead', description: 'Può visualizzare i lead' },
        create: { name: 'Creazione Lead', description: 'Può inserire nuovi lead' },
        update: { name: 'Modifica Lead', description: 'Può modificare i dati dei lead' },
        delete: { name: 'Eliminazione Lead', description: 'Può eliminare lead' },
        convert: { name: 'Conversione Lead', description: 'Può convertire un lead in cliente' },
        filter_by_user: { name: 'Solo Lead Assegnati', description: 'Vede solo i lead di cui è responsabile' },
        view_subordinates: { name: 'Lead dei Subordinati', description: 'Può vedere i lead assegnati ai subordinati diretti' },
        view_all_subordinates: { name: 'Lead di Tutti i Subordinati', description: 'Può vedere i lead assegnati a tutti i subordinati nella gerarchia' }
      },
      lead_notes: {
        view: { name: 'Accesso Note', description: 'Può visualizzare le note dei lead' },
        create: { name: 'Creazione Note', description: 'Può aggiungere note ai lead' },
        update: { name: 'Modifica Note', description: 'Può modificare le note esistenti' },
        delete: { name: 'Eliminazione Note', description: 'Può eliminare note' }
      },
      lead_sources: {
        view: { name: 'Accesso Fonti', description: 'Può visualizzare le fonti dei lead' },
        create: { name: 'Creazione Fonti', description: 'Può aggiungere nuove fonti' },
        update: { name: 'Modifica Fonti', description: 'Può modificare le fonti esistenti' },
        delete: { name: 'Eliminazione Fonti', description: 'Può eliminare fonti' },
        manage: { name: 'Gestione Completa', description: 'Accesso completo alla gestione fonti' }
      },
      contacts: {
        view: { name: 'Accesso Rubrica', description: 'Può visualizzare la rubrica contatti' },
        create: { name: 'Creazione Contatti', description: 'Può aggiungere nuovi contatti alla rubrica' },
        update: { name: 'Modifica Contatti', description: 'Può modificare i contatti esistenti' },
        delete: { name: 'Eliminazione Contatti', description: 'Può eliminare contatti dalla rubrica' },
        manage: { name: 'Gestione Sync', description: 'Può sincronizzare i contatti con Google' }
      },
      dashboard: {
        view: { name: 'Accesso Dashboard', description: 'Può visualizzare la dashboard principale' },
        filter_by_user: { name: 'Dashboard Personale', description: 'Vede solo i dati relativi alle proprie attività' }
      },
      calendar: {
        view: { name: 'Accesso Calendario', description: 'Può visualizzare il calendario' },
        create: { name: 'Creazione Eventi', description: 'Può creare eventi e appuntamenti' },
        update: { name: 'Modifica Eventi', description: 'Può modificare eventi esistenti' },
        delete: { name: 'Eliminazione Eventi', description: 'Può eliminare eventi' },
        share: { name: 'Gestisce le condivisioni', description: 'Decide chi vede il calendario di chi, anche per reparto e ruolo (ognuno può sempre condividere il proprio)' },
        sync: { name: 'Collega Google o Outlook', description: 'Collega il proprio account Google o Outlook al calendario' },
        configure: { name: 'Impostazioni del calendario', description: 'Tipi, colori, esiti, regole di Google e Outlook per tutta l\'azienda' }
      },
      calendar_others: {
        view: { name: 'Vede i calendari di tutti', description: 'Vede gli appuntamenti di tutte le persone dell\'azienda, senza bisogno di condivisioni' },
        update: { name: 'Modifica le cose dei colleghi', description: 'Sposta, cambia e dà l\'esito agli appuntamenti dei colleghi su tutti i calendari che vede' },
        delete: { name: 'Cancella le cose dei colleghi', description: 'Cancella gli appuntamenti dei colleghi su tutti i calendari che vede' },
        schedule: { name: 'Segna le indisponibilità dei colleghi', description: 'Segna sul calendario dei colleghi i periodi in cui non sono operativi (sul proprio calendario lo può fare chiunque)' }
      },
      // FINANCIAL
      fattureincloud: {
        view: { name: 'Accesso Fatturazione', description: 'Può visualizzare fatture e documenti fiscali' },
        create: { name: 'Creazione Documenti', description: 'Può creare fatture e documenti' },
        update: { name: 'Modifica Documenti', description: 'Può modificare documenti esistenti' },
        delete: { name: 'Eliminazione Documenti', description: 'Può eliminare documenti' },
        sync: { name: 'Sincronizzazione', description: 'Può sincronizzare i dati con Fatture in Cloud' },
        configure: { name: 'Configurazione', description: 'Può configurare l\'integrazione Fatture in Cloud' }
      },
      user_commissions: {
        view: { name: 'Visualizza Commissioni', description: 'Può vedere le commissioni degli utenti' },
        update: { name: 'Modifica Commissioni', description: 'Può modificare le commissioni' },
        manage: { name: 'Gestione Completa', description: 'Accesso completo alla gestione commissioni utenti' }
      },
      commissions: {
        view: { name: 'Visualizza Commissioni', description: 'Può vedere le commissioni maturate' },
        manage: { name: 'Gestione Commissioni', description: 'Può gestire e approvare commissioni' },
        update: { name: 'Modifica Commissioni', description: 'Può modificare le commissioni' },
        refresh: { name: 'Ricalcolo', description: 'Può ricalcolare le commissioni' }
      },
      commission_groups: {
        view: { name: 'Visualizza Gruppi', description: 'Può vedere i gruppi commissioni' },
        create: { name: 'Creazione Gruppi', description: 'Può creare nuovi gruppi' },
        update: { name: 'Modifica Gruppi', description: 'Può modificare gruppi esistenti' },
        delete: { name: 'Eliminazione Gruppi', description: 'Può eliminare gruppi' }
      },
      // COMMUNICATION
      whatsapp: {
        view: { name: 'Accesso WhatsApp', description: 'Può visualizzare le chat WhatsApp' },
        create: { name: 'Invio Messaggi', description: 'Può inviare messaggi WhatsApp' },
        assign: { name: 'Solo Chat Assegnate', description: 'Vede solo le chat di cui è responsabile' }
      },
      newsletter: {
        view: { name: 'Accesso Newsletter', description: 'Può visualizzare le campagne newsletter' },
        create: { name: 'Creazione Campagne', description: 'Può creare nuove campagne' },
        update: { name: 'Modifica Campagne', description: 'Può modificare campagne esistenti' },
        delete: { name: 'Eliminazione Campagne', description: 'Può eliminare campagne' },
        send: { name: 'Invio Newsletter', description: 'Può inviare le campagne ai destinatari' }
      },
      voice_agents: {
        view: { name: 'Accesso Agenti Vocali', description: 'Può visualizzare gli agenti vocali e lo storico chiamate' },
        create: { name: 'Creazione Agenti Vocali', description: 'Può creare e attivare i binding degli agenti vocali' },
        update: { name: 'Modifica Agenti Vocali', description: 'Può modificare la configurazione degli agenti vocali' },
        delete: { name: 'Eliminazione Agenti Vocali', description: 'Può rimuovere i binding degli agenti vocali' },
        dial: { name: 'Approva Chiamate Outbound', description: 'Può autorizzare e lanciare le chiamate outbound (approvatore HITL)' },
        manage: { name: 'Gestione Setup Vocale', description: 'Può gestire le chiavi BYOK dei provider e i numeri di telefono' }
      },
      email_signature: {
        generate: { name: 'Genera Firma', description: 'Può generare firme email personalizzate' }
      },
      // OPERATIONS
      inventory: {
        view: { name: 'Accesso Magazzino', description: 'Può visualizzare prodotti e giacenze' },
        create: { name: 'Creazione Prodotti', description: 'Può aggiungere nuovi prodotti' },
        update: { name: 'Modifica Prodotti', description: 'Può modificare i dati dei prodotti' },
        delete: { name: 'Eliminazione Prodotti', description: 'Può eliminare prodotti' },
        manage: { name: 'Gestione Completa', description: 'Accesso completo alla gestione magazzino' }
      },
      rentals: {
        view: { name: 'Accesso Noleggi', description: 'Può visualizzare i noleggi' },
        create: { name: 'Creazione Noleggi', description: 'Può creare nuovi noleggi' },
        update: { name: 'Modifica Noleggi', description: 'Può modificare noleggi esistenti' },
        delete: { name: 'Eliminazione Noleggi', description: 'Può eliminare noleggi' }
      },
      projects: {
        view: { name: 'Accesso Progetti', description: 'Può visualizzare i progetti' },
        create: { name: 'Creazione Progetti', description: 'Può creare nuovi progetti' },
        update: { name: 'Modifica Progetti', description: 'Può modificare progetti esistenti' },
        delete: { name: 'Eliminazione Progetti', description: 'Può eliminare progetti' },
        manage: { name: 'Gestione Completa', description: 'Accesso completo alla gestione progetti' }
      },
      tasks: {
        view: { name: 'Accesso Task', description: 'Può visualizzare i task' },
        create: { name: 'Creazione Task', description: 'Può creare nuovi task' },
        update: { name: 'Modifica Task', description: 'Può modificare task esistenti' },
        delete: { name: 'Eliminazione Task', description: 'Può eliminare task' },
        complete: { name: 'Completamento', description: 'Può segnare i task come completati' },
        assign: { name: 'Assegnazione', description: 'Può assegnare task ad altri utenti' },
        filter_by_user: { name: 'Solo Task Assegnati', description: 'Vede solo i task di cui è responsabile' }
      },
      tickets: {
        view: { name: 'Accesso Ticket', description: 'Può visualizzare i ticket' },
        create: { name: 'Creazione Ticket', description: 'Può creare nuovi ticket' },
        update: { name: 'Modifica Ticket', description: 'Può modificare ticket esistenti' },
        delete: { name: 'Eliminazione Ticket', description: 'Può eliminare ticket' },
        assign: { name: 'Assegnazione', description: 'Può assegnare ticket ad altri utenti' },
        generate: { name: 'Esporta Ticket', description: 'Può esportare i ticket in CSV/Excel' },
        manage: { name: 'Gestione Completa', description: 'Accesso completo alla gestione ticket' },
        report: { name: 'Report Ticket', description: 'Può generare report sui ticket' },
        export: { name: 'Esporta Dati', description: 'Può esportare i dati dei ticket' }
      },
      ticket_types: {
        view: { name: 'Accesso Tipi Intervento', description: 'Può visualizzare i tipi di intervento' },
        create: { name: 'Creazione Tipi Intervento', description: 'Può creare nuovi tipi di intervento' },
        update: { name: 'Modifica Tipi Intervento', description: 'Può modificare tipi di intervento esistenti' },
        delete: { name: 'Eliminazione Tipi Intervento', description: 'Può eliminare tipi di intervento' },
        manage: { name: 'Gestione Completa', description: 'Accesso completo alla gestione tipi intervento' }
      },
      ticket_time_entries: {
        view: { name: 'Accesso Ore Lavorate', description: 'Può visualizzare le ore lavorate sui ticket' },
        create: { name: 'Registra Ore', description: 'Può registrare ore di lavoro sui ticket' },
        update: { name: 'Modifica Ore', description: 'Può modificare le ore registrate' },
        delete: { name: 'Eliminazione Ore', description: 'Può eliminare le ore registrate' },
        manage: { name: 'Gestione Completa', description: 'Accesso completo a tutte le ore lavorate' }
      },
      ticket_sla_configs: {
        view: { name: 'Accesso Config SLA', description: 'Può visualizzare le configurazioni SLA' },
        create: { name: 'Creazione Config SLA', description: 'Può creare nuove configurazioni SLA' },
        update: { name: 'Modifica Config SLA', description: 'Può modificare configurazioni SLA esistenti' },
        delete: { name: 'Eliminazione Config SLA', description: 'Può eliminare configurazioni SLA' },
        manage: { name: 'Gestione Completa', description: 'Accesso completo alla gestione SLA' }
      },
      // SERVICES
      services: {
        view: { name: 'Accesso Servizi', description: 'Può visualizzare il catalogo servizi' },
        create: { name: 'Creazione Servizi', description: 'Può aggiungere nuovi servizi' },
        update: { name: 'Modifica Servizi', description: 'Può modificare servizi esistenti' },
        delete: { name: 'Eliminazione Servizi', description: 'Può eliminare servizi' },
        manage: { name: 'Gestione Completa', description: 'Accesso completo alla gestione servizi' }
      },
      service_costs: {
        view: { name: 'Accesso Costi', description: 'Può visualizzare i costi dei servizi' },
        create: { name: 'Creazione Costi', description: 'Può aggiungere nuovi costi' },
        update: { name: 'Modifica Costi', description: 'Può modificare costi esistenti' },
        delete: { name: 'Eliminazione Costi', description: 'Può eliminare costi' }
      },
      service_margins: {
        view: { name: 'Visualizza Margini', description: 'Può vedere i margini sui servizi' }
      },
      client_expenses: {
        view: { name: 'Accesso Spese', description: 'Può visualizzare le spese cliente' },
        create: { name: 'Creazione Spese', description: 'Può aggiungere nuove spese' },
        update: { name: 'Modifica Spese', description: 'Può modificare spese esistenti' },
        delete: { name: 'Eliminazione Spese', description: 'Può eliminare spese' },
        filter_by_user: { name: 'Solo Spese Assegnate', description: 'Vede solo le spese di cui è responsabile' }
      },
      service_task_templates: {
        view: { name: 'Accesso Template', description: 'Può visualizzare i template task' },
        create: { name: 'Creazione Template', description: 'Può creare nuovi template' },
        update: { name: 'Modifica Template', description: 'Può modificare template esistenti' },
        delete: { name: 'Eliminazione Template', description: 'Può eliminare template' },
        manage: { name: 'Gestione Completa', description: 'Accesso completo alla gestione template' }
      },
      external_partners: {
        view: { name: 'Accesso Partner', description: 'Può visualizzare i partner esterni' },
        create: { name: 'Creazione Partner', description: 'Può aggiungere nuovi partner' },
        update: { name: 'Modifica Partner', description: 'Può modificare partner esistenti' },
        delete: { name: 'Eliminazione Partner', description: 'Può eliminare partner' }
      },
      // SYSTEM
      users: {
        view: { name: 'Visualizza Utenti', description: 'Può vedere l\'elenco degli utenti' },
        create: { name: 'Creazione Utenti', description: 'Può invitare nuovi utenti' },
        update: { name: 'Modifica Utenti', description: 'Può modificare i dati degli utenti' },
        delete: { name: 'Eliminazione Utenti', description: 'Può rimuovere utenti dal sistema' }
      },
      roles: {
        view: { name: 'Visualizza Ruoli', description: 'Può vedere i ruoli disponibili' },
        create: { name: 'Creazione Ruoli', description: 'Può creare nuovi ruoli' },
        update: { name: 'Modifica Ruoli', description: 'Può modificare i permessi dei ruoli' },
        delete: { name: 'Eliminazione Ruoli', description: 'Può eliminare ruoli' }
      },
      settings: {
        view: { name: 'Accesso Impostazioni', description: 'Può visualizzare le impostazioni' },
        update: { name: 'Modifica Impostazioni', description: 'Può modificare le impostazioni' },
        manage: { name: 'Gestione Completa', description: 'Accesso completo a tutte le impostazioni' }
      },
      api_config: {
        view: { name: 'Accesso API', description: 'Può visualizzare le configurazioni API' },
        create: { name: 'Creazione API', description: 'Può creare nuove configurazioni' },
        update: { name: 'Modifica API', description: 'Può modificare configurazioni esistenti' },
        delete: { name: 'Eliminazione API', description: 'Può eliminare configurazioni' }
      },
      categories: {
        view: { name: 'Accesso Categorie', description: 'Può visualizzare le categorie' },
        create: { name: 'Creazione Categorie', description: 'Può creare nuove categorie' },
        update: { name: 'Modifica Categorie', description: 'Può modificare categorie esistenti' },
        delete: { name: 'Eliminazione Categorie', description: 'Può eliminare categorie' }
      },
      templates: {
        view: { name: 'Accesso Template', description: 'Può visualizzare i template' },
        create: { name: 'Creazione Template', description: 'Può creare nuovi template' },
        update: { name: 'Modifica Template', description: 'Può modificare template esistenti' },
        delete: { name: 'Eliminazione Template', description: 'Può eliminare template' }
      },
      signatures: {
        view: { name: 'Visualizza Firme', description: 'Può vedere le firme digitali' },
        create: { name: 'Creazione Firme', description: 'Può creare nuove firme' }
      },
      payment_methods: {
        view: { name: 'Accesso Pagamenti', description: 'Può visualizzare i metodi di pagamento' },
        create: { name: 'Creazione Pagamenti', description: 'Può aggiungere nuovi metodi' },
        update: { name: 'Modifica Pagamenti', description: 'Può modificare metodi esistenti' },
        delete: { name: 'Eliminazione Pagamenti', description: 'Può eliminare metodi' }
      },
      referrers: {
        view: { name: 'Accesso Referenti', description: 'Può visualizzare i referenti' },
        create: { name: 'Creazione Referenti', description: 'Può aggiungere nuovi referenti' },
        update: { name: 'Modifica Referenti', description: 'Può modificare referenti esistenti' },
        delete: { name: 'Eliminazione Referenti', description: 'Può eliminare referenti' }
      },
      custom_fields: {
        manage: { name: 'Gestione Campi', description: 'Può gestire i campi personalizzati' }
      },
      custom_queries: {
        view: { name: 'Accesso Query', description: 'Può visualizzare le query personalizzate' },
        create: { name: 'Creazione Query', description: 'Può creare nuove query' }
      },
      invoices: {
        view: { name: 'Accesso Fatture', description: 'Può visualizzare le fatture' },
        create: { name: 'Creazione Fatture', description: 'Può creare nuove fatture' },
        update: { name: 'Modifica Fatture', description: 'Può modificare fatture esistenti' },
        delete: { name: 'Eliminazione Fatture', description: 'Può eliminare fatture' }
      },
      settings_sendgrid: {
        view: { name: 'Accesso SendGrid', description: 'Può visualizzare i domini SendGrid' },
        create: { name: 'Creazione Domini', description: 'Può aggiungere nuovi domini' },
        update: { name: 'Modifica Domini', description: 'Può modificare domini esistenti' },
        delete: { name: 'Eliminazione Domini', description: 'Può eliminare domini' },
        verify: { name: 'Verifica Domini', description: 'Può verificare i domini' }
      },
      settings_microsoft_workspace: {
        view: { name: 'Accesso Integrazione Microsoft', description: 'Può accedere alle impostazioni di integrazione Microsoft/OneDrive' }
      },
      settings_google_workspace: {
        view: { name: 'Accesso Integrazione Google', description: 'Può accedere alle impostazioni di integrazione Google Workspace' }
      },
      // ANALYTICS
      reports: {
        view: { name: 'Visualizza Report', description: 'Può visualizzare i report personalizzati' },
        create: { name: 'Crea Report', description: 'Può creare nuovi report personalizzati' },
        update: { name: 'Modifica Report', description: 'Può modificare report esistenti' },
        delete: { name: 'Elimina Report', description: 'Può eliminare i report' },
        execute: { name: 'Esegui Report', description: 'Può eseguire i report e vedere i risultati' },
        export: { name: 'Esporta Report', description: 'Può esportare i report in PDF, Excel o CSV' },
        schedule: { name: 'Schedula Report', description: 'Può programmare l\'esecuzione automatica dei report' },
        share: { name: 'Condividi Report', description: 'Può condividere i report con altri utenti' }
      },
      dashboards: {
        view: { name: 'Visualizza Dashboard', description: 'Può visualizzare le dashboard analytics' },
        create: { name: 'Crea Dashboard', description: 'Può creare nuove dashboard con widget' },
        update: { name: 'Modifica Dashboard', description: 'Può modificare layout e widget delle dashboard' },
        delete: { name: 'Elimina Dashboard', description: 'Può eliminare le dashboard' },
        share: { name: 'Condividi Dashboard', description: 'Può condividere le dashboard con altri utenti' }
      },
      kpis: {
        view: { name: 'Visualizza KPI', description: 'Può visualizzare gli indicatori chiave di performance' },
        create: { name: 'Crea KPI', description: 'Può definire nuovi KPI e obiettivi' },
        update: { name: 'Modifica KPI', description: 'Può modificare le configurazioni dei KPI' },
        delete: { name: 'Elimina KPI', description: 'Può eliminare i KPI' }
      },
      insights: {
        view: { name: 'Visualizza Insights', description: 'Può visualizzare gli insight automatici sui dati' },
        dismiss: { name: 'Nascondi Insights', description: 'Può nascondere gli insight non rilevanti' }
      },
      // PRENOTAZIONI
      booking: {
        view: { name: 'Accesso Prenotazioni', description: 'Può visualizzare le prenotazioni e gli appuntamenti' },
        create: { name: 'Creazione Prenotazioni', description: 'Può creare nuove prenotazioni' },
        update: { name: 'Modifica Prenotazioni', description: 'Può modificare prenotazioni esistenti' },
        delete: { name: 'Eliminazione Prenotazioni', description: 'Può annullare o eliminare prenotazioni' },
        manage: { name: 'Gestione Completa Prenotazioni', description: 'Gestione completa delle prenotazioni (tipi evento, disponibilità)' },
        filter_by_user: { name: 'Visibilità Personale', description: 'Visualizza solo le prenotazioni assegnate all\'utente corrente' }
      },
      // SONDAGGI
      surveys: {
        view: { name: 'Accesso Sondaggi', description: 'Può visualizzare sondaggi e risposte' },
        create: { name: 'Creazione Sondaggi', description: 'Può creare nuovi sondaggi' },
        update: { name: 'Modifica Sondaggi', description: 'Può modificare sondaggi esistenti' },
        delete: { name: 'Eliminazione Sondaggi', description: 'Può eliminare sondaggi' }
      },
      // TASK PARTNER
      partner_tasks: {
        view: { name: 'Accesso Task Partner', description: 'Può visualizzare i task assegnati ai partner' },
        create: { name: 'Creazione Task Partner', description: 'Può creare nuovi task per i partner' },
        update: { name: 'Modifica Task Partner', description: 'Può modificare task partner esistenti' },
        delete: { name: 'Eliminazione Task Partner', description: 'Può eliminare task partner' },
        manage: { name: 'Gestione Completa', description: 'Accesso completo alla gestione task partner' },
        assign: { name: 'Assegnazione Task Partner', description: 'Può assegnare task ai partner' }
      },
      // IMPOSTAZIONI
      settings_company: {
        view: { name: 'Accesso Impostazioni Azienda', description: 'Può accedere alle impostazioni aziendali' },
        manage: { name: 'Gestione Impostazioni Azienda', description: 'Può modificare le impostazioni aziendali' }
      },
      settings_clients: {
        view: { name: 'Accesso Impostazioni Clienti', description: 'Può accedere alle impostazioni clienti' },
        manage: { name: 'Gestione Impostazioni Clienti', description: 'Può modificare le impostazioni clienti' }
      },
      settings_partners: {
        view: { name: 'Accesso Impostazioni Partner', description: 'Può accedere alle impostazioni partner' },
        manage: { name: 'Gestione Impostazioni Partner', description: 'Può modificare le impostazioni partner' }
      },
      settings_services: {
        view: { name: 'Accesso Impostazioni Servizi', description: 'Può accedere alle impostazioni servizi' },
        manage: { name: 'Gestione Impostazioni Servizi', description: 'Può modificare le impostazioni servizi' }
      },
      settings_payment_methods: {
        view: { name: 'Accesso Impostazioni Pagamenti', description: 'Può accedere alle impostazioni metodi di pagamento' },
        manage: { name: 'Gestione Impostazioni Pagamenti', description: 'Può modificare le impostazioni metodi di pagamento' }
      },
      settings_api: {
        view: { name: 'Accesso Impostazioni API', description: 'Può accedere alle impostazioni API' },
        manage: { name: 'Gestione Impostazioni API', description: 'Può modificare le impostazioni API' }
      },
      settings_categories: {
        view: { name: 'Accesso Impostazioni Categorie', description: 'Può accedere alle impostazioni categorie' },
        manage: { name: 'Gestione Impostazioni Categorie', description: 'Può modificare le impostazioni categorie' }
      },
      settings_interest_categories: {
        view: { name: 'Accesso Categorie Interessi', description: 'Può accedere alle impostazioni categorie di interessi' },
        manage: { name: 'Gestione Categorie Interessi', description: 'Può modificare le impostazioni categorie di interessi' }
      },
      settings_menu: {
        view: { name: 'Accesso Impostazioni Menu', description: 'Può accedere alle impostazioni del menu' },
        manage: { name: 'Gestione Impostazioni Menu', description: 'Può modificare le impostazioni del menu' }
      },
      settings_quote_templates: {
        view: { name: 'Accesso Template Preventivi', description: 'Può accedere alle impostazioni template preventivi' },
        manage: { name: 'Gestione Template Preventivi', description: 'Può modificare i template preventivi' }
      },
      settings_deals: {
        view: { name: 'Accesso Impostazioni Offerte', description: 'Può accedere alle impostazioni offerte' },
        manage: { name: 'Gestione Impostazioni Offerte', description: 'Può modificare le impostazioni offerte' }
      },
      settings_sales_targets: {
        view: { name: 'Accesso Obiettivi Vendita', description: 'Può accedere alle impostazioni obiettivi di vendita' },
        manage: { name: 'Gestione Obiettivi Vendita', description: 'Può modificare gli obiettivi di vendita' }
      },
      settings_users: {
        view: { name: 'Accesso Gestione Utenti', description: 'Può accedere alle impostazioni gestione utenti' },
        manage: { name: 'Gestione Impostazioni Utenti', description: 'Può modificare le impostazioni gestione utenti' }
      },
      settings_language: {
        view: { name: 'Accesso Impostazioni Lingua', description: 'Può accedere alle impostazioni lingua' },
        manage: { name: 'Gestione Impostazioni Lingua', description: 'Può modificare le impostazioni lingua' }
      },
      settings_search: {
        view: { name: 'Accesso Impostazioni Ricerca', description: 'Può accedere alle impostazioni di ricerca' },
        manage: { name: 'Gestione Impostazioni Ricerca', description: 'Può modificare le impostazioni di ricerca' }
      },
      settings_import_export: {
        view: { name: 'Accesso Import/Export', description: 'Può accedere alle impostazioni import/export' },
        manage: { name: 'Gestione Import/Export', description: 'Può eseguire operazioni di import/export' }
      },
      settings_commissions: {
        view: { name: 'Accesso Impostazioni Commissioni', description: 'Può accedere alle impostazioni commissioni' },
        manage: { name: 'Gestione Impostazioni Commissioni', description: 'Può modificare le impostazioni commissioni' }
      },
      settings_whatsapp: {
        view: { name: 'Accesso Impostazioni WhatsApp', description: 'Può accedere alle impostazioni WhatsApp' },
        manage: { name: 'Gestione Impostazioni WhatsApp', description: 'Può modificare le impostazioni WhatsApp' }
      },
      settings_automation: {
        view: { name: 'Accesso Impostazioni Automazioni', description: 'Può accedere alle impostazioni automazioni workflow' },
        manage: { name: 'Gestione Impostazioni Automazioni', description: 'Può modificare le automazioni workflow' }
      },
      settings_fattureincloud: {
        view: { name: 'Accesso Impostazioni Fatture in Cloud', description: 'Può accedere alle impostazioni integrazione Fatture in Cloud' },
        manage: { name: 'Gestione Impostazioni Fatture in Cloud', description: 'Può modificare l\'integrazione Fatture in Cloud' }
      },
      settings_business_central: {
        view: { name: 'Accesso Impostazioni Business Central', description: 'Può accedere alle impostazioni integrazione Business Central' }
      },
      settings_smtp: {
        view: { name: 'Accesso Impostazioni SMTP', description: 'Può accedere alle impostazioni email SMTP' },
        manage: { name: 'Gestione Impostazioni SMTP', description: 'Può modificare le impostazioni email SMTP' }
      },
      settings_custom_fields: {
        view: { name: 'Accesso Impostazioni Campi Personalizzati', description: 'Può accedere alle impostazioni campi personalizzati' },
        manage: { name: 'Gestione Impostazioni Campi Personalizzati', description: 'Può modificare le impostazioni campi personalizzati' }
      },
      settings_subscription: {
        view: { name: 'Accesso Abbonamento', description: 'Può accedere alle impostazioni abbonamento' },
        manage: { name: 'Gestione Abbonamento', description: 'Può modificare le impostazioni abbonamento' }
      },
      settings_team_chat: {
        view: { name: 'Accesso Impostazioni Chat Team', description: 'Può accedere alle impostazioni chat di team' },
        manage: { name: 'Gestione Impostazioni Chat Team', description: 'Può modificare le impostazioni chat di team' }
      },
      settings_bulk_delete: {
        view: { name: 'Accesso Eliminazione Massiva', description: 'Può accedere allo strumento di eliminazione massiva' }
      },
      settings_woocommerce: {
        view: { name: 'Accesso Impostazioni WooCommerce', description: 'Può accedere alle impostazioni integrazione WooCommerce' }
      },
      settings_ecommerce: {
        view: { name: 'Visualizza Negozi collegati', description: 'Può vedere i negozi online collegati, il loro stato, il registro e la cronologia' },
        manage: { name: 'Gestisci Negozi collegati', description: 'Può collegare, mettere in pausa, ricollegare e scollegare i negozi e cambiarne le impostazioni' }
      },
      settings_notifications: {
        view: { name: 'Vedi Notifiche del team', description: "Può vedere le notifiche predefinite dell'organizzazione e chi riceve ogni notifica" },
        manage: { name: 'Gestisci Notifiche del team', description: "Può decidere quali notifiche riceve l'organizzazione e su quali canali" }
      },
      settings_security: {
        view: { name: 'Accesso Impostazioni Sicurezza', description: 'Può accedere alle impostazioni di sicurezza' }
      },
      settings_meeting_bot: {
        view: { name: 'Accesso Impostazioni Meeting Bot', description: 'Può accedere alle impostazioni del meeting bot' }
      },
      settings_legal: {
        view: { name: 'Accesso Informazioni Legali', description: 'Può accedere alle impostazioni informazioni legali' }
      },
      settings_booking: {
        view: { name: 'Accesso Impostazioni Prenotazioni', description: 'Può accedere alle impostazioni prenotazioni' },
        manage: { name: 'Gestione Impostazioni Prenotazioni', description: 'Può modificare le impostazioni prenotazioni' }
      },
      // MARKETING
      marketing: {
        view: { name: 'Accesso Marketing', description: 'Può visualizzare la dashboard marketing e le analisi campagne' },
        create: { name: 'Creazione Campagne', description: 'Può creare nuove campagne marketing' },
        update: { name: 'Modifica Campagne', description: 'Può modificare campagne esistenti' },
        delete: { name: 'Eliminazione Campagne', description: 'Può eliminare campagne' }
      },
      settings_meta_ads: {
        view: { name: 'Accesso Impostazioni Meta Ads', description: 'Può accedere alle impostazioni integrazione Meta Ads' },
        manage: { name: 'Gestione Impostazioni Meta Ads', description: 'Può configurare l\'integrazione Meta Ads' }
      },
      settings_google_ads: {
        view: { name: 'Accesso Impostazioni Google Ads', description: 'Può accedere alle impostazioni integrazione Google Ads' },
        manage: { name: 'Gestione Impostazioni Google Ads', description: 'Può configurare l\'integrazione Google Ads' }
      },
      ai_text_generation: {
        view: { name: 'Generazione Testi AI', description: 'Può utilizzare l\'AI per generare contenuti testuali (descrizioni, contratti, ecc.)' }
      },
      ai_task_generation: {
        view: { name: 'Generazione Task AI', description: 'Può utilizzare l\'AI per generare automaticamente task di progetto' }
      },
      ai_suggestions: {
        view: { name: 'Suggerimenti AI', description: 'Può utilizzare i suggerimenti AI (prossima attività, offerta da WhatsApp)' }
      },
      ai_upsell_scanner: {
        view: { name: 'Scansione Upsell AI', description: 'Può utilizzare l\'AI per analizzare opportunità di upsell nei progetti' }
      },
      ai_insights: {
        view: { name: 'Insights AI', description: 'Può utilizzare l\'AI per generare insights e analisi dei dati' }
      },
      ai_whatsapp_agent: {
        view: { name: 'Visualizza WhatsApp Agent', description: 'Può visualizzare risposte e stato dell\'agente AI WhatsApp' },
        manage: { name: 'Gestione WhatsApp Agent', description: 'Può configurare le impostazioni dell\'agente AI WhatsApp' }
      },
      ai_meeting_bot: {
        view: { name: 'Visualizza Meeting Bot', description: 'Può visualizzare registrazioni e riassunti del meeting bot AI' },
        manage: { name: 'Gestione Meeting Bot', description: 'Può configurare le impostazioni del meeting bot AI (Vexa)' }
      },
      ai_status_updates: {
        view: { name: 'Visualizza Aggiornamenti AI', description: 'Può visualizzare gli aggiornamenti di stato generati dall\'AI' },
        manage: { name: 'Gestione Aggiornamenti AI', description: 'Può configurare la generazione automatica degli aggiornamenti AI' }
      }
    },
    resourceNames: {
      // Core CRM
      clients: 'Schede Clienti',
      deals: 'Offerte e Preventivi',
      leads: 'Schede Lead',
      lead_notes: 'Note e Commenti sui Lead',
      lead_sources: 'Fonti di Acquisizione Lead',
      lead_enrichment: 'Arricchimento Lead B2B',
      contacts: 'Rubrica Contatti Personali',
      dashboard: 'Dashboard Riepilogativa',
      calendar: 'Calendario Appuntamenti',
      calendar_others: 'Calendari dei colleghi',
      meetings: 'Riunioni e Videocall',
      documents: 'Documenti Allegati a Clienti/Lead',
      categories: 'Categorie e Tag su Clienti/Lead',
      templates: 'Template per Preventivi e Offerte',
      lead_forms: 'Form Web di Acquisizione Lead',
      // Comunicazione
      whatsapp: 'Messaggi WhatsApp',
      meta_messaging: 'Messaggi Instagram e Messenger',
      newsletter: 'Campagne Newsletter e Mailing List',
      email_signature: 'Firma Email Aziendale',
      surveys: 'Sondaggi e Questionari per Clienti',
      qr_codes: 'QR Code Dinamici (link tracciabili)',
      screen_recordings: 'Video Registrazioni (schermo + webcam)',
      agents: 'Agenti AI (marketplace + binding workflow)',
      voice_agents: 'Agenti vocali',
      // Servizi e Pagamenti
      services: 'Catalogo Servizi Offerti',
      service_costs: 'Tariffe Orarie per Servizio',
      service_margins: 'Margini di Profitto sui Servizi',
      service_task_templates: 'Checklist Attività per Servizio',
      service_cost_templates: 'Modelli di Costo per Servizio',
      client_expenses: 'Costi e Spese per Cliente',
      signatures: 'Raccolta Firme sui Preventivi',
      payment_methods: 'Metodi di Pagamento Accettati',
      // Fatturazione e Finanza
      fattureincloud: 'Sincronizzazione Fatture in Cloud',
      invoices: 'Fatture e Documenti Fiscali',
      financial_dashboard: 'Dashboard Finanziaria',
      bank_movements: 'Movimenti Bancari e Prima Nota',
      expense_categories: 'Categorie per Classificare le Spese',
      contracts: 'Contratti Associati alle Offerte',
      // Provvigioni
      commissions: 'Calcolo e Gestione Provvigioni',
      user_commissions: 'Le Mie Provvigioni Personali',
      commission_groups: 'Gruppi di Regole Provvigionali',
      commission_reports: 'Report Riepilogo Provvigioni',
      commission_advanced: 'Provvigioni Multi-Livello e Avanzate',
      // Progetti e Operazioni
      projects: 'Gestione Progetti',
      tasks: 'Task e Attività Assegnate',
      tickets: 'Ticket di Assistenza Clienti',
      ticket_types: 'Tipi di Intervento sui Ticket',
      ticket_time_entries: 'Ore Lavorate sui Ticket (Timesheet)',
      ticket_sla_configs: 'Regole SLA (tempi di risposta)',
      booking: 'Prenotazioni Online',
      orders: 'Ordini',
      products: 'Catalogo Prodotti',
      warehouse: 'Magazzino',
      inventory: 'Gestione Magazzino Prodotti',
      rentals: 'Gestione Noleggi',
      // Partner e Referral
      external_partners: 'Anagrafica Partner Esterni',
      partner_tasks: 'Task Assegnati ai Partner',
      referrers: 'Segnalatori che Portano Nuovi Clienti',
      // Report e Analytics
      analytics: 'Pagina Analytics e Grafici',
      reports: 'Reportistica (dashboard e report)',
      dashboards: 'Dashboard con Grafici e Widget',
      kpis: 'Indicatori KPI Configurabili',
      insights: 'Suggerimenti Automatici dai Dati',
      marketing: 'Campagne e Attribuzioni Marketing',
      // Sistema
      users: 'Gestione Account Utenti',
      roles: 'Ruoli e Permessi di Accesso',
      settings: 'Accesso alle Impostazioni',
      custom_fields: 'Campi Aggiuntivi su Clienti/Lead/Offerte',
      custom_queries: 'Query SQL Personalizzate',
      api_config: 'Configurazione Endpoint API',
      // Impostazioni (tab Settings)
      settings_company: 'Dati Aziendali (nome, logo, indirizzo)',
      settings_clients: 'Clienti (stati, etichette, colonne, kanban)',
      settings_partners: 'Partner (etichette e tipologie)',
      settings_services: 'Servizi (categorie, unità di misura)',
      settings_payment_methods: 'Metodi di Pagamento Accettati',
      settings_categories: 'Categorie Clienti (tag, gruppi)',
      settings_interest_categories: 'Interessi Clienti (servizi preferiti)',
      settings_quote_templates: 'Template Preventivi (layout, campi)',
      settings_deals: 'Offerte (stati pipeline, campi)',
      settings_sales_targets: 'Obiettivi di Vendita per Utente/Periodo',
      settings_users: 'Utenti e Ruoli',
      settings_language: 'Lingua Interfaccia',
      settings_search: 'Ricerca Globale',
      settings_menu: 'Personalizzazione Voci Menu',
      settings_import_export: 'Import/Export Dati (CSV, Excel)',
      settings_security: 'Sicurezza (2FA, sessioni, password)',
      settings_legal: 'Informazioni Legali e Privacy',
      settings_subscription: 'Piano di Abbonamento',
      // Impostazioni Integrazioni
      settings_whatsapp: 'Connessione WhatsApp Business',
      settings_smtp: 'Server SMTP per Invio Email',
      settings_sendgrid: 'Domini Email SendGrid',
      settings_google_workspace: 'Google Calendar e Drive',
      settings_microsoft_workspace: 'Microsoft 365 e Outlook',
      settings_fattureincloud: 'Connessione Fatture in Cloud',
      settings_business_central: 'Business Central',
      settings_woocommerce: 'Sincronizzazione WooCommerce',
      settings_ecommerce: 'E-commerce: negozi collegati',
      settings_notifications: 'Notifiche del team',
      settings_team_chat: 'Chat Interna del Team',
      settings_booking: 'Pagina Prenotazioni Online',
      settings_automation: 'Workflow e Automazioni',
      settings_commissions: 'Regole di Calcolo Provvigioni',
      settings_custom_fields: 'Definizione Campi Personalizzati',
      settings_newsletter: 'Piattaforma Newsletter',
      settings_meta_ads: 'Connessione Meta Ads (Facebook/Instagram)',
      settings_google_ads: 'Connessione Google Ads',
      settings_meeting_bot: 'Bot Registrazione Meeting',
      settings_api: 'Token API e Documentazione',
      settings_bulk_delete: 'Eliminazione Massiva Record',
      // Intelligenza Artificiale
      ai_text_generation: 'AI — Genera Descrizioni per Servizi e Offerte',
      ai_task_generation: 'AI — Crea Task da Registrazioni Meeting',
      ai_suggestions: 'AI — Suggerisce Azioni sulle Note Lead',
      ai_upsell_scanner: 'AI — Identifica Opportunità di Upsell',
      ai_insights: 'AI — Genera Analisi Automatiche dai Dati',
      ai_whatsapp_agent: 'AI — Risponde ai Messaggi WhatsApp',
      ai_meeting_bot: 'AI — Registra e Trascrive Meeting',
      ai_status_updates: 'AI — Aggiorna lo Stato Offerte/Progetti',
      // Magazzino Manifatturiero
      wh_suppliers: 'Magazzino — Anagrafica Fornitori',
      wh_contractors: 'Magazzino — Anagrafica Terzisti',
      wh_ddts: 'Magazzino — Bolle in Entrata (DDT)',
      wh_lots: 'Magazzino — Gestione Lotti',
      wh_bom: 'Magazzino — Distinte Base (BOM)',
      wh_production: 'Magazzino — Cicli di Produzione',
      wh_processing: 'Magazzino — Lavorazioni Esterne',
      wh_samples: 'Magazzino — Campioni di Lotto',
      wh_labels: 'Magazzino — Etichette e Template',
      wh_disposals: 'Magazzino — Registro Smaltimenti',
      wh_traceability: 'Magazzino — Tracciabilità Lotti',
    },
    voice: {
      title: 'Agenti vocali',
      setup: {
        title: 'Configurazione vocale',
        stepTos: 'Accetta i termini di utilizzo',
        stepElevenlabsKey: 'Chiave API ElevenLabs',
        stepWebhookSecret: 'Secret del webhook',
        stepTwilio: 'Connessione Twilio',
        stepImportNumber: 'Importa numero di telefono',
        stepTestCall: 'Chiamata di test',
        skipForNow: 'Salta per ora',
        intro: 'Configura gli agenti vocali in pochi passaggi. Puoi saltare qualsiasi passaggio e riprendere in seguito.',
        back: 'Indietro',
        next: 'Continua',
        finish: 'Fine',
        saved: 'Salvato',
        tosIntro: 'Prima di iniziare, accetta i termini di utilizzo degli agenti vocali.',
        tosAcceptContinue: 'Accetta e continua',
        apiKeyLabel: 'Chiave API',
        apiKeyExisting: 'Una chiave è già salvata — inseriscine una nuova per sostituirla.',
        apiKeyPlaceholder: 'Incolla qui la chiave',
        elevenlabsKeyDesc: 'Inserisci la tua chiave API ElevenLabs. Viene salvata cifrata e usata solo lato server.',
        webhookSecretLabel: 'Secret del webhook',
        webhookSecretDesc: 'Inserisci il secret di firma usato da ElevenLabs per i webhook post-chiamata.',
        webhookUrlTitle: 'URL del webhook da registrare',
        webhookUrlInstructions: 'Copia questo URL nel dashboard ElevenLabs in Conversational AI → Webhooks (post-call):',
        copyUrl: 'Copia URL',
        copied: 'Copiato',
        twilioSidLabel: 'Twilio Account SID',
        twilioTokenLabel: 'Twilio Auth Token',
        twilioDesc: 'Collega il tuo account Twilio per effettuare chiamate in uscita.',
        importNumberDesc: 'Inserisci un numero del tuo account Twilio (acquistato o verificato come caller ID): verrà collegato a ElevenLabs per le chiamate. Formato E.164, es. +393331234567 — spazi e trattini vengono rimossi automaticamente.',
        testCallDesc: 'Facoltativamente effettua una chiamata di test per verificare la configurazione. La chiamata va al numero di test interno.',
        testCallNumber: 'Numero di test',
        testCallAgent: 'Agente vocale',
        testCallAgentHint: 'La chiamata di test viene effettuata da uno dei tuoi agenti vocali installati.',
        testCallSelectPlaceholder: 'Seleziona un agente…',
        testCallNoAgents:
          'Non hai ancora installato un agente vocale, quindi la chiamata di test non è disponibile qui. La configurazione è comunque completa: clicca Fine, poi installa un agente dalla sezione Agenti vocali — da lì potrai fare una chiamata di prova.',
        startTestCall: 'Avvia chiamata di test',
        testCallStarted: 'Chiamata di test avviata',
        completeTitle: 'Configurazione completata',
        completeDesc: 'I tuoi agenti vocali sono pronti. Puoi modificare queste impostazioni in qualsiasi momento.',
        completeNextStep: 'La connessione è configurata. Prossimo passo: installa il tuo primo agente vocale per iniziare a ricevere ed effettuare chiamate.',
        goToAgents: 'Installa un agente vocale',
        goToNumbers: 'Gestisci numeri di telefono',
        saveError: 'Impossibile salvare. Riprova.',
        errors: {
          twilioRejected:
            'Twilio ha rifiutato la connessione. Controlla sul tuo account Twilio:\n- Account SID e Auth Token corretti (copiali dalla Console Twilio)\n- L\'account è attivo e ha credito — se è sospeso o senza fondi, ricaricalo da Billing → Add funds\n- Il numero appartiene a questo account Twilio',
          providerAuth:
            'ElevenLabs ha rifiutato la API key (401). Verifica la chiave BYOK allo step "Chiave API ElevenLabs".',
          providerQuota:
            'Quota ElevenLabs esaurita (402). Verifica il piano del tuo account ElevenLabs.',
          providerGeneric: 'La chiamata al provider vocale è fallita. Riprova tra qualche minuto.',
          numberAlreadyImported:
            'Questo numero è già importato e attivo — non serve reimportarlo. Clicca Continua per proseguire.',
        },
      },
      byok: {
        title: 'Credenziali provider',
        keyConfigured: 'Chiave API configurata',
        keyMissing: 'Chiave API non configurata',
        subtitle: 'Credenziali dei provider vocali (BYOK). Le chiavi sono salvate cifrate e usate solo lato server.',
        updatedAt: 'Aggiornata il {date}',
        edit: 'Modifica',
        configure: 'Configura',
        active: 'attiva',
        paused: 'in pausa',
        notConfigured: 'non configurata',
        providerElevenlabsKey: 'Chiave API ElevenLabs',
        providerElevenlabsSecret: 'Secret webhook ElevenLabs',
        providerTwilioSid: 'Twilio Account SID',
        providerTwilioToken: 'Twilio Auth Token',
        drawerDesc: 'BYOK — la credenziale è salvata cifrata e usata lato server. Paghi direttamente il provider.',
        save: 'Salva',
        cancel: 'Annulla',
        saveError: 'Impossibile salvare la credenziale.',
        loading: 'Caricamento credenziali…',
        secretHidden: 'Valore nascosto per sicurezza',
      },
      numbers: {
        title: 'Numeri di telefono',
        import: 'Importa numero',
        label: 'Etichetta',
        inboundAgent: 'Agente inbound',
        subtitle: 'Numeri di telefono importati da ElevenLabs per chiamate in entrata e in uscita.',
        empty: 'Nessun numero di telefono importato.',
        phoneNumber: 'Numero di telefono',
        countryCode: 'Prefisso paese',
        importTitle: 'Importa un numero',
        importDesc: 'Inserisci un numero del tuo account Twilio: verrà collegato a ElevenLabs.',
        importButton: 'Importa',
        importError: 'Impossibile importare il numero.',
        sipTab: 'SIP trunk',
        sipDesc:
          'Collega un numero esistente del tuo centralino via SIP trunk, senza spostarlo su Twilio. Chiedi al tuo fornitore di telefonia le credenziali del trunk.',
        sipInboundTitle: 'In entrata (ricevere chiamate)',
        sipOutboundTitle: 'In uscita (effettuare chiamate)',
        sipUsername: 'Username SIP',
        sipPassword: 'Password SIP',
        sipAddressPlaceholder: 'Indirizzo del trunk (es. sip.provider.com)',
        sipHint: 'Compila almeno una sezione: in entrata per rispondere, in uscita per chiamare.',
        saveError: 'Impossibile salvare le modifiche.',
        deleteConfirm: 'Rimuovere questo numero? L\'instradamento in entrata si interromperà immediatamente.',
        delete: 'Rimuovi',
        noInboundAgent: 'Nessun agente inbound',
        save: 'Salva',
        edit: 'Modifica',
        cancel: 'Annulla',
        loading: 'Caricamento numeri…',
        phonePlaceholder: '+39...',
        labelPlaceholder: 'es. Reception, Assistenza',
        disabled: 'disabilitato',
      },
      list: {
        title: 'Agenti vocali',
        subtitle: 'Agenti AI che effettuano e rispondono a chiamate telefoniche tramite ElevenLabs.',
        count: '{count} agenti',
        countOne: '1 agente',
        empty: 'Nessun agente vocale',
        emptyDesc: 'Installa un template vocale pronto per iniziare. La creazione della voce avviene lato server.',
        installFromTemplate: 'Installa da template',
        installTemplate: 'Installa',
        installing: 'Installazione…',
        installError: 'Impossibile installare il template.',
        pickerTitle: 'Template vocali',
        pickerSubtitle: 'Scegli un template da installare come agente vocale.',
        pickerEmpty: 'Nessun template vocale disponibile.',
        off: 'Spento',
        openDetail: 'Apri',
        openBindings: 'Binding',
      },
      calls: {
        title: 'Storico chiamate',
        subtitle: 'Chiamate effettuate e ricevute dai tuoi agenti vocali.',
        duration: 'Durata',
        transcript: 'Trascrizione',
        viewTranscript: 'Vedi trascrizione',
        summary: 'Riassunto',
        noTranscript: 'Nessuna trascrizione disponibile.',
        recording: 'Registrazione',
        playRecording: 'Ascolta la registrazione',
        audioError: 'Impossibile caricare la registrazione.',
        noSummary: 'Nessun riassunto disponibile.',
        status: 'Stato',
        empty: 'Nessuna chiamata registrata.',
        filterStatus: 'Stato',
        filterTemplate: 'Agente',
        filterAll: 'Tutti',
        filterFrom: 'Da',
        filterTo: 'A',
        columnDate: 'Data',
        columnTitle: 'Chiamata',
        columnCost: 'Costo',
        test: 'Test',
        credits: '{n} crediti',
        close: 'Chiudi',
        contactId: 'Contatto',
      },
      hitl: {
        title: 'Approvazioni in attesa',
        subtitle: 'Chiamate in uscita in attesa della tua approvazione prima di comporre.',
        approve: 'Approva',
        reject: 'Rifiuta',
        approveBatch: 'Approva selezionate',
        rejectReason: 'Motivo del rifiuto',
        rejectPlaceholder: 'Motivo opzionale…',
        confirmReject: 'Conferma rifiuto',
        cancel: 'Annulla',
        pendingCount: '{count} chiamate in attesa di approvazione',
        empty: 'Nessuna chiamata in attesa di approvazione.',
        selectAll: 'Seleziona tutte',
        deselectAll: 'Deseleziona',
        selectedCount: '{count} selezionate',
        groupBinding: 'Binding',
        startedAt: 'Richiesta',
        requestedBy: 'Richiesta da',
        actionError: "Non è stato possibile completare l'azione.",
      },
      usage: {
        title: 'Consumi e budget',
        subtitle: 'Crediti vocali consumati e limiti di budget mensili.',
        monthQuota: 'Quota mensile',
        dailyBudget: 'Budget giornaliero',
        alertThreshold: 'Soglia di avviso',
        byTemplate: 'Per template',
        currentMonth: 'Mese corrente',
        creditsUsed: 'Crediti usati',
        callCount: 'Chiamate',
        totalUsd: 'Costo stimato',
        history: 'Ultimi 6 mesi',
        month: 'Mese',
        quotaUsed: '{used} di {max} crediti',
        noBudget: 'Nessun budget configurato — consumo illimitato.',
        budgetTitle: 'Limiti di budget',
        maxCreditsDay: 'Crediti massimi al giorno',
        maxCreditsMonth: 'Crediti massimi al mese',
        save: 'Salva budget',
        saving: 'Salvataggio…',
        saved: 'Budget salvato',
        saveError: 'Impossibile salvare il budget.',
        empty: 'Nessun consumo registrato.',
      },
      dial: {
        button: 'Chiama con AI',
        cardTitle: 'Chiamate AI',
        pickTemplate: 'Scegli un template',
        pickTemplateHint: 'Seleziona l\'agente vocale da usare per questa chiamata.',
        confirm: 'Avvia chiamata',
        dialing: 'Avvio…',
        queued: 'Chiamata in coda. Partirà a breve.',
        consentMissing: 'Questo contatto non ha dato il consenso a essere contattato telefonicamente.',
        tosMissing: 'Devi accettare i termini d\'uso degli agenti vocali prima di chiamare.',
        tosLink: 'Vai alla configurazione voce',
        noTemplates: 'Nessun template vocale disponibile per la chiamata.',
        error: 'Impossibile avviare la chiamata.',
        empty: 'Nessuna chiamata AI per questo contatto.',
      },
      agent: {
        detail: 'Dettaglio agente vocale',
        subtitle: 'Configura la voce, controlla il prompt e gestisci HITL per binding.',
        voiceId: 'Voce',
        disclaimerVerified: 'Disclaimer verificato',
        disclaimerNotVerified: 'Disclaimer non verificato',
        promptTitle: 'Prompt',
        promptReadonly: 'Il prompt è definito dal template installato ed è di sola lettura qui.',
        promptEditDesc:
          'Personalizza il comportamento dell\'agente: descrivi il caso d\'uso, il tono di voce e le informazioni da raccogliere. Le modifiche vengono applicate al motore vocale al salvataggio.',
        promptCustomizedBadge: 'Personalizzato',
        firstMessageLabel: 'Messaggio iniziale',
        firstMessagePlaceholder: 'Lascia vuoto per usare il messaggio del template',
        firstMessageHint:
          'Obbligo di legge: il messaggio deve presentare l\'assistente come virtuale e avvisare che la chiamata viene registrata.',
        promptSave: 'Salva prompt',
        promptSaved: 'Salvato',
        promptDisclaimerError:
          'Il messaggio iniziale deve presentare l\'assistente come virtuale e avvisare che la chiamata viene registrata (es. "sono l\'assistente virtuale di... la chiamata viene registrata...").',
        transferTitle: 'Trasferimento a operatore',
        transferDesc:
          'Se configurato, l\'agente può passare la chiamata in corso a questo numero quando il cliente chiede una persona o la richiesta non è gestibile.',
        transferPhoneLabel: 'Numero dell\'operatore (formato E.164)',
        transferInvalidPhone: 'Inserisci un numero in formato internazionale, es. +393331234567',
        transferDisabledHint: 'Vuoto = trasferimento disattivato.',
        transferModeLabel: 'Modalità di trasferimento',
        transferModeConference: 'Warm transfer (conferenza con messaggio di contesto)',
        transferModeBlind: 'Blind transfer (diretto, mantiene il numero del chiamante)',
        transferConferenceHint:
          'L\'operatore entra in conferenza e ascolta un breve messaggio di contesto prima di parlare col cliente.',
        transferBlindHint:
          'La chiamata viene inoltrata direttamente; l\'operatore vede il numero originale del chiamante.',
        transferSave: 'Salva trasferimento',
        transferSaved: 'Salvato',
        transferRubricaDesc:
          'Rubrica reparti: ogni voce è un numero a cui l\'agente può passare la chiamata in corso, con la sua condizione. Lista vuota = trasferimento disattivato.',
        transferLabelPlaceholder: 'Reparto/persona (es. Assistenza)',
        transferConditionPlaceholder: 'Quando trasferire qui (es. "domande su fatture") — opzionale',
        transferAdd: 'Aggiungi destinazione',
        transferRemove: 'Rimuovi',
        hoursTitle: 'Orari di apertura',
        hoursDesc:
          'Fuori da questi orari l\'agente comunica che gli uffici sono chiusi e prende un messaggio (che diventa un task) invece di trasferire. Disattivato = sempre aperto.',
        hoursEnable: 'Attiva orari di apertura',
        hoursDayLabels: 'Lun,Mar,Mer,Gio,Ven,Sab,Dom',
        hoursNoDays: 'Seleziona almeno un giorno',
        hoursSave: 'Salva orari',
        hoursSaved: 'Salvato',
        triageTitle: 'Passaggio ad assistenti specializzati',
        triageDesc:
          'Regole di smistamento: l\'agente passa la conversazione a un altro assistente vocale (stessa chiamata) quando la condizione corrisponde — es. un centralinista che passa all\'assistente prenotazioni.',
        triageNoTargets: 'Nessun altro agente vocale attivo a cui passare. Installa prima un altro agente.',
        triageEmptyHint: 'Nessuna regola: l\'agente non passa mai ad altri assistenti.',
        triageSelectAgent: 'Scegli assistente...',
        triageConditionPlaceholder: 'Quando passare (es. "vuole prenotare un appuntamento")',
        triageAdd: 'Aggiungi regola',
        triageMissingCondition: 'Ogni regola richiede una condizione',
        voicePick: 'Scegli una voce',
        voiceFallbackHint: 'Configura la chiave API ElevenLabs per caricare le voci disponibili. Puoi inserire un ID voce manualmente.',
        voiceIdManual: 'ID voce',
        voiceSave: 'Salva voce',
        voiceSaved: 'Voce salvata',
        voiceSaveError: 'Impossibile salvare la voce.',
        voiceSearchPlaceholder: 'Cerca voci (es. female, italian...)',
        voiceNoResults: 'Nessuna voce corrisponde alla ricerca.',
        voicePlayPreview: 'Ascolta esempio',
        voiceStopPreview: 'Ferma esempio',
        voiceNoPreview: 'Nessun esempio disponibile per questa voce',
        voiceLangIt: 'Italiane',
        voiceLangAll: 'Tutte le voci',
        voiceNoItalianHint:
          'Nessuna voce italiana nel tuo account: aggiungile dalla libreria ElevenLabs qui sotto.',
        testTitle: 'Prova l\'agente dal browser',
        testDesc: 'Parla subito con l\'agente usando il microfono, senza telefonata. Zero costi di telefonia, ideale per mettere a punto prompt e voce.',
        testStart: 'Avvia prova vocale',
        testConnecting: 'Connessione...',
        testStop: 'Termina prova',
        testListening: 'In ascolto... parla pure',
        testSpeaking: 'L\'agente sta parlando',
        testError: 'Impossibile avviare la sessione di prova.',
        testMicDenied: 'Accesso al microfono negato: consentilo nel browser per fare la prova.',
        testToolsHint: 'Nella prova dal browser gli strumenti CRM (prenotazioni, messaggi, trasferimenti) non sono attivi: per testarli fai una chiamata reale.',
        libraryToggle: 'Aggiungi voci dalla libreria ElevenLabs',
        libraryDesc: 'Cerca tra migliaia di voci condivise, ascolta l\'esempio e aggiungi al tuo account quelle che ti piacciono.',
        librarySearchPlaceholder: 'Cerca per nome o stile...',
        libraryAnyLanguage: 'Qualsiasi lingua',
        libraryAdd: 'Aggiungi',
        libraryAdded: 'Aggiunta',
        speedLabel: 'Velocità di risposta',
        speedBalanced: 'Bilanciata (qualità maggiore)',
        speedFast: 'Rapida (latenza minima)',
        bookingTitle: 'Prenotazioni',
        bookingDesc: 'Collega l\'agente ai tuoi tipi evento di prenotazione: proporrà gli stessi orari del tuo link di prenotazione pubblico, e conferme, link videochiamata e promemoria partono in automatico.',
        bookingVideoLabel: 'Tipo evento per videochiamata',
        bookingOnsiteLabel: 'Tipo evento per appuntamento in sede',
        bookingNone: 'Non offerta',
        bookingNoEventTypes: 'Nessun tipo evento di prenotazione trovato. Creane uno in Calendario → Prenotazioni, poi torna qui per collegarlo.',
        bookingFallbackHint: 'Senza tipi evento collegati, l\'agente propone gli slot di lavoro del calendario CRM (comportamento precedente).',
        bookingWhatsappLabel: 'Numero WhatsApp per link e conferme',
        bookingWhatsappAuto: 'Automatico (numero attivo dell\'organizzazione)',
        bookingWhatsappHint: 'L\'agente invia su WhatsApp il link di prenotazione per le videochiamate e la conferma automatica per gli appuntamenti in sede.',
        bookingWhatsappMissing: 'Nessun numero WhatsApp collegato: l\'agente non può inviare link o conferme. Collegane uno in Impostazioni → WhatsApp.',
        bookingSave: 'Salva prenotazioni',
        bookingSaved: 'Salvato',
        hitlTitle: 'Approvazione per binding (HITL)',
        hitlDesc: 'Se attivo, le chiamate in uscita di questo binding attendono approvazione manuale prima di comporre.',
        hitlOn: 'Chiedi prima di chiamare',
        hitlOff: 'Chiama automaticamente',
        noBindings: 'Nessun binding per questo agente.',
        manageBindings: 'Gestisci binding',
        back: 'Torna agli agenti vocali',
        notFound: 'Agente vocale non trovato.',
        loading: 'Caricamento…',
      },
      bindings: {
        title: 'Binding',
        subtitle: 'Trigger che attivano questo agente vocale.',
        back: "Torna all'agente",
      },
      tos: {
        checkbox1: 'Confermo di avere una base giuridica valida: consenso espresso e specifico per le chiamate promozionali automatizzate (art. 130 Codice Privacy); per le sole comunicazioni di servizio mi baso su contratto o legittimo interesse.',
        checkbox2: 'Prima di ogni chiamata commerciale verificherò il Registro Pubblico delle Opposizioni e rispetterò le richieste di opt-out e cancellazione.',
        checkbox3: 'La chiamata dichiarerà da subito di essere un assistente vocale AI e di essere registrata: non rimuoverò questi avvisi dai messaggi (AI Act art. 50 + informativa registrazione).',
        checkbox4: 'Fornirò agli interessati l\'informativa privacy (art. 13 GDPR) e agisco come titolare del trattamento dei dati.',
        checkbox5: 'Userò un numero chiamante identificabile e riconducibile a me (niente spoofing) e, se svolgo telemarketing commerciale, sono iscritto al ROC.',
        checkbox6: 'Utilizzerò le mie credenziali ElevenLabs e Twilio (BYOK) e accetto i loro termini di servizio, inclusa l\'elaborazione dei dati anche fuori dall\'UE.',
        disclaimer: 'Queste conferme sono un promemoria degli obblighi principali (Italia/UE) e non sostituiscono un parere legale: la responsabilità della conformità — consensi, informative, registri, contratti — resta della tua organizzazione.',
      },
    },
    twoFactor: {
      title: 'Autenticazione a Due Fattori',
      description: 'Proteggi il tuo account con un secondo livello di sicurezza',
      status: {
        active: '2FA Attivo',
        activeMessage: 'Il tuo account è protetto',
        notConfigured: '2FA Non Configurato',
        notConfiguredMessage: 'Configura almeno un metodo per proteggere il tuo account'
      },
      methods: {
        totp: {
          title: 'App Authenticator',
          configuredOn: 'Configurato il',
          notConfigured: 'Non configurato'
        },
        email: {
          title: 'Codice via Email',
          configuredOn: 'Configurato il',
          notConfigured: 'Non configurato'
        }
      },
      buttons: {
        configure: 'Configura',
        disable: 'Disabilita',
        required: 'Obbligatorio'
      },
      badges: {
        active: 'Attivo'
      },
      messages: {
        totpConfigured: 'App Authenticator configurato con successo',
        emailConfigured: 'Email OTP configurato con successo',
        methodDisabled: 'disabilitato',
        disableError: 'Errore nella disabilitazione',
        mustHaveOneMethod: 'Devi avere almeno un metodo 2FA attivo',
        superadminCannotDisable: 'I superadmin non possono disabilitare l\'autenticazione via app'
      },
      confirm: {
        title: 'Disabilita',
        message: 'Sei sicuro di voler disabilitare questo metodo di autenticazione?',
        confirmButton: 'Disabilita',
        cancelButton: 'Annulla'
      },
      securityInfo: {
        title: 'Informazioni sulla sicurezza',
        authenticatorOffline: 'L\'app authenticator genera codici anche offline ed è il metodo più sicuro',
        emailCodeExpiry: 'I codici email scadono dopo 5 minuti',
        lostAccess: 'Se perdi l\'accesso al 2FA, contatta un amministratore per il reset',
        superadminRequired: 'Come superadmin, l\'autenticazione via app è obbligatoria e non può essere disabilitata'
      },
      accountLabel: 'Account'
    },
    gdpr: {
      deleteAccount: {
        modalTitle: 'Elimina il tuo account',
        modalTitleSuccess: 'Account disattivato',
        warningTitle: 'Stai per eliminare il tuo account',
        warningMessage: 'Questa azione è irreversibile dopo il periodo di grazia.',
        consequences: 'Conseguenze:',
        consequencesList: {
          deactivated: 'Il tuo account verrà disattivato immediatamente',
          deletion: 'Cancellazione definitiva dopo 30 giorni',
          dataDeleted: 'I tuoi dati personali verranno eliminati permanentemente',
          email: 'Riceverai un\'email per annullare (valida 7 giorni)'
        },
        understoodCheckbox: 'Ho capito le conseguenze e voglio procedere con la cancellazione del mio account',
        exportCheckbox: 'Voglio esportare i miei dati prima di eliminare l\'account',
        confirmEmail: 'Conferma la tua email',
        confirmEmailDescription: 'Digita la tua email per confermare che sei tu',
        enterPassword: 'Inserisci la password',
        confirmIdentity: 'Conferma la tua identità inserendo la password',
        verify2FA: 'Verifica 2FA',
        enter2FACode: 'Inserisci il codice a 6 cifre dalla tua app di autenticazione',
        code2FA: 'Codice 2FA',
        requestSent: 'Richiesta inviata',
        accountDeactivated: 'Il tuo account è stato disattivato.',
        permanentDeletion: 'Cancellazione definitiva:',
        cancelUntil: 'Puoi annullare entro:',
        emailSentInfo: 'Ti abbiamo inviato un\'email con un link per annullare la richiesta se cambi idea.',
        errors: {
          mustConfirm: 'Devi confermare di aver compreso le conseguenze',
          emailMismatch: 'L\'email non corrisponde al tuo account',
          enterPassword: 'Inserisci la password',
          enter6DigitCode: 'Inserisci il codice a 6 cifre',
          deletionError: 'Errore durante la cancellazione'
        },
        buttons: {
          cancel: 'Annulla',
          back: 'Indietro',
          continue: 'Continua',
          deleteAccount: 'Elimina Account',
          deleting: 'Eliminazione...',
          close: 'Chiudi'
        }
      },
      dataExport: {
        modalTitle: 'Esporta i tuoi dati',
        gdprTitle: 'GDPR Art. 20 - Diritto alla Portabilità',
        gdprDescription: 'Scarica tutti i tuoi dati personali in formato JSON, leggibile e riutilizzabile.',
        includedData: 'Dati inclusi nell\'export:',
        categories: {
          personalProfile: 'Profilo Personale',
          personalProfileDesc: 'Email, nome, telefono, avatar',
          organization: 'Organizzazione',
          organizationDesc: 'Nome azienda, ruolo, data iscrizione',
          preferences: 'Preferenze',
          preferencesDesc: 'Impostazioni e configurazioni personali',
          consents: 'Consensi',
          consentsDesc: 'Privacy policy, marketing, cookie (con date)',
          activityLog: 'Log Attività',
          activityLogDesc: 'Ultime 100 azioni effettuate'
        },
        notIncludedTitle: 'Dati aziendali non inclusi',
        notIncludedDesc: 'Clienti, lead, trattative e altri dati aziendali non sono inclusi in questo export perché sono dati di terzi gestiti dalla tua organizzazione.',
        notIncludedHint: 'Per esportare i dati aziendali, vai su',
        settingsImportExport: 'Impostazioni → Import/Export',
        lastExport: 'Ultimo export:',
        totalExports: 'export totali',
        noExportsYet: 'Non hai ancora effettuato nessun export.',
        exportComplete: 'Export completato! Il file è stato scaricato.',
        rateLimitWarning: 'Puoi effettuare un solo export al giorno. Riprova domani.',
        buttons: {
          close: 'Chiudi',
          download: 'Scarica i miei dati',
          exporting: 'Esportazione...'
        },
        errors: {
          exportError: 'Errore durante l\'esportazione'
        }
      }
    },
    notificationPreferences: {
      title: 'Preferenze Notifiche',
      description: 'Personalizza come e quando ricevere le notifiche per ogni tipo di evento',
      categories: {
        tasks: 'Task',
        leads: 'Lead',
        deals: 'Offerte',
        calendar: 'Calendario',
        clients: 'Clienti',
        projects: 'Progetti',
        whatsapp: 'WhatsApp',
        invoices: 'Fatture',
        partners: 'Partner',
        tickets: 'Ticket',
        inventory: 'Magazzino',
        orders: 'Ordini',
        ecommerce: 'E-commerce',
        newsletter: 'Newsletter',
        system: 'Sistema'
      },
      digestFrequency: {
        title: 'Frequenza Email Digest',
        description: 'Le notifiche con digest attivo verranno raggruppate e inviate con questa frequenza',
        daily: 'Giornaliero (alle 8:00)',
        weekly: 'Settimanale (lunedì alle 8:00)'
      },
      pushNotifications: {
        title: 'Notifiche Push del Browser',
        blocked: 'Le notifiche push sono bloccate. Abilita i permessi dalle impostazioni del browser.',
        enable: 'Abilita le notifiche push per ricevere avvisi anche quando non sei sulla pagina',
        enableButton: 'Abilita Push'
      },
      tableHeaders: {
        notification: 'Notifica',
        inApp: 'In-App',
        email: 'Email',
        digest: 'Digest',
        push: 'Push'
      },
      info: {
        inApp: 'Notifiche visibili nella campanella in alto a destra',
        email: 'Ricevi un\'email per ogni notifica in tempo reale',
        digest: 'Ricevi un riepilogo periodico delle notifiche',
        push: 'Notifiche native del browser anche quando non sei sulla pagina'
      },
      messages: {
        loadError: 'Errore nel caricamento delle preferenze',
        saveError: 'Errore nel salvataggio'
      }
    },
    clientTags: {
      title: 'Tag Clienti',
      description: 'Gestisci i tag per categorizzare clienti e lead. Trascina per riordinare.',
      newTag: 'Nuovo Tag',
      emptyState: {
        title: 'Nessun tag configurato',
        description: 'Crea il tuo primo tag per iniziare a categorizzare clienti e lead.',
        createFirst: 'Crea il primo tag'
      },
      form: {
        newTitle: 'Nuovo Tag',
        editTitle: 'Modifica Tag',
        nameLabel: 'Nome Tag *',
        namePlaceholder: 'Es. Cliente VIP, Lead Caldo, ecc.',
        descriptionLabel: 'Descrizione',
        descriptionPlaceholder: 'Descrizione opzionale del tag',
        colorLabel: 'Colore Tag',
        customColor: 'Colore personalizzato:',
        activeLabel: 'Tag attivo',
        cancel: 'Annulla',
        saving: 'Salvando...',
        update: 'Aggiorna',
        create: 'Crea Tag'
      },
      actions: {
        hideTag: 'Nascondi tag',
        showTag: 'Mostra tag',
        editTag: 'Modifica tag',
        deleteTag: 'Elimina tag'
      },
      messages: {
        orderUpdated: 'Ordine tag aggiornato',
        tagUpdated: 'Tag aggiornato con successo',
        tagCreated: 'Tag creato con successo',
        tagDeleted: 'Tag eliminato con successo',
        tagActivated: 'Tag attivato',
        tagDeactivated: 'Tag disattivato',
        saveError: 'Errore durante il salvataggio del tag',
        deleteConfirm: 'Sei sicuro di voler eliminare questo tag? Questa azione rimuoverà il tag da tutti i clienti.'
      }
    },
    salesTargets: {
      title: 'Obiettivi di Vendita',
      description: 'Imposta e gestisci gli obiettivi di fatturato per il tuo team di vendita',
      addTarget: 'Aggiungi Obiettivo',
      editTarget: 'Modifica Obiettivo',
      periodTypes: {
        yearly: 'Annuale',
        quarterly: 'Trimestrale',
        monthly: 'Mensile'
      },
      form: {
        salesRep: 'Referente Commerciale',
        selectUser: 'Seleziona un utente',
        targetAmount: 'Importo Obiettivo',
        month: 'Mese',
        quarter: 'Trimestre',
        year: 'Anno',
        notes: 'Note',
        cancel: 'Annulla',
        createTarget: 'Crea Obiettivo',
        updateTarget: 'Aggiorna Obiettivo'
      },
      table: {
        salesRep: 'Referente Commerciale',
        targetAmount: 'Importo Obiettivo',
        period: 'Periodo',
        notes: 'Note',
        actions: 'Azioni'
      },
      quarters: {
        q1: 'Q1 (Gen-Mar)',
        q2: 'Q2 (Apr-Giu)',
        q3: 'Q3 (Lug-Set)',
        q4: 'Q4 (Ott-Dic)'
      },
      emptyState: {
        title: 'Nessun obiettivo di vendita trovato per questo periodo',
        description: 'Crea obiettivi per monitorare le performance del tuo team di vendita'
      },
      messages: {
        loadUsersError: 'Impossibile caricare gli utenti',
        monthlyUpdated: 'Obiettivo mensile aggiornato con successo',
        monthlyCreated: 'Obiettivo mensile creato con successo',
        quarterlyUpdated: 'Obiettivo trimestrale aggiornato con successo',
        quarterlyCreated: 'Obiettivo trimestrale creato con successo',
        yearlyUpdated: 'Obiettivo annuale aggiornato con successo',
        yearlyCreated: 'Obiettivo annuale creato con successo',
        duplicateError: 'Esiste già un obiettivo per questo utente in questo periodo',
        saveError: 'Impossibile salvare l\'obiettivo di vendita',
        deleteSuccess: 'Obiettivo di vendita eliminato con successo',
        deleteError: 'Impossibile eliminare l\'obiettivo di vendita',
        organizationRequired: 'ID Organizzazione richiesto'
      },
      confirmDelete: {
        title: 'Elimina Obiettivo di Vendita',
        message: 'Sei sicuro di voler eliminare questo obiettivo di vendita? Questa azione non può essere annullata.',
        confirm: 'Elimina',
        cancel: 'Annulla'
      },
      confirmUpdate: {
        title: 'Obiettivo Esistente',
        messageMonthly: 'Esiste già un obiettivo mensile per questo utente nel periodo {period}. Vuoi aggiornarlo con il nuovo importo?',
        messageQuarterly: 'Esiste già un obiettivo trimestrale per questo utente nel periodo {period}. Vuoi aggiornarlo con il nuovo importo?',
        messageYearly: 'Esiste già un obiettivo annuale per questo utente per l\'anno {year}. Vuoi aggiornarlo con il nuovo importo?',
        confirm: 'Aggiorna',
        cancel: 'Annulla'
      },
      autoGenerate: {
        quarterlyCreated: '{count} obiettivi trimestrali creati con successo',
        monthlyCreated: '{count} obiettivi mensili creati con successo',
        quarterlyExist: 'Esistono già obiettivi trimestrali per questo utente nell\'anno {year}. Impossibile generare automaticamente.',
        monthlyExist: 'Esistono già obiettivi mensili per questo utente nel periodo {period}. Impossibile generare automaticamente.',
        error: 'Errore durante la generazione automatica degli obiettivi',
        fromYearly: 'Generato automaticamente da obiettivo annuale',
        fromQuarterly: 'Generato automaticamente da obiettivo trimestrale Q{quarter}'
      },
      unknownUser: 'Utente Sconosciuto',
      autoGenerateDialog: {
        title: 'Generazione Automatica Obiettivi',
        yearLabel: 'Anno {year}',
        quarterLabel: 'Q{quarter} {year}',
        targetYearly: 'Obiettivo Annuale',
        targetQuarterly: 'Obiettivo Trimestrale',
        generateQuarterly: 'Genera Obiettivi Trimestrali',
        generateQuarterlyDesc: 'Crea 4 obiettivi trimestrali di {amount} ciascuno',
        generateMonthly: 'Genera Obiettivi Mensili',
        generateMonthlyYearly: 'Crea 12 obiettivi mensili di {amount} ciascuno',
        generateMonthlyQuarterly: 'Crea 3 obiettivi mensili di {amount} ciascuno',
        selectOption: 'Seleziona almeno un\'opzione per procedere con la generazione automatica.',
        cancel: 'Annulla',
        generate: 'Genera Obiettivi',
        generating: 'Generazione...',
        months: {
          january: 'Gennaio',
          february: 'Febbraio',
          march: 'Marzo',
          april: 'Aprile',
          may: 'Maggio',
          june: 'Giugno',
          july: 'Luglio',
          august: 'Agosto',
          september: 'Settembre',
          october: 'Ottobre',
          november: 'Novembre',
          december: 'Dicembre'
        }
      }
    },
    subscription: {
      title: 'Il tuo abbonamento',
      trial: {
        active: 'Prova Gratuita Attiva',
        lastDay: 'Ultimo giorno di prova!',
        daysRemaining: 'Solo {days} giorni rimasti',
        expiresOn: 'Scade il',
        activateToday: 'Attiva oggi per non perdere l\'accesso',
        daysLeft: 'Hai ancora {days} giorni per esplorare Flusia'
      },
      status: {
        active: 'Attivo',
        cancelling: 'In cancellazione',
        cancelled: 'Cancellato'
      },
      paymentFailed: {
        title: 'Pagamento non riuscito',
        description: 'L\'ultimo pagamento non è andato a buon fine.',
        updateBefore: 'Aggiorna entro il {date} per evitare la sospensione.',
        updatePayment: 'Aggiorna Metodo di Pagamento'
      },
      cancellingAlert: {
        title: 'Abbonamento in cancellazione',
        activeUntil: 'Il tuo abbonamento resterà attivo fino al',
        loseAccess: '. Dopo questa data, perderai l\'accesso alle funzionalità premium.',
        reactivate: 'Riattiva Abbonamento'
      },
      info: {
        plan: 'Piano',
        price: 'Prezzo',
        users: 'Utenti',
        nextRenewal: 'Prossimo rinnovo',
        endsOn: 'Termina il',
        perMonth: '/mese',
        perYear: '/anno'
      },
      actions: {
        managePayment: 'Gestisci pagamento',
        cancelSubscription: 'Annulla abbonamento',
        reactivateSubscription: 'Riattiva abbonamento',
        comparePlans: 'Confronta altri piani'
      },
      invoices: {
        title: 'Fatture recenti',
        viewAll: 'Vedi tutte',
        noInvoices: 'Nessuna fattura disponibile',
        paid: 'Pagata',
        downloadPdf: 'Scarica PDF'
      },
      messages: {
        activated: 'Abbonamento attivato! Grazie per aver scelto Flusia.',
        checkoutCancelled: 'Checkout annullato. Puoi riprovare quando vuoi.',
        cancelled: 'Abbonamento annullato',
        reactivated: 'Abbonamento riattivato!',
        cancelError: 'Errore durante l\'annullamento',
        reactivateError: 'Errore durante la riattivazione'
      },
      cancelDialog: {
        title: 'Annulla abbonamento',
        message: 'Sei sicuro di voler annullare il tuo abbonamento? Il tuo abbonamento resterà attivo fino al {date}. Dopo questa data, perderai l\'accesso alle funzionalità premium. Potrai sempre riattivarlo prima della scadenza.',
        confirm: 'Conferma annullamento',
        confirming: 'Annullamento...',
        cancel: 'Mantieni abbonamento'
      },
      help: {
        questions: 'Hai domande? Scrivici a'
      }
    },
    whatsappSetup: {
      title: 'Configura WhatsApp',
      subtitle: 'Inserisci le credenziali della tua istanza WAHA',
      wahaUrl: {
        label: 'URL Istanza WAHA',
        placeholder: 'https://waha.example.com',
        hint: "L'URL della tua istanza WAHA (senza trailing slash)"
      },
      apiKey: {
        label: 'API Key',
        placeholder: '••••••••••••••••••••••',
        hint: 'La tua API Key WAHA'
      },
      buttons: {
        cancel: 'Annulla',
        back: 'Indietro',
        testAndContinue: 'Test e Continua',
        testing: 'Test in corso...',
        goToWhatsApp: 'Vai a WhatsApp',
        saving: 'Salvataggio...'
      },
      qrStep: {
        title: 'Scansiona QR Code',
        subtitle: 'Usa WhatsApp sul tuo telefono per scansionare il QR code',
        creatingSession: 'Creazione sessione...',
        howToConnect: 'Come collegare:',
        steps: {
          step1: 'Apri WhatsApp sul tuo telefono',
          step2: 'Vai su Menu (⋮) o Impostazioni',
          step3: 'Tocca "Dispositivi collegati"',
          step4: 'Tocca "Collega un dispositivo"',
          step5: 'Scansiona questo QR code'
        },
        waitingForScan: 'In attesa di scansione...',
        connected: 'Connesso!',
        connectionFailed: 'Connessione fallita'
      },
      successStep: {
        title: 'WhatsApp Configurato!',
        subtitle: "La tua configurazione WhatsApp è completa e pronta all'uso",
        session: 'Sessione:',
        phone: 'Telefono:',
        status: 'Status:',
        statusConnected: 'Connesso',
        webhookConfigured: '🔗 Webhook configurato automaticamente',
        webhookFeatures: {
          realtime: 'Sincronizzazione messaggi in tempo reale',
          events: 'Eventi: message.any',
          auth: 'Autenticazione configurata'
        }
      },
      messages: {
        enterUrlAndKey: 'Inserisci URL e API Key',
        invalidUrl: 'URL non valido',
        connectionFailed: 'Impossibile connettersi a WAHA. Verifica URL e API Key',
        connectionSuccess: 'Connessione riuscita!',
        connectionTestError: 'Errore durante il test della connessione',
        creatingConfig: 'Creazione configurazione...',
        creatingSessionWithWebhook: 'Creazione sessione con webhook...',
        sessionCreated: 'Sessione creata! Scansiona il QR code',
        sessionCreationError: 'Errore durante la creazione della sessione',
        connectionFailedRetry: 'Connessione fallita. Riprova',
        configSaved: 'Configurazione salvata!',
        saveError: 'Errore durante il salvataggio'
      }
    },
    ficSetup: {
      title: 'Setup Guidato Fatture in Cloud',
      steps: {
        connection: 'Verifica Connessione',
        clients: 'Sincronizzazione Clienti',
        invoices: 'Fatture Storiche',
        complete: 'Completato'
      },
      connectionStep: {
        title: 'Verifica Connessione',
        subtitle: 'Controlliamo che la connessione a Fatture in Cloud sia attiva',
        notConnected: {
          title: 'Fatture in Cloud non connesso',
          description: 'Prima di procedere con il setup, è necessario connettere Fatture in Cloud. Vai alla tab "Connessione" per completare l\'autenticazione OAuth.'
        },
        connected: {
          title: 'Connessione Attiva',
          companyId: 'Company ID:',
          lastSync: 'Ultima sincronizzazione:',
          never: 'Mai'
        },
        testConnection: 'Testa Connessione',
        testing: 'Test in corso...',
        testSuccess: 'Connessione valida!',
        testFailed: 'Connessione non valida'
      },
      clientsStep: {
        title: 'Sincronizzazione Clienti',
        subtitle: 'Scegli come gestire la sincronizzazione dei clienti tra CRM e Fatture in Cloud',
        ficClients: 'Clienti in FIC',
        crmClients: 'Clienti in CRM',
        importFromFic: {
          title: 'Importa da FIC',
          description: 'Importa clienti da Fatture in Cloud nel CRM'
        },
        exportToFic: {
          title: 'Esporta a FIC',
          description: 'Esporta clienti dal CRM a Fatture in Cloud'
        },
        skip: {
          title: 'Salta',
          description: 'Sincronizzerò i clienti in seguito'
        },
        infoMessages: {
          import: 'Hai scelto di importare clienti da FIC. Completa l\'importazione nel modal per continuare.',
          export: 'Hai scelto di esportare clienti a FIC. Completa l\'esportazione nel modal per continuare.',
          skip: 'Puoi sincronizzare i clienti in qualsiasi momento dalla sezione "Clienti" nelle impostazioni.'
        }
      },
      invoicesStep: {
        title: 'Fatture Storiche',
        subtitle: 'Importa le fatture storiche da Fatture in Cloud per completare il setup',
        syncPeriod: 'Periodo di Sincronizzazione',
        startDate: 'Data Inizio',
        endDate: 'Data Fine',
        defaultPeriodInfo: {
          title: 'Periodo Predefinito',
          description: 'Per default viene impostato un periodo di 12 mesi. Puoi modificare le date per sincronizzare un periodo diverso.'
        },
        syncButton: 'Sincronizza Fatture',
        syncing: 'Sincronizzazione in corso...'
      },
      completeStep: {
        title: 'Setup Completato!',
        subtitle: 'La configurazione iniziale è stata completata con successo',
        summary: 'Riepilogo Operazioni',
        items: {
          connectionVerified: 'Connessione verificata',
          clientsImported: 'Clienti importati da FIC',
          clientsExported: 'Clienti esportati a FIC',
          clientsSkipped: 'Sincronizzazione clienti saltata',
          invoicesSynced: '{count} fatture sincronizzate'
        },
        nextSteps: {
          title: 'Prossimi Passi',
          step1: 'Vai alla sezione "Matching Fatture" per collegare le fatture ai deals',
          step2: 'Configura la sincronizzazione automatica nelle impostazioni',
          step3: 'Rivedi i mapping clienti nella tab "Clienti"'
        }
      },
      buttons: {
        cancel: 'Annulla',
        back: 'Indietro',
        next: 'Avanti',
        close: 'Chiudi'
      },
      messages: {
        loadConfigError: 'Errore durante il caricamento della configurazione',
        testSuccess: 'Connessione valida!',
        testFailed: 'Connessione non valida',
        testError: 'Errore durante il test della connessione',
        syncComplete: 'Sincronizzazione completata',
        syncError: 'Errore durante la sincronizzazione delle fatture'
      }
    },
    importChoice: {
      back: 'Indietro',
      title: 'Importa Clienti o Leads?',
      subtitle: 'Scegli se importare clienti acquisiti o potenziali leads',
      clientCard: {
        title: 'Importa Clienti',
        description: 'Importa clienti già acquisiti con tutti i dati anagrafici e contrattuali completi'
      },
      leadCard: {
        title: 'Importa Leads',
        description: 'Importa potenziali clienti da qualificare e convertire attraverso il funnel di vendita'
      }
    },
    quoteEditor: {
      tabs: {
        editor: 'Editor',
        blocks: 'Blocchi Template',
        images: 'Immagini',
        pdfSettings: 'Impostazioni PDF',
        terms: 'Termini e Condizioni',
        preview: 'Anteprima'
      },
      variables: {
        show: 'Mostra Variabili',
        hide: 'Nascondi Variabili',
        autocompleteHint: 'Usa {shortcut} nell\'editor per l\'autocomplete delle variabili.'
      },
      sections: {
        header: 'Intestazione',
        content: 'Contenuto',
        footer: 'Piè di pagina'
      },
      placeholders: {
        header: 'Inserisci il codice HTML dell\'intestazione qui...',
        content: 'Inserisci il codice HTML del contenuto qui...',
        footer: 'Inserisci il codice HTML del piè di pagina qui...'
      },
      blocks: {
        description: 'Seleziona un blocco pre-disegnato da aggiungere al tuo template. I blocchi saranno inseriti alla posizione del cursore nell\'editor.'
      },
      images: {
        description: 'Seleziona un\'immagine dalla libreria per inserirla nel template.'
      },
      preview: {
        info: 'Anteprima con dati di esempio',
        infoDescription: 'Le variabili del template sono state sostituite con dati placeholder per mostrare come apparirà il preventivo finale.',
        sectionLabels: {
          header: 'Intestazione',
          content: 'Contenuto',
          footer: 'Piè di Pagina',
          terms: 'Termini e Condizioni'
        }
      }
    },
    quoteToolbar: {
      formatting: {
        bold: 'Grassetto',
        italic: 'Corsivo',
        bulletList: 'Lista puntata',
        orderedList: 'Lista numerata'
      },
      alignment: {
        left: 'Allinea a sinistra',
        center: 'Centra',
        right: 'Allinea a destra',
        justify: 'Giustifica'
      },
      logos: {
        horizontal: 'Inserisci Logo Orizzontale',
        icon: 'Inserisci Logo Icona'
      },
      modeToggle: {
        toVisual: 'Passa a modalità visuale',
        toHtml: 'Passa a modalità HTML',
        visual: 'Visuale',
        html: 'HTML'
      }
    },
    pdfCustomField: {
      title: 'Campo PDF Personalizzabile',
      subtitle: 'Configura un testo personalizzato che apparirà nei documenti PDF generati',
      howToUse: {
        title: 'Come utilizzare il campo personalizzato:',
        item1: 'Il testo inserito qui apparirà in tutti i PDF di contratti e preventivi',
        item2: 'Puoi utilizzare variabili come {variable} nei template',
        item3: 'Supporta testo semplice e alcuni tag HTML di base',
        item4: 'Lascia vuoto per non mostrare nessun campo personalizzato'
      },
      form: {
        label: 'Testo Personalizzato PDF',
        placeholder: 'Inserisci il testo che apparirà nei PDF...'
      },
      preview: 'Anteprima:',
      buttons: {
        save: 'Salva Modifiche',
        saving: 'Salvando...',
        cancel: 'Annulla Modifiche'
      },
      messages: {
        saved: 'Campo PDF personalizzato salvato con successo',
        saveError: 'Errore durante il salvataggio del campo PDF'
      },
      templateVariable: {
        title: 'Variabile Template',
        description: 'Per utilizzare questo campo nei template di preventivi e contratti, inserisci la variabile {variable} nel punto dove vuoi che appaia il testo personalizzato.'
      }
    },
    quoteTemplates: {
      confirmDelete: {
        title: 'Sei sicuro?',
        message: 'Questa azione eliminerà permanentemente il template.'
      },
      messages: {
        deleted: 'Template eliminato con successo',
        deleteError: 'Errore durante l\'eliminazione del template',
        nameRequired: 'Il nome del template è obbligatorio',
        updated: 'Template aggiornato con successo',
        created: 'Template creato con successo',
        unknownError: 'Errore sconosciuto',
        saveError: 'Errore durante il salvataggio del template: {error}',
        variableAddedToHeader: 'Variabile {variable} aggiunta all\'intestazione',
        variableAddedToFooter: 'Variabile {variable} aggiunta al piè di pagina',
        variableAddedToContent: 'Variabile {variable} aggiunta al contenuto',
        blockInserted: 'Blocco inserito con successo',
        imageInserted: 'Immagine inserita con successo'
      }
    },
    ficSync: {
      // Toast messages
      connectFirst: 'Connetti prima Fatture in Cloud',
      tokenExpired: 'Il token di Fatture in Cloud è scaduto. Riconnetti il tuo account nelle impostazioni.',
      malformedResponse: 'Risposta malformata dal server',
      syncCompleted: 'Sincronizzazione completata con successo!',
      syncCompletedNow: 'Sincronizzazione completata! Ora sincronizza i pagamenti.',
      clientsCompleted: 'Sincronizzazione clienti completata: {imported} nuovi, {updated} aggiornati',
      clientsSyncError: 'Errore durante la sincronizzazione dei clienti',
      invoicesFetchError: 'Errore nel recuperare le fatture',
      invoicesUpdateError: 'Errore durante l\'aggiornamento delle fatture',
      invoicesSyncError: 'Errore durante la sincronizzazione delle fatture',
      partialSync: 'Sincronizzazione parziale: {invoices} fatture, {payments} pagamenti. Avvisi: {warnings}',
      fullSyncCompleted: 'Sincronizzazione completata: {invoices} fatture, {payments} pagamenti',
      syncBatch: 'Sincronizzazione {label} ({current}/{total})...',
      error: 'Errore: {message}',
      functionCallError: 'Errore durante la chiamata alla funzione',
      noDataReceived: 'Nessun dato ricevuto dalla sincronizzazione',
      savingToDatabase: 'Salvataggio nel database...',
      startingClientSync: 'Avvio sincronizzazione clienti...',
      fetchingClientsFromFIC: 'Recupero clienti da Fatture in Cloud...',
      preparingBatches: 'Preparazione {total} batch da {startYear} a {endYear}...',
      updatingBatch: 'Aggiornamento {label} ({current}/{total})...',
      updateCompleted: 'Aggiornamento completato! Ora sincronizza i pagamenti.',
      invoicesUpdated: '{total} fatture aggiornate! Ora clicca "Sincronizza Pagamenti" per completare.',
      invoicesSynced: '{total} fatture sincronizzate! Ora clicca "Sincronizza Pagamenti" per completare.',
      extractingPayments: 'Estrazione pagamenti {label} ({current}/{total})...',
      paymentsCompleted: 'Sincronizzazione pagamenti completata!',
      paymentsSynced: '{payments} pagamenti sincronizzati, {deals} deal aggiornati',
      paymentsSyncError: 'Errore durante la sincronizzazione dei pagamenti',
      fetchingInvoicesFromFIC: 'Recupero fatture da Fatture in Cloud...',
      // UI labels
      ui: {
        connectToSync: 'Connetti Fatture in Cloud per sincronizzare i dati',
        clientsSynced: 'Clienti sincronizzati',
        invoicesSynced: 'Fatture sincronizzate',
        paymentsTracked: 'Pagamenti tracciati',
        invoicesWithoutDeal: 'Fatture senza deal',
        syncClients: 'Sincronizza Clienti',
        syncClientsDesc: 'Scarica i clienti da Fatture in Cloud. Usa poi il tab "Clienti" per importarli nel CRM.',
        sync: 'Sincronizza',
        syncing: 'Sync...',
        lastSync: 'Ultima sincronizzazione:',
        syncInvoices: 'Sincronizza Fatture',
        syncInvoicesDesc: 'Importa le fatture da Fatture in Cloud nel CRM',
        fullSyncCheckbox: 'Sincronizzazione completa (tutto lo storico dal 2015)',
        destructiveOperation: 'Operazione Distruttiva',
        destructiveWarning: 'Questa operazione cancellerà tutte le {count} fatture esistenti e i loro collegamenti ai deal. I pagamenti verranno scollegati. Questa azione è irreversibile.',
        smartSyncActive: 'Sincronizzazione Intelligente Attiva',
        lastInvoice: 'Ultima fattura:',
        willSyncFrom: 'Verranno sincronizzate le fatture dal {fromDate} ad oggi',
        from: 'Dal',
        to: 'Al',
        syncNew: 'Sincronizza Nuove',
        updateExisting: 'Aggiorna Esistenti',
        updating: 'Aggiornamento...',
        updateExistingDesc: 'Aggiorna le {count} fatture per includere i dati dei pagamenti.',
        syncPayments: 'Sincronizza Pagamenti',
        syncPaymentsDesc: 'Aggiorna lo stato dei pagamenti per tutte le fatture',
        recalcCommissions: 'Ricalcola provvigioni',
        unmatchedInvoicesWarning: '{count} fatture non sono associate ad alcun deal',
        goToMatching: 'Vai al Matching',
        goToMatchingDesc: 'Vai alla sezione "Matching Fatture" per associarle manualmente',
        syncFromFIC: 'Sincronizzazione da Fatture in Cloud',
        confirmFullSync: 'Conferma Sincronizzazione Completa',
        confirmFullSyncMessage: 'ATTENZIONE: Stai per cancellare tutte le {count} fatture esistenti e i loro collegamenti ai deal. I pagamenti verranno scollegati. Questa azione è IRREVERSIBILE.\n\nSei sicuro di voler procedere?',
        proceedAnyway: 'Procedi comunque',
        cancel: 'Annulla'
      }
    },
    templateBlockLibrary: {
      categories: {
        header: 'Intestazioni',
        section: 'Sezioni',
        footer: 'Piè di Pagina'
      },
      blocks: {
        standardHeader: { name: 'Intestazione Standard', description: 'Intestazione base con info azienda e logo' },
        modernHeader: { name: 'Intestazione Moderna', description: 'Intestazione moderna con info azienda' },
        quoteInfo: { name: 'Informazioni Preventivo', description: 'Mostra dettagli e data del preventivo' },
        clientInfo: { name: 'Informazioni Cliente', description: 'Sezione dettagli cliente' },
        servicesList: { name: 'Elenco Servizi', description: 'Elenco dei servizi con descrizioni' },
        costSummary: { name: 'Riepilogo Costi', description: 'Tabella con costi e totale' },
        paymentSchedule: { name: 'Modalità di Pagamento', description: 'Sezione dedicata alle modalità di pagamento per servizio' },
        termsConditions: { name: 'Termini e Condizioni', description: 'Termini e condizioni dalle impostazioni aziendali' },
        notesSection: { name: 'Sezione Note', description: 'Sezione per note aggiuntive' },
        imageSection: { name: 'Sezione Immagine', description: 'Sezione con immagine e didascalia' },
        standardFooter: { name: 'Piè di Pagina Standard', description: 'Piè di pagina base con info azienda' },
        signatureFooter: { name: 'Piè di Pagina con Firma', description: 'Piè di pagina con aree per firma' }
      }
    },
    chunkedUploader: {
      title: 'Step 4: Upload nel Database',
      summary: {
        title: 'Riepilogo Upload',
        validRecords: 'Record Validi',
        batchSize: 'Dimensione Batch',
        totalBatches: 'Batch Totali',
        organization: 'Organizzazione'
      },
      clientState: {
        title: 'Stato Cliente (Opzionale)',
        label: 'Assegna un stato ai clienti importati:',
        placeholder: '-- Nessuno stato specifico --',
        loading: 'Caricamento stati...',
        selected: 'Stato selezionato:',
        noSelectionInfo: 'Se non selezioni alcun stato, i clienti manterranno lo stato di default o quello specificato nelle regole di trasformazione.'
      },
      userMapping: {
        title: 'Associazione Utenti (Opzionale)',
        hide: 'Nascondi',
        configure: 'Configura',
        description: 'Configura regole per assegnare automaticamente i clienti importati a specifici utenti.',
        excelColumn: 'Colonna Excel',
        selectColumn: '-- Seleziona Colonna --',
        matchType: 'Tipo Match',
        contains: 'Contiene',
        equals: 'Uguale a',
        startsWith: 'Inizia con',
        endsWith: 'Finisce con',
        valueToSearch: 'Valore da Cercare',
        valuePlaceholder: 'es. Milano, @gmail.com',
        assignToUser: 'Assegna a Utente',
        selectUser: '-- Seleziona Utente --',
        active: 'Attivo',
        remove: 'Rimuovi',
        rulePreview: 'Regola:',
        addRule: '+ Aggiungi Regola di Associazione'
      },
      leadStage: {
        title: 'Fase Lead (Opzionale)',
        label: 'Assegna una fase ai lead importati:',
        placeholder: '-- Nessuna fase specifica --',
        loading: 'Caricamento fasi...',
        selected: 'Fase selezionata:',
        info: 'La fase lead determina a che punto del processo di vendita si trova il cliente.'
      },
      leadSource: {
        title: 'Fonte Lead (Opzionale)',
        label: 'Assegna una fonte ai lead importati:',
        placeholder: '-- Nessuna fonte specifica --',
        loading: 'Caricamento fonti...',
        selected: 'Fonte selezionata:',
        info: 'La fonte lead indica da dove provengono i clienti (es. Google Ads, Facebook, Referral).',
        newButton: 'Nuova'
      },
      updateExisting: {
        title: 'Aggiorna record esistenti',
        selectFieldsLabel: 'Seleziona i campi da usare per identificare i record esistenti:',
        matchWarning: 'Un record viene considerato esistente se almeno uno dei campi selezionati corrisponde',
        selectAtLeastOne: 'Seleziona almeno un campo per il matching quando abiliti l\'aggiornamento record esistenti'
      },
      activeSelections: {
        title: 'Selezioni Attive per Import:',
        state: 'Stato:',
        stage: 'Fase:',
        source: 'Fonte:'
      },
      buttons: {
        startUpload: 'Avvia Upload',
        startImportUpdate: 'Avvia Import/Aggiornamento',
        processing: 'Elaborazione...',
        uploading: 'Caricamento...'
      },
      progress: {
        processingBatch: 'Elaborazione batch {current} di {total}',
        insertUpdateMode: '(Modalità Insert/Update)',
        uploaded: 'Caricati',
        successful: 'Successi',
        failed: 'Falliti'
      },
      results: {
        title: 'Risultati Upload',
        recordsProcessed: 'Record Processati',
        recordsUploaded: 'Record Caricati',
        recordsFailed: 'Record Falliti',
        errorsTitle: 'Errori Upload',
        exportErrors: 'Esporta Errori',
        exportTip: 'Clicca "Esporta Errori" per scaricare un Excel con tutti i record falliti'
      },
      newSourceModal: {
        title: 'Crea Nuova Fonte Lead',
        nameLabel: 'Nome Fonte *',
        namePlaceholder: 'es. Google Ads, Facebook, Fiera Milano',
        descLabel: 'Descrizione (Opzionale)',
        descPlaceholder: 'Descrizione della fonte lead...',
        cancel: 'Annulla',
        create: 'Crea Fonte',
        creating: 'Creando...'
      },
      matchFields: {
        email: 'Email',
        vatNumber: 'P.IVA',
        phone: 'Telefono',
        taxCode: 'Codice Fiscale',
        businessName: 'Ragione Sociale'
      },
      messages: {
        noErrorsToExport: 'Nessun record con errori da esportare',
        fileExported: 'File esportato: {filename}',
        exportError: 'Errore durante l\'esportazione del file',
        sourceNameRequired: 'Nome fonte lead richiesto',
        sourceCreateError: 'Errore nella creazione della fonte lead',
        sourceCreated: 'Fonte lead "{name}" creata e selezionata',
        noValidRecords: 'Nessun record valido da caricare',
        uploadError: 'Errore durante l\'upload dei dati',
        uploadComplete: 'Processo completato! {count} record {action} con successo.',
        uploadCompleteWithErrors: '{action} completato con errori: {success} successi, {failed} fallimenti.',
        importInProgress: 'Import in corso. Sei sicuro di voler chiudere? I dati potrebbero andare persi.'
      },
      leadStages: {
        newLead: 'Nuovo Lead',
        contacted: 'Contattato',
        qualified: 'Qualificato',
        proposalSent: 'Proposta Inviata',
        negotiation: 'Negoziazione',
        closedWon: 'Chiuso Vinto',
        closedLost: 'Chiuso Perso',
        closed: 'Chiuso'
      }
    },
    unifiedImportExport: {
      title: 'Import & Export Dati',
      description: 'Gestisci l\'importazione e l\'esportazione dei tuoi dati aziendali. Scegli l\'opzione più adatta alle tue esigenze.',
      mainFunctions: 'Funzioni Principali',
      recommended: 'Consigliato',
      backToOverview: '← Torna alla panoramica',
      importClientsAI: 'Import Clienti con AI',
      exportData: 'Esporta Dati',
      options: {
        importClients: {
          title: 'Import Clienti/Lead da Excel',
          description: 'Sistema potenziato con AI per importare clienti, contatti e categorie da file Excel con mapping intelligente e processing in batch.',
          badge: 'AI-Powered'
        },
        exportData: {
          title: 'Esporta Dati',
          description: 'Esporta clienti, fatture, progetti e altri dati in formato Excel, CSV o PDF per backup e analisi.'
        }
      },
      recommendedEfficiency: 'Consigliato per la massima efficienza',
      helpSection: {
        title: 'Quale sistema di import scegliere?',
        aiPowered: {
          label: 'Import AI-Powered:',
          description: 'Perfetto per Excel complessi con molte colonne. L\'AI riconosce automaticamente i tipi di dato e gestisce trasformazioni intelligenti.'
        },
        export: {
          label: 'Export Dati:',
          description: 'Per esportare i tuoi dati esistenti per backup, analisi o migrazione verso altri sistemi.'
        }
      },
      legacySystems: 'Sistemi Legacy (Non Consigliati)',
      legacy: {
        basicImport: {
          title: 'Import Base Excel',
          description: 'Sistema base per import semplici (deprecato)',
          badge: 'Legacy'
        },
        advancedImport: {
          title: 'Import Avanzato Manuale',
          description: 'Sistema con mapping manuale (deprecato)',
          badge: 'Legacy'
        },
        note: 'I sistemi legacy sono mantenuti per compatibilità ma non sono più consigliati. Usa il nuovo sistema AI-Powered per prestazioni e affidabilità superiori.'
      }
    },
    userCommissionModal: {
      title: 'Modifica Percentuale Provvigione',
      user: 'Utente',
      currentCommission: 'Commissione attuale: {rate}% (dal {date})',
      label: 'Percentuale Provvigione (%)',
      placeholder: 'Percentuale di commissione',
      description: 'Questa percentuale sarà utilizzata per calcolare le provvigioni sugli affari conclusi dall\'utente.',
      cancel: 'Annulla',
      saving: 'Salvataggio...',
      save: 'Salva',
      messages: {
        loadError: 'Impossibile caricare la percentuale di commissione',
        invalidRange: 'La percentuale deve essere compresa tra 0 e 100',
        noChanges: 'Nessuna modifica effettuata',
        updateSuccess: 'Percentuale di commissione aggiornata con successo',
        updateError: 'Errore durante l\'aggiornamento della percentuale di commissione'
      }
    },
    emailBlacklist: {
      title: 'Blacklist Email',
      subtitle: 'Gestisci le email bloccate che non riceveranno newsletter',
      addEmail: 'Aggiungi Email',
      loading: 'Caricamento blacklist...',
      stats: {
        total: 'Totale',
        hardBounce: 'Hard Bounce',
        softBounce: 'Soft Bounce',
        spam: 'Spam',
        unsubscribed: 'Disiscritti',
        manual: 'Manuali'
      },
      filters: {
        searchPlaceholder: 'Cerca email...',
        allReasons: 'Tutti i motivi',
        unsubscribed: 'Disiscritti'
      },
      table: {
        email: 'Email',
        reason: 'Motivo',
        bounceCount: 'Bounce Count',
        lastBounce: 'Ultimo Bounce',
        notes: 'Note',
        actions: 'Azioni'
      },
      badges: {
        hardBounce: 'Hard Bounce',
        softBounce: 'Soft Bounce',
        spam: 'Spam',
        unsubscribed: 'Disiscritto',
        manual: 'Manuale'
      },
      emptyState: {
        noResults: 'Nessuna email trovata con i filtri applicati',
        empty: 'Nessuna email in blacklist'
      },
      confirmReactivate: 'Sei sicuro di voler riattivare {email}? Questa email potrà ricevere nuovamente newsletter.',
      confirmDelete: 'Sei sicuro di voler rimuovere {email} dalla blacklist?',
      addModal: {
        title: 'Aggiungi Email alla Blacklist',
        emailLabel: 'Indirizzo Email',
        emailPlaceholder: 'email@esempio.com',
        notesLabel: 'Note (opzionale)',
        notesPlaceholder: 'Motivo dell\'aggiunta...',
        cancel: 'Annulla',
        add: 'Aggiungi alla Blacklist'
      },
      buttons: {
        reactivate: 'Riattiva email',
        remove: 'Rimuovi dalla blacklist'
      },
      messages: {
        invalidEmail: 'Inserisci un indirizzo email valido',
        alreadyExists: 'Questa email è già nella blacklist',
        addError: 'Errore durante l\'aggiunta alla blacklist',
        reactivateError: 'Errore durante la riattivazione dell\'email',
        deleteError: 'Errore durante la rimozione dalla blacklist',
        addedManually: 'Aggiunto manualmente'
      }
    },
    unlayerEditor: {
      tip: 'Suggerimento',
      tipDescription: 'Usa il menu "Merge Tags" sulla destra per inserire le variabili del template. Tutte le tabelle e gli attributi HTML saranno preservati correttamente.',
      applyChanges: 'Applica Modifiche'
    },
    commissionReports: {
      title: 'Report Commissioni',
      subtitle: 'Analisi performance e commissioni per periodo',
      exportExcel: 'Esporta Excel',
      period: 'Periodo',
      userReports: 'Report Utenti',
      noDataAvailable: 'Nessun dato disponibile per il periodo selezionato',
      stats: {
        totalCommissions: 'Commissioni Totali',
        closedDeals: 'Trattative Chiuse',
        avgRate: 'Tasso Medio',
        topPerformer: 'Top Performer'
      },
      table: {
        user: 'Utente',
        group: 'Gruppo',
        deals: 'Trattative',
        totalValue: 'Valore Totale',
        commissions: 'Commissioni',
        rate: 'Tasso %',
        target: 'Obiettivo'
      },
      excel: {
        userName: 'Nome Utente',
        email: 'Email',
        group: 'Gruppo',
        noGroup: 'Nessun gruppo',
        dealsCount: 'Numero Trattative',
        totalDealValue: 'Valore Totale Trattative',
        totalCommissions: 'Commissioni Totali',
        avgCommissionRate: 'Tasso Commissione Medio',
        monthlyTarget: 'Obiettivo Mensile',
        notDefined: 'Non definito',
        targetAchievement: 'Raggiungimento Obiettivo',
        period: 'Periodo',
        sheetName: 'Report Commissioni',
        summarySheetName: 'Riepilogo',
        summary: 'Riepilogo',
        value: 'Valore',
        summaryTotalCommissions: 'Commissioni Totali',
        summaryTotalDeals: 'Trattative Totali',
        summaryAvgRate: 'Tasso Medio Commissioni',
        summaryTopPerformer: 'Top Performer',
        summaryTopPerformerCommission: 'Commissione Top Performer'
      },
      messages: {
        loadError: 'Errore nel caricamento dei report commissioni',
        exportSuccess: 'Report commissioni esportato con successo',
        exportError: 'Errore durante l\'esportazione del report'
      }
    },
    variableCatalog: {
      categories: {
        company: 'Variabili Azienda',
        client: 'Variabili Cliente',
        deal: 'Variabili Preventivo Base',
        totals: 'Variabili Totali',
        signatures: 'Variabili Firme',
        user: 'Variabili Utente',
        referente: 'Variabili Referente',
        rappresentanteLegale: 'Variabili Rappresentante Legale',
        articles: 'Variabili Articoli'
      },
      conditionalBlocks: {
        title: 'Blocchi Condizionali',
        conditional: 'Condizionale',
        snippet: 'Snippet',
        copy: 'Copia',
        copied: 'Copiato!'
      },
      badges: {
        new: 'Nuovo',
        discounted: 'Scontato',
        original: 'Originale',
        conditional: 'Condizionale'
      },
      howToUse: {
        title: 'Come usare le variabili:',
        clickVariable: 'Clicca su una variabile per inserirla',
        typeForAutocomplete: 'Oppure digita { nell\'editor per l\'autocomplete',
        conditionalContent: 'I blocchi condizionali permettono di mostrare contenuto dinamico',
        ctrlSpace: 'Usa Ctrl+Space nell\'editor per vedere tutti i suggerimenti'
      },
      variables: {
        businessName: 'Ragione sociale',
        logoHorizontal: 'URL del logo orizzontale',
        logoIcon: 'URL del logo icona',
        address: 'Indirizzo completo',
        vatNumber: 'Partita IVA',
        sdiCode: 'Codice SDI',
        email: 'Email',
        phone: 'Telefono',
        termsConditions: 'Termini e condizioni',
        clientBusinessName: 'Ragione sociale cliente',
        clientAddress: 'Indirizzo completo cliente',
        clientVat: 'Partita IVA cliente',
        taxCode: 'Codice fiscale',
        clientSdi: 'Codice SDI cliente',
        pec: 'PEC',
        clientEmail: 'Email cliente',
        clientPhone: 'Telefono cliente',
        assignee: 'Referente',
        salesRep: 'Commerciale assegnato',
        quoteTitle: 'Titolo del preventivo',
        date: 'Data',
        quoteNumber: 'Numero identificativo',
        shippingAddress: 'Luogo di consegna (scelto sull\'offerta, altrimenti la sede del cliente)',
        description: 'Descrizione',
        dealAssignee: 'Assegnatario del deal',
        total: 'Totale complessivo (con sconti)',
        monthlyTotal: 'Totale servizi mensili (con sconti)',
        annualTotal: 'Totale servizi annuali (con sconti)',
        onetimeTotal: 'Totale one-time scontato',
        onetimeOriginal: 'Totale one-time prima degli sconti',
        totalWords: 'Totale in lettere',
        costTotal: 'Totale costi interni',
        costTotalWords: 'Totale costi in lettere',
        legalSignature: 'Firma del legale rappresentante',
        clientSignature: 'Firma cliente (solo firma digitale)',
        signatureDate: 'Data firma digitale',
        signatureTime: 'Ora firma digitale',
        clientIp: 'IP del cliente che ha firmato',
        userName: 'Nome utente corrente',
        userEmail: 'Email utente corrente',
        referenteName: 'Nome referente',
        referentePhone: 'Telefono referente',
        referenteEmail: 'Email referente',
        referenteRole: 'Ruolo referente',
        referenteArea: 'Area referente',
        rappresentanteLegaleName: 'Nome rappresentante legale',
        rappresentanteLegalePhone: 'Telefono rappresentante legale',
        rappresentanteLegaleEmail: 'Email rappresentante legale',
        rappresentanteLegaleRole: 'Ruolo rappresentante legale',
        rappresentanteLegaleArea: 'Area rappresentante legale',
        productsTotal: 'Totale articoli'
      },
      conditionalDescriptions: {
        hasOnetimeDiscount: 'Mostra contenuto diverso se ci sono sconti sui servizi one-time',
        hasOnetimeDiscountExample: 'Evidenzia quando ci sono sconti sui servizi one-time',
        hasServices: 'Verifica se ci sono servizi nel preventivo',
        hasServicesExample: 'Mostra un messaggio se non ci sono servizi',
        hasProducts: 'Verifica se ci sono articoli nel preventivo',
        hasProductsExample: 'Mostra un messaggio se non ci sono articoli',
        client: 'Verifica se c\'è un cliente associato al preventivo',
        clientExample: 'Mostra informazioni cliente solo se presente',
        digitalSignature: 'Contenuto visibile solo nel PDF firmato digitalmente',
        digitalSignatureExample: 'Informazioni di firma digitale',
        unlessDigitalSignature: 'Contenuto visibile solo nel PDF normale (non firmato)',
        unlessDigitalSignatureExample: 'Spazio per firma manuale nei PDF non digitali',
        discountAmount: 'Verifica se un servizio ha uno sconto (usa dentro {{#each services}})',
        discountAmountExample: 'Mostra prezzo originale barrato se c\'è sconto',
        eachProducts: 'Cicla sugli articoli del preventivo, separati dai servizi',
        eachProductsExample: 'Mostra una riga tabella per ogni articolo con nome, SKU, quantità, prezzo e sconto. Disponibili anche {{discount}}, {{discount_percentage}}, {{packaging}} (es. 5 × 1.000 g) con {{#if discount}} / {{#if packaging}}',
        referente: 'Verifica se il cliente ha un contatto referente',
        referenteExample: 'Mostra informazioni del referente solo se presente',
        rappresentanteLegale: 'Verifica se il cliente ha un contatto rappresentante legale',
        rappresentanteLegaleExample: 'Mostra informazioni del rappresentante legale solo se presente'
      }
    },
    ficClientImport: {
      modalTitle: 'Importa Clienti nel CRM',
      syncRequired: 'Sincronizzazione Necessaria',
      syncDescription: 'Prima di importare i clienti nel CRM, è necessario sincronizzarli da Fatture in Cloud.',
      syncOutdated: '(I dati non sono stati sincronizzati negli ultimi 7 giorni o la tabella è vuota)',
      whatWillHappen: 'Cosa succederà:',
      syncAllClients: 'Sincronizzerò tutti i clienti da Fatture in Cloud (senza limiti)',
      canSelectAfter: 'Potrai poi selezionare quali importare nel CRM',
      operationTakesTime: 'L\'operazione potrebbe richiedere alcuni minuti',
      cancel: 'Annulla',
      startSync: 'Avvia Sincronizzazione',
      selectClientsTitle: 'Seleziona Clienti da Importare',
      selectClientsDescription: 'Scegli i clienti da Fatture in Cloud che vuoi importare nel CRM',
      searchPlaceholder: 'Cerca per nome, P.IVA, CF...',
      filterAll: 'Tutti',
      filterUnmapped: 'Solo non mappati',
      filterWithMatch: 'Solo con match',
      noClientsFound: 'Nessun cliente trovato',
      syncFirstMessage: 'Sincronizza prima i clienti da FIC nella sezione "Sincronizzazione"',
      selectAll: 'Seleziona tutti',
      alreadyMapped: '✓ Già mappato',
      vatLabel: 'P.IVA:',
      taxCodeLabel: 'CF:',
      mappedTo: 'Mappato a:',
      clientsSelected: 'clienti selezionati',
      importOptionsTitle: 'Opzioni di Importazione',
      importOptionsDescription: 'Scegli come gestire i clienti che esistono già nel CRM',
      mappingOnly: 'Solo Mapping',
      mappingOnlyDescription: 'Crea solo il collegamento tra cliente CRM e FIC senza modificare i dati esistenti',
      overwriteCRM: 'Sovrascrivi Dati CRM',
      overwriteCRMDescription: 'Aggiorna i clienti CRM esistenti con i dati da Fatture in Cloud',
      matchingLogic: 'Logica di Matching',
      matchExactVat: 'Match esatto su P.IVA o Codice Fiscale',
      matchEmail: 'Match su email (se disponibile)',
      noMatchCreate: 'Se nessun match trovato, viene creato un nuovo cliente',
      resultsTitle: 'Risultati Importazione',
      imported: 'Importati',
      mapped: 'Mappati',
      errors: 'Errori',
      importErrors: 'Errori di Importazione',
      clientId: 'Cliente ID:',
      importComplete: 'Import completato! I clienti sono ora disponibili nel CRM e collegati a Fatture in Cloud.',
      back: 'Indietro',
      close: 'Chiudi',
      next: 'Avanti',
      importing: 'Importazione...',
      importClients: 'Importa Clienti',
      syncModalTitle: 'Sincronizzazione Clienti da Fatture in Cloud',
      messages: {
        checkSyncError: 'Errore durante il controllo dello stato di sincronizzazione',
        syncStarting: 'Avvio sincronizzazione clienti...',
        fetchingFromFIC: 'Recupero clienti da Fatture in Cloud...',
        functionCallError: 'Errore durante la chiamata alla funzione',
        noDataReceived: 'Nessun dato ricevuto dalla sincronizzazione',
        savingToDb: 'Salvataggio nel database...',
        syncCompleted: 'Sincronizzazione completata con successo!',
        syncSuccess: 'Sincronizzazione clienti completata: {imported} nuovi, {updated} aggiornati',
        syncError: 'Errore durante la sincronizzazione dei clienti',
        loadError: 'Errore durante il caricamento dei clienti',
        selectAtLeastOne: 'Seleziona almeno un cliente da importare',
        importSuccess: 'Importati con successo {count} clienti',
        importWithErrors: 'Import completato con {count} errori',
        importError: 'Errore durante l\'importazione dei clienti',
        selectClient: 'Seleziona almeno un cliente'
      }
    },
    apiDocumentation: {
      title: 'Documentazione API',
      subtitle: 'Integra Flusia con le tue applicazioni',
      manageTokens: 'Gestisci API Tokens',
      copyCode: 'Copia codice',
      copyId: 'Copia ID',
      sections: {
        gettingStarted: 'Iniziare',
        zapierIntegration: 'Integrazione Zapier',
        leadsApi: 'Leads API',
        pipelinesApi: 'Pipeline API',
        dealsApi: 'Deals API',
        tasksApi: 'Tasks API',
        projectsApi: 'Projects API',
        servicesApi: 'Services API',
        activitiesApi: 'Activities API',
        invoicesApi: 'Invoices API (Sola Lettura)',
        whatsappApi: 'WhatsApp API',
        newsletterApi: 'Newsletter API',
        warehouseApi: 'Warehouse API',
        errorCodes: 'Codici di Errore',
        bestPractices: 'Best Practices'
      },
      gettingStarted: {
        step1: 'Genera un token API dalla sezione',
        step1Link: '"API Tokens"',
        step1End: 'nelle impostazioni',
        step2: 'Includi il token nell\'header',
        step2End: 'di ogni richiesta',
        step3: 'L\'API base URL è:',
        apiGatewayTitle: 'API Gateway - api.flusia.it',
        apiGatewayDescription: 'Tutte le richieste API sono gestite dal subdomain dedicato',
        apiGatewayFeatures: {
          validateToken: 'Valida automaticamente il tuo token API',
          addJwt: 'Aggiunge l\'autenticazione JWT necessaria per Supabase',
          rateLimit: 'Applica rate limiting per prevenire abusi (100 req/min per token)',
          logging: 'Registra tutte le chiamate per debugging e analytics'
        },
        dontWorry: 'Non devi preoccuparti di gestire JWT o chiavi Supabase - usa solo il tuo token API!',
        authentication: 'Autenticazione',
        authDescription: 'Ogni richiesta deve includere il token API nell\'header Authorization:',
        granularPermissions: '🔐 Permessi Granulari',
        permissionsDescription: 'Ogni token API ha permessi granulari per ciascuna risorsa. Quando crei un token, puoi specificare per ogni risorsa (leads, deals, tasks, projects, activities):',
        permissionRead: 'Permette operazioni GET (lista e dettaglio risorse)',
        permissionWrite: 'Permette operazioni POST e PUT (creazione e modifica)',
        permissionDelete: 'Permette operazioni DELETE (eliminazione permanente)',
        permissionsWarning: 'Se provi a eseguire un\'operazione senza il permesso necessario, riceverai un errore',
        permissionsWarningMessage: 'con il messaggio "Insufficient permissions to [action] [resource]".',
        permissionsExample: 'Esempio struttura permessi:',
        ipWhitelist: '🌐 IP Whitelist',
        ipWhitelistDescription: 'Puoi opzionalmente configurare una whitelist di indirizzi IP autorizzati per ogni token. Se configurata, solo le richieste provenienti dagli IP specificati potranno utilizzare il token.',
        ipSingleAddress: 'Supporta indirizzi IPv4 singoli (es.',
        ipCidr: 'Supporta notazione CIDR per range (es.',
        ipEmpty: 'Se la whitelist è vuota (non configurata), il token funziona da qualsiasi IP',
        ipDenied: 'Accesso Negato:',
        ipDeniedMessage: 'Se un IP non autorizzato prova ad usare il token, riceverà un errore'
      },
      zapier: {
        intro: 'Flusia è ora disponibile su Zapier!',
        introDescription: 'Connetti Flusia a oltre 6.000 app e automatizza i tuoi workflow: crea lead da form, sincronizza contatti, invia notifiche e molto altro.',
        inviteTitle: 'Accetta l\'invito per utilizzare Flusia su Zapier',
        inviteDescription: 'L\'app Flusia è attualmente disponibile tramite invito. Clicca il link qui sotto per accettare l\'invito e abilitare Flusia nel tuo account Zapier.',
        acceptInvite: 'Accetta invito Zapier →',
        howToStart: '🚀 Come Iniziare',
        step1: 'Accetta l\'invito Zapier',
        step1Desc: 'Clicca il pulsante qui sopra per accettare l\'invito e abilitare Flusia nel tuo account Zapier.',
        step2: 'Genera un API Token',
        step2Desc: 'Vai nella sezione API Tokens qui sopra e crea un nuovo token con i permessi necessari (almeno "Leads").',
        step3: 'Cerca "Flusia" su Zapier',
        step3Desc: 'Vai su zapier.com, crea un nuovo Zap e cerca "Flusia" come app.',
        step4: 'Connetti il tuo account',
        step4Desc: 'Quando richiesto, incolla il token API che hai generato. Zapier verificherà automaticamente la connessione.',
        step5: 'Configura il tuo Zap',
        step5Desc: 'Scegli un trigger (es. nuovo contatto da Typeform) e l\'azione Flusia (es. "New Lead"). Mappa i campi e attiva!',
        availableActions: '⚡ Azioni Disponibili',
        newLeadAction: 'New Lead',
        newLeadDescription: 'Crea automaticamente un nuovo lead in Flusia quando si verifica un evento in un\'altra app.',
        requiredFields: 'Campi Obbligatori:',
        availableFields: 'Campi disponibili:',
        field: 'Campo',
        description: 'Descrizione',
        notes: 'Note',
        required: 'Obbligatorio',
        moreFieldsComing: 'Altri campi in arrivo:',
        moreFieldsDescription: 'Nelle prossime versioni saranno disponibili ulteriori campi come indirizzo completo, P.IVA, codice fiscale, WhatsApp, PEC e altri dati aziendali.',
        useCases: '💡 Esempi di Utilizzo',
        autoAssignment: '🎯 Auto-Assegnazione Lead',
        autoAssignmentDescription: 'I lead creati tramite Zapier possono essere assegnati automaticamente agli utenti del tuo team in base a:',
        autoAssignByInterest: 'Categorie di interesse - Assegna a chi gestisce specifiche categorie',
        autoAssignByPipeline: 'Pipeline - Assegna in base alla pipeline selezionata',
        autoAssignBySource: 'Fonte lead - Assegna in base a da dove arriva il lead',
        autoAssignLogic: 'Il sistema assegna automaticamente il lead all\'utente del pool con meno carico di lavoro (meno lead/deal attivi).',
        configureAutoAssign: 'Configura Auto-Assegnazione →',
        comingSoon: '🔔 In Arrivo',
        comingSoonDescription: 'Stiamo lavorando a nuovi trigger e azioni:',
        troubleshooting: 'Problemi di Connessione',
        troubleConnectionFails: 'La connessione fallisce',
        troubleConnectionFailsDesc: 'Verifica di aver copiato correttamente il token API (deve iniziare con',
        troublePermissions: 'Errore "permessi insufficienti"',
        troublePermissionsDesc: 'Il token non ha i permessi necessari. Crea un nuovo token con il permesso "Leads" abilitato.',
        troubleLeadNotCreated: 'Lead non viene creato',
        troubleLeadNotCreatedDesc: 'Assicurati di aver mappato sia il Nome che la Pipeline (campi obbligatori).'
      },
      leads: {
        importantInfo: 'Informazioni Importanti',
        orgIsolation: 'Organization Isolation:',
        orgIsolationDesc: 'Tutti i dati sono automaticamente filtrati per la tua organization.',
        pipelineDefault: 'Pipeline Default:',
        pipelineDefaultDesc: 'Se pipeline_id e pipeline_stage_id sono null, il lead è nella pipeline di default.',
        relationalData: 'Dati Relazionali:',
        relationalDataDesc: 'Puoi includere contacts, selected_tags, e selected_interest_categories in fase di creazione/aggiornamento.',
        autoAssignment: 'Auto-Assegnazione Lead',
        autoAssignmentDesc: 'Se crei un lead senza specificare il campo assigned_to, il sistema può assegnarlo automaticamente in base a regole configurabili.',
        howItWorks: 'Come funziona:',
        autoAssignRules: 'Le regole vengono valutate in ordine di priorità (la prima che matcha vince)',
        autoAssignFilters: 'Ogni regola può filtrare per: Categorie di Interesse, Pipeline, Fonte Lead',
        autoAssignConditions: 'Le condizioni possono essere combinate con AND o OR',
        autoAssignAlgorithm: 'L\'utente viene scelto dal pool della regola usando l\'algoritmo "least-loaded + random"',
        autoAssignDefault: 'Se nessuna regola matcha, viene usato il pool di default',
        autoAssignConfig: 'Configurazione:',
        autoAssignConfigPath: 'Vai su Impostazioni → Clienti e Leads → Auto-Assegnazione Lead',
        autoAssignTip: 'Tip:',
        autoAssignTipDesc: 'Usa la sezione "Dati di Riferimento" per ottenere gli ID da usare nelle regole.',
        parameters: 'Parametri',
        supportedFields: 'Campi Supportati (70+ campi)',
        relationalDataTitle: 'Dati Relazionali',
        pipelineTitle: 'Pipeline',
        pipelineOmit: 'Se ometti pipeline_id/pipeline_stage_id:',
        pipelineOmitDesc: 'Il lead sarà nella pipeline di default (entrambi null)',
        pipelineIdOnly: 'Se specifichi solo pipeline_id:',
        pipelineIdOnlyDesc: 'Il lead verrà assegnato automaticamente al primo stage attivo della pipeline',
        pipelineBoth: 'Se specifichi entrambi:',
        pipelineBothDesc: 'Devi assicurarti che lo stage appartenga alla pipeline specificata',
        pipelineWarning: 'Se specifichi pipeline_stage_id, devi SEMPRE specificare anche pipeline_id',
        updateNote: 'Nota:',
        updateNoteDesc: 'L\'aggiornamento supporta tutti i campi del POST. Se includi contacts, selected_tags, o selected_interest_categories, verranno sostituiti completamente (non merge).',
        deleteWarning: '⚠️ Attenzione:',
        deleteWarningDesc: 'Questa operazione elimina permanentemente il lead e tutti i dati associati (contacts, tags, interests). Non è reversibile.'
      },
      pipelines: {
        note: 'Nota:',
        noteDesc: 'Le pipeline permettono di organizzare i lead in flussi di lavoro personalizzati con stage specifici.',
        management: 'Pipeline Management',
        stageManagement: 'Stage Management'
      },
      deals: {
        listDescription: 'Lista di tutte le trattative',
        detailDescription: 'Dettagli di una trattativa con servizi inclusi',
        createDescription: 'Crea una nuova trattativa',
        updateDescription: 'Aggiorna una trattativa',
        statusDescription: 'Cambia lo stato di una trattativa',
        deleteDescription: 'Elimina una trattativa'
      },
      tasks: {
        listDescription: 'Lista di tutte le attività',
        detailDescription: 'Dettagli di un\'attività con commenti e checklist',
        createDescription: 'Crea una nuova attività',
        updateDescription: 'Aggiorna un\'attività',
        completeDescription: 'Segna un\'attività come completata',
        commentsDescription: 'Ottieni i commenti di un\'attività',
        addCommentDescription: 'Aggiungi un commento',
        deleteDescription: 'Elimina un\'attività'
      },
      projects: {
        listDescription: 'Lista di tutti i progetti',
        detailDescription: 'Dettagli di un progetto con cliente e tasks',
        createDescription: 'Crea un nuovo progetto',
        updateDescription: 'Aggiorna un progetto',
        statusDescription: 'Cambia lo stato di un progetto',
        deleteDescription: 'Elimina un progetto'
      },
      services: {
        listDescription: 'Lista di tutti i servizi',
        detailDescription: 'Dettagli di un servizio',
        createDescription: 'Crea un nuovo servizio',
        updateDescription: 'Aggiorna un servizio',
        deleteDescription: 'Elimina un servizio'
      },
      activities: {
        listDescription: 'Lista di tutte le attività cliente',
        detailDescription: 'Dettagli di un\'attività cliente',
        createDescription: 'Registra una nuova attività cliente',
        updateDescription: 'Aggiorna un\'attività',
        deleteDescription: 'Elimina un\'attività'
      },
      invoices: {
        warning: 'Nota:',
        warningDesc: 'Le fatture sono di sola lettura via API. La creazione è gestita internamente dal sistema.',
        listDescription: 'Lista di tutte le fatture',
        detailDescription: 'Dettagli di una fattura con items e cliente'
      },
      whatsapp: {
        messagesListDescription: 'Lista di tutti i messaggi WhatsApp',
        messageDetailDescription: 'Dettagli di un messaggio',
        sendDescription: 'Invia un messaggio WhatsApp',
        sessionsListDescription: 'Lista sessioni WhatsApp Worker attive',
        sessionDetailDescription: 'Dettagli di una sessione'
      },
      newsletter: {
        campaignsListDescription: 'Lista di tutte le campagne newsletter',
        campaignDetailDescription: 'Dettagli di una campagna',
        createCampaignDescription: 'Crea una nuova campagna',
        updateCampaignDescription: 'Aggiorna una campagna',
        deleteCampaignDescription: 'Elimina una campagna',
        subscribersListDescription: 'Lista di tutti gli iscritti',
        addSubscriberDescription: 'Aggiungi un iscritto',
        removeSubscriberDescription: 'Rimuovi un iscritto',
        listsDescription: 'Lista di tutte le liste newsletter'
      },
      warehouse: {
        productsListDescription: 'Lista di tutti i prodotti',
        productDetailDescription: 'Dettagli di un prodotto',
        createProductDescription: 'Crea un nuovo prodotto',
        updateProductDescription: 'Aggiorna un prodotto',
        deleteProductDescription: 'Elimina un prodotto',
        movementsListDescription: 'Lista movimenti di magazzino',
        createMovementDescription: 'Registra un movimento di magazzino',
        zonesListDescription: 'Lista zone di magazzino',
        reservationsListDescription: 'Lista prenotazioni stock'
      },
      errorCodes: {
        title: 'Codici di Errore',
        code: 'Codice',
        description: 'Descrizione',
        code200: 'Success - Richiesta completata',
        code201: 'Created - Risorsa creata',
        code400: 'Bad Request - Parametri mancanti o non validi',
        code401: 'Unauthorized - API Token mancante o non valido',
        code403: 'Forbidden - Permessi insufficienti',
        code404: 'Not Found - Risorsa non trovata',
        code405: 'Method Not Allowed - Metodo HTTP non supportato',
        code500: 'Internal Server Error - Errore del server',
        responseFormat: 'Formato risposta errore'
      },
      bestPractices: {
        title: 'Best Practices',
        rateLimiting: 'Rate Limiting:',
        rateLimitingDesc: 'Implementa exponential backoff per i retry',
        errorHandling: 'Error Handling:',
        errorHandlingDesc: 'Controlla sempre lo status code e gestisci gli errori',
        pagination: 'Paginazione:',
        paginationDesc: 'Usa la paginazione per dataset grandi',
        filters: 'Filtri:',
        filtersDesc: 'Filtra lato server usando i query parameters',
        security: 'Sicurezza:',
        securityDesc: 'Non esporre mai i token API nel codice client-side',
        caching: 'Caching:',
        cachingDesc: 'Implementa caching per ridurre il numero di chiamate API'
      },
      referenceData: {
        title: 'Dati di Riferimento',
        searchPlaceholder: 'Cerca per nome o ID...',
        loading: 'Caricamento dati...',
        interestCategories: 'Categorie di Interesse',
        pipelines: 'Pipeline',
        leadSources: 'Fonti Lead',
        noResults: 'Nessun risultato per',
        noDataConfigured: 'Nessun dato di riferimento configurato'
      },
      noCodeInstructions: {
        zapier: {
          step1: '1. Aggiungi un\'azione "Webhooks by Zapier"',
          step2: '2. Scegli "Custom Request"',
          step3: '3. Configura:',
          method: 'Method:',
          url: 'URL:',
          headers: 'Headers:',
          data: 'Data:',
          step4: '4. Testa la connessione'
        },
        make: {
          step1: '1. Aggiungi modulo "HTTP" → "Make a request"',
          step2: '2. Configura:',
          bodyType: 'Body type: Raw',
          contentType: 'Content type: JSON',
          requestContent: 'Request content:',
          step3: '3. Esegui per testare'
        },
        n8n: {
          step1: '1. Aggiungi nodo "HTTP Request"',
          step2: '2. Configura:',
          authentication: 'Authentication: Header Auth',
          name: 'Name:',
          value: 'Value:',
          bodyContentType: 'Body Content Type: JSON',
          body: 'Body:',
          step3: '3. Esegui il nodo per testare'
        }
      }
    },
    cadenceSettings: {
      title: 'Periodicità ordini',
      description: 'Configura soglie alert cliente in calo e reminder riordino',
      declineSection: 'Alert cliente in calo',
      reorderSection: 'Reminder riordino',
      baselineSection: 'Baseline calcolo',
      overridesSection: 'Override per prodotto',
      declineDropPct: 'Soglia calo quantità (%)',
      declineLookback: 'Finestra confronto (giorni)',
      reorderAdvance: 'Fattore anticipo (×)',
      reorderOverdue: 'Fattore ritardo (×)',
      minOrders: 'N. ordini minimo',
      baselineLookback: 'Finestra baseline (giorni)',
      enabled: 'Abilitato',
      save: 'Salva impostazioni',
      reset: 'Ripristina default',
      saved: 'Impostazioni salvate'
    },
    duplicates: {
      title: 'Gestione duplicati',
      description: 'Trova e unisci clienti e lead duplicati',
      tabScan: 'Scansione',
      tabSearch: 'Ricerca',
      rescan: 'Riscansiona',
      scanStarted: 'Scansione avviata. I risultati saranno disponibili al termine.',
      scanInProgress: 'Scansione in corso...',
      noRecentScan: 'Nessuna scansione recente',
      clustersFound: '{count} cluster di duplicati trovati',
      loading: 'Caricamento...',
      searchPlaceholder: 'Inserisci ID cliente o nome...',
      search: 'Cerca',
      examineAndMerge: 'Esamina e unisci',
      confirmed: 'Sicuri',
      possible: 'Possibili',
      noResults: 'Nessun duplicato trovato. Esegui una scansione per cercare record simili.',
      mergeViewTitle: 'Esamina e unisci duplicati',
      master: 'Master',
      linked: 'Collegato',
      noLinkedData: 'Nessun dato collegato',
      conflictingFields: 'Campi in conflitto — scegli quale valore mantenere',
      resultingState: 'Stato risultante',
      backendStateRule: 'Il backend applica la regola "cliente vince" se non viene specificato.',
      confirmTitle: 'Conferma unione',
      confirmText: 'Stai per unire',
      confirmInto: 'in',
      itemsToMove: 'Verranno spostati',
      confirmWarning: 'Il record verrà disattivato. L\'operazione può essere annullata subito dopo.',
      cancel: 'Annulla',
      merge: 'Unisci',
      mergeSuccess: 'Unione completata.',
      mergeError: 'Errore durante l\'unione',
      undoMerge: 'Annulla',
      undoSuccess: 'Unione annullata',
      undoError: 'Impossibile annullare l\'unione',
      close: 'Chiudi'
    }
  }
};
